import React, { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertTriangleIcon, ArrowLeftIcon, Avatar, Button, Card, CenteredView, CheckIcon, Checkbox, Chip, CircleUserIcon, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, ErrorBoundary, IconButton, LanguagesIcon, LoadingButton, LucideIconByName, MailIcon, Menu, MenuItem, MoonIcon, Popover, SearchBar, SunIcon, SunMoonIcon, Tab, Tabs, TextField, Tooltip, Trash2Icon, Typography, Wand2Icon, cls, colorClassesMapping, coolIconKeys, defaultBorderMixin, getColorSchemeForKey, getColorSchemeForSeed, iconKeys, iconSize, lazyChunk } from "@rebasepro/ui";
import { ALL_WHERE_FILTER_OPS, DEFAULT_DATA_SOURCE_KEY, DEFAULT_FILTERABLE_RELATION_KINDS, DEFAULT_STORAGE_SOURCE_KEY, EntityReference, EntityRelation, GeoPoint, RebaseApiError, Vector, getDataSourceCapabilities, isLazyComponentRef, isRelationalCollectionConfig, resourceKeyOf } from "@rebasepro/types";
import { UNRENDERED_SLOTS, resolveAdminCollection } from "@rebasepro/cms-types";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { SnackbarProvider as SnackbarProvider$1, useSnackbar } from "notistack";
import { buildRebaseData, canCreateEntity, canDeleteEntity, canEditEntity, canReadCollection, evaluateCondition, getChildViewDeclaringProperties, getChildViewRelationPropertyKeys, getEntityChildViews, getLabelOrConfigFrom, getPrimaryKeys, getSubcollections, isPropertyBuilder, normalizeOrderBy, resolveStorageFilenameString, resolveStoragePathString, resolveStorageSource, serializeOrderBy, stripCollectionPath, wrapAsEntityData, wrapAsSdkData } from "@rebasepro/common";
import { getTitlePropertyKey as getTitlePropertyKey$1, removeInitialAndTrailingSlashes as removeInitialAndTrailingSlashes$1 } from "@rebasepro/app";
import { Link, Routes, createBrowserRouter, useBlocker, useLocation } from "react-router";
import { generateForeignKeyName, hashString, isArrayValue, isObject, isPlainObject, isRecordValue, mergeDeep, randomString, readStoredJson, slugify, writeStoredJson } from "@rebasepro/utils";
import { I18nextProvider, initReactI18next, useTranslation as useTranslation$1 } from "react-i18next";
import { deepEqual } from "fast-equals";
import i18next from "i18next";
import { RouterProvider } from "react-router/dom";
import Fuse from "fuse.js";
import Compressor from "compressorjs";
//#region src/core/PluginProviderStack.tsx
/**
* Wraps children with all provider components from plugins that match the given scope.
*
* Replaces the 3 copy-pasted `plugins.forEach(plugin => { if (plugin.form?.provider) ... })` patterns
* in Rebase.tsx, EditViewBinding.tsx, and PopupFormField.tsx.
*
* @param plugins - Array of plugins to extract providers from.
* @param scope - `"root"` or `"form"` — which providers to apply.
* @param scopeProps - Additional props passed to each provider component.
* @param children - The content to wrap.
*
* @group Core
*/
function PluginProviderStack({ plugins, scope, scopeProps, children }) {
	return plugins.flatMap((p) => p.providers ?? []).filter((p) => p.scope === scope).reduceRight((acc, provider) => /* @__PURE__ */ jsx(provider.Component, {
		...provider.props,
		...scopeProps,
		children: acc
	}), children);
}
//#endregion
//#region src/core/PluginLifecycleManager.tsx
/**
* Render-less component that manages plugin lifecycle hooks.
*
* - Calls `lifecycle.onMount(context)` when plugins mount.
* - Calls `lifecycle.onUnmount()` when plugins unmount.
* - Subscribes to auth state changes and calls `lifecycle.onAuthStateChange`.
*
* Mount this component inside the Rebase tree, below PluginProviderStack,
* so that the RebaseContext is fully available.
*
* @internal
*/
function PluginLifecycleManager({ plugins, context }) {
	const mountedRef = useRef(false);
	const prevUserRef = useRef(void 0);
	useEffect(() => {
		if (mountedRef.current) return;
		mountedRef.current = true;
		for (const plugin of plugins) if (plugin.lifecycle?.onMount) try {
			const result = plugin.lifecycle.onMount(context);
			if (result instanceof Promise) result.catch((err) => console.error(`[Rebase] Plugin "${plugin.key}" onMount error:`, err));
		} catch (err) {
			console.error(`[Rebase] Plugin "${plugin.key}" onMount error:`, err);
		}
		return () => {
			mountedRef.current = false;
			for (const plugin of plugins) if (plugin.lifecycle?.onUnmount) try {
				plugin.lifecycle.onUnmount();
			} catch (err) {
				console.error(`[Rebase] Plugin "${plugin.key}" onUnmount error:`, err);
			}
		};
	}, []);
	const currentUser = context.authController?.user ?? null;
	useEffect(() => {
		if (prevUserRef.current === void 0) {
			prevUserRef.current = currentUser;
			return;
		}
		if (prevUserRef.current?.uid === currentUser?.uid) return;
		prevUserRef.current = currentUser;
		for (const plugin of plugins) if (plugin.lifecycle?.onAuthStateChange) try {
			plugin.lifecycle.onAuthStateChange(currentUser);
		} catch (err) {
			console.error(`[Rebase] Plugin "${plugin.key}" onAuthStateChange error:`, err);
		}
	}, [currentUser, plugins]);
	return null;
}
//#endregion
//#region src/contexts/SnackbarProvider.tsx
var SnackbarProvider = ({ children }) => {
	return /* @__PURE__ */ jsx(SnackbarProvider$1, {
		maxSnack: 3,
		preventDuplicate: true,
		autoHideDuration: 3500,
		children
	});
};
var ModeControllerContext = React.createContext({
	mode: "light",
	setMode: (mode) => {}
});
var ModeControllerProvider = ModeControllerContext.Provider;
var AdminModeControllerContext = React.createContext({
	mode: "cms",
	setMode: (mode) => {}
});
var AdminModeControllerProvider = AdminModeControllerContext.Provider;
var EffectiveRoleControllerContext = React.createContext({
	effectiveRole: null,
	setEffectiveRole: (_role) => {}
});
var EffectiveRoleControllerProvider = EffectiveRoleControllerContext.Provider;
//#endregion
//#region src/contexts/AuthControllerContext.tsx
/** `null` outside `<Rebase>` — see {@link RebaseDataContext}. */
var AuthControllerContext = React.createContext(null);
//#endregion
//#region src/contexts/DataDriverContext.tsx
var DataDriverContext = React.createContext({});
/** @internal */
var DataSourcesContext = React.createContext({
	registry: {},
	sources: {}
});
/**
* Access the data-source configuration provided to `<Rebase>` via the
* `dataSources` prop.
* @internal
*/
var useDataSources = () => React.useContext(DataSourcesContext);
//#endregion
//#region src/contexts/RebaseDataContext.tsx
/**
* `null` outside `<Rebase>`, so {@link useData} can say so.
*
* The default used to be `{} as RebaseData` — a lie the type system agreed
* with. A hook called outside the tree handed back an empty object, and the
* first thing anyone does with it is `data.collection("posts")`, which fails as
* "data.collection is not a function" somewhere in a component's render. That
* names neither the hook nor the missing provider.
*/
var RebaseDataContext = React.createContext(null);
//#endregion
//#region src/contexts/AnalyticsContext.tsx
var AnalyticsContext = React.createContext({});
//#endregion
//#region src/contexts/StorageSourceContext.tsx
/**
* Three states, and they mean different things:
*
* - `null` — outside `<Rebase>`.
* - `undefined` — inside it, but the project configured no storage. `<Rebase>`
*   already warns about this on the console.
* - a source — the resolved default storage.
*
* See {@link RebaseDataContext} for why the default is not `{}`.
*/
var StorageSourceContext = React.createContext(null);
/** @internal */
var StorageSourcesContext = React.createContext({
	registry: {},
	sources: {}
});
/**
* Access the storage-source configuration provided to `<Rebase>` via the
* `storageSources` prop.
* @internal
*/
var useStorageSources = () => React.useContext(StorageSourcesContext);
//#endregion
//#region src/contexts/UserConfigurationPersistenceContext.tsx
var UserConfigurationPersistenceContext = React.createContext(void 0);
//#endregion
//#region src/contexts/DialogsProvider.tsx
/** `null` outside `<Rebase>` — see `RebaseDataContext`. */
var DialogsControllerContext = React.createContext(null);
var DialogsProvider = ({ children }) => {
	const [dialogEntries, setDialogEntries] = useState([]);
	const dialogEntriesRef = useRef(dialogEntries);
	const updateDialogEntries = (newPanels) => {
		dialogEntriesRef.current = newPanels;
		setDialogEntries(newPanels);
	};
	const close = useCallback(() => {
		if (dialogEntries.length === 0) return;
		const updatedPanels = [...dialogEntriesRef.current.slice(0, -1)];
		updateDialogEntries(updatedPanels);
	}, [dialogEntries]);
	const open = useCallback((dialogEntry) => {
		const updatedPanels = [...dialogEntriesRef.current, dialogEntry];
		updateDialogEntries(updatedPanels);
		return { closeDialog: () => {
			const updatedPanels = dialogEntriesRef.current.filter((e) => e.key !== dialogEntry.key);
			updateDialogEntries(updatedPanels);
		} };
	}, [dialogEntries]);
	const dialogsController = useMemo(() => ({
		open,
		close
	}), [open, close]);
	return /* @__PURE__ */ jsxs(DialogsControllerContext.Provider, {
		value: dialogsController,
		children: [children, dialogEntries.map((entry, i) => /* @__PURE__ */ jsx(entry.Component, {
			open: true,
			closeDialog: close,
			...entry.props
		}, `dialog_${i}`))]
	});
};
//#endregion
//#region src/contexts/RebaseClientInstanceContext.tsx
/**
* Context that exposes the full RebaseClient instance.
* Used by the JS Editor to give developer scripts access to `client.data`, `client.auth`, etc.
*/
var RebaseClientInstanceContext = React.createContext(void 0);
//#endregion
//#region src/contexts/CustomizationControllerContext.tsx
/** `null` outside `<Rebase>` — see {@link RebaseDataContext}. */
var CustomizationControllerContext = React.createContext(null);
//#endregion
//#region src/contexts/ComponentOverrideContext.tsx
/** Stable empty reference to avoid re-creating context values on every render. */
var EMPTY_OVERRIDES = {};
var ComponentOverrideContext = createContext({
	globalOverrides: EMPTY_OVERRIDES,
	collectionOverrides: EMPTY_OVERRIDES
});
/**
* Provider set at the `<Rebase>` root level to supply global component overrides.
*
* @internal — Used by the Rebase component. End users set overrides via
* the `components` prop on `<Rebase>`.
*/
function GlobalComponentOverrideProvider({ overrides, children }) {
	const value = useMemo(() => ({
		globalOverrides: overrides ?? EMPTY_OVERRIDES,
		collectionOverrides: EMPTY_OVERRIDES
	}), [overrides]);
	return /* @__PURE__ */ jsx(ComponentOverrideContext.Provider, {
		value,
		children
	});
}
/**
* Provider set at the collection level to layer collection-scoped overrides
* on top of global overrides.
*
* When a collection defines `components`, this provider is mounted
* around the collection's view subtree. Components within the subtree
* will resolve overrides in order: collection → global → default.
*
* @internal — Used by DataCollectionView when a collection has
* `components` defined.
*/
function CollectionComponentOverrideProvider({ overrides, children }) {
	const parent = useContext(ComponentOverrideContext);
	const value = useMemo(() => ({
		globalOverrides: parent.globalOverrides,
		collectionOverrides: overrides ?? EMPTY_OVERRIDES
	}), [parent.globalOverrides, overrides]);
	return /* @__PURE__ */ jsx(ComponentOverrideContext.Provider, {
		value,
		children
	});
}
//#endregion
//#region src/contexts/CollectionScopeContext.tsx
/**
* Carries the collection a UI subtree is currently bound to.
*
* Mounted by every collection binding (main collection views, entity
* edit/detail views, side panels, reference-selection dialogs, import
* previews) via {@link CollectionScopeProvider}. Leaf components read it with
* {@link useCollectionScope} to derive collection-dependent behavior — e.g.
* filter fields resolve the engine's supported operators from
* `collection.engine` — without every intermediate component threading
* collection props.
*
* @internal
*/
var CollectionScopeContext = createContext(void 0);
/**
* Read the collection the current subtree is bound to, or `undefined` when
* rendered outside any collection scope (e.g. a fully standalone table).
*
* @group Hooks
*/
function useCollectionScope() {
	return useContext(CollectionScopeContext);
}
/**
* Scopes a UI subtree to a collection.
*
* Provides two things at once:
* 1. The collection itself — readable via {@link useCollectionScope} —
*    so leaf components (filter fields, previews, …) can derive
*    collection-dependent behavior such as engine capabilities.
* 2. The collection's component overrides (`collection.components`),
*    by mounting {@link CollectionComponentOverrideProvider}.
*
* Mount this wherever a subtree is bound to a specific collection. If you
* build a custom view around `SelectableTable` / `CollectionTableBinding`,
* wrap it in this provider to get engine-aware filters and collection-scoped
* component overrides for free:
*
* ```tsx
* <CollectionScopeProvider collection={myCollection}>
*     <CollectionTableBinding ... />
* </CollectionScopeProvider>
* ```
*
* @group Components
*/
function CollectionScopeProvider({ collection, children }) {
	return /* @__PURE__ */ jsx(CollectionScopeContext.Provider, {
		value: collection,
		children: /* @__PURE__ */ jsx(CollectionComponentOverrideProvider, {
			overrides: collection?.components,
			children
		})
	});
}
//#endregion
//#region src/contexts/CollectionResolverContext.tsx
/**
* Lets the layer that owns the collections hand a resolver *up* to the data
* layer, which needs a collection's primary keys to give its rows an address.
*
* The inversion is forced by the composition: `<Rebase client>` builds the data
* layer, and `<RebaseCMS collections>` sits inside it — so the collections are
* not in scope where the data is created, and `Rebase` cannot take them as a
* prop without giving every headless BaaS app a collections argument it has no
* use for.
*
* Registration is a ref write, so it is safe to call during render — and it has
* to be. The views that fetch rows are *below* the registrar, and child effects
* run before parent effects, so registering in an effect would bind after the
* first page of rows had already been converted.
*/
var CollectionResolverRegistrationContext = React.createContext(() => void 0);
//#endregion
//#region src/hooks/data/useData.tsx
/**
* Use this hook to access the unified data API.
*
* ```ts
* const data = useData();
* const { data: products } = await data.products.find({ where: { status: ["==", "published"] } });
* await data.products.create({ name: "Camera", price: 299 });
* ```
*
* @group Hooks and utilities
*/
var useData = () => {
	const data = useContext(RebaseDataContext);
	if (!data) throw new Error("useData must be used inside <Rebase>");
	return data;
};
//#endregion
//#region src/components/SchemaDriftBanner.tsx
var SchemaDriftContext = createContext({
	reportSchemaDrift: () => {},
	schemaDriftMessage: null,
	dismiss: () => {}
});
function useSchemaDriftContext() {
	return useContext(SchemaDriftContext);
}
/**
* Detects schema drift from an error object.
* Checks both the error code (from `RebaseApiError`) and the message
* for schema drift indicators.
*/
function isSchemaDriftError(error) {
	if (!error || typeof error !== "object") return false;
	if ("code" in error && error.code === "SCHEMA_DRIFT") return true;
	if (error instanceof Error && error.message.includes("Schema drift:")) return true;
	return false;
}
function SchemaDriftProvider({ children }) {
	const [schemaDriftMessage, setSchemaDriftMessage] = useState(null);
	const [dismissed, setDismissed] = useState(false);
	const reportSchemaDrift = useCallback((message) => {
		setSchemaDriftMessage((prev) => prev ?? message);
	}, []);
	const dismiss = useCallback(() => {
		setDismissed(true);
	}, []);
	const value = useMemo(() => ({
		reportSchemaDrift,
		schemaDriftMessage: dismissed ? null : schemaDriftMessage,
		dismiss
	}), [
		reportSchemaDrift,
		schemaDriftMessage,
		dismissed,
		dismiss
	]);
	return /* @__PURE__ */ jsx(SchemaDriftContext.Provider, {
		value,
		children
	});
}
/**
* Persistent banner shown when a schema drift error is detected.
*/
function SchemaDriftBanner({ className }) {
	const { schemaDriftMessage, dismiss } = useSchemaDriftContext();
	if (!schemaDriftMessage) return null;
	return /* @__PURE__ */ jsx(Alert, {
		color: "warning",
		size: "small",
		onDismiss: dismiss,
		className,
		children: /* @__PURE__ */ jsxs("div", {
			className: "flex flex-col gap-1",
			children: [/* @__PURE__ */ jsx(Typography, {
				variant: "label",
				className: "text-amber-800 dark:text-amber-200",
				children: "Schema drift detected"
			}), /* @__PURE__ */ jsx(Typography, {
				variant: "body2",
				className: "text-amber-700 dark:text-amber-300",
				children: schemaDriftMessage
			})]
		})
	});
}
//#endregion
//#region src/hooks/data/collectionQuery.ts
/**
* Build the parameters for one collection read.
*
* Derived options live here rather than at the call sites, so they cannot
* disagree: `searchExplain` rides along exactly when there is a search string
* to explain, which is also the only time it costs anything.
*/
function toFindParams(input) {
	const { searchString, limit, ...rest } = input;
	return {
		...rest,
		...resolveLimit(limit),
		...searchString ? {
			searchString,
			searchExplain: true
		} : {}
	};
}
/**
* Keep a meaningless `limit` inside the browser.
*
* The API refuses a `limit` below 1 — rightly: a window nobody can be served is
* better refused than quietly widened. But a client that computes one has a bug
* *here*, and forwarding it turns that bug into a 400 the user reads as the
* server being broken. That is exactly how a restored, empty scroll entry
* presented: an `Invalid limit: 0` error where the table should have been.
*
* Only the meaningless end is guarded. An absent limit is a supported request —
* every ingress defaults it (`resolveClientListLimit`), so dropping one cannot
* produce an unbounded read — whereas a limit *above* the ceiling states an
* intent, and silently trimming it would hand back a page the caller cannot
* tell apart from the whole collection. That one still travels, and is still
* refused where the ceiling lives.
*/
function resolveLimit(limit) {
	if (limit === void 0) return {};
	if (Number.isInteger(limit) && limit >= 1) return { limit };
	console.warn(`Ignoring an unusable \`limit\` of ${limit} — a page size must be a whole number of at least 1. Reading with the server's default page size instead.`);
	return {};
}
//#endregion
//#region src/collections/summary-property.ts
/**
* Whether a property can stand in for a record in *one line*.
*
* Preview surfaces — the reference card, a list row, a board card — have a
* single line per property and no way to grow. That is a constraint on the
* *value*, not on the property's importance: a Markdown biography is the most
* interesting column on an author, and it is still the wrong thing to paste
* into a 44px card. Ranking it here means the picker can skip it without
* anyone having to configure their way out of a broken card.
*
* Kept beside {@link getTitlePropertyCandidates} because the two answer
* neighbouring questions — "what is this record called" and "what else can be
* said about it in a line" — from the same property schema.
*
* @group Collections
*/
var SUMMARY_RANK = {
	/**
	* No single-line form exists. A map renders as a key/value table and an
	* array of maps as a stack of them; there is no first line to take.
	*/
	UNUSABLE: 0,
	/**
	* Readable in one line, but only as an excerpt — the property holds a
	* document, and the line is its opening. Worth showing when nothing better
	* is available, never in preference to a value that fits whole.
	*/
	EXCERPT: 1,
	/** Short and self-contained: the line *is* the value. */
	DIRECT: 2
};
/**
* File-storage backed content (single image, array of images, generic upload…).
* Preview surfaces give these their own image slot, so they are never also a
* text line.
*
* @group Collections
*/
function isStorageProperty(property) {
	if (!property) return false;
	if (property.type === "string" && (property.storage || property.admin?.urlPreview === "image")) return true;
	if (property.type === "array" && property.of && !Array.isArray(property.of)) {
		const inner = property.of;
		if (inner.type === "string" && (inner.storage || inner.admin?.urlPreview === "image")) return true;
	}
	return false;
}
/**
* True when the property holds free text long enough to be a document rather
* than a value — Markdown, or an explicitly multi-line string.
*
* @group Collections
*/
function isLongTextProperty(property) {
	if (!property || property.type !== "string") return false;
	return Boolean(property.admin?.markdown || property.admin?.multiline);
}
/**
* How well a property reads as one line of a preview. See {@link SUMMARY_RANK}.
*
* Decided from the property *schema* only: no values are consulted, so the
* answer is stable for a collection and can be memoised per collection rather
* than per row.
*
* @group Collections
*/
function rankSummaryProperty(property) {
	if (!property) return SUMMARY_RANK.UNUSABLE;
	if (isStorageProperty(property)) return SUMMARY_RANK.UNUSABLE;
	if (property.type === "map") return SUMMARY_RANK.UNUSABLE;
	if (property.type === "array") {
		if (Array.isArray(property.of)) return SUMMARY_RANK.UNUSABLE;
		if (!property.of) return property.oneOf ? SUMMARY_RANK.UNUSABLE : SUMMARY_RANK.DIRECT;
		const innerRank = rankSummaryProperty(property.of);
		if (innerRank === SUMMARY_RANK.UNUSABLE) return SUMMARY_RANK.UNUSABLE;
		if (innerRank === SUMMARY_RANK.EXCERPT) return SUMMARY_RANK.UNUSABLE;
		return SUMMARY_RANK.DIRECT;
	}
	if (isLongTextProperty(property)) return SUMMARY_RANK.EXCERPT;
	return SUMMARY_RANK.DIRECT;
}
/**
* True when the property has any single-line form at all.
*
* @group Collections
*/
function canSummariseProperty(property) {
	return rankSummaryProperty(property) !== SUMMARY_RANK.UNUSABLE;
}
//#endregion
//#region src/util/previews.ts
function isReferenceProperty(property) {
	if (!property) return null;
	if (property.type === "reference") return true;
	if (property.type === "array") if (Array.isArray(property.of)) return false;
	else return property.of?.type === "reference";
	return false;
}
function isRelationProperty(property) {
	if (!property) return null;
	if (property.type === "relation") return true;
	if (property.type === "array") if (Array.isArray(property.of)) return false;
	else return property.of?.type === "relation";
	return false;
}
function isHiddenProperty(property) {
	if (!property) return false;
	return Boolean(property.admin?.hideFromCollection);
}
/**
* The properties a preview surface should render for a record, best first.
*
* Three things decide the answer, in this order:
*
* 1. `previewProperties` — passed in, or declared on the collection. A stated
*    list is returned verbatim, ranking and limit included: a developer who
*    asks for the Markdown biography gets the Markdown biography.
* 2. Whether the value has a one-line form at all. A map renders as a
*    key/value table and a Markdown field as a document; neither fits a card
*    line, so they never take a slot from a value that does. See
*    {@link rankSummaryProperty}.
* 3. `propertiesOrder`, which breaks ties.
*
* The middle step is the one that is easy to get wrong by leaving out.
* `propertiesOrder` states the *column* order of a collection table — it is
* not a statement that the first three columns summarise a record, and reading
* it as one is how a card ends up rendering somebody's entire biography.
*/
function getEntityPreviewKeys(authController, targetCollection, fields, previewProperties, limit = 3) {
	const allProperties = Object.keys(targetCollection.properties);
	let listProperties = previewProperties?.filter((p) => allProperties.includes(p));
	if (!listProperties && targetCollection.previewProperties) listProperties = targetCollection.previewProperties?.filter((p) => allProperties.includes(p));
	if (listProperties && listProperties.length > 0) return listProperties;
	const hasExplicitOrder = !!targetCollection.propertiesOrder;
	const order = targetCollection.propertiesOrder || allProperties;
	const direct = [];
	const excerpt = [];
	for (const key of order) {
		const property = targetCollection.properties[key];
		if (!property || isPropertyBuilder(property)) continue;
		if (key === "id" || "isId" in property && Boolean(property.isId)) continue;
		if (isReferenceProperty(property)) continue;
		if (!hasExplicitOrder && isRelationProperty(property)) continue;
		if (isHiddenProperty(property)) continue;
		if (isStorageProperty(property)) continue;
		const rank = rankSummaryProperty(property);
		if (rank === SUMMARY_RANK.UNUSABLE) continue;
		(rank === SUMMARY_RANK.EXCERPT ? excerpt : direct).push(key);
		if (direct.length >= limit) break;
	}
	return [...direct, ...excerpt].slice(0, limit);
}
var INCLUDE_ALL_RELATIONS = ["*"];
/**
* The `include` params that eager-load a collection's relations in the same
* request as its rows, so previews never fetch once per relation cell.
*
* Only the REST transport reads `include`; the realtime transport embeds
* relation data unconditionally and ignores it. Passing it either way keeps a
* realtime-less deployment rendering the same cells as a realtime one.
*/
function getRelationIncludeParams(collection) {
	if (!collection.properties) return void 0;
	return Object.values(collection.properties).some((property) => property && !isPropertyBuilder(property) && (isRelationProperty(property) || isReferenceProperty(property))) ? INCLUDE_ALL_RELATIONS : void 0;
}
/**
* The property that fills the title slot for a collection. Ranking lives in
* `@rebasepro/common`, shared with the admin package so both agree on what an
* entity is called.
*/
function getEntityTitlePropertyKey(collection, propertyConfigs) {
	return getTitlePropertyKey$1(collection);
}
//#endregion
//#region src/hooks/data/useCollection.tsx
/**
* This hook is used to fetch collections using a given collection
* @param path
* @param collection
* @param filterValues
* @param sortBy
* @param itemCount
* @param offset
* @param page
* @param searchString
* @group Hooks and utilities
*/
function useCollection({ path, collection, filterValues, sortBy, itemCount, offset, page, searchString }) {
	const dataClient = useData();
	const { reportSchemaDrift } = useSchemaDriftContext();
	const sortByProperty = sortBy ? sortBy[0] : void 0;
	const currentSort = sortBy ? sortBy[1] : void 0;
	const orderByParams = sortBy ? [String(sortBy[0]), sortBy[1]] : void 0;
	const whereParams = filterValues && Object.keys(filterValues).length > 0 ? filterValues : void 0;
	const [data, setData] = useState([]);
	const [dataLoading, setDataLoading] = useState(false);
	const [dataLoadingError, setDataLoadingError] = useState();
	const [noMoreToLoad, setNoMoreToLoad] = useState(false);
	const [totalCount, setTotalCount] = useState();
	useEffect(() => {
		setDataLoading(true);
		const onEntitiesUpdate = async (res) => {
			const entities = res.data;
			setDataLoading(false);
			setDataLoadingError(void 0);
			setData(entities.map((e) => ({ ...e })));
			setNoMoreToLoad(!res.meta.hasMore);
			setTotalCount(res.meta.total);
		};
		const onError = (error) => {
			console.error("ERROR", error);
			setDataLoading(false);
			setData([]);
			setDataLoadingError(error);
			setTotalCount(void 0);
			if (isSchemaDriftError(error)) reportSchemaDrift(error.message);
		};
		const accessor = dataClient.collection(path);
		const includeParams = getRelationIncludeParams(collection);
		if (accessor.listen) return accessor.listen(toFindParams({
			where: whereParams,
			limit: itemCount,
			offset,
			page,
			orderBy: orderByParams,
			searchString,
			include: includeParams
		}), (res) => onEntitiesUpdate({
			data: res.data,
			meta: res.meta
		}), onError);
		else {
			let cancelled = false;
			accessor.find(toFindParams({
				where: whereParams,
				limit: itemCount,
				offset,
				page,
				orderBy: orderByParams,
				searchString,
				include: includeParams
			})).then((res) => {
				if (cancelled) return;
				onEntitiesUpdate({
					data: res.data,
					meta: res.meta
				});
			}).catch((e) => {
				if (cancelled) return;
				onError(e);
			});
			return () => {
				cancelled = true;
			};
		}
	}, [
		path,
		itemCount,
		offset,
		page,
		currentSort,
		sortByProperty,
		filterValues,
		searchString,
		dataClient,
		collection
	]);
	return useMemo(() => ({
		data,
		dataLoading,
		dataLoadingError,
		noMoreToLoad,
		totalCount
	}), [
		data,
		dataLoading,
		dataLoadingError,
		noMoreToLoad,
		totalCount
	]);
}
//#endregion
//#region src/hooks/useAuthController.tsx
/**
* Hook to retrieve the AuthContext.
*
* Consider that in order to use this hook you need to have a parent
* `Rebase`
*
* @see AuthController
* @group Hooks and utilities
*/
var useAuthController = () => {
	const authController = useContext(AuthControllerContext);
	if (!authController) throw new Error("useAuthController must be used inside <Rebase>");
	return authController;
};
//#endregion
//#region src/hooks/useRebaseClient.tsx
/**
* Hook to retrieve the full RebaseClient instance from context.
* Returns `undefined` if no client was provided to `<Rebase>`.
*/
function useRebaseClient() {
	return useContext(RebaseClientInstanceContext);
}
//#endregion
//#region src/hooks/useStorageSource.tsx
/**
* Use this hook to get the storage source being used
* @group Hooks and utilities
*/
var useStorageSource = () => {
	const storageSource = useContext(StorageSourceContext);
	if (storageSource === null) throw new Error("useStorageSource must be used inside <Rebase>");
	return storageSource;
};
//#endregion
//#region src/hooks/useSnackbarController.tsx
/**
* Hook to retrieve the SnackbarContext.
*
* Consider that in order to use this hook you need to have a parent
* `Rebase`
*
* @see SnackbarController
* @group Hooks and utilities
*/
var useSnackbarController = () => {
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const open = useCallback((props) => {
		const { type, message, autoHideDuration, action } = props;
		enqueueSnackbar({
			message: props.title ? /* @__PURE__ */ jsxs("div", {
				className: "flex flex-col",
				children: [/* @__PURE__ */ jsx("strong", { children: props.title }), message]
			}) : message,
			variant: type,
			autoHideDuration,
			...action ? { key: nextActionKey() } : {},
			action: action ? (key) => /* @__PURE__ */ jsx(Button, {
				variant: "text",
				size: "small",
				className: "text-inherit",
				onClick: () => {
					action.onClick();
					closeSnackbar(key);
				},
				children: action.label
			}) : void 0
		});
	}, [enqueueSnackbar, closeSnackbar]);
	const close = useCallback(() => {
		closeSnackbar();
	}, [closeSnackbar]);
	return useMemo(() => ({
		open,
		close
	}), [open, close]);
};
var actionKeySeq = 0;
var nextActionKey = () => `rebase-snackbar-action-${actionKeySeq++}`;
//#endregion
//#region src/hooks/useUserConfigurationPersistence.tsx
/**
* Use this controller to access the configuration that is stored externally,
* and not defined in code
*
* @group Hooks and utilities
*/
var useUserConfigurationPersistence = () => useContext(UserConfigurationPersistenceContext);
//#endregion
//#region src/hooks/useDialogsController.tsx
/**
* Use this hook to open a dialog imperatively.
* Alternatively, you can use dialogs declaratively using the `Dialog` component.
*
* Consider that in order to use this hook you need to have a parent
* `Rebase`
*
* @group Hooks and utilities
*/
var useDialogsController = () => {
	const controller = useContext(DialogsControllerContext);
	if (!controller) throw new Error("useDialogsController must be used inside <Rebase>");
	return controller;
};
//#endregion
//#region src/hooks/useCustomizationController.tsx
/**
* Use this hook to retrieve the customization controller.
* This hook includes all the customization options that can be used
* to customize the admin.
*
* You will likely not need to use this hook directly.
*
* @group Hooks and utilities
*/
var useCustomizationController = () => {
	const controller = useContext(CustomizationControllerContext);
	if (!controller) throw new Error("useCustomizationController must be used inside <Rebase>");
	return controller;
};
//#endregion
//#region src/hooks/useAnalyticsController.tsx
/**
* @group Hooks and utilities
*/
var useAnalyticsController = () => useContext(AnalyticsContext);
//#endregion
//#region src/hooks/useEffectiveRoleController.tsx
/**
* Hook to retrieve the {@link EffectiveRoleController}
*
* @see EffectiveRoleController
* @category Hooks and utilities
*/
var useEffectiveRoleController = () => useContext(EffectiveRoleControllerContext);
//#endregion
//#region src/contexts/DatabaseAdminContext.tsx
var DatabaseAdminContext = React.createContext(void 0);
//#endregion
//#region src/hooks/useRebaseContext.tsx
/**
* Hook to retrieve the {@link RebaseContext}.
*
* Consider that in order to use this hook you need to have a parent
* `Rebase` component.
*
* @see RebaseContext
* @group Hooks and utilities
*/
var useRebaseContext = () => {
	const client = useRebaseClient();
	const authController = useAuthController();
	const entityData = useData();
	const data = React.useMemo(() => wrapAsSdkData(entityData), [entityData]);
	const storageSource = useStorageSource();
	const snackbarController = useSnackbarController();
	const userConfigPersistence = useUserConfigurationPersistence();
	const dialogsController = useDialogsController();
	const customizationController = useCustomizationController();
	const analyticsController = useAnalyticsController();
	const effectiveRoleController = useEffectiveRoleController();
	const databaseAdmin = useContext(DatabaseAdminContext);
	const rebaseContextRef = React.useRef({
		authController,
		data,
		storageSource,
		snackbarController,
		userConfigPersistence,
		dialogsController,
		customizationController,
		analyticsController,
		effectiveRoleController,
		databaseAdmin,
		client
	});
	React.useEffect(() => {
		rebaseContextRef.current = {
			authController,
			data,
			storageSource,
			snackbarController,
			userConfigPersistence,
			dialogsController,
			customizationController,
			analyticsController,
			effectiveRoleController,
			databaseAdmin,
			client
		};
	}, [
		authController,
		data,
		storageSource,
		snackbarController,
		userConfigPersistence,
		dialogsController,
		customizationController,
		analyticsController,
		effectiveRoleController,
		databaseAdmin,
		client
	]);
	return rebaseContextRef.current;
};
//#endregion
//#region src/hooks/data/useFetch.tsx
var CACHE = {};
/**
* Pre-populate the entity fetch cache with entities loaded from a collection.
* This allows entity detail views to render instantly using cached data,
* while the background fetch/listener brings in fresh data.
* @param path - The collection path (e.g. "products")
* @param entities - Array of entities to cache
*/
function populateFetchCache(path, entities) {
	for (const entity of entities) CACHE[`${path}/${entity.id}`] = entity;
}
/**
* Clear the entity fetch cache. Call this on auth state changes (e.g. logout)
* to prevent stale data from a previous session leaking into the next.
*/
function clearFetchCache() {
	for (const key of Object.keys(CACHE)) delete CACHE[key];
}
/**
* This hook is used to fetch a entity.
* It gives real time updates if the driver supports it.
* @param path
* @param collection
* @param entityId
* @param useCache
* @group Hooks and utilities
*/
function useFetch({ path, entityId, collection, databaseId, useCache = false }) {
	const dataClient = useData();
	useRebaseContext();
	const cacheKey = entityId ? `${path}/${entityId}` : void 0;
	const cachedEntity = cacheKey ? CACHE[cacheKey] : void 0;
	const [entity, setEntity] = useState(cachedEntity);
	const [dataLoading, setDataLoading] = useState(!cachedEntity);
	const [dataLoadingError, setDataLoadingError] = useState();
	useEffect(() => {
		if (!CACHE[`${path}/${entityId}`]) setDataLoading(true);
		const onEntityUpdate = async (updatedEntity) => {
			CACHE[`${path}/${entityId}`] = updatedEntity ?? void 0;
			setEntity(updatedEntity ?? void 0);
			setDataLoading(false);
			setDataLoadingError(void 0);
		};
		const onError = (error) => {
			console.error("ERROR fetching entity", error);
			setDataLoading(false);
			setEntity(void 0);
			setDataLoadingError(error);
		};
		if (entityId && useCache && CACHE[`${path}/${entityId}`]) {
			setEntity(CACHE[`${path}/${entityId}`]);
			setDataLoading(false);
			setDataLoadingError(void 0);
			return () => {};
		} else if (entityId && path && collection) {
			const accessor = dataClient.collection(path);
			if (accessor.listenById) return accessor.listenById(entityId, (entity) => onEntityUpdate(entity), onError);
			else {
				let cancelled = false;
				accessor.findById(entityId).then((entity) => {
					if (cancelled) return;
					onEntityUpdate(entity);
				}).catch((e) => {
					if (cancelled) return;
					onError(e);
				});
				return () => {
					cancelled = true;
				};
			}
		} else {
			onEntityUpdate(void 0);
			return () => {};
		}
	}, [
		entityId,
		path,
		dataClient,
		collection,
		useCache,
		databaseId
	]);
	return useMemo(() => ({
		entity,
		dataLoading,
		dataLoadingError
	}), [
		entity,
		dataLoading,
		dataLoadingError
	]);
}
//#endregion
//#region src/hooks/data/useRelationSelector.tsx
var DEFAULT_PAGE_SIZE$1 = 10;
/**
* Hook to manage relation selection with data fetching from Rebase data source
*/
function useRelationSelector({ path, collection, fixedFilter, pageSize = DEFAULT_PAGE_SIZE$1, getLabelFromEntity, getDescriptionFromEntity, descriptionProperty, enabled = true }) {
	const dataClient = useData();
	const [items, setItems] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const isLoadingRef = useRef(false);
	const [error, setError] = useState();
	const [hasMore, setHasMore] = useState(true);
	const [currentSearch, setCurrentSearch] = useState("");
	const [limit, setLimit] = useState(pageSize);
	const unsubscribeRef = useRef(null);
	const searchTimeoutRef = useRef(null);
	const hasLoadedRef = useRef(false);
	const setLoading = useCallback((loading) => {
		isLoadingRef.current = loading;
		setIsLoading(loading);
	}, []);
	const entityToRelationItem = useCallback((entity, relation) => {
		let label;
		let description;
		if (getLabelFromEntity) label = getLabelFromEntity(entity);
		else {
			const commonLabelProps = [
				"name",
				"title",
				"label",
				"displayName"
			];
			let foundProp;
			if (entity.values) foundProp = commonLabelProps.find((prop) => entity.values[prop] != null && entity.values[prop] !== "");
			if (foundProp && entity.values[foundProp]) label = String(entity.values[foundProp]);
			else label = String(entity.id);
		}
		if (getDescriptionFromEntity) description = getDescriptionFromEntity(entity);
		else if (descriptionProperty && entity.values && entity.values[descriptionProperty]) description = String(entity.values[descriptionProperty]);
		return {
			id: entity.id,
			label,
			description,
			data: entity,
			relation: relation ? relation : new EntityRelation(entity.id, path)
		};
	}, [
		getLabelFromEntity,
		getDescriptionFromEntity,
		descriptionProperty
	]);
	const cleanupSubscription = useCallback(() => {
		if (unsubscribeRef.current) {
			unsubscribeRef.current();
			unsubscribeRef.current = null;
		}
	}, []);
	const includeParams = getRelationIncludeParams(collection);
	const fetchData = useCallback(() => {
		cleanupSubscription();
		setError(void 0);
		setLoading(true);
		const whereParams = fixedFilter && Object.keys(fixedFilter).length > 0 ? fixedFilter : void 0;
		const onEntitiesUpdate = (res) => {
			const newItems = res.data.map((e) => entityToRelationItem(e));
			hasLoadedRef.current = true;
			setItems(newItems);
			setHasMore(res.meta.hasMore);
			setLoading(false);
		};
		const onErrorUpdate = (fetchError) => {
			console.error("useRelationSelector: Error fetching data:", fetchError);
			hasLoadedRef.current = true;
			setError(fetchError);
			setLoading(false);
		};
		const accessor = dataClient.collection(path);
		let unsubscribe;
		if (accessor.listen) unsubscribe = accessor.listen({
			where: whereParams,
			limit,
			orderBy: void 0,
			searchString: currentSearch,
			include: includeParams
		}, (res) => onEntitiesUpdate({
			data: res.data,
			meta: res.meta
		}), onErrorUpdate);
		else {
			let cancelled = false;
			accessor.find({
				where: whereParams,
				limit,
				offset: 0,
				orderBy: void 0,
				searchString: currentSearch,
				include: includeParams
			}).then((res) => {
				if (cancelled) return;
				onEntitiesUpdate({
					data: res.data,
					meta: res.meta
				});
			}).catch((e) => {
				if (cancelled) return;
				onErrorUpdate(e);
			});
			unsubscribe = () => {
				cancelled = true;
			};
		}
		unsubscribeRef.current = unsubscribe || null;
	}, [
		dataClient,
		path,
		fixedFilter,
		limit,
		currentSearch,
		entityToRelationItem,
		cleanupSubscription,
		setLoading,
		includeParams
	]);
	const search = useCallback((searchString) => {
		if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		searchTimeoutRef.current = setTimeout(() => {
			setLimit(pageSize);
			setCurrentSearch(searchString);
		}, searchString.trim() ? 300 : 0);
	}, [pageSize]);
	const loadMore = useCallback(() => {
		if (!isLoadingRef.current && hasMore && items.length > 0) {
			setLoading(true);
			setLimit((prev) => prev + pageSize);
		}
	}, [
		hasMore,
		items.length,
		pageSize,
		setLoading
	]);
	useEffect(() => {
		if (!enabled) return;
		fetchData();
		return () => {
			cleanupSubscription();
		};
	}, [
		fetchData,
		enabled,
		cleanupSubscription
	]);
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		};
	}, []);
	return useMemo(() => ({
		items,
		isLoading: isLoading || enabled && !hasLoadedRef.current,
		error,
		search,
		loadMore,
		hasMore,
		entityToRelationItem
	}), [
		items,
		isLoading,
		error,
		search,
		loadMore,
		hasMore,
		entityToRelationItem,
		enabled
	]);
}
//#endregion
//#region src/hooks/data/save.ts
/**
* This function is in charge of saving a entity.
*
* It runs the collection's **`admin.browserCallbacks`** around the write —
* `beforeSave`, then `afterSave` or `afterSaveError`. Not `callbacks`: that
* block belongs to the server, which runs it inside the write it serves, and
* its bodies are stripped from this bundle entirely.
*
* Which matters most for a collection on a `direct`/`custom` transport, where
* the panel talks to the store itself and no server sees the write at all —
* before this ran them, such a collection had no write callbacks anywhere. This
* function has been named `saveEntityWithCallbacks` since it was written and
* did not run any.
*
* A `beforeSave` that throws blocks the write, exactly as the server's does:
* nothing is sent, and the error reaches `afterSaveError` and the caller.
*
* `afterSave`/`afterSaveError` below are the *caller's* UI callbacks — the
* form's "close the dialog", the table's "clear the editing state" — and are
* unrelated to the collection's. Both run: the collection's first.
*
* @param collection
* @param path
* @param entityId
* @param values
* @param previousValues
* @param status
* @param data
* @param context
* @param afterSave
* @param afterSaveError
* @group Hooks and utilities
*/
async function saveEntityWithCallbacks({ collection, path, entityId, values, previousValues, status, data, context, afterSave, afterSaveError }) {
	if (status !== "new" && status !== "copy" && !entityId) throw new Error("Entity id must be specified when updating an existing entity");
	const browserCallbacks = collection.browserCallbacks;
	let valuesToSave = values;
	if (browserCallbacks?.beforeSave) try {
		valuesToSave = await browserCallbacks.beforeSave({
			collection,
			path,
			id: entityId,
			values,
			previousValues,
			status,
			context
		});
	} catch (e) {
		const error = e instanceof Error ? e : new Error(String(e));
		await browserCallbacks.afterSaveError?.({
			collection,
			path,
			id: entityId,
			values,
			previousValues,
			status,
			error,
			context
		});
		if (afterSaveError) afterSaveError(error);
		throw error;
	}
	const accessor = data.collection(path);
	let savePromise;
	if (status === "new" || status === "copy") savePromise = accessor.create(valuesToSave, entityId);
	else savePromise = accessor.update(entityId, valuesToSave);
	return savePromise.then(async (entity) => {
		await browserCallbacks?.afterSave?.({
			collection,
			path,
			id: entity.id,
			values: entity.values,
			previousValues,
			status,
			context
		});
		if (afterSave) afterSave(entity);
		return entity;
	}).catch(async (e) => {
		await browserCallbacks?.afterSaveError?.({
			collection,
			path,
			id: entityId,
			values: valuesToSave,
			previousValues,
			status,
			error: e,
			context
		});
		if (afterSaveError) afterSaveError(e);
		throw e;
	});
}
//#endregion
//#region src/hooks/data/delete.ts
/**
* This function is in charge of deleting a entity.
*
* It runs the collection's **`admin.browserCallbacks`** around the delete —
* `beforeDelete`, then `afterDelete`. Not `callbacks`: that block belongs to
* the server, which runs it inside the delete it serves, and its bodies are
* stripped from this bundle entirely.
*
* Which matters most for a collection on a `direct`/`custom` transport, where
* the panel talks to the store itself and no server sees the delete at all —
* before this ran them, such a collection had no delete callbacks anywhere.
* This function has been named `deleteEntityWithCallbacks` since it was written
* and did not run any; it even took a `callbacks` prop and ignored it.
*
* A `beforeDelete` that throws blocks the delete, exactly as the server's does:
* nothing is sent, and `onDeleteFailure` hears about it.
*
* @param data
* @param entity
* @param collection
* @param onDeleteSuccess
* @param onDeleteFailure
* @param context
* @group Hooks and utilities
*/
async function deleteEntityWithCallbacks({ data, entity, collection, onDeleteSuccess, onDeleteFailure, context }) {
	console.debug("Deleting entity", entity.path, entity.id);
	const browserCallbacks = collection.browserCallbacks;
	if (browserCallbacks?.beforeDelete) try {
		await browserCallbacks.beforeDelete({
			collection,
			path: entity.path,
			id: entity.id,
			row: {
				id: entity.id,
				...entity.values
			},
			context
		});
	} catch (e) {
		const error = e instanceof Error ? e : new Error(String(e));
		if (onDeleteFailure) onDeleteFailure(entity, error);
		return false;
	}
	return data.collection(entity.path).delete(entity.id).then(async () => {
		await browserCallbacks?.afterDelete?.({
			collection,
			path: entity.path,
			id: entity.id,
			row: {
				id: entity.id,
				...entity.values
			},
			context
		});
		if (onDeleteSuccess) onDeleteSuccess(entity);
		return true;
	}).catch((e) => {
		if (onDeleteFailure) onDeleteFailure(entity, e);
		return false;
	});
}
//#endregion
//#region src/hooks/useNavigationBlocker.tsx
var IDLE_BLOCKER = {
	state: "unblocked",
	proceed: void 0,
	reset: void 0,
	location: void 0
};
var NavigationBlockerContext = createContext(null);
/**
* Owns the single React Router blocker for the whole app.
*
* React Router only honours **one** blocker at a time: `shouldBlockNavigation`
* picks the last-registered blocker function and silently ignores every other
* one (it only logs a dev warning). Because blockers register in a `useEffect`,
* the winner is whichever surface mounted most recently — so an unsaved-changes
* guard could be disabled by an unrelated component mounting after it.
*
* This provider registers the only `useBlocker` in the tree and multiplexes it,
* so every surface that needs to guard navigation gets a say regardless of
* mount order. Surfaces register through {@link useNavigationBlocker}.
*/
function NavigationBlockerProvider({ children }) {
	const predicates = useRef(/* @__PURE__ */ new Map());
	const [blockedBy, setBlockedBy] = useState(null);
	const blocker = useBlocker(useCallback((args) => {
		for (const [id, predicate] of predicates.current) if (predicate(args)) {
			setBlockedBy(id);
			return true;
		}
		setBlockedBy(null);
		return false;
	}, []));
	const register = useCallback((id, predicate) => {
		predicates.current.set(id, predicate);
	}, []);
	const unregister = useCallback((id) => {
		predicates.current.delete(id);
		setBlockedBy((current) => current === id ? null : current);
	}, []);
	const value = useMemo(() => ({
		register,
		unregister,
		blocker,
		blockedBy
	}), [
		register,
		unregister,
		blocker,
		blockedBy
	]);
	return /* @__PURE__ */ jsx(NavigationBlockerContext.Provider, {
		value,
		children
	});
}
/**
* Guard navigation with `predicate`, sharing the app-wide blocker.
*
* Returns a {@link Blocker} that is only ever in the `blocked` state when *this*
* registration is what blocked the navigation — so several guards can coexist
* without each of them popping a dialog.
*
* Returns an idle blocker when no {@link NavigationBlockerProvider} is mounted
* above, rather than competing for React Router's single blocker slot.
*/
function useNavigationBlocker(predicate) {
	const context = useContext(NavigationBlockerContext);
	const id = useId();
	const predicateRef = useRef(predicate);
	predicateRef.current = predicate;
	const register = context?.register;
	const unregister = context?.unregister;
	useEffect(() => {
		if (!register || !unregister) return;
		register(id, (args) => predicateRef.current(args));
		return () => unregister(id);
	}, [
		register,
		unregister,
		id
	]);
	if (!context) return IDLE_BLOCKER;
	return context.blockedBy === id ? context.blocker : IDLE_BLOCKER;
}
//#endregion
//#region src/hooks/useUnsavedChangesDialog.tsx
/**
* A single, unified hook to prevent navigation when there are unsaved changes.
*
* It automatically handles:
* 1. Internal React Router navigation using `useBlocker`.
* 2. External browser navigation (page refresh, tab close) using `beforeunload`.
*/
function useUnsavedChangesDialog(when, onOk) {
	const [manualDialogOpen, setManualDialogOpen] = useState(false);
	const blocker = useNavigationBlocker(useCallback(({ currentLocation, nextLocation }) => when && currentLocation.pathname !== nextLocation.pathname, [when]));
	useEffect(() => {
		if (!when) return;
		const handleBeforeUnload = (e) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [when]);
	const handleOk = useCallback(() => {
		onOk();
		if (blocker.state === "blocked") blocker.proceed();
		setManualDialogOpen(false);
	}, [blocker, onOk]);
	const handleCancel = useCallback(() => {
		if (blocker.state === "blocked") blocker.reset();
		setManualDialogOpen(false);
	}, [blocker]);
	const triggerDialog = useCallback(() => setManualDialogOpen(true), []);
	return useMemo(() => ({
		dialogProps: {
			open: manualDialogOpen || blocker.state === "blocked",
			handleOk,
			handleCancel,
			body: "There are unsaved changes in this collection"
		},
		triggerDialog
	}), [
		manualDialogOpen,
		blocker.state,
		handleOk,
		handleCancel,
		triggerDialog
	]);
}
//#endregion
//#region src/hooks/useAuthSubscription.ts
function useAuthSubscription(authClient) {
	const currentSession = authClient?.getSession();
	const [user, setUser] = useState(currentSession?.user ?? null);
	const [authLoading, setAuthLoading] = useState(false);
	const [authError, setAuthError] = useState();
	const [loginSkipped, setLoginSkipped] = useState(false);
	const [extra, setExtra] = useState();
	/**
	* Whether asking the server "who am I?" could possibly answer.
	*
	* A client configured with `persistSession: false` and JSON auth — the
	* shape used when one client borrows another's credential, as the hosted
	* Studio does — has no stored session and no auth cookie, so the probe
	* below is guaranteed to 401. Sending it anyway put three failing
	* `GET /auth/me` calls on the wire per mount, each one landing in the
	* *customer's* request log as an authentication failure against their own
	* backend.
	*
	* A client that does not implement the capability is treated as "might
	* work" — the historical behaviour.
	*/
	const mayHaveRestorableSession = authClient?.canRestoreSession?.() ?? true;
	/**
	* `true` only while a probe is genuinely outstanding.
	*
	* It used to be seeded from `!currentSession` alone, which left a client
	* that cannot restore a session — and therefore never runs the probe that
	* clears the flag — reporting "still loading" forever. Anything gated on
	* `initialLoading` (a spinner, a redirect to the login view) would never
	* resolve.
	*/
	const [initialLoading, setInitialLoading] = useState(!currentSession && mayHaveRestorableSession);
	useEffect(() => {
		if (!authClient) return;
		if (!currentSession && mayHaveRestorableSession) {
			setInitialLoading(true);
			authClient.getUser().then((user) => {
				if (user) setUser(user);
				setAuthError(void 0);
			}).catch((e) => {
				const status = e?.status;
				if (status === 401 || status === 403) {
					setAuthError(void 0);
					return;
				}
				setAuthError(e instanceof Error ? e : new Error(String(e)));
			}).finally(() => {
				setInitialLoading(false);
			});
		}
	}, [
		authClient,
		currentSession,
		mayHaveRestorableSession
	]);
	useEffect(() => {
		if (!authClient) return;
		return authClient.onAuthStateChange((event, session) => {
			if (event === "SIGNED_IN" || event === "USER_UPDATED" || event === "TOKEN_REFRESHED") setUser(session?.user ?? null);
			else if (event === "SIGNED_OUT") setUser(null);
		});
	}, [authClient]);
	const signOut = useCallback(async () => {
		if (!authClient) return;
		setAuthLoading(true);
		try {
			await authClient.signOut();
		} finally {
			setAuthLoading(false);
		}
	}, [authClient]);
	const getAuthToken = useCallback(async () => {
		if (!authClient) return "";
		const session = authClient.getSession();
		if (!session) return "";
		if (session.expiresAt < Date.now()) try {
			return (await authClient.refreshSession()).accessToken;
		} catch (e) {
			return "";
		}
		return session.accessToken;
	}, [authClient]);
	return useMemo(() => ({
		user,
		initialLoading: authClient ? initialLoading : false,
		authLoading,
		signOut,
		authError,
		getAuthToken,
		loginSkipped,
		extra,
		setExtra
	}), [
		user,
		initialLoading,
		authLoading,
		signOut,
		authError,
		getAuthToken,
		loginSkipped,
		extra,
		setExtra,
		authClient
	]);
}
//#endregion
//#region src/hooks/useAdminModeController.tsx
/**
* Hook to retrieve the current admin mode (`"cms"` | `"studio"`), and `setMode`
* to change it.
*
* Consider that in order to use this hook you need to have a parent
* `Rebase`
*
* @see AdminModeController
* @group Hooks and utilities
*/
var useAdminModeController = () => useContext(AdminModeControllerContext);
//#endregion
//#region src/util/local_storage.ts
/**
* localStorage that tolerates not being there.
*
* `typeof window !== "undefined"` was the guard these call sites used, and it
* answers the wrong question: it says "am I in a browser", not "can I read
* storage". Three things make that distinction real rather than theoretical:
*
* - A browser can have storage **disabled**. Safari in private mode, a blocked
*   third-party cookie policy, and a sandboxed iframe all give you a `window`
*   whose `localStorage` property *throws on access* — a SecurityError raised
*   before any method is called, which no amount of null-checking the result
*   catches.
* - A test environment can be **half torn down**. jsdom is disposed at the end
*   of a file while React is still finishing a concurrent render, so `window`
*   survives as a husk and `localStorage.getItem` is no longer a function.
*   That is how this was found: `TypeError: localStorage.getItem is not a
*   function`, thrown out of a render, failing a suite in which every one of
*   709 tests passed.
* - Server rendering has no `window` at all, which is the only case the old
*   guard actually covered.
*
* Reading a UI preference is never worth a crash. Every function here fails to
* `null` / no-op, so a user with storage switched off gets the default
* appearance instead of a blank page.
*/
/** The Storage object, or null when it is absent, blocked, or not usable. */
function usableStorage() {
	try {
		if (typeof window === "undefined") return null;
		const candidate = window.localStorage;
		return typeof candidate?.getItem === "function" ? candidate : null;
	} catch {
		return null;
	}
}
/** The stored string for `key`, or null if it is unset or unreadable. */
function readStoredString(key) {
	try {
		return usableStorage()?.getItem(key) ?? null;
	} catch {
		return null;
	}
}
/** Persist `value` under `key`. A no-op when storage is unavailable. */
function writeStoredString(key, value) {
	try {
		usableStorage()?.setItem(key, value);
	} catch {}
}
/** Remove `key`. A no-op when storage is unavailable. */
function removeStoredString(key) {
	try {
		usableStorage()?.removeItem(key);
	} catch {}
}
//#endregion
//#region src/hooks/useBuildAdminModeController.tsx
/**
* Use this hook to build an admin mode controller that determines
* whether the UI shows Developer or Editor tools.
*/
function useBuildAdminModeController() {
	const stored = readStoredString("rebase-admin-mode");
	const migrated = stored === "content" ? "cms" : stored;
	const [mode, setMode] = useState((migrated === "cms" || migrated === "studio" ? migrated : null) ?? "cms");
	const setModeInternal = useCallback((newMode) => {
		writeStoredString("rebase-admin-mode", newMode);
		setMode(newMode);
	}, []);
	return useMemo(() => ({
		mode,
		setMode: setModeInternal
	}), [mode, setModeInternal]);
}
//#endregion
//#region src/hooks/useBuildEffectiveRoleController.tsx
/**
* Use this hook to build an effective role controller that determines
* what role is simulated in Editor mode when Dev mode is active.
*
* It uses localStorage to persist the simulated role across reloads.
*/
function useBuildEffectiveRoleController() {
	const [effectiveRole, setEffectiveRole] = useState(readStoredString("rebase-effective-role") ?? null);
	const setRoleInternal = useCallback((newRole) => {
		if (newRole) writeStoredString("rebase-effective-role", newRole);
		else removeStoredString("rebase-effective-role");
		setEffectiveRole(newRole);
	}, []);
	return useMemo(() => ({
		effectiveRole,
		setEffectiveRole: setRoleInternal
	}), [effectiveRole, setRoleInternal]);
}
//#endregion
//#region src/hooks/useModeController.tsx
/**
* Hook to retrieve the current mode ("light" | "dark"), and `setMode`
* or `toggle` functions to change it.
*
* Consider that in order to use this hook you need to have a parent
* `Rebase`
*
* @see ModeController
* @group Hooks and utilities
*/
var useModeController = () => useContext(ModeControllerContext);
//#endregion
//#region src/hooks/useClipboard.tsx
var useClipboard = (options) => {
	const { onSuccess, onError, disableClipboardAPI = false, copiedDuration } = options || {};
	const ref = useRef(null);
	const [isCoppied, setIsCoppied] = useState(false);
	const [clipboard, setClipbaord] = useState("");
	useEffect(() => {
		if (copiedDuration) setTimeout(() => setIsCoppied(false), copiedDuration);
	}, [isCoppied]);
	const isSupported = useCallback(() => navigator.clipboard !== void 0, []);
	const handleError = useCallback((error) => {
		if (onError) onError(error);
		else throw new Error(error);
	}, [onError]);
	const handleSuccess = useCallback((text) => {
		if (onSuccess) onSuccess(text);
		setIsCoppied(true);
		setClipbaord(text);
	}, [onSuccess]);
	const copyToClipboard = useCallback((text) => {
		navigator.clipboard.writeText(text).then(() => handleSuccess(text)).catch((err) => {
			handleError(err);
			setIsCoppied(false);
		});
	}, [handleError, handleSuccess]);
	const clearClipboard = useCallback(() => {
		if (isSupported()) navigator.clipboard.writeText("");
	}, [isSupported]);
	const action = useCallback((operation = "copy", text) => {
		const element = ref.current;
		const isInput = element && (element.tagName === "INPUT" || element.tagName === "TEXTAREA");
		const input = ref.current;
		if (isSupported() && !disableClipboardAPI) if (text) copyToClipboard(text);
		else if (element) if (isInput) {
			copyToClipboard(input.value);
			if (operation === "cut") input.value = "";
		} else copyToClipboard(element.innerText);
		else handleError("Both the ref & text were undefined");
	}, [
		disableClipboardAPI,
		copyToClipboard,
		handleError
	]);
	const copy = useCallback((text) => action("copy", typeof text === "object" ? void 0 : text), [action]);
	const cut = useCallback(() => action("cut"), [action]);
	return useMemo(() => ({
		ref,
		isCoppied,
		clipboard,
		clearClipboard,
		isSupported,
		copy,
		cut
	}), [
		isCoppied,
		clipboard,
		clearClipboard,
		isSupported,
		copy,
		cut
	]);
};
//#endregion
//#region src/hooks/useLargeLayout.tsx
var breakpoints = {
	xs: 0,
	sm: 640,
	md: 768,
	lg: 1024,
	xl: 1280,
	"2xl": 1536,
	"3xl": 1920
};
var isLargeLayoutGlobal = checkLargeLayout("lg");
var listeners = [];
var notifyListeners = () => {
	listeners.forEach((listener) => listener(isLargeLayoutGlobal));
};
if (typeof window !== "undefined") window.addEventListener("resize", () => {
	const newIsLargeLayout = checkLargeLayout("lg");
	if (newIsLargeLayout !== isLargeLayoutGlobal) {
		isLargeLayoutGlobal = newIsLargeLayout;
		notifyListeners();
	}
});
var useLargeLayout = () => {
	const [isLargeLayout, setIsLargeLayout] = useState(isLargeLayoutGlobal);
	useEffect(() => {
		const listener = (newIsLargeLayout) => {
			setIsLargeLayout(newIsLargeLayout);
		};
		listeners.push(listener);
		setIsLargeLayout(isLargeLayoutGlobal);
		return () => {
			const index = listeners.indexOf(listener);
			if (index > -1) listeners.splice(index, 1);
		};
	}, []);
	return isLargeLayout;
};
function checkLargeLayout(breakpoint = "lg") {
	if (typeof window === "undefined") return false;
	return window.matchMedia(`(min-width: ${breakpoints[breakpoint] + 1}px)`).matches;
}
//#endregion
//#region src/hooks/useCollapsedGroups.ts
var STORAGE_KEY_PREFIX = "rebase-collapsed-groups";
/**
* Custom hook for managing collapsed/expanded state of navigation groups
* with localStorage persistence. Automatically cleans up stale group entries
* when groups are removed from the navigation.
*
* Groups that have never been toggled by the user fall back to
* `defaults[groupName]` (driven by `collapsedByDefault` in config).
*
* @param groupNames - Array of group names to track
* @param namespace - Namespace for localStorage key (e.g., "home", "drawer") to allow independent state
* @param defaults - Optional map of group name → collapsed boolean from config
*/
function useCollapsedGroups(groupNames, namespace = "default", defaults) {
	const storageKey = `${STORAGE_KEY_PREFIX}-${namespace}`;
	const [collapsedGroups, setCollapsedGroups] = useState(() => {
		try {
			const stored = localStorage.getItem(storageKey);
			return stored ? JSON.parse(stored) : {};
		} catch {
			return {};
		}
	});
	useEffect(() => {
		try {
			localStorage.setItem(storageKey, JSON.stringify(collapsedGroups));
		} catch {}
	}, [collapsedGroups, storageKey]);
	useEffect(() => {
		if (groupNames.length === 0) return;
		const currentGroupNames = new Set(groupNames);
		setCollapsedGroups((prev) => {
			const cleaned = Object.fromEntries(Object.entries(prev).filter(([groupName]) => currentGroupNames.has(groupName)));
			const prevKeys = Object.keys(prev);
			const cleanedKeys = Object.keys(cleaned);
			if (prevKeys.length === cleanedKeys.length && prevKeys.every((key) => cleanedKeys.includes(key))) return prev;
			return cleaned;
		});
	}, [groupNames]);
	const isGroupCollapsed = useCallback((name) => {
		if (name in collapsedGroups) return collapsedGroups[name];
		return defaults?.[name] ?? false;
	}, [collapsedGroups, defaults]);
	const toggleGroupCollapsed = useCallback((name) => {
		setCollapsedGroups((prev) => {
			const currentlyCollapsed = name in prev ? prev[name] : defaults?.[name] ?? false;
			return {
				...prev,
				[name]: !currentlyCollapsed
			};
		});
	}, [defaults]);
	return useMemo(() => ({
		isGroupCollapsed,
		toggleGroupCollapsed
	}), [isGroupCollapsed, toggleGroupCollapsed]);
}
/**
* Build a defaults map from navigationGroupMappings for a given namespace.
* Returns a Record<groupName, collapsed> that can be passed to useCollapsedGroups.
*/
function buildCollapsedDefaults(mappings, namespace) {
	if (!mappings) return {};
	const result = {};
	for (const mapping of mappings) {
		const val = mapping.collapsedByDefault;
		if (val === void 0) continue;
		if (typeof val === "boolean") result[mapping.name] = val;
		else {
			const ns = val[namespace];
			if (ns !== void 0) result[mapping.name] = ns;
		}
	}
	return result;
}
//#endregion
//#region src/hooks/useBrowserTitleAndIcon.tsx
var rebaseLogo = "data:image/png;base64,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";
/**
* Internal hook to handle the browser title and icon
* @param name
* @param logo
*/
function useBrowserTitleAndIcon(name, logo) {
	useEffect(() => {
		if (document) {
			document.title = `${name} - Rebase`;
			let link = document.querySelector("link[rel~='icon']");
			if (!link) {
				link = document.createElement("link");
				link.rel = "icon";
				document.getElementsByTagName("head")[0].appendChild(link);
			}
			link.href = logo ?? rebaseLogo;
		}
	}, [name, logo]);
}
//#endregion
//#region src/hooks/useSlot.tsx
/**
* Hook that retrieves and renders all slot contributions for a given slot name.
*
* @param slot - The slot name to render contributions for.
* @param props - Props passed to each slot component.
* @returns An array of rendered React nodes, each wrapped in an ErrorBoundary.
*
* @example
* ```tsx
* const actions = useSlot("home.actions", { context });
* return <div>{actions}</div>;
* ```
*
* @group Hooks
*/
/**
* Are these two prop objects the same, one level down?
*
* Its own function so the comparison can be typed on `object` rather than on
* the slot's props. `SlotRegistry[K]` is a union of interfaces, and an
* interface has no implicit index signature, so reading it as
* `Record<string, unknown>` — which the loop this replaces did, to both
* arguments — is a conversion tsc refuses and `as unknown as` was suppressing.
* `Object.keys` needs no such claim.
*/
function shallowEqual(a, b) {
	if (a === b) return true;
	const keys = Object.keys(a);
	if (keys.length !== Object.keys(b).length) return false;
	return keys.every((key) => a[key] === b[key]);
}
function useSlot(slot, props) {
	const { resolvedSlots } = useCustomizationController();
	const propsRef = React.useRef(props);
	if (!shallowEqual(props, propsRef.current)) propsRef.current = props;
	const stableProps = propsRef.current;
	return useMemo(() => {
		return resolvedSlots.filter((s) => s.slot === slot).sort((a, b) => (a.order ?? 50) - (b.order ?? 50)).map((s, i) => {
			const Component = s.Component;
			return /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsx(Component, {
				...stableProps,
				...s.props ?? {}
			}) }, `${slot}_${i}`);
		});
	}, [
		resolvedSlots,
		slot,
		stableProps
	]);
}
//#endregion
//#region src/hooks/useBuildLocalConfigurationPersistence.tsx
function useBuildLocalConfigurationPersistence() {
	const configCache = useRef({});
	const getCollectionFromStorage = useCallback((storageKey) => {
		return readStoredJson(storageKey, {
			fallback: {},
			accept: isRecordValue
		});
	}, []);
	const getCollectionConfig = useCallback((path) => {
		const storageKey = `collection_config::${stripCollectionPath(path)}`;
		if (configCache.current[storageKey]) return configCache.current[storageKey];
		return getCollectionFromStorage(storageKey);
	}, [getCollectionFromStorage]);
	const [configVersion, setConfigVersion] = useState(0);
	const onCollectionModified = useCallback((path, data) => {
		const storageKey = `collection_config::${stripCollectionPath(path)}`;
		const merged = mergeDeep(configCache.current[storageKey] ?? getCollectionFromStorage(storageKey), data);
		configCache.current[storageKey] = merged;
		writeStoredJson(storageKey, merged);
		setConfigVersion((version) => version + 1);
	}, [getCollectionFromStorage]);
	const [recentlyVisitedPaths, _setRecentlyVisitedPaths] = useState([]);
	const [favouritePaths, _setFavouritePaths] = useState([]);
	const [collapsedGroups, _setCollapsedGroups] = useState([]);
	useEffect(() => {
		const readPaths = (key) => readStoredJson(key, {
			fallback: [],
			accept: isArrayValue
		});
		_setRecentlyVisitedPaths(readPaths("recently_visited_paths"));
		_setFavouritePaths(readPaths("favourite_paths"));
		_setCollapsedGroups(readPaths("collapsed_groups"));
	}, []);
	const setRecentlyVisitedPaths = useCallback((paths) => {
		writeStoredJson("recently_visited_paths", paths);
		_setRecentlyVisitedPaths(paths);
	}, []);
	const setFavouritePaths = useCallback((paths) => {
		writeStoredJson("favourite_paths", paths);
		_setFavouritePaths(paths);
	}, []);
	const setCollapsedGroups = useCallback((paths) => {
		writeStoredJson("collapsed_groups", paths);
		_setCollapsedGroups(paths);
	}, []);
	return useMemo(() => ({
		onCollectionModified,
		getCollectionConfig,
		recentlyVisitedPaths,
		setRecentlyVisitedPaths,
		favouritePaths,
		setFavouritePaths,
		collapsedGroups,
		setCollapsedGroups
	}), [
		configVersion,
		onCollectionModified,
		getCollectionConfig,
		recentlyVisitedPaths,
		setRecentlyVisitedPaths,
		favouritePaths,
		setFavouritePaths,
		collapsedGroups,
		setCollapsedGroups
	]);
}
//#endregion
//#region src/hooks/useBuildModeController.tsx
/**
* Use this hook to build a color mode controller that determines
* the theme of the admin
*/
function useBuildModeController() {
	const prefersDarkModeQuery = useCallback(() => {
		if (typeof window === "undefined") return false;
		return window.matchMedia("(prefers-color-scheme: dark)").matches;
	}, []);
	const [mode, setMode] = useState(() => {
		const storedPrefersDarkMode = readStoredString("prefers-dark-mode");
		return (storedPrefersDarkMode != null ? storedPrefersDarkMode === "true" : null) ?? prefersDarkModeQuery() ? "dark" : "light";
	});
	useEffect(() => {
		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handleChange = (e) => {
			if (readStoredString("prefers-dark-mode") == null) {
				setMode(e.matches ? "dark" : "light");
				setDocumentMode(e.matches ? "dark" : "light");
			}
		};
		setDocumentMode(mode);
		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, []);
	const setDocumentMode = (mode) => {
		if (mode === "dark") document.documentElement.classList.add("dark");
		else document.documentElement.classList.remove("dark");
	};
	const setModeInternal = useCallback((mode) => {
		if (mode === "light") {
			setDocumentMode("light");
			writeStoredString("prefers-dark-mode", "false");
			setMode("light");
		} else if (mode === "dark") {
			setDocumentMode("dark");
			writeStoredString("prefers-dark-mode", "true");
			setMode("dark");
		} else {
			const preferredMode = prefersDarkModeQuery() ? "dark" : "light";
			setDocumentMode(preferredMode);
			removeStoredString("prefers-dark-mode");
			setMode(preferredMode);
		}
	}, [prefersDarkModeQuery]);
	return useMemo(() => ({
		mode,
		setMode: setModeInternal
	}), [mode, setModeInternal]);
}
//#endregion
//#region src/hooks/useRebaseRegistry.tsx
var RegistryDispatchContext = createContext(void 0);
var RegistryStateContext = createContext({
	cmsConfig: null,
	studioConfig: null,
	authConfig: null
});
function RebaseRegistryProvider({ children }) {
	const [cmsConfig, setCmsConfig] = useState(null);
	const [studioConfig, setStudioConfig] = useState(null);
	const [authConfig, setAuthConfig] = useState(null);
	const dispatch = useMemo(() => ({
		registerAdmin: (config) => setCmsConfig(config),
		unregisterAdmin: () => setCmsConfig(null),
		registerStudio: (config) => setStudioConfig(config),
		unregisterStudio: () => setStudioConfig(null),
		registerAuth: (config) => setAuthConfig(config),
		unregisterAuth: () => setAuthConfig(null)
	}), []);
	const state = useMemo(() => ({
		cmsConfig,
		studioConfig,
		authConfig
	}), [
		cmsConfig,
		studioConfig,
		authConfig
	]);
	return /* @__PURE__ */ jsx(RegistryDispatchContext.Provider, {
		value: dispatch,
		children: /* @__PURE__ */ jsx(RegistryStateContext.Provider, {
			value: state,
			children
		})
	});
}
/**
* Returns the full registry (state + dispatch).
* Use this in RebaseShell where you need to read configs.
*/
function useRebaseRegistry() {
	const dispatch = useContext(RegistryDispatchContext);
	const state = useContext(RegistryStateContext);
	if (!dispatch) throw new Error("useRebaseRegistry must be used inside <Rebase>");
	return useMemo(() => ({
		...state,
		...dispatch
	}), [state, dispatch]);
}
/**
* Returns only the stable dispatch functions.
* Use this in feature components (RebaseAuth, RebaseCMS, RebaseStudio)
* to avoid re-render loops.
*/
function useRebaseRegistryDispatch() {
	const dispatch = useContext(RegistryDispatchContext);
	if (!dispatch) throw new Error("useRebaseRegistryDispatch must be used inside <Rebase>");
	return dispatch;
}
//#endregion
//#region src/hooks/ApiConfigContext.tsx
/** What the server uses when `basePath` is not configured. */
var DEFAULT_API_PATH = "/api";
var ApiConfigContext = React.createContext(void 0);
/**
* Read the API config from context. Returns `undefined` if no provider is present,
* allowing hooks to fall back to their own props.
*/
function useApiConfig() {
	return useContext(ApiConfigContext);
}
/**
* `apiUrl` and `apiPath` joined, with no trailing slash — the prefix to build a
* request URL from.
*
* Returns `undefined` when there is no provider, so a caller can keep its own
* fallback rather than silently requesting a relative path.
*/
function useApiBase() {
	const config = useApiConfig();
	if (!config?.apiUrl) return void 0;
	return config.apiUrl.replace(/\/+$/, "") + (config.apiPath || "/api");
}
/**
* The same prefix, derived from a client rather than from context.
*
* For the code that holds a `RebaseClient` but sits outside (or above) an
* `ApiConfigProvider`. Returns `undefined` when the client has no base URL,
* which is the signal every caller already treats as "cannot build a request".
*/
function apiBaseOf(client) {
	if (!client?.baseUrl) return void 0;
	return client.baseUrl.replace(/\/+$/, "") + (client.apiPath || "/api");
}
/**
* Provide API configuration (apiUrl, apiPath, getAuthToken) to the entire subtree.
* Typically rendered inside `<Rebase>` or at the app root.
*/
function ApiConfigProvider({ apiUrl, apiPath = DEFAULT_API_PATH, getAuthToken, children }) {
	const value = React.useMemo(() => ({
		apiUrl,
		apiPath,
		getAuthToken
	}), [
		apiUrl,
		apiPath,
		getAuthToken
	]);
	return /* @__PURE__ */ jsx(ApiConfigContext.Provider, {
		value,
		children
	});
}
//#endregion
//#region src/hooks/useBackendStorageSource.ts
/**
* React hook for using backend storage API as a StorageSource
*/
/**
* Hook to create a StorageSource that uses the backend storage REST API.
* Use this for self-hosted Rebase with local or S3 storage.
*
* @example
* ```tsx
* const storageSource = useBackendStorageSource({
*     apiUrl: 'http://localhost:3001',
*     getAuthToken: authController.getAuthToken
* });
*
* // Then pass to Rebase:
* <Rebase storageSource={storageSource} ... />
* ```
*/
function useBackendStorageSource({ apiUrl, apiPath = DEFAULT_API_PATH, getAuthToken }) {
	const storageBasePath = `${apiUrl.replace(/\/+$/, "")}${apiPath}/storage`;
	const urlsCache = useMemo(() => /* @__PURE__ */ new Map(), []);
	/**
	* Make an authenticated request to the storage API
	*/
	const fetchWithAuth = useCallback(async (url, options = {}) => {
		const token = await getAuthToken();
		return fetch(url, {
			...options,
			headers: {
				...options.headers,
				"Authorization": `Bearer ${token}`
			}
		});
	}, [getAuthToken]);
	/**
	* Upload a file to storage
	*/
	const putObject = useCallback(async ({ file, key, metadata, bucket }) => {
		const formData = new FormData();
		formData.append("file", file);
		if (key) formData.append("key", key);
		if (bucket) formData.append("bucket", bucket);
		if (metadata) {
			for (const [key, value] of Object.entries(metadata)) if (value !== void 0 && value !== null) formData.append(`metadata_${key}`, typeof value === "string" ? value : JSON.stringify(value));
		}
		const response = await fetchWithAuth(`${storageBasePath}/upload`, {
			method: "POST",
			body: formData
		});
		if (!response.ok) {
			const error = await response.json().catch(() => ({ error: "Upload failed" }));
			throw new Error(error.error || "Upload failed");
		}
		return (await response.json()).data;
	}, [fetchWithAuth, storageBasePath]);
	/**
	* Get download URL for a file
	*/
	const getSignedUrl = useCallback(async (keyOrUrl, bucket) => {
		const cacheKey = bucket ? `${bucket}/${keyOrUrl}` : keyOrUrl;
		const cached = urlsCache.get(cacheKey);
		if (cached) return cached;
		let filePath = keyOrUrl;
		if (filePath && (filePath.startsWith("local://") || filePath.startsWith("s3://"))) filePath = filePath.substring(filePath.indexOf("://") + 3);
		if (bucket && filePath && !filePath.startsWith(bucket)) filePath = `${bucket}/${filePath}`;
		if (!filePath || filePath.trim() === "" || filePath === "/") return {
			url: null,
			fileNotFound: true
		};
		const response = await fetchWithAuth(`${storageBasePath}/metadata/${filePath}`);
		if (response.status === 404) return {
			url: null,
			fileNotFound: true
		};
		if (!response.ok) {
			const error = await response.json().catch(() => ({ error: "Failed to get download URL" }));
			throw new Error(error.error || "Failed to get download URL");
		}
		const result = await response.json();
		const token = await getAuthToken();
		const tokenQuery = token ? `?token=${token}` : "";
		const downloadConfig = {
			url: `${storageBasePath}/file/${filePath}${tokenQuery}`,
			metadata: result.data
		};
		urlsCache.set(cacheKey, downloadConfig);
		return downloadConfig;
	}, [
		fetchWithAuth,
		storageBasePath,
		urlsCache
	]);
	/**
	* Get file as a File object
	*/
	const getObject = useCallback(async (key, bucket) => {
		let filePath = key;
		if (filePath && (filePath.startsWith("local://") || filePath.startsWith("s3://"))) filePath = filePath.substring(filePath.indexOf("://") + 3);
		if (bucket && filePath && !filePath.startsWith(bucket)) filePath = `${bucket}/${filePath}`;
		if (!filePath || filePath.trim() === "" || filePath === "/") return null;
		const response = await fetchWithAuth(`${storageBasePath}/file/${filePath}`);
		if (response.status === 404) return null;
		if (!response.ok) throw new Error("Failed to get file");
		const blob = await response.blob();
		const fileName = filePath.split("/").pop() || "file";
		return new File([blob], fileName, { type: blob.type });
	}, [fetchWithAuth, storageBasePath]);
	/**
	* Delete a file
	*/
	const deleteObject = useCallback(async (key, bucket) => {
		let filePath = key;
		if (filePath && (filePath.startsWith("local://") || filePath.startsWith("s3://"))) filePath = filePath.substring(filePath.indexOf("://") + 3);
		if (bucket && filePath && !filePath.startsWith(bucket)) filePath = `${bucket}/${filePath}`;
		if (!filePath || filePath.trim() === "" || filePath === "/") return;
		const response = await fetchWithAuth(`${storageBasePath}/file/${filePath}`, { method: "DELETE" });
		if (!response.ok && response.status !== 404) {
			const error = await response.json().catch(() => ({ error: "Failed to delete file" }));
			throw new Error(error.error || "Failed to delete file");
		}
		urlsCache.delete(bucket ? `${bucket}/${key}` : key);
	}, [
		fetchWithAuth,
		storageBasePath,
		urlsCache
	]);
	/**
	* List files in a path
	*/
	const listObjects = useCallback(async (prefix, options) => {
		const params = new URLSearchParams();
		if (prefix) params.set("prefix", prefix);
		if (options?.bucket) params.set("bucket", options.bucket);
		if (options?.maxResults) params.set("maxResults", String(options.maxResults));
		if (options?.pageToken) params.set("pageToken", options.pageToken);
		const response = await fetchWithAuth(`${storageBasePath}/list?${params.toString()}`);
		if (!response.ok) {
			const error = await response.json().catch(() => ({ error: "Failed to list files" }));
			throw new Error(error.error || "Failed to list files");
		}
		return (await response.json()).data;
	}, [fetchWithAuth, storageBasePath]);
	return useMemo(() => ({
		putObject,
		getSignedUrl,
		getObject,
		deleteObject,
		listObjects
	}), [
		putObject,
		getSignedUrl,
		getObject,
		deleteObject,
		listObjects
	]);
}
//#endregion
//#region src/hooks/usePermissions.ts
/**
* Hook to evaluate roles and permissions for the current user.
* It abstracts away the need to pass `authController` to permission evaluation functions.
*/
function usePermissions() {
	const authController = useAuthController();
	const canCreate = useCallback((collection, path) => canCreateEntity(collection, authController, path, null), [authController]);
	const canEdit = useCallback((collection, path, entity) => canEditEntity(collection, authController, path, entity), [authController]);
	const canDelete = useCallback((collection, path, entity) => canDeleteEntity(collection, authController, path, entity), [authController]);
	const canRead = useCallback((collection) => canReadCollection(collection, authController), [authController]);
	return useMemo(() => ({
		canCreate,
		canEdit,
		canDelete,
		canRead
	}), [
		canCreate,
		canEdit,
		canDelete,
		canRead
	]);
}
//#endregion
//#region src/hooks/useTranslation.ts
var REBASE_NS$1 = "rebase_core";
/**
* Internal hook for translating Rebase UI strings.
*
* Uses the `rebase_core` i18next namespace that is initialised by
* `RebaseI18nProvider`. Do NOT use `react-i18next` directly in internal
* components — always go through this hook so the namespace is consistent.
*
* @example
* const { t } = useTranslation();
* <Button>{t("save")}</Button>
*
* @internal
*/
function useTranslation() {
	const { t, i18n } = useTranslation$1(REBASE_NS$1);
	/**
	* Typed translation function scoped to RebaseTranslations keys.
	* Also supports i18next interpolation variables, e.g.
	*   t("add_to_field", { fieldName: "Tags" })
	*   t("error_deleting", { message: err.message })
	*   t("sort_key_position", { position: 2 })
	*
	* Numbers are accepted as well as strings: i18next interpolates them
	* either way, and a signature that took only strings pushed a `String()`
	* onto every call site with a count in it — `{{count}}`, `{{position}}`.
	*/
	const typedT = useCallback((key, vars) => t(key, vars), [t]);
	return useMemo(() => ({
		t: typedT,
		i18n
	}), [typedT, i18n]);
}
//#endregion
//#region src/hooks/useNavigationGroupLabel.ts
/**
* The display label for a navigation group.
*
* A group *name* is an identifier: `StudioHomePage` orders the built-in groups
* by comparing against `["Database", "Compute", …]`, `navigationGroupMappings`
* keys its icons off it, and `AppView.group` sorts views into one by string
* equality. Translating the name at the point it is declared therefore breaks
* the ordering and drops the icons in every locale but English — which is
* exactly what happened the first time the Studio tool list was translated.
*
* So the name stays English and the *label* is translated here, at the one
* place it is drawn. A group Rebase does not ship — anything an app declares
* itself — has no key and is rendered as written.
*
* @group Hooks and utilities
*/
function useNavigationGroupLabel() {
	const { t } = useTranslation();
	return useCallback((group) => {
		const key = `studio_group_${group.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
		const label = t(key);
		return label === key ? group : label;
	}, [t]);
}
//#endregion
//#region src/hooks/useResolvedComponent.tsx
/**
* Internal cache for resolved lazy components.
*
* `React.lazy()` must return the SAME wrapper across renders — if we call
* `lazy(loader)` on every render, React will treat each result as a new
* component type and unmount/remount on each render cycle.
*
* This WeakMap keeps a stable mapping from a `ComponentRef` (object/function)
* to the `React.lazy()` wrapper it produced. Strings are keyed by a separate
* plain Map since they can't be WeakMap keys.
*/
var lazyCache = /* @__PURE__ */ new WeakMap();
/**
* Resolves a `ComponentRef` into a renderable `React.ComponentType`.
*
* This hook handles all three forms of `ComponentRef`:
*
* 1. **`LazyComponentRef`** (produced by the Vite plugin from string paths):
*    Wraps the lazy loader with `React.lazy()` for automatic code-splitting.
*
* 2. **`() => Promise<{ default: ComponentType }>`** (manual lazy import):
*    Also wraps with `React.lazy()`. Distinguished from regular components
*    by checking that the function has no parameters and is not a known
*    React internal (no `$$typeof`).
*
* 3. **Direct `React.ComponentType`**:
*    Returned as-is.
*
* If the ref is `undefined` or a raw string (which should never happen at
* runtime in the browser since the Vite plugin transforms strings), returns `undefined`.
*
* The returned component is stable across re-renders as long as the `ref`
* is referentially stable.
*
* **Usage:** Wrap the rendered component in `<Suspense>` to handle the
* loading state of lazy components.
*
* @example
* ```tsx
* const ResolvedField = useResolvedComponent(property.ui?.Field);
* if (!ResolvedField) return null;
* return (
*     <Suspense fallback={<CircularProgress />}>
*         <ResolvedField {...fieldProps} />
*     </Suspense>
* );
* ```
* */
function useResolvedComponent(ref) {
	return useMemo(() => resolveComponentRef(ref), [ref]);
}
/**
* Wraps a loader function with `React.lazy()`, caching the result so the
* same loader always returns the same lazy component identity.
*/
function getOrCreateLazy(key, loader) {
	const cached = lazyCache.get(key);
	if (cached) return cached;
	const LazyComponent = lazyChunk(loader);
	lazyCache.set(key, LazyComponent);
	return LazyComponent;
}
/**
* Pure function version of the resolver, for use outside React components.
* Same resolution logic as `useResolvedComponent`.
*
* Results are cached per reference identity — calling this multiple times
* with the same `ref` object returns the same `React.ComponentType`.
*/
function resolveComponentRef(ref) {
	if (ref == null) return void 0;
	if (typeof ref === "string") {
		console.warn(`[Rebase] Encountered a raw string ComponentRef ("${ref}") at runtime. This usually means the Vite transform plugin did not process this file. Ensure the file is inside the configured collectionsDir.`);
		return;
	}
	if (isLazyComponentRef(ref)) return getOrCreateLazy(ref, () => ref.load());
	if (typeof ref === "function") {
		const fn = ref;
		if (fn.prototype?.isReactComponent) return ref;
		if ("$$typeof" in fn) return ref;
		if (fn.length > 0) return ref;
		const source = (() => {
			try {
				return Function.prototype.toString.call(fn);
			} catch {
				return "";
			}
		})();
		if (!/\b(?:import|require)\s*\(/.test(source) && fn.name && /^[A-Z]/.test(fn.name)) return ref;
		return getOrCreateLazy(fn, ref);
	}
	if (typeof ref === "object" && "$$typeof" in ref) return ref;
}
//#endregion
//#region src/hooks/useComponentOverride.tsx
/**
* Resolves a potentially overridden component.
*
* Resolution order:
* 1. Collection-scoped override (highest priority)
* 2. Global override
* 3. Default component (fallback)
*
* Supports two override modes:
* - **Eject** (default): The override component fully replaces the default.
* - **Wrap** (`wrap: true`): The override component wraps the default.
*   The default is passed as `OriginalComponent` in props.
*
* @param name - The overridable component name (e.g. `"Entity.Form"`)
* @param DefaultComponent - The built-in default component
* @returns The resolved component — either the default, a full replacement, or a wrapper
*
* @example
* ```tsx
* import { useComponentOverride } from "@rebasepro/app";
*
* function EntityFormWrapper(props: EntityFormProps) {
*     const ResolvedForm = useComponentOverride("Entity.Form", DefaultEntityForm);
*     return <ResolvedForm {...props} />;
* }
* ```
*
* @group Hooks
*/
function useComponentOverride(name, DefaultComponent) {
	const { globalOverrides, collectionOverrides } = useContext(ComponentOverrideContext);
	return useMemo(() => {
		const override = collectionOverrides[name] ?? globalOverrides[name];
		if (!override) return DefaultComponent;
		const UserComponent = resolveComponentRef(override.Component);
		if (!UserComponent) return DefaultComponent;
		if (override.wrap) {
			const Wrapper = (props) => /* @__PURE__ */ jsx(UserComponent, {
				...props,
				OriginalComponent: DefaultComponent
			});
			Wrapper.displayName = `Wrapped(${name})`;
			return Wrapper;
		}
		return UserComponent;
	}, [
		collectionOverrides,
		globalOverrides,
		name,
		DefaultComponent
	]);
}
//#endregion
//#region src/components/ErrorTooltip.tsx
function ErrorTooltip(props) {
	return /* @__PURE__ */ jsx(Tooltip, {
		...props,
		className: props.className,
		tooltipClassName: "text-red-500! bg-red-50",
		children: props.children
	});
}
//#endregion
//#region src/components/ErrorView.tsx
/**
* Generic error view. Displayed for example when an unexpected value comes
* from the driver in a collection view.
* @param title
* @param error
* @param tooltip
* @param onRetry

* @group Components
*/
function ErrorView({ title, error, tooltip, onRetry }) {
	const message = error instanceof Error ? error.message : error;
	const errorCode = error instanceof Error && "code" in error ? error.code : void 0;
	const body = /* @__PURE__ */ jsx("div", {
		className: "flex flex-col m-2",
		children: /* @__PURE__ */ jsxs("div", {
			className: "flex items-start gap-2.5",
			children: [/* @__PURE__ */ jsx(AlertTriangleIcon, {
				size: iconSize.smallest,
				className: "shrink-0 text-red-500 dark:text-red-400 mt-0.5"
			}), /* @__PURE__ */ jsxs("div", {
				className: "flex-1 min-w-0",
				children: [
					title && /* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						color: "primary",
						className: "font-semibold mb-0.5",
						children: title
					}),
					/* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						color: "secondary",
						className: "leading-relaxed",
						children: message
					}),
					errorCode && /* @__PURE__ */ jsx("span", {
						className: "inline-block mt-1 px-1.5 py-0.5 text-[10px] font-mono rounded bg-surface-raised text-text-secondary",
						children: errorCode
					}),
					onRetry && /* @__PURE__ */ jsx("div", {
						className: "mt-3",
						children: /* @__PURE__ */ jsx(Button, {
							variant: "outlined",
							color: "neutral",
							size: "small",
							onClick: onRetry,
							className: "font-semibold",
							children: "Try again"
						})
					})
				]
			})]
		})
	});
	if (tooltip) return /* @__PURE__ */ jsx(ErrorTooltip, {
		title: tooltip,
		children: body
	});
	return body;
}
//#endregion
//#region src/components/common/useDebouncedData.ts
/**
* Hack to prevent data updates for incomplete callbacks from Firestore
* triggers.
* If any deps change, the update is immediate
* @param data
* @param deps
* @param timeoutMs
*/
function useDebouncedData(data, deps, timeoutMs = 5e3) {
	const [deferredData, setDeferredData] = React.useState(data);
	const dataLength = React.useRef(deferredData.length ?? 0);
	const pendingUpdate = React.useRef(false);
	const currentDeps = React.useRef(deps);
	const haveDepsChanged = !deepEqual(currentDeps.current, deps);
	const immediateUpdate = data.length >= dataLength.current || haveDepsChanged;
	React.useEffect(() => {
		const performUpdate = () => {
			if (!deepEqual(deferredData, data)) {
				currentDeps.current = deps;
				dataLength.current = data.length;
				setDeferredData(data);
			}
			pendingUpdate.current = false;
		};
		pendingUpdate.current = true;
		let handler;
		if (immediateUpdate) performUpdate();
		else handler = setTimeout(performUpdate, timeoutMs);
		return () => {
			clearTimeout(handler);
			if (pendingUpdate.current && immediateUpdate) performUpdate();
		};
	}, [
		data,
		timeoutMs,
		deps,
		immediateUpdate
	]);
	return immediateUpdate ? data : deferredData;
}
//#endregion
//#region src/collections/property-path.ts
/**
* The property at a dotted path, walking `map` children — `address.street`.
*
* The value counterpart is `getValueInPath` in `@rebasepro/utils`; this is the
* schema half, and the two have to be used together. Reading a dotted path off
* an entity while looking its property up with a flat `properties[path]` gives
* the value and `undefined` for how to render it, which is how a declared title
* on a nested field silently fell back to a derived one.
*
* There were three copies of this: one private to `useColumnsIds`, one exported
* from the admin layer, and the flat lookup in the title resolver that was not
* this function at all. This is the one, in the lowest layer that needs it —
* admin re-exports it under the name it already published.
*/
function getPropertyInPath(properties, path) {
	if (typeof properties !== "object" || !properties) return void 0;
	if (path in properties) return properties[path];
	if (path.includes(".")) {
		const pathSegments = path.split(".");
		const childProperty = properties[pathSegments[0]];
		if (typeof childProperty === "object" && childProperty?.type === "map" && childProperty.properties) return getPropertyInPath(childProperty.properties, pathSegments.slice(1).join("."));
	}
}
//#endregion
//#region src/collections/property_presentation.ts
/**
* A condition stated as a literal rather than as a rule.
*
* `PropertyConditions` accepts either, and the two are answered in different
* places: a rule needs a context and so can only be evaluated while rendering a
* particular entity, but a literal is already the answer. Reading it here is
* what makes `hidden: true` work without every gate below having to build a
* condition context first — and without the caller reaching for
* `{ "==": [1, 1] }` to say something it can say with a boolean.
*/
function literalCondition(condition) {
	return condition === true;
}
function isReadOnly(property) {
	if (property.admin?.readOnly) return true;
	if (literalCondition(property.conditions?.readOnly)) return true;
	if (property.type === "date") {
		if (property.autoValue) return true;
	}
	if (property.type === "reference") return !property.path && !("Field" in (property.admin || {}) && property.admin?.Field);
	return false;
}
function isHidden(property) {
	if (literalCondition(property.conditions?.hidden)) return true;
	return typeof property.admin?.disabled === "object" && Boolean(property.admin?.disabled.hidden);
}
/**
* Whether the field is disabled by its own declaration, ignoring form state.
*
* The `admin.disabled` block and `conditions.disabled: true` say the same thing
* two ways, so every caller that gated on the first now asks here instead of
* growing a second check of its own.
*/
function isDisabled(property) {
	return Boolean(property.admin?.disabled) || literalCondition(property.conditions?.disabled);
}
function applyPropertyConditions(property, context) {
	const { conditions } = property;
	if (!conditions) return property;
	const result = { ...property };
	if (conditions.disabled) {
		if (evaluateCondition(conditions.disabled, context)) {
			result.admin = result.admin || {};
			result.admin.disabled = {
				clearOnDisabled: conditions.clearOnDisabled ?? false,
				disabledMessage: conditions.disabledMessage,
				hidden: false
			};
		}
	}
	if (conditions.hidden) {
		if (evaluateCondition(conditions.hidden, context)) {
			result.admin = result.admin || {};
			result.admin.disabled = {
				...typeof result.admin?.disabled === "object" ? result.admin.disabled : {},
				hidden: true,
				clearOnDisabled: conditions.clearOnDisabled ?? false
			};
		}
	}
	if (conditions.readOnly) {
		if (evaluateCondition(conditions.readOnly, context)) {
			result.admin = result.admin || {};
			result.admin.readOnly = true;
		}
	}
	if (conditions.required !== void 0) {
		const isRequired = evaluateCondition(conditions.required, context);
		result.validation = {
			...result.validation,
			required: isRequired,
			requiredMessage: conditions.requiredMessage
		};
	}
	if (context.isNew && conditions.defaultValue !== void 0) result.defaultValue = evaluateCondition(conditions.defaultValue, context);
	if ("enum" in result && result.enum && (conditions.enumConditions || conditions.allowedEnumValues || conditions.excludedEnumValues)) result.enum = applyEnumConditions(result.enum, conditions, context);
	if (result.type === "reference") {
		if (conditions.referencePath) result.path = evaluateCondition(conditions.referencePath, context);
		if (conditions.referenceFilter) {
			result.admin = result.admin || {};
			result.admin.fixedFilter = evaluateCondition(conditions.referenceFilter, context);
		}
	}
	if (result.type === "array") {
		if (conditions.canAddElements !== void 0) {
			result.admin = result.admin || {};
			result.admin.canAddElements = evaluateCondition(conditions.canAddElements, context);
		}
		if (conditions.sortable !== void 0) {
			result.admin = result.admin || {};
			result.admin.sortable = evaluateCondition(conditions.sortable, context);
		}
	}
	return result;
}
/**
* Convert an object with numeric keys back to an array.
* Firestore stores arrays as {"0": "a", "1": "b"} to avoid nested arrays.
*/
function objectToArray(obj) {
	if (Array.isArray(obj)) return obj.map(String);
	if (obj && typeof obj === "object") {
		const keys = Object.keys(obj);
		if (keys.length > 0 && keys.every((k) => !isNaN(Number(k)))) return keys.sort((a, b) => Number(a) - Number(b)).map((k) => obj[k]).filter((v) => typeof v === "string" || typeof v === "number").map(String);
	}
	return [];
}
/**
* Apply enum-specific conditions to filter and modify enum values.
*/
function applyEnumConditions(enumValues, conditions, context) {
	let result = [...enumValues];
	if (conditions.allowedEnumValues) {
		const allowedArray = objectToArray(evaluateCondition(conditions.allowedEnumValues, context));
		if (allowedArray.length > 0) result = result.filter((ev) => allowedArray.includes(String(ev.id)));
	}
	if (conditions.excludedEnumValues) {
		const excludedArray = objectToArray(evaluateCondition(conditions.excludedEnumValues, context));
		if (excludedArray.length > 0) result = result.filter((ev) => !excludedArray.includes(String(ev.id)));
	}
	if (conditions.enumConditions) result = result.map((ev) => {
		const evConditions = conditions.enumConditions?.[ev.id];
		if (!evConditions) return ev;
		if (evConditions.hidden && evaluateCondition(evConditions.hidden, context)) return null;
		if (evConditions.disabled && evaluateCondition(evConditions.disabled, context)) return {
			...ev,
			disabled: true
		};
		return ev;
	}).filter((ev) => ev !== null);
	return result;
}
//#endregion
//#region src/components/common/useColumnsIds.tsx
function getSubcollectionColumnId(collection) {
	return `subcollection:${collection.slug}`;
}
/**
* The jump-to-tab columns a collection table does not need, because the relation
* behind them already has a column of its own.
*
* Every child view gets a 200px button column that opens its tab. For a relation
* declared in `relations` that button is the relation's only presence in the
* table. For one declared as a property it is the second: the property's own
* column is already there, hydrated by the list fetch's `include: ["*"]`, showing
* the child rows themselves — and carrying the *same heading*, because the tab
* takes its name from the declaring property. Two columns called "Applications",
* one of them a button, and nothing in the header to tell them apart.
*
* The property column wins: it shows what the children are, and each chip in it
* opens one. The tab stays reachable by opening the record, which is what the
* rest of the table's rows do anyway.
*
* Unless the author hid that column — `hideFromCollection` on the property, or a
* `propertiesOrder` that omits it, is a statement about the column and not about
* the relation, so the button comes back rather than the relation dropping out
* of the table altogether.
*/
function getRedundantChildViewColumnIds(collection) {
	const redundant = /* @__PURE__ */ new Set();
	const properties = collection.properties ?? {};
	const order = collection.propertiesOrder;
	for (const [viewKey, propertyKey] of getChildViewDeclaringProperties(collection)) {
		const property = properties[propertyKey];
		if (!property || property.admin?.hideFromCollection) continue;
		if (order && !order.includes(propertyKey)) continue;
		redundant.add(`subcollection:${viewKey}`);
	}
	return redundant;
}
function useColumnIds(collection, includeSubcollections) {
	return useMemo(() => {
		if (collection.propertiesOrder) return hideAndExpandKeys(collection, collection.propertiesOrder);
		return getDefaultColumnKeys(collection, includeSubcollections);
	}, [collection, includeSubcollections]);
}
function hideAndExpandKeys(collection, keys) {
	const rootsWithExplicitChildren = /* @__PURE__ */ new Set();
	for (const key of keys) if (key.includes(".")) {
		const rootKey = key.split(".")[0];
		const rootProperty = collection.properties[rootKey];
		if (rootProperty && rootProperty.type === "map" && rootProperty.admin?.spreadChildren && rootProperty.properties) rootsWithExplicitChildren.add(rootKey);
	}
	const processedPropertyKeys = /* @__PURE__ */ new Set();
	const redundantChildViewColumns = getRedundantChildViewColumnIds(collection);
	const result = keys.flatMap((key) => {
		if (processedPropertyKeys.has(key)) return [null];
		if (redundantChildViewColumns.has(key)) return [null];
		const property = collection.properties[key];
		if (property) {
			processedPropertyKeys.add(key);
			if (property.admin?.hideFromCollection) return [null];
			if (property.admin?.disabled && typeof property.admin?.disabled === "object" && property.admin?.disabled.hidden) return [null];
			if (property.type === "map" && property.admin?.spreadChildren && property.properties) {
				if (rootsWithExplicitChildren.has(key)) return [null];
				const childConfigs = getColumnKeysForProperty(property, key);
				childConfigs.forEach((c) => processedPropertyKeys.add(c.key));
				return childConfigs;
			}
			return [{
				key,
				disabled: isDisabled(property) || Boolean(property.admin?.readOnly)
			}];
		}
		if (key.includes(".")) {
			const rootKey = key.split(".")[0];
			const rootProperty = collection.properties[rootKey];
			if (rootProperty && rootProperty.type === "map" && rootProperty.properties) {
				const nestedProperty = getPropertyInPath(collection.properties, key);
				if (nestedProperty) {
					processedPropertyKeys.add(key);
					processedPropertyKeys.add(rootKey);
					if (nestedProperty.admin?.hideFromCollection) return [null];
					if (nestedProperty.admin?.disabled && typeof nestedProperty.admin?.disabled === "object" && nestedProperty.admin?.disabled.hidden) return [null];
					return [{
						key,
						disabled: isDisabled(rootProperty) || Boolean(rootProperty.admin?.readOnly) || isDisabled(nestedProperty) || Boolean(nestedProperty.admin?.readOnly)
					}];
				}
			}
		}
		if (collection.additionalFields?.find((field) => field.key === key)) return [{
			key,
			disabled: true
		}];
		const subcollections = getSubcollections(collection);
		if (subcollections) {
			if (subcollections.find((subCol) => getSubcollectionColumnId(subCol) === key)) return [{
				key,
				disabled: true
			}];
		}
		return [null];
	}).filter(Boolean);
	for (const propKey of Object.keys(collection.properties)) {
		if (processedPropertyKeys.has(propKey)) continue;
		const property = collection.properties[propKey];
		if (!property) continue;
		if (property.admin?.hideFromCollection) continue;
		if (property.admin?.disabled && typeof property.admin?.disabled === "object" && property.admin?.disabled.hidden) continue;
		if (property.type === "map" && property.admin?.spreadChildren && property.properties) {
			const allChildConfigs = getColumnKeysForProperty(property, propKey);
			for (const childConfig of allChildConfigs) if (!processedPropertyKeys.has(childConfig.key)) {
				result.push(childConfig);
				processedPropertyKeys.add(childConfig.key);
			}
		} else {
			result.push({
				key: propKey,
				disabled: isDisabled(property) || Boolean(property.admin?.readOnly)
			});
			processedPropertyKeys.add(propKey);
		}
	}
	return result;
}
function getDefaultColumnKeys(collection, includeSubCollections) {
	const propertyKeys = Object.keys(collection.properties);
	const additionalFields = collection.additionalFields ?? [];
	const subCollections = getSubcollections(collection) ?? [];
	const columnIds = [...propertyKeys, ...additionalFields.filter((field) => !propertyKeys.includes(field.key)).map((field) => field.key)];
	if (includeSubCollections) {
		const subCollectionIds = subCollections.map((collection) => getSubcollectionColumnId(collection));
		columnIds.push(...subCollectionIds.filter((subColId) => !columnIds.includes(subColId)));
	}
	return hideAndExpandKeys(collection, columnIds);
}
function getColumnKeysForProperty(property, key, disabled) {
	if (!property) return [];
	if (property.type === "map" && property.admin?.spreadChildren && property.properties) return Object.entries(property.properties).flatMap(([childKey, childProperty]) => getColumnKeysForProperty(childProperty, `${key}.${childKey}`, disabled || isDisabled(property) || Boolean(property.admin?.readOnly)));
	return [{
		key,
		disabled: disabled || isDisabled(property) || Boolean(property.admin?.readOnly)
	}];
}
function getFormFieldKeys(collection) {
	const properties = collection.properties ?? {};
	const consumedByChildView = getChildViewRelationPropertyKeys(collection);
	const propertyKeys = Object.keys(properties).filter((key) => {
		if (!consumedByChildView.has(key)) return true;
		return properties[key].admin?.renderInForm === true;
	});
	const additionalFields = collection.additionalFields ?? [];
	const allKeys = [...propertyKeys, ...additionalFields.map((field) => field.key)];
	if (collection.propertiesOrder) return collection.propertiesOrder.filter((key) => allKeys.includes(key));
	return allKeys;
}
//#endregion
//#region src/hooks/data/useDataOrder.ts
/**
* This hook is used to have some entities at the beginning of data.
* @param path
* @param entitiesDisplayedFirst
* @group Hooks and utilities
*/
function useDataOrder({ data, entitiesDisplayedFirst }) {
	if (!entitiesDisplayedFirst) return data;
	const displayedFirstId = new Set(entitiesDisplayedFirst.map((e) => e.id));
	return [...entitiesDisplayedFirst, ...data.filter((e) => !displayedFirstId.has(e.id))];
}
//#endregion
//#region src/components/common/useDataTableController.tsx
var DEFAULT_PAGE_SIZE = 50;
/**
* Use this hook to build a controller for the {@link DataCollectionTable}.
* This controller is bound to data in a path in your specified driver.
*
* Note that you can build your own hook returning a {@link EntityTableController}
* if you would like to display different data.
*
* @param path
* @param collection
* @param scrollRestoration
* @param entitiesDisplayedFirst
* @param lastDeleteTimestamp
* @param fixedFilterFromProps
* @param updateUrl
*/
function useDataTableController({ path, collection, scrollRestoration, entitiesDisplayedFirst, lastDeleteTimestamp: _lastDeleteTimestamp, fixedFilter: fixedFilterFromProps, updateUrl }) {
	const { defaultFilter, sort, fixedFilter: fixedFilterFromCollection } = collection;
	const [popupCell, setPopupCell] = React.useState(void 0);
	const dataClient = useData();
	const fixedFilter = fixedFilterFromProps ?? fixedFilterFromCollection;
	const paginationEnabled = collection.pagination === void 0 || Boolean(collection.pagination);
	const pageSize = typeof collection.pagination === "number" && collection.pagination > 0 ? collection.pagination : 50;
	const location = useLocation();
	const [searchString, setSearchString] = React.useState(() => {
		if (updateUrl) {
			const urlSearch = new URLSearchParams(location.search).get("search");
			return urlSearch ? decodeURIComponent(urlSearch) : void 0;
		}
	});
	const checkFilterCombination = useCallback((filterValues, sortBy) => {
		return true;
	}, []);
	const sortInternal = useMemo(() => {
		const keys = normalizeOrderBy(sort);
		if (keys && fixedFilter && !checkFilterCombination(fixedFilter, keys)) {
			console.warn("Initial sort is not compatible with the force filter. Ignoring initial sort");
			return;
		}
		return keys;
	}, [sort, fixedFilter]);
	const { filterValues: filterUrl, sortBy: sortUrl } = parseFilterAndSort(location.search);
	const [filterValues, setFilterValues] = React.useState(fixedFilter ?? (updateUrl ? filterUrl : void 0) ?? defaultFilter ?? void 0);
	const [sortBy, setSortBy] = React.useState((updateUrl ? sortUrl : void 0) ?? sortInternal);
	const lastSyncedSearchRef = React.useRef(location.search);
	useEffect(() => {
		if (!updateUrl) return;
		if (lastSyncedSearchRef.current === location.search) return;
		lastSyncedSearchRef.current = location.search;
		const { filterValues: urlFilterValues, sortBy: urlSortBy } = parseFilterAndSort(location.search);
		if (!fixedFilter) setFilterValues(urlFilterValues ?? defaultFilter);
		if (urlSortBy && fixedFilter && !checkFilterCombination(fixedFilter, urlSortBy)) console.warn("URL sort is not compatible with the force filter.");
		else setSortBy(urlSortBy);
		const urlSearch = new URLSearchParams(location.search).get("search");
		setSearchString(urlSearch ? decodeURIComponent(urlSearch) : void 0);
	}, [
		location.search,
		updateUrl,
		fixedFilter,
		checkFilterCombination
	]);
	useUpdateUrl(filterValues, sortBy, searchString, updateUrl);
	const collectionScroll = scrollRestoration?.getCollectionScroll(path, filterValues);
	/**
	* How many rows to ask for on mount: as many as were loaded last time this
	* (path, filters) pair was on screen, so returning to a view that had been
	* scrolled does not snap back to the first page.
	*
	* The floor is not cosmetic. This used to be `?? pageSize`, which falls back
	* on `null`/`undefined` but not on `0` — and a saved entry whose `data` is
	* empty is entirely ordinary: `updateCollectionScroll` records what was on
	* screen, so any filter combination matching no rows persists `data: []`
	* under its own key. Restoring that gave `itemCount === 0`, which became
	* `limit=0` on the read, which the API rejects (400 `INVALID_LIMIT`) —
	* the collection rendered an error instead of a table, and a client bug
	* read as an API failure.
	*
	* The cache is left free to record an empty view honestly: that it *was*
	* empty is a true fact, and refusing to store it would leave the previous,
	* now-wrong offset and rows in place for a view whose rows have gone. What
	* is wrong is reading "nothing was restored" as "ask for nothing".
	*/
	const restoredItemCount = collectionScroll?.data.length ?? 0;
	const initialItemCount = restoredItemCount > 0 ? restoredItemCount : pageSize;
	useEffect(() => {
		if (scrollRestoration) scrollRestoration.updateCollectionScroll({
			path,
			scrollOffset: collectionScroll?.scrollOffset ?? 0,
			data: rawData,
			filters: filterValues
		});
	}, []);
	const [itemCount, setItemCount] = React.useState(paginationEnabled ? initialItemCount : void 0);
	const sortKey = sortBy ? serializeOrderBy(sortBy) : void 0;
	const context = useRebaseContext();
	const [rawData, setRawData] = useState(collectionScroll?.data ?? []);
	const onScroll = useCallback(({ scrollOffset }) => {
		if (scrollRestoration) scrollRestoration.updateCollectionScroll({
			path,
			scrollOffset,
			data: rawData,
			filters: filterValues
		});
	}, [
		scrollRestoration,
		path,
		rawData,
		filterValues
	]);
	const [dataLoading, setDataLoading] = useState(false);
	const [dataLoadingError, setDataLoadingError] = useState();
	const [noMoreToLoad, setNoMoreToLoad] = useState(false);
	/**
	* Clear the user's filters. `fixedFilter` survives — it is the collection
	* forcing a scope, not something the user chose — but `defaultFilter` does
	* not: a default is where the view *opens*, not a floor it can never go
	* below.
	*
	* It used to reset to `defaultFilter`, which made "clear" unable to clear
	* whenever a collection defined one, and every caller here wanted the
	* opposite. `ClearFilterSortButton` is labelled "clear filter" and left one
	* set. `EditorCollectionActionStart` calls this and then re-applies
	* `collection.defaultFilter` on the next line — redundant under the old
	* behaviour, and the clearest statement of what it expected.
	* `FilterPresetsButton` documents "clearing all filters deactivates all
	* chips", which was false: it derives a chip's active state from the
	* filters, so a preset whose values equalled `defaultFilter` switched
	* itself straight back on. That chip could not be turned off at all — click
	* it, the keys are removed, the filter resets to the identical default, and
	* the chip lights up again.
	*/
	const clearFilter = useCallback(() => setFilterValues(fixedFilter ?? void 0), [fixedFilter]);
	const updateFilterValues = useCallback((updatedFilter) => {
		if (fixedFilter) {
			console.warn("Filter is not compatible with the force filter. Ignoring filter");
			return;
		}
		if (updatedFilter && Object.keys(updatedFilter).length === 0) setFilterValues(void 0);
		else setFilterValues(updatedFilter);
	}, [fixedFilter]);
	useEffect(() => {
		setDataLoading(true);
		const onEntitiesUpdate = async (entities) => {
			if (collection.browserCallbacks?.afterRead) try {
				entities = await Promise.all(entities.map(async (entity) => {
					const processedRow = await collection.browserCallbacks.afterRead({
						collection,
						path,
						row: {
							id: entity.id,
							...entity.values
						},
						context
					});
					return {
						...entity,
						values: processedRow
					};
				}));
			} catch (_e) {
				console.error(_e);
			}
			setDataLoading(false);
			setDataLoadingError(void 0);
			setRawData(entities.map((e) => ({ ...e })));
			setNoMoreToLoad(!itemCount || entities.length < itemCount);
			populateFetchCache(path, entities);
		};
		const onError = (error) => {
			console.error("ERROR", error);
			setDataLoading(false);
			setRawData((prev) => prev && prev.length > 0 ? prev : []);
			setDataLoadingError(error);
		};
		const accessor = dataClient.collection(path);
		const whereParams = filterValues && Object.keys(filterValues).length > 0 ? filterValues : void 0;
		const orderByParams = sortBy && sortBy.length > 0 ? sortBy.map(([field, direction]) => [String(field), direction]) : void 0;
		let unsubscribe;
		const includeParams = getRelationIncludeParams(collection);
		if (accessor.listen) unsubscribe = accessor.listen(toFindParams({
			where: whereParams,
			limit: itemCount,
			orderBy: orderByParams,
			searchString,
			include: includeParams
		}), (res) => onEntitiesUpdate(res.data), onError);
		else {
			accessor.find(toFindParams({
				where: whereParams,
				limit: itemCount,
				orderBy: orderByParams,
				searchString,
				include: includeParams
			})).then((res) => onEntitiesUpdate(res.data)).catch(onError);
			unsubscribe = () => void 0;
		}
		return unsubscribe;
	}, [
		dataClient,
		path,
		itemCount,
		sortKey,
		filterValues,
		searchString
	]);
	const data = useDataOrder({
		data: rawData,
		entitiesDisplayedFirst
	});
	return useMemo(() => ({
		data,
		dataLoading,
		noMoreToLoad,
		dataLoadingError,
		filterValues,
		setFilterValues: updateFilterValues,
		sortBy,
		setSortBy,
		searchString,
		setSearchString,
		clearFilter,
		itemCount,
		setItemCount,
		initialScroll: collectionScroll?.scrollOffset,
		onScroll,
		paginationEnabled,
		pageSize,
		checkFilterCombination,
		popupCell,
		setPopupCell
	}), [
		data,
		dataLoading,
		noMoreToLoad,
		dataLoadingError,
		filterValues,
		updateFilterValues,
		sortBy,
		searchString,
		clearFilter,
		itemCount,
		collectionScroll?.scrollOffset,
		onScroll,
		paginationEnabled,
		pageSize,
		checkFilterCombination,
		popupCell
	]);
}
function useUpdateUrl(filterValues, sortBy, searchString, updateUrl) {
	useEffect(() => {
		if (updateUrl) {
			const existingParams = new URLSearchParams(window.location.search);
			const preservedParams = new URLSearchParams();
			existingParams.forEach((value, key) => {
				if (key.startsWith("__") && key !== "__sort" && key !== "__sort_order") preservedParams.set(key, value);
			});
			const filterSortState = encodeFilterAndSort(filterValues, sortBy);
			const search = searchString ? `search=${encodeURIComponent(searchString)}` : "";
			const state = [
				preservedParams.toString(),
				filterSortState,
				search
			].filter(Boolean).join("&");
			const hash = window.location.hash;
			if (state === "") window.history.replaceState({}, "", `${window.location.pathname}${hash}`);
			else window.history.replaceState({}, "", `?${state}${hash}`);
		}
	}, [
		filterValues,
		sortBy,
		searchString,
		updateUrl
	]);
}
function encodeFilterAndSort(filterValues, sortBy) {
	const entries = {};
	if (sortBy && sortBy.length > 0) {
		entries["__sort"] = sortBy.map(([field]) => encodeURIComponent(field)).join(",");
		entries["__sort_order"] = sortBy.map(([, direction]) => direction).join(",");
	}
	if (filterValues) Object.entries(filterValues).forEach(([key, value]) => {
		if (value) {
			const [op, val] = (Array.isArray(value[0]) ? value : [value])[0] || [];
			if (op) {
				let encodedValue = val;
				try {
					if (typeof val === "object") {
						if (val instanceof Date) encodedValue = val.toISOString();
						else if (Array.isArray(val)) encodedValue = JSON.stringify(val, (k, v) => {
							if (v instanceof EntityRelation) return encodeRelation(v);
							if (v instanceof EntityReference) return encodeReference(v);
							return v;
						});
						else if (val instanceof EntityRelation) encodedValue = encodeRelation(val);
						else if (val instanceof EntityReference) encodedValue = encodeReference(val);
					}
				} catch (e) {
					encodedValue = val;
				}
				if (encodedValue !== void 0) {
					entries[encodeURIComponent(`${key}_op`)] = encodeURIComponent(op);
					entries[encodeURIComponent(`${key}_value`)] = encodedValue ? encodeURIComponent(String(encodedValue)) : "null";
				}
			}
		}
	});
	if (!Object.keys(entries).length) return "";
	return Object.entries(entries).map(([key, value]) => `${key}=${value}`).join("&");
}
function parseFilterAndSort(search) {
	const entries = new URLSearchParams(search);
	const filterValues = {};
	let sortBy = void 0;
	entries.forEach((value, key) => {
		if (key === "__sort") {
			const directions = (entries.get("__sort_order") ?? "").split(",");
			const keys = value.split(",").map((field) => decodeURIComponent(field).trim()).filter(Boolean).map((field, index) => [field, directions[index] === "desc" ? "desc" : "asc"]);
			sortBy = keys.length > 0 ? keys : void 0;
		} else if (key.endsWith("_op")) {
			const field = key.replace("_op", "");
			const filterOp = decodeURIComponent(value);
			const filterValStr = entries.get(`${field}_value`);
			if (filterValStr !== null) filterValues[field] = [filterOp, decodeString(filterValStr)];
		}
	});
	return {
		filterValues: Object.keys(filterValues).length ? filterValues : void 0,
		sortBy
	};
}
function isDate(dateString) {
	if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(dateString)) return false;
	return new Date(dateString).toISOString() === dateString;
}
function encodeReference(val) {
	return `ref::${val.path}/${val.id}`;
}
function encodeRelation(val) {
	return `rel::${val.path}/${val.id}`;
}
function decodeString(val) {
	let parsedFilterVal = val;
	if (isDate(val)) try {
		parsedFilterVal = new Date(val);
	} catch (_e) {}
	if (typeof parsedFilterVal === "string") try {
		parsedFilterVal = JSON.parse(parsedFilterVal, (key, value) => {
			if (typeof value === "string") {
				if (value.startsWith("ref::")) {
					const [path, id] = value.substring(5).split("/");
					return new EntityReference({
						id,
						path
					});
				}
				if (value.startsWith("rel::")) {
					const [path, id] = value.substring(5).split("/");
					return new EntityRelation(id, path);
				}
			}
			return value;
		});
	} catch (_e) {}
	if (typeof parsedFilterVal === "string") {
		if (parsedFilterVal.startsWith("ref::")) {
			const [path, id] = parsedFilterVal.substring(5).split("/");
			return new EntityReference({
				id,
				path
			});
		}
		if (parsedFilterVal.startsWith("rel::")) {
			const [path, id] = parsedFilterVal.substring(5).split("/");
			return new EntityRelation(id, path);
		}
	}
	return parsedFilterVal;
}
//#endregion
//#region src/components/common/useScrollRestoration.tsx
var collectionScrollCache = /* @__PURE__ */ new Map();
function useScrollRestoration() {
	const updateCollectionScroll = ({ path, filters, scrollOffset, data }) => {
		collectionScrollCache.set(createCacheKey(path, filters), {
			scrollOffset,
			data
		});
	};
	const getCollectionScroll = (path, filters) => {
		return collectionScrollCache.get(createCacheKey(path, filters));
	};
	return {
		getCollectionScroll,
		updateCollectionScroll
	};
}
function createCacheKey(path, filters) {
	if (!filters) return path;
	return `${path}?${filters ? Object.keys(filters).map((key) => {
		return `${key}=${JSON.stringify(filters[key])}`;
	}).join("&") : ""}`;
}
//#endregion
//#region src/components/common/table_height.tsx
function getRowHeight(size) {
	switch (size) {
		case "xl": return 160;
		case "l": return 96;
		case "m": return 48;
		case "s": return 42;
		case "xs": return 36;
		default: throw Error("Missing mapping for collection size -> height");
	}
}
//#endregion
//#region src/components/NotFoundPage.tsx
function NotFoundPage() {
	const { t } = useTranslation();
	return /* @__PURE__ */ jsx("div", {
		className: "flex w-full h-full",
		children: /* @__PURE__ */ jsxs("div", {
			className: "m-auto flex items-center flex-col",
			children: [
				/* @__PURE__ */ jsx(Typography, {
					variant: "h4",
					align: "center",
					gutterBottom: true,
					children: t("page_not_found")
				}),
				/* @__PURE__ */ jsx(Typography, {
					align: "center",
					gutterBottom: true,
					children: t("page_not_found_body")
				}),
				/* @__PURE__ */ jsx(Button, {
					variant: "text",
					component: Link,
					to: "/",
					children: t("back_to_home")
				})
			]
		})
	});
}
//#endregion
//#region src/components/ConfirmationDialog.tsx
function ConfirmationDialog({ open, onAccept, onCancel, title, loading, body }) {
	const { t } = useTranslation();
	return /* @__PURE__ */ jsxs(Dialog, {
		open,
		onOpenChange: (open) => !open ? onCancel() : void 0,
		children: [
			/* @__PURE__ */ jsx(DialogTitle, {
				variant: "h6",
				className: "mb-2",
				children: title
			}),
			/* @__PURE__ */ jsx(DialogContent, { children: body }),
			/* @__PURE__ */ jsxs(DialogActions, { children: [/* @__PURE__ */ jsx(Button, {
				variant: "text",
				onClick: onCancel,
				autoFocus: true,
				children: t("cancel")
			}), /* @__PURE__ */ jsx(LoadingButton, {
				type: "submit",
				loading,
				onClick: onAccept,
				autoFocus: true,
				children: t("ok")
			})] })
		]
	});
}
//#endregion
//#region src/components/RebaseLogo.tsx
function RebaseLogo({ width, height, className, style }) {
	return /* @__PURE__ */ jsxs("svg", {
		width: width ?? "100%",
		height: height ?? "100%",
		version: "1.1",
		style,
		className,
		viewBox: "112.1405 1079.711 306.058 306.058",
		fill: "none",
		xmlns: "http://www.w3.org/2000/svg",
		children: [
			/* @__PURE__ */ jsx("path", {
				fill: "#34b6d9",
				d: "M237.01 1087.234a56 56 0 0 1 56.07 0l83.819 48.473a56 56 0 0 1 27.965 48.477v97.108a56 56 0 0 1-27.965 48.477l-83.819 48.473a56 56 0 0 1-56.07 0l-83.818-48.473a56 56 0 0 1-27.965-48.477v-97.108a56 56 0 0 1 27.965-48.477Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#358fd8",
				d: "M264.64 1286.718c27.352 0 49.525 22.173 49.525 49.526 0 27.352-22.173 49.525-49.525 49.525-27.353 0-49.526-22.173-49.526-49.525Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#ff9197",
				d: "M174.941 1131.547c27.352 0 49.526 22.174 49.526 49.526s-22.174 49.526-49.526 49.526-49.526-22.174-49.526-49.526Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#358fd8",
				d: "M355.338 1131.547c27.352 0 49.526 22.174 49.526 49.526s-22.174 49.526-49.526 49.526-49.526-22.174-49.526-49.526Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fe9197",
				d: "M125.227 1196.44v-14.661c0-13.35 7.55-20.303 17.99-18.168l56.424 31.13 2.72 1.572 27.643 31.545 35.193 30.52q.173 1.522.229 3.013l-54.046-11.395-28.464-26.045Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#013afd",
				d: "M151.207 1328.696c-14.348-8.285-25.98-28.432-25.98-45v-35.214l11.299-.401c.614.866 34.062 48.973 34.606 49.656.394.495 22.156 40.224 34.409 62.328Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2c09d1",
				d: "m211.38 1249.996 30.436 6.417 9.53 9.1-2.926 15.797 16.939 30.08-14.049 6.672-35.072-10.288-33.83-61.32 9.09-.323Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2ac1cf",
				d: "M240.774 1380.37a34 34 0 0 1-1.304-.716l-16.141-9.319-49.383-74.238 29.211 7.839Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2c42d0",
				d: "m173.359 1219.393 9.557 4.558 28.464 26.045-19.883-3.865-12.782.453-31.643-13.936-15.11-11.819Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#8b66ed",
				d: "M257.113 1381.863c-4.336 2.13-10.043 1.783-16.34-1.493l-37.616-76.434 13.081 3.838Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2c09d1",
				d: "m145.888 1247.749 7.224-.257 23.615 41.804 11.98 1.728-24.421-43.544-.014-.383 10.725-.38 28.16 57.219-29.211-7.839-7.37-11.08Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2ac1cf",
				d: "M265.451 1311.554v53.1q0 .255-.004.505l-.25-.26-29.148-38.794-6.308-14.37 21.57 6.328 14.048-6.672Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fd485f",
				d: "m223.329 1370.335-17.788-10.27c-12.253-22.104-34.015-61.833-34.41-62.328-.543-.683-33.991-48.79-34.605-49.656l9.362-.332 20.688 37.269Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2d42d0",
				d: "m202.362 1196.313 37.108 21.424c13.11 7.57 23.953 25.043 25.727 40.641l-35.193-30.52Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2b0ad1",
				d: "M265.447 1365.16c-.095 6.58-2.056 11.56-5.304 14.604l-39.936-70.826 9.534 2.797 6.308 14.37 29.148 38.794Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fe9196",
				d: "M125.227 1240.59v-16.4l21.845 8.458 31.643 13.936-35.367 1.255Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#8b66ed",
				d: "m174.997 1246.716 7.411-.263 33.83 61.32-13.08-3.837Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2cc5d2",
				d: "m153.112 1247.492 11.16-.395.014.383 24.421 43.544-11.98-1.728Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fc4962",
				d: "M125.227 1224.19v-8.878l30.211-4.464 17.92 8.545-41.396 1.436 15.11 11.82Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fe475f",
				d: "M265.451 1292.508v18.84l-.092.043-16.939-30.08 2.927-15.799.214.206Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2d43cd",
				d: "M125.227 1215.312v-18.871l30.211 14.407Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2ac4d1",
				d: "M260.143 1379.764a12.5 12.5 0 0 1-3.03 2.099l-40.875-74.09 3.969 1.165Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fe475f",
				d: "M265.426 1261.391q.025.677.025 1.346v16.241l-13.89-13.26-.186-.36-.028.154-9.53-9.099Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#40b6d9",
				d: "M265.451 1278.978v13.53c-4.95-9.535-12.604-24.31-13.89-26.79Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#013afd",
				d: "M125.227 1248.482v-7.892l18.12 7.249Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fe9197",
				d: "M143.217 1163.611c2.517.515 5.203 1.558 7.99 3.168l48.434 27.963Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fec404",
				d: "m251.347 1265.512.028-.154.186.36Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#ccc",
				d: "M265.451 1311.347v.207l-.092-.163Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2fc7d5",
				d: "M383.274 1164.17a31 31 0 0 1-3.887 2.64l-2.59 1.495-95.242 18.988-50.727-18.022 25.55-32.892 63.642 26.919-61.075-31.394Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#320fdd",
				d: "m215.866 1112.967 18.535-16.15 5.766 32.704 16.212 6.858-25.551 32.892-57.218-19.484Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fd4a61",
				d: "m305.806 1094.328 73.58 42.482c7.16 4.134 10.748 9.55 10.757 14.968l-40.264-5.624-25.491-7.867-33.1-30.491-18.842-4.065Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fec504",
				d: "M388.516 1158.077c-1.145 2.145-2.892 4.205-5.242 6.093l-124.33-32.266 6.47-29.689 25.874 5.58 33.1 30.492Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#320fdd",
				d: "m179.744 1183.464-26.935-15.551 18.147-15.813 55.184 23.206-15.732 20.252Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#33b6d9",
				d: "m358.403 1178.925-52.998 30.599-44.794-17.995 89.572-18.117Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fd4a63",
				d: "m234.401 1096.817 12.723-11.087 19.395 11.42-7.574 34.754 61.075 31.394-79.853-33.777Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fe9197",
				d: "m152.809 1167.913-1.91-1.103c-10.42-6.016-13.273-14.747-8.558-22.25l56.907-27.791 15.854-3.136Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#8b69f1",
				d: "m305.405 1209.524-14.281 8.245c-5.252 3.032-11.697 4.954-18.462 5.767l-43.574-25.631 31.523-6.376Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#3015d8",
				d: "M252.294 1080.973c13.06-2.81 28.331-1.183 38.83 4.879l14.682 8.476-33.36 9.403-7.031-1.516 1.104-5.064-19.395-11.42Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2c0ace",
				d: "M239.162 1085.852c3.85-2.223 8.342-3.85 13.132-4.88l-37.192 32.66-15.854 3.137-56.907 27.791c1.777-2.828 4.63-5.482 8.559-7.75Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2fc7d5",
				d: "m229.088 1197.905-8.773 1.774-9.907-4.121 15.732-20.252 37.462 15.618Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#33b6d9",
				d: "M272.662 1223.536c-11.721 1.407-24.402-.515-33.5-5.767l-28.093-16.22 18.019-3.644Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fe9197",
				d: "m264.078 1190.828-.476.096-37.462-15.618 4.688-6.035 50.727 18.022ZM170.956 1152.1l2.654-2.313 57.218 19.484-4.688 6.035Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#30c3d9",
				d: "M390.143 1151.778c.01 2.125-.533 4.25-1.627 6.3l-38.637-11.924Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#ff4c60",
				d: "m211.07 1201.55-31.326-18.086 40.57 16.215Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2d08d0",
				d: "m376.798 1168.305-18.395 10.62-8.22-5.513Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2fc7d5",
				d: "M264.887 1262.737c0-16.568 11.632-36.715 25.98-45l40.425-23.339 11.932 35.4-25.718 46.274-26.284-2.855-1.382.145-1.411-.146-23.542.675Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#32abd6",
				d: "M383.486 1164.684c2.376-.909 4.642-1.382 6.752-1.446l-7.859 43.045-.292 5.18 8.911 13.519 5.344-41.78 4.102-15.889c2.942 3.164 4.667 8.084 4.667 14.466v91.585h-1.324c-1.104 3.308-34.555 60.887-34.486 61.007l-21.13 12.2 8.305-32.902-10.115 14.693 10.115-20.342 47.315-78.499-7.45 6.694-15.674 18.243 2.62-89.698.155-.059Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2eabd6",
				d: "m324.54 1360.214-33.672 19.44c-13.695 7.907-24.915 2.15-25.91-12.796l8.385-12.077 47.855-71.354 7.12-26.81 5.119-9.209.57 31.212-13.987 50.442 6.42-5.847-2.408 22.126.459 8.254Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#8b65f0",
				d: "M264.959 1366.858a33 33 0 0 1-.072-2.204v-88.582l24.953-2.71 26.235 2.554 1.43.156 10.813-19.454-7.12 26.81-47.855 71.353Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fd4861",
				d: "m378.764 1192.394 3.89-5.923c-1.987-.058-4.42 64.146-4.402 65.055.019.91-5.901 22.365-5.901 22.365l-17.658 29.778-14.983 14.696 9.72-45.149.036-1.115 6.455-7.894Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#32abd6",
				d: "m377.832 1167.529.229-.133.703 24.998-22.843 71.813-6.455 7.894.572-17.63-3.443-30.74Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#8c66ef",
				d: "m328.775 1297.488.034-.126 20.657-25.261-.036 1.115-9.72 45.15 14.983-14.697-13.8 22.045-16.86 19.627 2.407-22.126-6.42 5.847Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2c08d3",
				d: "m331.292 1194.398 27.203-15.705 5.51 13.714-20.781 37.391Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#300bd2",
				d: "m324.491 1353.595-.459-8.254 16.861-19.627 13.8-22.045 17.658-29.778 20.08-31.948 3.91-5.728 7.45-6.694-47.315 78.5-10.115 20.341-21.821 31.852Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2c08d3",
				d: "m333.437 1247.408 13.158-23.676 3.443 30.738-4.056 14.678-14.316 17.914 2.34-8.442Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#8b68ef",
				d: "M405.111 1273.364v10.332c0 16.568-11.632 36.715-25.98 45l-9.83 5.675c-.07-.12 33.382-57.7 34.486-61.007Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fec504",
				d: "M390.238 1163.238c4.109-.125 7.625 1.3 10.206 4.075l-4.102 15.89-5.344 41.779-8.91-13.518.291-5.18Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#300bd2",
				d: "m348.171 1346.57-23.631 13.644 31.936-46.545Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#348fd8",
				d: "M372.35 1273.891s5.921-21.455 5.902-22.365 2.415-65.113 4.402-65.055l-1.987 67.987 15.675-18.243-3.91 5.728Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#32abd6",
				d: "m358.495 1178.693 19.337-11.164-13.827 24.878Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#fd4861",
				d: "m331.391 1288.05.275-.988 14.316-17.914 4.056-14.678-.572 17.63-20.657 25.262Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#ccc",
				fillOpacity: ".579",
				d: "m378.06 1167.396 1.07-.617a31 31 0 0 1 4.158-2.019l-.634 21.71-3.89 5.924Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#2fc7d5",
				d: "M264.887 1276.072v-2.18l23.542-.676 1.411.146Z"
			}),
			/* @__PURE__ */ jsx("path", {
				fill: "#8b65f0",
				d: "m291.193 1273.502-1.353-.14 1.382-.145 24.853 2.7Z"
			})
		]
	});
}
//#endregion
//#region src/components/AIIcon.tsx
/**
* AI-styled AutoAwesome icon with gradient coloring.
* Used consistently across AI features for visual identification.
*/
function AIIcon({ size = "small", className }) {
	return /* @__PURE__ */ jsx(Wand2Icon, {
		size: typeof size === "string" ? {
			smallest: 16,
			small: 20,
			medium: 24,
			large: 28
		}[size] || 20 : size,
		className,
		style: {
			background: "linear-gradient(to right, var(--color-primary), var(--color-secondary))",
			WebkitBackgroundClip: "text",
			WebkitTextFillColor: "transparent",
			backgroundClip: "text"
		}
	});
}
/**
* Small animated dot indicator for AI-modified elements.
* Shows a pulsing gradient dot.
*/
function AIModifiedIndicator({ className }) {
	return /* @__PURE__ */ jsx("div", {
		className: `w-2 h-2 rounded-full bg-gradient-to-r from-primary to-secondary animate-pulse ${className ?? ""}`,
		title: "AI modified"
	});
}
//#endregion
//#region src/components/UserSettingsView.tsx
function UserSettingsView() {
	const authController = useAuthController();
	const user = authController.user;
	const { t } = useTranslation();
	const hasPasswordChange = !!authController.changePassword;
	const [activeTab, setActiveTab] = useState("profile");
	const [displayName, setDisplayName] = useState(user?.displayName || "");
	const [photoURL, setPhotoURL] = useState(user?.photoURL || "");
	const [savingProfile, setSavingProfile] = useState(false);
	const [profileError, setProfileError] = useState(null);
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [changingPassword, setChangingPassword] = useState(false);
	const [passwordError, setPasswordError] = useState(null);
	const [passwordSuccess, setPasswordSuccess] = useState(null);
	const [sessions, setSessions] = useState([]);
	const [loadingSessions, setLoadingSessions] = useState(false);
	const [sessionsError, setSessionsError] = useState(null);
	const [revokingSessionId, setRevokingSessionId] = useState(null);
	const [revokingAll, setRevokingAll] = useState(false);
	useEffect(() => {
		setDisplayName(user?.displayName || "");
		setPhotoURL(user?.photoURL || "");
		if (activeTab === "sessions") loadSessions();
	}, [activeTab, user]);
	const handleSaveProfile = async () => {
		setSavingProfile(true);
		setProfileError(null);
		try {
			if (authController.updateProfile) await authController.updateProfile(displayName, photoURL);
			else throw new Error("updateProfile not implemented in this auth controller.");
		} catch (e) {
			setProfileError(e instanceof Error ? e.message : String(e));
		} finally {
			setSavingProfile(false);
		}
	};
	const handleChangePassword = async () => {
		setPasswordError(null);
		setPasswordSuccess(null);
		if (newPassword.length < 8) {
			setPasswordError(t("password_too_short"));
			return;
		}
		if (newPassword !== confirmPassword) {
			setPasswordError(t("passwords_dont_match"));
			return;
		}
		setChangingPassword(true);
		try {
			if (authController.changePassword) {
				await authController.changePassword(currentPassword, newPassword);
				setPasswordSuccess(t("password_changed"));
				setCurrentPassword("");
				setNewPassword("");
				setConfirmPassword("");
				setTimeout(() => {
					authController.signOut();
				}, 2e3);
			}
		} catch (e) {
			setPasswordError(e instanceof Error ? e.message : String(e));
		} finally {
			setChangingPassword(false);
		}
	};
	const loadSessions = async () => {
		setLoadingSessions(true);
		setSessionsError(null);
		try {
			if (authController.fetchSessions) {
				const sortedSessions = (await authController.fetchSessions() || []).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
				setSessions(sortedSessions);
			} else throw new Error("fetchSessions not implemented in this auth controller.");
		} catch (e) {
			setSessionsError(e instanceof Error ? e.message : String(e));
		} finally {
			setLoadingSessions(false);
		}
	};
	const handleRevokeSession = async (id, isCurrentSession) => {
		setRevokingSessionId(id);
		try {
			if (authController.revokeSession) {
				await authController.revokeSession(id);
				setSessions(sessions.filter((s) => s.id !== id));
				if (isCurrentSession) await authController.signOut();
			} else throw new Error("revokeSession not implemented in this auth controller.");
		} catch (e) {
			setSessionsError(e instanceof Error ? e.message : String(e));
		} finally {
			setRevokingSessionId(null);
		}
	};
	const handleRevokeAll = async () => {
		setRevokingAll(true);
		try {
			if (authController.revokeAllSessions) await authController.revokeAllSessions();
			else throw new Error("revokeAllSessions not implemented in this auth controller.");
		} catch (e) {
			setSessionsError(e instanceof Error ? e.message : String(e));
		} finally {
			setRevokingAll(false);
		}
	};
	if (!user) return null;
	return /* @__PURE__ */ jsxs("div", {
		className: "flex-grow max-w-4xl w-full mx-auto p-4 sm:p-6 md:p-12",
		children: [
			/* @__PURE__ */ jsx(Typography, {
				variant: "h4",
				className: "mb-8",
				children: t("account_settings")
			}),
			/* @__PURE__ */ jsxs(Tabs, {
				value: activeTab,
				onValueChange: (v) => setActiveTab(v),
				className: "mb-8",
				children: [
					/* @__PURE__ */ jsx(Tab, {
						value: "profile",
						children: t("profile")
					}),
					hasPasswordChange && /* @__PURE__ */ jsx(Tab, {
						value: "security",
						children: t("security")
					}),
					/* @__PURE__ */ jsx(Tab, {
						value: "sessions",
						children: t("sessions")
					})
				]
			}),
			activeTab === "profile" && /* @__PURE__ */ jsxs("div", {
				className: "flex flex-col gap-6 max-w-xl",
				children: [
					/* @__PURE__ */ jsx("div", {
						className: "flex items-center gap-6 mb-2",
						children: /* @__PURE__ */ jsx(Avatar, {
							src: photoURL || void 0,
							className: "w-24 h-24 text-3xl",
							children: displayName ? displayName[0].toUpperCase() : user.email ? user.email[0].toUpperCase() : "A"
						})
					}),
					/* @__PURE__ */ jsx(TextField, {
						label: t("display_name"),
						value: displayName,
						onChange: (e) => setDisplayName(e.target.value)
					}),
					/* @__PURE__ */ jsx(TextField, {
						label: t("photo_url"),
						value: photoURL,
						onChange: (e) => setPhotoURL(e.target.value)
					}),
					profileError && /* @__PURE__ */ jsx(Typography, {
						color: "error",
						children: profileError
					}),
					/* @__PURE__ */ jsx("div", {
						className: "mt-4",
						children: /* @__PURE__ */ jsx(Button, {
							variant: "filled",
							onClick: handleSaveProfile,
							disabled: savingProfile,
							children: savingProfile ? t("saving") : t("save_profile")
						})
					})
				]
			}),
			activeTab === "security" && hasPasswordChange && /* @__PURE__ */ jsxs("div", {
				className: "flex flex-col gap-6 max-w-xl",
				children: [
					/* @__PURE__ */ jsx(Typography, {
						variant: "h6",
						className: "mb-2",
						children: t("change_password")
					}),
					/* @__PURE__ */ jsx(TextField, {
						label: t("current_password"),
						type: "password",
						value: currentPassword,
						onChange: (e) => setCurrentPassword(e.target.value),
						autoComplete: "current-password"
					}),
					/* @__PURE__ */ jsx(TextField, {
						label: t("new_password"),
						type: "password",
						value: newPassword,
						onChange: (e) => setNewPassword(e.target.value),
						autoComplete: "new-password"
					}),
					/* @__PURE__ */ jsx(TextField, {
						label: t("confirm_password"),
						type: "password",
						value: confirmPassword,
						onChange: (e) => setConfirmPassword(e.target.value),
						autoComplete: "new-password"
					}),
					passwordError && /* @__PURE__ */ jsx(Typography, {
						color: "error",
						children: passwordError
					}),
					passwordSuccess && /* @__PURE__ */ jsx(Typography, {
						className: "text-emerald-600 dark:text-emerald-400",
						children: passwordSuccess
					}),
					/* @__PURE__ */ jsx("div", {
						className: "mt-4",
						children: /* @__PURE__ */ jsx(Button, {
							variant: "filled",
							onClick: handleChangePassword,
							disabled: changingPassword || !currentPassword || !newPassword || !confirmPassword,
							children: changingPassword ? t("changing_password") : t("change_password")
						})
					})
				]
			}),
			activeTab === "sessions" && /* @__PURE__ */ jsx("div", {
				className: "flex flex-col gap-4 max-w-3xl",
				children: loadingSessions ? /* @__PURE__ */ jsx("div", {
					className: "flex justify-center p-8",
					children: /* @__PURE__ */ jsx(CircularProgress, {})
				}) : sessionsError ? /* @__PURE__ */ jsx(Typography, {
					color: "error",
					children: sessionsError
				}) : sessions.length === 0 ? /* @__PURE__ */ jsx(Typography, { children: t("no_active_sessions") }) : /* @__PURE__ */ jsxs("div", {
					className: "flex flex-col gap-4",
					children: [/* @__PURE__ */ jsx("div", {
						className: "flex justify-end mb-2",
						children: /* @__PURE__ */ jsx(Button, {
							variant: "text",
							color: "error",
							onClick: handleRevokeAll,
							disabled: revokingAll,
							children: revokingAll ? t("revoking") : t("revoke_all_sessions")
						})
					}), sessions.map((session) => /* @__PURE__ */ jsxs(Card, {
						className: "flex justify-between items-center p-4",
						children: [/* @__PURE__ */ jsxs("div", {
							className: "flex flex-col",
							children: [/* @__PURE__ */ jsxs("div", {
								className: "flex items-center gap-2 mb-1",
								children: [/* @__PURE__ */ jsx(Typography, {
									variant: "body1",
									children: session.userAgent || t("unknown_device")
								}), session.isCurrentSession && /* @__PURE__ */ jsx(Chip, {
									colorScheme: "blue",
									size: "small",
									children: t("current")
								})]
							}), /* @__PURE__ */ jsxs(Typography, {
								variant: "caption",
								color: "secondary",
								children: [
									"IP: ",
									session.ipAddress || "Unknown",
									" • Created: ",
									new Date(session.createdAt).toLocaleString()
								]
							})]
						}), /* @__PURE__ */ jsx("div", {
							className: "ml-4",
							children: revokingSessionId === session.id ? /* @__PURE__ */ jsx(CircularProgress, { size: "small" }) : /* @__PURE__ */ jsx(IconButton, {
								onClick: () => handleRevokeSession(session.id, session.isCurrentSession),
								"aria-label": "Revoke Session",
								children: /* @__PURE__ */ jsx(Trash2Icon, {})
							})
						})]
					}, session.id))]
				})
			})
		]
	});
}
//#endregion
//#region src/components/LanguageToggle.tsx
function LanguageToggle() {
	const { t, i18n } = useTranslation();
	return /* @__PURE__ */ jsxs(Menu, {
		trigger: /* @__PURE__ */ jsx(IconButton, {
			color: "inherit",
			"aria-label": t("change_language"),
			children: /* @__PURE__ */ jsx(LanguagesIcon, { size: iconSize.small })
		}),
		children: [
			/* @__PURE__ */ jsx(MenuItem, {
				onClick: () => i18n.changeLanguage("en"),
				children: /* @__PURE__ */ jsxs("div", {
					className: "flex w-full items-center justify-between gap-4",
					children: [/* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						className: i18n.language === "en" ? "font-semibold" : "",
						children: "English"
					}), i18n.language === "en" && /* @__PURE__ */ jsx(CheckIcon, { size: iconSize.small })]
				})
			}),
			/* @__PURE__ */ jsx(MenuItem, {
				onClick: () => i18n.changeLanguage("es"),
				children: /* @__PURE__ */ jsxs("div", {
					className: "flex w-full items-center justify-between gap-4",
					children: [/* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						className: i18n.language === "es" ? "font-semibold" : "",
						children: "Español"
					}), i18n.language === "es" && /* @__PURE__ */ jsx(CheckIcon, { size: iconSize.small })]
				})
			}),
			/* @__PURE__ */ jsx(MenuItem, {
				onClick: () => i18n.changeLanguage("de"),
				children: /* @__PURE__ */ jsxs("div", {
					className: "flex w-full items-center justify-between gap-4",
					children: [/* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						className: i18n.language === "de" ? "font-semibold" : "",
						children: "Deutsch"
					}), i18n.language === "de" && /* @__PURE__ */ jsx(CheckIcon, { size: iconSize.small })]
				})
			}),
			/* @__PURE__ */ jsx(MenuItem, {
				onClick: () => i18n.changeLanguage("fr"),
				children: /* @__PURE__ */ jsxs("div", {
					className: "flex w-full items-center justify-between gap-4",
					children: [/* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						className: i18n.language === "fr" ? "font-semibold" : "",
						children: "Français"
					}), i18n.language === "fr" && /* @__PURE__ */ jsx(CheckIcon, { size: iconSize.small })]
				})
			}),
			/* @__PURE__ */ jsx(MenuItem, {
				onClick: () => i18n.changeLanguage("it"),
				children: /* @__PURE__ */ jsxs("div", {
					className: "flex w-full items-center justify-between gap-4",
					children: [/* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						className: i18n.language === "it" ? "font-semibold" : "",
						children: "Italiano"
					}), i18n.language === "it" && /* @__PURE__ */ jsx(CheckIcon, { size: iconSize.small })]
				})
			}),
			/* @__PURE__ */ jsx(MenuItem, {
				onClick: () => i18n.changeLanguage("hi"),
				children: /* @__PURE__ */ jsxs("div", {
					className: "flex w-full items-center justify-between gap-4",
					children: [/* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						className: i18n.language === "hi" ? "font-semibold" : "",
						children: "हिन्दी"
					}), i18n.language === "hi" && /* @__PURE__ */ jsx(CheckIcon, { size: iconSize.small })]
				})
			}),
			/* @__PURE__ */ jsx(MenuItem, {
				onClick: () => i18n.changeLanguage("pt"),
				children: /* @__PURE__ */ jsxs("div", {
					className: "flex w-full items-center justify-between gap-4",
					children: [/* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						className: i18n.language === "pt" ? "font-semibold" : "",
						children: "Português"
					}), i18n.language === "pt" && /* @__PURE__ */ jsx(CheckIcon, { size: iconSize.small })]
				})
			})
		]
	});
}
//#endregion
//#region src/components/UserSelectPopover.tsx
var ITEM_HEIGHT = 44;
var MAX_VISIBLE_ITEMS = 7;
/**
* A reusable user picker popover with search support.
* Designed to handle very large user lists (100k+) by filtering
* client-side and limiting the rendered set.
*
* @group Components
*/
function UserSelectPopover({ selectedUser, onUserSelected, users, loading = false, currentUser, defaultLabel = "Current user", renderLimit = 100, className, size = "small" }) {
	const [open, setOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const inputRef = useRef(null);
	const listRef = useRef(null);
	const effectiveUserLabel = selectedUser ? selectedUser.displayName || selectedUser.email || selectedUser.uid : currentUser?.displayName || currentUser?.email || defaultLabel;
	const filteredUsers = useMemo(() => {
		const query = searchText.trim().toLowerCase();
		const base = users.filter((u) => u.uid !== currentUser?.uid);
		if (!query) return base.slice(0, renderLimit);
		return base.filter((u) => {
			const name = (u.displayName || "").toLowerCase();
			const email = (u.email || "").toLowerCase();
			const uid = u.uid.toLowerCase();
			const roles = (u.roles || []).join(" ").toLowerCase();
			return name.includes(query) || email.includes(query) || uid.includes(query) || roles.includes(query);
		}).slice(0, renderLimit);
	}, [
		users,
		currentUser?.uid,
		searchText,
		renderLimit
	]);
	const totalFilterable = useMemo(() => {
		const query = searchText.trim().toLowerCase();
		const base = users.filter((u) => u.uid !== currentUser?.uid);
		if (!query) return base.length;
		return base.filter((u) => {
			const name = (u.displayName || "").toLowerCase();
			const email = (u.email || "").toLowerCase();
			const uid = u.uid.toLowerCase();
			const roles = (u.roles || []).join(" ").toLowerCase();
			return name.includes(query) || email.includes(query) || uid.includes(query) || roles.includes(query);
		}).length;
	}, [
		users,
		currentUser?.uid,
		searchText
	]);
	const handleSelect = useCallback((user) => {
		onUserSelected(user);
		setOpen(false);
		setSearchText("");
	}, [onUserSelected]);
	const handleOpenChange = useCallback((isOpen) => {
		setOpen(isOpen);
		if (isOpen) setTimeout(() => inputRef.current?.focus(), 50);
		else setSearchText("");
	}, []);
	const handleKeyDown = useCallback((e) => {
		if (e.key === "Escape") {
			setOpen(false);
			setSearchText("");
		}
	}, []);
	Math.min((filteredUsers.length + 1) * ITEM_HEIGHT, MAX_VISIBLE_ITEMS * ITEM_HEIGHT);
	return /* @__PURE__ */ jsx(Popover, {
		open,
		onOpenChange: handleOpenChange,
		side: "bottom",
		align: "end",
		sideOffset: 4,
		trigger: /* @__PURE__ */ jsxs("button", {
			className: cls("flex items-center gap-1.5 text-xs font-medium transition-colors rounded border border-transparent", "text-text-secondary dark:text-text-secondary-dark", "hover:text-text-primary dark:hover:text-text-primary-dark", "bg-surface-raised hover:bg-surface-raised-hover", size === "small" ? "px-2 py-1" : "px-3 py-1.5", className),
			children: [
				selectedUser?.photoURL ? /* @__PURE__ */ jsx("img", {
					src: selectedUser.photoURL,
					alt: "",
					className: "w-4 h-4 rounded-full object-cover"
				}) : /* @__PURE__ */ jsx("svg", {
					className: "w-3.5 h-3.5 text-text-disabled dark:text-text-disabled-dark",
					fill: "none",
					stroke: "currentColor",
					viewBox: "0 0 24 24",
					children: /* @__PURE__ */ jsx("path", {
						strokeLinecap: "round",
						strokeLinejoin: "round",
						strokeWidth: 2,
						d: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
					})
				}),
				/* @__PURE__ */ jsx("span", {
					className: "max-w-[120px] truncate",
					children: effectiveUserLabel
				}),
				selectedUser && /* @__PURE__ */ jsx("span", {
					className: "text-[9px] text-amber-600 dark:text-amber-400 font-semibold uppercase",
					children: "impersonating"
				}),
				/* @__PURE__ */ jsx("svg", {
					className: "w-3 h-3 opacity-50 ml-0.5",
					fill: "none",
					stroke: "currentColor",
					viewBox: "0 0 24 24",
					children: /* @__PURE__ */ jsx("path", {
						strokeLinecap: "round",
						strokeLinejoin: "round",
						strokeWidth: 2,
						d: "M19 9l-7 7-7-7"
					})
				})
			]
		}),
		children: /* @__PURE__ */ jsxs("div", {
			className: "flex flex-col w-[320px] max-w-[90vw] overflow-hidden",
			onKeyDown: handleKeyDown,
			children: [
				/* @__PURE__ */ jsxs("div", {
					className: cls("px-3 py-2 border-b flex items-center justify-between shrink-0", defaultBorderMixin),
					children: [/* @__PURE__ */ jsx(Typography, {
						variant: "caption",
						className: "font-semibold uppercase tracking-wider text-[9px] text-text-disabled dark:text-text-disabled-dark",
						children: "Run as user"
					}), users.length > 0 && /* @__PURE__ */ jsxs(Typography, {
						variant: "caption",
						className: "text-[9px] text-text-disabled dark:text-text-disabled-dark tabular-nums",
						children: [
							totalFilterable,
							" user",
							totalFilterable !== 1 ? "s" : ""
						]
					})]
				}),
				/* @__PURE__ */ jsx("div", {
					className: cls("px-2 py-1.5 border-b shrink-0", defaultBorderMixin),
					children: /* @__PURE__ */ jsx(SearchBar, {
						inputRef,
						size: "smallest",
						placeholder: "Search by name, email, or role…",
						onTextSearch: (val) => setSearchText(val ?? "")
					})
				}),
				/* @__PURE__ */ jsxs("div", {
					ref: listRef,
					className: "overflow-y-auto overscroll-contain",
					style: { maxHeight: MAX_VISIBLE_ITEMS * ITEM_HEIGHT },
					children: [
						currentUser && (!searchText.trim() || matchesSearch(currentUser, searchText)) && /* @__PURE__ */ jsx(UserRow, {
							user: currentUser,
							isSelected: !selectedUser,
							isSelf: true,
							onClick: () => handleSelect(null)
						}),
						loading && /* @__PURE__ */ jsx("div", {
							className: "flex items-center justify-center py-6",
							children: /* @__PURE__ */ jsx(CircularProgress, { size: "small" })
						}),
						!loading && filteredUsers.length === 0 && searchText.trim() && /* @__PURE__ */ jsx("div", {
							className: "flex flex-col items-center justify-center py-6 px-4",
							children: /* @__PURE__ */ jsxs(Typography, {
								variant: "caption",
								className: "text-text-disabled dark:text-text-disabled-dark text-center",
								children: [
									"No users matching “",
									searchText,
									"”"
								]
							})
						}),
						!loading && filteredUsers.map((user) => /* @__PURE__ */ jsx(UserRow, {
							user,
							isSelected: selectedUser?.uid === user.uid,
							onClick: () => handleSelect(user)
						}, user.uid)),
						!loading && totalFilterable > renderLimit && /* @__PURE__ */ jsx("div", {
							className: cls("px-3 py-2 border-t text-center", defaultBorderMixin),
							children: /* @__PURE__ */ jsxs(Typography, {
								variant: "caption",
								className: "text-[10px] text-text-disabled dark:text-text-disabled-dark",
								children: [
									"Showing ",
									renderLimit,
									" of ",
									totalFilterable,
									" results. Use search to narrow down."
								]
							})
						})
					]
				})
			]
		})
	});
}
function UserRow({ user, isSelected, isSelf, onClick }) {
	return /* @__PURE__ */ jsxs("button", {
		onClick,
		className: cls("w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors", "hover:bg-surface-hover", isSelected && "bg-primary/5 dark:bg-primary-dark/10"),
		style: { minHeight: ITEM_HEIGHT },
		children: [
			user.photoURL ? /* @__PURE__ */ jsx("img", {
				src: user.photoURL,
				alt: "",
				className: "w-7 h-7 rounded-full object-cover shrink-0"
			}) : /* @__PURE__ */ jsx("div", {
				className: "w-7 h-7 rounded-full bg-surface-raised flex items-center justify-center shrink-0",
				children: /* @__PURE__ */ jsx(CircleUserIcon, {
					size: iconSize.smallest,
					className: "text-text-disabled dark:text-text-disabled-dark"
				})
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "flex flex-col min-w-0 flex-grow",
				children: [/* @__PURE__ */ jsxs("div", {
					className: "flex items-center gap-1.5",
					children: [/* @__PURE__ */ jsx("span", {
						className: cls("text-xs font-medium truncate", isSelected ? "text-primary dark:text-primary-dark" : "text-text-primary dark:text-text-primary-dark"),
						children: user.displayName || user.email || user.uid
					}), isSelf && /* @__PURE__ */ jsx("span", {
						className: "text-[9px] text-text-disabled dark:text-text-disabled-dark font-medium shrink-0",
						children: "(you)"
					})]
				}), (user.email || user.roles?.length) && /* @__PURE__ */ jsxs("span", {
					className: "text-[10px] text-text-disabled dark:text-text-disabled-dark truncate",
					children: [
						user.email && user.displayName ? user.email : "",
						user.email && user.displayName && user.roles?.length ? " · " : "",
						user.roles?.length ? user.roles.join(", ") : ""
					]
				})]
			}),
			isSelected && /* @__PURE__ */ jsx("svg", {
				className: "w-4 h-4 text-primary dark:text-primary-dark shrink-0",
				fill: "none",
				stroke: "currentColor",
				viewBox: "0 0 24 24",
				children: /* @__PURE__ */ jsx("path", {
					strokeLinecap: "round",
					strokeLinejoin: "round",
					strokeWidth: 2.5,
					d: "M5 13l4 4L19 7"
				})
			})
		]
	});
}
function matchesSearch(user, searchText) {
	const query = searchText.trim().toLowerCase();
	if (!query) return true;
	const name = (user.displayName || "").toLowerCase();
	const email = (user.email || "").toLowerCase();
	const uid = user.uid.toLowerCase();
	return name.includes(query) || email.includes(query) || uid.includes(query);
}
//#endregion
//#region src/components/UserDisplay.tsx
/**
* Component to render a single user with name and email
*/
function UserDisplay({ user }) {
	if (!user) return /* @__PURE__ */ jsx("span", {
		className: cls("italic", "text-text-disabled dark:text-text-disabled-dark", "text-sm"),
		children: "Not set"
	});
	const avatarSizeClass = "w-6 h-6";
	return /* @__PURE__ */ jsxs("div", {
		className: cls("inline-flex items-center gap-4 px-2 py-1 rounded-xl text-left max-w-full overflow-hidden", "bg-surface-raised", "border", defaultBorderMixin),
		children: [user.photoURL ? /* @__PURE__ */ jsx("img", {
			src: user.photoURL,
			alt: user.displayName || user.email || "User",
			className: cls("rounded-full object-cover", avatarSizeClass)
		}) : /* @__PURE__ */ jsx(CircleUserIcon, { className: cls("text-text-secondary dark:text-text-secondary-dark", avatarSizeClass) }), /* @__PURE__ */ jsxs("div", {
			className: "flex flex-col min-w-0",
			children: [/* @__PURE__ */ jsx("span", {
				className: cls("font-regular truncate", "text-sm"),
				children: user.displayName || user.email || "-"
			}), user.displayName && user.email && /* @__PURE__ */ jsx("span", {
				className: cls("text-text-secondary dark:text-text-secondary-dark truncate", "text-xs"),
				children: user.email
			})]
		})]
	});
}
//#endregion
//#region src/components/LoginView/oauth-redirect-flow.ts
/**
* The browser half of the OAuth authorization-code flow.
*
* The server only ever sees a `code` and a `redirectUri` taken from the
* request body, so `state`, PKCE and the binding between "this browser started
* a login" and "this code came back" are, by construction, the client's job.
* In the admin login they had been nobody's job: the authorize URL carried no
* `state`, and the return leg accepted any `?code=` as long as a localStorage
* marker naming the *provider* was set — a marker written on button click and
* removed only on a callback that carried a code, so abandoning the flow left
* it set indefinitely.
*
* Everything here is provider-agnostic on purpose. A button that wants a new
* provider calls {@link startOAuthRedirect} and gets state, expiry and PKCE
* without deciding anything, which is what stops the next provider shipping
* without them.
*/
/** sessionStorage, not localStorage: a login attempt belongs to one tab. */
var STORAGE_KEY = "rebase_oauth_redirect";
/** How long a started authorization stays valid. */
var PENDING_TTL_MS = 600 * 1e3;
function defaultStorage() {
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
}
function base64Url(bytes) {
	let binary = "";
	for (const byte of bytes) binary += String.fromCharCode(byte);
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function randomToken(byteLength = 32) {
	const bytes = new Uint8Array(byteLength);
	crypto.getRandomValues(bytes);
	return base64Url(bytes);
}
async function pkceChallenge(verifier) {
	const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
	return base64Url(new Uint8Array(digest));
}
function readPendingOAuthRedirect(storage = defaultStorage()) {
	if (!storage) return null;
	const raw = storage.getItem(STORAGE_KEY);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw);
		if (!parsed?.provider || !parsed?.state) return null;
		return parsed;
	} catch {
		return null;
	}
}
function clearPendingOAuthRedirect(storage = defaultStorage()) {
	storage?.removeItem(STORAGE_KEY);
}
/**
* Build the authorize URL and the pending record that will validate its
* callback. Split out from {@link startOAuthRedirect} so it can be tested
* without navigating.
*/
async function buildOAuthAuthorization(request, now = Date.now()) {
	const state = randomToken();
	const url = new URL(request.authorizeUrl);
	url.searchParams.set("response_type", "code");
	url.searchParams.set("client_id", request.clientId);
	url.searchParams.set("redirect_uri", request.redirectUri);
	url.searchParams.set("scope", request.scope);
	url.searchParams.set("state", state);
	for (const [key, value] of Object.entries(request.params ?? {})) url.searchParams.set(key, value);
	let codeVerifier;
	if (request.pkce) {
		codeVerifier = randomToken(32);
		url.searchParams.set("code_challenge", await pkceChallenge(codeVerifier));
		url.searchParams.set("code_challenge_method", "S256");
	}
	return {
		url: url.toString(),
		pending: {
			provider: request.provider,
			state,
			codeVerifier,
			redirectUri: request.redirectUri,
			startedAt: now
		}
	};
}
/** Remember the authorization and send the browser to the provider. */
async function startOAuthRedirect(request, storage = defaultStorage()) {
	const { url, pending } = await buildOAuthAuthorization(request);
	storage?.setItem(STORAGE_KEY, JSON.stringify(pending));
	window.location.href = url;
}
/**
* Interpret a return from the provider.
*
* Clears the pending record on **every** path that saw a callback — including
* `?error=` and a state mismatch — so a declined or abandoned consent screen
* cannot leave the browser primed to accept somebody else's code later.
*/
function consumeOAuthCallback(search, storage = defaultStorage(), now = Date.now()) {
	const params = new URLSearchParams(search);
	const code = params.get("code");
	const state = params.get("state");
	const error = params.get("error");
	if (!code && !error && !state) return { status: "none" };
	const pending = readPendingOAuthRedirect(storage);
	clearPendingOAuthRedirect(storage);
	if (error) return {
		status: "error",
		error
	};
	if (!code) return { status: "mismatch" };
	if (!pending) return { status: "mismatch" };
	if (now - pending.startedAt > PENDING_TTL_MS) return { status: "mismatch" };
	if (!state || state !== pending.state) return { status: "mismatch" };
	return {
		status: "ok",
		provider: pending.provider,
		code,
		codeVerifier: pending.codeVerifier,
		redirectUri: pending.redirectUri
	};
}
//#endregion
//#region src/components/LoginView/auth-error-message.ts
/**
* What the login screen says about a refused sign-in.
*
* The server's message is written for whoever reads the API response, and some
* of them are instructions for a developer. A Google sign-in onto an address
* that already has an unverified password account used to put this in front of
* the visitor:
*
*     … Sign in with your existing method, then POST to /auth/link/google to
*     link google to your account.
*
* So the codes a person can act on get the screen's own, translated words, and
* every other error keeps the server's message, which is still the most
* specific thing there is to show.
*/
function authErrorMessage(error, t) {
	if (error instanceof Error && "code" in error && error.code === "EMAIL_NOT_VERIFIED") return ("details" in error && typeof error.details === "object" && error.details !== null && "reason" in error.details ? error.details.reason : void 0) === "local-account-unverified" ? t("auth_account_exists_sign_in_with_password") : t("auth_account_exists_with_different_credential");
	return error instanceof Error ? error.message : String(error);
}
//#endregion
//#region src/components/LoginView/LoginView.tsx
/**
* The shared field background mixin (`dark:bg-black/30`) is invisible on the
* near-black login card, so login inputs get an explicit border in dark mode.
*/
var loginFieldClasses = "w-full dark:border dark:border-surface-700/70 dark:hover:border-surface-600";
/** One warning per provider per page load — this is a config hint, not a log stream. */
var warnedProviders = /* @__PURE__ */ new Set();
/**
* An OAuth button needs a client id in the frontend *and* the provider
* configured on the backend. Half a configuration renders nothing at all, with
* no error anywhere — the button simply is not there, which reads as "the
* feature broke" rather than "one env var is missing". Say which half is
* missing instead.
*/
function warnAboutHalfConfiguredOAuth(provider, clientId, backendProviders) {
	const backendHasIt = backendProviders.includes(provider);
	if (Boolean(clientId) === backendHasIt) return;
	if (warnedProviders.has(provider)) return;
	warnedProviders.add(provider);
	const envVar = `${provider.toUpperCase()}_CLIENT_ID`;
	console.warn(clientId ? `[Rebase] ${provider} login is hidden: the login view has a client id, but the backend reports no "${provider}" provider. Set ${envVar} (and ${provider.toUpperCase()}_CLIENT_SECRET) on the server and restart it.` : `[Rebase] ${provider} login is hidden: the backend has a "${provider}" provider, but the login view was given no client id. Pass it to the login view (e.g. VITE_${envVar}) and restart the frontend so the new env var is picked up.`);
}
/**
* Whether a provider's error code means "the visitor backed out" rather than
* "the sign-in failed". Closing the popup is an answer, not a fault, and
* showing it in red reads as a broken login.
*/
function isUserCancellation(code) {
	if (!code) return false;
	return [
		"access_denied",
		"popup_closed",
		"popup_closed_by_user",
		"user_cancel",
		"immediate_failed"
	].includes(code);
}
/**
* Generic login view component that works with any AuthControllerExtended.
* Feature-detects capabilities to show/hide login methods.
* @group Core
*/
function LoginView({ logo, authController, noUserComponent, disableSignupScreen = false, disabled = false, notAllowedError, googleClientId, githubClientId, linkedinClientId, title, subtitle, needsSetup, registrationEnabled, additionalComponent, topComponent, defaultEmail, defaultPassword, onNewsletterOptIn }) {
	const { mode: colorMode, setMode: setColorMode } = useModeController();
	const { t } = useTranslation();
	const [mode, setMode] = useState("buttons");
	const [fadeIn, setFadeIn] = useState(false);
	const [viewVisible, setViewVisible] = useState(true);
	const [providerError, setProviderError] = useState(null);
	const [newsletterOptIn, setNewsletterOptIn] = useState(false);
	const authControllerRef = useRef(authController);
	authControllerRef.current = authController;
	/**
	* Subscribe the address that just authenticated, if the visitor asked for
	* it. Every sign-in path calls this, which is the point: the checkbox sits
	* beside the provider buttons, so it has to mean the same thing whichever
	* one is pressed.
	*
	* Only ever from a resolution path — a ticked box on a *failed* attempt
	* must not subscribe an address nobody proved they control.
	*/
	const subscribeIfOptedIn = useCallback((email) => {
		if (!newsletterOptIn || !onNewsletterOptIn || !email) return;
		onNewsletterOptIn(email);
	}, [newsletterOptIn, onNewsletterOptIn]);
	const switchMode = (newMode) => {
		setProviderError(null);
		setViewVisible(false);
		setTimeout(() => {
			setMode(newMode);
			setViewVisible(true);
		}, 150);
	};
	const caps = authController.capabilities ?? {};
	const isBootstrapMode = needsSetup ?? ("needsSetup" in authController && !!authController.needsSetup) ?? false;
	const canRegister = registrationEnabled ?? caps.registrationEnabled ?? false;
	const backendProviders = caps.enabledProviders ?? [];
	const hasGoogleLogin = Boolean(googleClientId) && backendProviders.includes("google");
	const hasGitHubLogin = githubClientId && backendProviders.includes("github");
	const hasLinkedinLogin = linkedinClientId && backendProviders.includes("linkedin");
	useEffect(() => {
		const timer = setTimeout(() => {
			warnAboutHalfConfiguredOAuth("google", googleClientId, backendProviders);
			warnAboutHalfConfiguredOAuth("github", githubClientId, backendProviders);
			warnAboutHalfConfiguredOAuth("linkedin", linkedinClientId, backendProviders);
		}, 3e3);
		return () => clearTimeout(timer);
	}, [
		googleClientId,
		githubClientId,
		linkedinClientId,
		backendProviders.join(",")
	]);
	const hasPasswordReset = caps.passwordReset ?? !!authController.forgotPassword;
	const showRegistration = !disableSignupScreen && canRegister;
	useEffect(() => {
		const timer = setTimeout(() => setFadeIn(true), 50);
		return () => clearTimeout(timer);
	}, []);
	useEffect(() => {
		const result = consumeOAuthCallback(window.location.search);
		if (result.status === "none") return;
		const cleanUrl = window.location.origin + window.location.pathname;
		window.history.replaceState({}, document.title, cleanUrl);
		if (result.status === "error") {
			console.error(`OAuth sign-in failed: ${result.error}`);
			if (!isUserCancellation(result.error)) setProviderError(`Sign-in failed: ${result.error}`);
			return;
		}
		if (result.status === "mismatch") {
			console.error("Ignoring an OAuth code that does not match a sign-in this browser started.");
			setProviderError("This sign-in could not be verified as one you started in this browser. Please try again.");
			return;
		}
		if (authController.oauthLogin) authController.oauthLogin(result.provider, {
			code: result.code,
			redirectUri: result.redirectUri,
			...result.codeVerifier ? { codeVerifier: result.codeVerifier } : {}
		}).catch((err) => {
			console.error(`${result.provider} login failed:`, err);
		});
	}, [authController]);
	let logoComponent;
	if (logo) logoComponent = /* @__PURE__ */ jsx("img", {
		src: logo,
		style: {
			height: "100%",
			width: "100%",
			objectFit: "cover"
		},
		alt: "Logo"
	});
	else logoComponent = /* @__PURE__ */ jsx(RebaseLogo, {});
	const controllerError = authController.authProviderError && !authController.user ? authErrorMessage(authController.authProviderError, t) : null;
	const buttonsErrorMessage = providerError ?? controllerError;
	let notAllowedMessage;
	if (notAllowedError) if (typeof notAllowedError === "string") notAllowedMessage = notAllowedError;
	else if (notAllowedError instanceof Error) notAllowedMessage = notAllowedError.message;
	else notAllowedMessage = "It looks like you don't have access, based on the specified access configuration";
	return /* @__PURE__ */ jsxs("div", {
		className: cls("relative flex items-center justify-center h-screen w-screen p-4 transition-opacity duration-500 bg-surface-frame overflow-hidden", fadeIn ? "opacity-100" : "opacity-0"),
		children: [/* @__PURE__ */ jsxs("div", {
			className: "absolute top-4 right-4 flex items-center gap-1 z-10",
			children: [/* @__PURE__ */ jsx(LanguageToggle, {}), /* @__PURE__ */ jsxs(Menu, {
				trigger: /* @__PURE__ */ jsx(IconButton, {
					color: "inherit",
					"aria-label": "Toggle theme",
					children: colorMode === "dark" ? /* @__PURE__ */ jsx(MoonIcon, { size: iconSize.small }) : /* @__PURE__ */ jsx(SunIcon, { size: iconSize.small })
				}),
				children: [
					/* @__PURE__ */ jsxs(MenuItem, {
						onClick: () => setColorMode("dark"),
						children: [
							/* @__PURE__ */ jsx(MoonIcon, { size: iconSize.smallest }),
							" ",
							t("dark_mode")
						]
					}),
					/* @__PURE__ */ jsxs(MenuItem, {
						onClick: () => setColorMode("light"),
						children: [
							/* @__PURE__ */ jsx(SunIcon, { size: iconSize.smallest }),
							" ",
							t("light_mode")
						]
					}),
					/* @__PURE__ */ jsxs(MenuItem, {
						onClick: () => setColorMode("system"),
						children: [
							/* @__PURE__ */ jsx(SunMoonIcon, { size: iconSize.smallest }),
							" ",
							t("system_mode")
						]
					})
				]
			})]
		}), /* @__PURE__ */ jsxs("div", {
			className: "relative flex flex-col items-center w-[440px] max-w-full p-8 sm:p-10 bg-surface-card/70 backdrop-blur-xl border border-hairline-strong rounded-2xl shadow-2xl z-10 transition-all duration-300 hover:shadow-primary/5",
			children: [
				/* @__PURE__ */ jsx("div", {
					className: "w-24 h-24 m-2 mb-4 drop-shadow-md",
					children: logoComponent
				}),
				notAllowedMessage && /* @__PURE__ */ jsx("div", {
					className: "p-4 w-full",
					children: /* @__PURE__ */ jsx(ErrorView, { error: notAllowedMessage })
				}),
				/* @__PURE__ */ jsxs("div", {
					className: cls("w-full transition-opacity duration-150", viewVisible ? "opacity-100" : "opacity-0"),
					children: [isBootstrapMode && !authController.user && /* @__PURE__ */ jsx(LoginForm, {
						authController,
						registrationMode: true,
						onClose: () => {},
						onForgotPassword: () => {},
						disableSignupScreen: false,
						bootstrapMode: true,
						defaultEmail,
						defaultPassword,
						onSignedIn: subscribeIfOptedIn,
						newsletterOptIn,
						setNewsletterOptIn,
						showNewsletterOptIn: Boolean(onNewsletterOptIn)
					}), !isBootstrapMode && /* @__PURE__ */ jsxs(Fragment, { children: [
						mode === "buttons" && /* @__PURE__ */ jsxs("div", {
							className: "w-full flex flex-col gap-3 mt-2",
							children: [
								buttonsErrorMessage && /* @__PURE__ */ jsx(ErrorView, { error: buttonsErrorMessage }),
								(title || subtitle) && /* @__PURE__ */ jsxs("div", {
									className: "text-center mb-2",
									children: [title && /* @__PURE__ */ jsx(Typography, {
										variant: "h6",
										className: "mb-0.5 font-semibold",
										children: title
									}), subtitle && /* @__PURE__ */ jsx(Typography, {
										variant: "body2",
										color: "secondary",
										className: "mb-4",
										children: subtitle
									})]
								}),
								topComponent && /* @__PURE__ */ jsx("div", {
									className: "w-full",
									children: topComponent
								}),
								onNewsletterOptIn && /* @__PURE__ */ jsxs("label", {
									className: cls("flex items-center gap-2 cursor-pointer", (topComponent || title || subtitle) && "-mt-3"),
									children: [/* @__PURE__ */ jsx(Checkbox, {
										checked: newsletterOptIn,
										onCheckedChange: (checked) => setNewsletterOptIn(checked === true),
										size: "small"
									}), /* @__PURE__ */ jsx(Typography, {
										variant: "caption",
										color: "secondary",
										className: "select-none",
										children: t("join_newsletter")
									})]
								}),
								/* @__PURE__ */ jsx(LoginButton, {
									disabled,
									text: "Sign in with email",
									icon: /* @__PURE__ */ jsx(MailIcon, {}),
									onClick: () => switchMode("login")
								}),
								hasGoogleLogin && googleClientId && /* @__PURE__ */ jsx(GoogleLoginButton, {
									disabled,
									googleClientId,
									authController,
									onSignedIn: () => subscribeIfOptedIn(authControllerRef.current.user?.email),
									onError: setProviderError
								}),
								hasGitHubLogin && githubClientId && /* @__PURE__ */ jsx(GitHubLoginButton, {
									disabled,
									githubClientId
								}),
								hasLinkedinLogin && linkedinClientId && /* @__PURE__ */ jsx(LinkedInLoginButton, {
									disabled,
									linkedinClientId
								}),
								showRegistration && /* @__PURE__ */ jsx("div", {
									className: "mt-2 text-center",
									children: /* @__PURE__ */ jsxs(Typography, {
										variant: "body2",
										color: "secondary",
										children: [
											"Don't have an account?",
											" ",
											/* @__PURE__ */ jsx("button", {
												type: "button",
												className: "font-semibold hover:underline cursor-pointer text-primary dark:text-primary-light",
												onClick: () => switchMode("register"),
												children: "Create one"
											})
										]
									})
								})
							]
						}),
						mode === "login" && /* @__PURE__ */ jsx(LoginForm, {
							authController,
							registrationMode: false,
							onClose: () => switchMode("buttons"),
							onForgotPassword: hasPasswordReset ? () => switchMode("forgot") : void 0,
							noUserComponent,
							disableSignupScreen,
							switchToRegister: showRegistration ? () => switchMode("register") : void 0,
							defaultEmail,
							defaultPassword,
							onSignedIn: subscribeIfOptedIn
						}),
						mode === "register" && /* @__PURE__ */ jsx(LoginForm, {
							authController,
							registrationMode: true,
							onClose: () => switchMode("buttons"),
							onForgotPassword: hasPasswordReset ? () => switchMode("forgot") : void 0,
							noUserComponent,
							disableSignupScreen,
							switchToLogin: () => switchMode("login"),
							defaultEmail,
							defaultPassword,
							onSignedIn: subscribeIfOptedIn
						}),
						mode === "forgot" && authController.forgotPassword && /* @__PURE__ */ jsx(ForgotPasswordForm, {
							authController,
							onClose: () => switchMode("login")
						})
					] })]
				}),
				additionalComponent && !isBootstrapMode && mode === "buttons" && /* @__PURE__ */ jsx("div", {
					className: "w-full",
					children: additionalComponent
				})
			]
		})]
	});
}
function LoginButton({ icon, onClick, text, disabled }) {
	return /* @__PURE__ */ jsx(Button, {
		disabled,
		className: "w-full transition-transform duration-200 active:scale-[0.98]",
		variant: "outlined",
		size: "large",
		onClick,
		children: /* @__PURE__ */ jsxs("div", {
			className: "flex items-center justify-center w-full gap-3 py-1",
			children: [/* @__PURE__ */ jsx("span", {
				className: "flex items-center justify-center w-5 h-5",
				children: icon
			}), /* @__PURE__ */ jsx(Typography, {
				variant: "button",
				children: text
			})]
		})
	});
}
var GoogleIcon = () => /* @__PURE__ */ jsxs("svg", {
	viewBox: "0 0 24 24",
	width: "20",
	height: "20",
	children: [
		/* @__PURE__ */ jsx("path", {
			fill: "#4285F4",
			d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
		}),
		/* @__PURE__ */ jsx("path", {
			fill: "#34A853",
			d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
		}),
		/* @__PURE__ */ jsx("path", {
			fill: "#FBBC05",
			d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
		}),
		/* @__PURE__ */ jsx("path", {
			fill: "#EA4335",
			d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
		})
	]
});
function GoogleLoginButton({ disabled, googleClientId, authController, onSignedIn, onError }) {
	const codeClientRef = useRef(null);
	const onSignedInRef = useRef(onSignedIn);
	onSignedInRef.current = onSignedIn;
	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;
	useEffect(() => {
		if (!authController.googleLogin) return;
		const google = window.google;
		if (!google || codeClientRef.current) return;
		codeClientRef.current = google.accounts.oauth2.initCodeClient({
			client_id: googleClientId,
			scope: "openid email profile",
			ux_mode: "popup",
			callback: async (response) => {
				if (response.error || !response.code) {
					console.error("Google login error:", response.error);
					if (!isUserCancellation(response.error)) onErrorRef.current?.(response.error ? `Google sign-in failed: ${response.error}` : "Google sign-in failed. Please try again.");
					return;
				}
				onErrorRef.current?.(null);
				try {
					await authController.googleLogin({
						code: response.code,
						redirectUri: "postmessage"
					});
					onSignedInRef.current?.();
				} catch (err) {
					console.error("Google login error:", err);
				}
			}
		});
	}, [googleClientId, authController]);
	const handleClick = () => {
		if (!codeClientRef.current) {
			console.error("Google Sign-In not loaded");
			onErrorRef.current?.("Google Sign-In could not be loaded. Check your connection or any script blockers, then try again.");
			return;
		}
		onErrorRef.current?.(null);
		codeClientRef.current.requestCode();
	};
	return /* @__PURE__ */ jsx(LoginButton, {
		disabled,
		text: "Sign in with Google",
		icon: /* @__PURE__ */ jsx(GoogleIcon, {}),
		onClick: handleClick
	});
}
var GitHubIcon = () => /* @__PURE__ */ jsx("svg", {
	viewBox: "0 0 24 24",
	width: "20",
	height: "20",
	fill: "currentColor",
	children: /* @__PURE__ */ jsx("path", { d: "M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.137 20.162 22 16.418 22 12c0-5.523-4.477-10-10-10z" })
});
function GitHubLoginButton({ disabled, githubClientId }) {
	const handleClick = () => {
		startOAuthRedirect({
			provider: "github",
			authorizeUrl: "https://github.com/login/oauth/authorize",
			clientId: githubClientId,
			redirectUri: window.location.origin + window.location.pathname,
			scope: "read:user,user:email"
		});
	};
	return /* @__PURE__ */ jsx(LoginButton, {
		disabled,
		text: "Sign in with GitHub",
		icon: /* @__PURE__ */ jsx(GitHubIcon, {}),
		onClick: handleClick
	});
}
var LinkedInIcon = () => /* @__PURE__ */ jsx("svg", {
	viewBox: "0 0 24 24",
	width: "20",
	height: "20",
	fill: "currentColor",
	children: /* @__PURE__ */ jsx("path", { d: "M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" })
});
function LinkedInLoginButton({ disabled, linkedinClientId }) {
	const handleClick = () => {
		startOAuthRedirect({
			provider: "linkedin",
			authorizeUrl: "https://www.linkedin.com/oauth/v2/authorization",
			clientId: linkedinClientId,
			redirectUri: window.location.origin + window.location.pathname,
			scope: "openid profile email"
		});
	};
	return /* @__PURE__ */ jsx(LoginButton, {
		disabled,
		text: "Sign in with LinkedIn",
		icon: /* @__PURE__ */ jsx(LinkedInIcon, {}),
		onClick: handleClick
	});
}
function LoginForm({ onClose, onForgotPassword, authController, registrationMode, noUserComponent, disableSignupScreen, bootstrapMode = false, switchToRegister, switchToLogin, defaultEmail, defaultPassword, onSignedIn, newsletterOptIn = false, setNewsletterOptIn, showNewsletterOptIn = false }) {
	const passwordRef = useRef(null);
	const { t } = useTranslation();
	const emailId = useId();
	const passwordId = useId();
	const displayNameId = useId();
	const [email, setEmail] = useState(defaultEmail);
	const [password, setPassword] = useState(defaultPassword);
	const [displayName, setDisplayName] = useState();
	useEffect(() => {
		if (!document) return;
		const escFunction = (event) => {
			if (event.key === "Escape") onClose();
		};
		document.addEventListener("keydown", escFunction, false);
		return () => {
			document.removeEventListener("keydown", escFunction, false);
		};
	}, [onClose]);
	function reportSignedIn(email) {
		onSignedIn?.(email);
	}
	function handleEnterPassword() {
		if (email && password && authController.emailPasswordLogin) Promise.resolve(authController.emailPasswordLogin(email, password)).then(() => reportSignedIn(email)).catch(() => void 0);
	}
	function handleRegistration() {
		if (email && password && authController.register) Promise.resolve(authController.register(email, password, displayName)).then(() => reportSignedIn(email)).catch(() => void 0);
	}
	const handleSubmit = (event) => {
		event.preventDefault();
		if (registrationMode) handleRegistration();
		else handleEnterPassword();
	};
	const title = bootstrapMode ? "Welcome!" : registrationMode ? "Create account" : "Sign in";
	const subtitle = bootstrapMode ? "Create your admin account to get started. This account will have admin privileges." : registrationMode ? "Fill in your details to create a new account" : "Enter your credentials to continue";
	const buttonLabel = registrationMode ? "Create account" : "Sign in";
	return /* @__PURE__ */ jsxs("form", {
		onSubmit: handleSubmit,
		className: "flex flex-col w-full gap-1 mt-2",
		children: [
			!bootstrapMode && /* @__PURE__ */ jsx("div", {
				className: "w-full mb-2 -ml-2.5",
				children: /* @__PURE__ */ jsx(IconButton, {
					onClick: onClose,
					children: /* @__PURE__ */ jsx(ArrowLeftIcon, {})
				})
			}),
			(() => {
				const err = authController.authProviderError;
				if (!err || authController.user) return null;
				return /* @__PURE__ */ jsx("div", {
					className: "w-full mb-2",
					children: /* @__PURE__ */ jsx(ErrorView, { error: authErrorMessage(err, t) })
				});
			})(),
			/* @__PURE__ */ jsx(Typography, {
				variant: "h6",
				className: "mb-0.5",
				children: title
			}),
			/* @__PURE__ */ jsx(Typography, {
				variant: "body2",
				color: "secondary",
				className: "mb-5",
				children: subtitle
			}),
			registrationMode && !bootstrapMode && noUserComponent && /* @__PURE__ */ jsx("div", {
				className: "w-full mb-2",
				children: noUserComponent
			}),
			registrationMode && /* @__PURE__ */ jsxs("div", {
				className: "w-full mb-3",
				children: [/* @__PURE__ */ jsx(Typography, {
					variant: "label",
					component: "label",
					color: "secondary",
					className: "mb-1",
					htmlFor: displayNameId,
					children: "Display Name"
				}), /* @__PURE__ */ jsx(TextField, {
					placeholder: "Jane Doe (optional)",
					id: displayNameId,
					className: loginFieldClasses,
					value: displayName ?? "",
					disabled: authController.initialLoading,
					type: "text",
					size: "medium",
					onChange: (event) => setDisplayName(event.target.value)
				})]
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "w-full mb-3",
				children: [/* @__PURE__ */ jsx(Typography, {
					variant: "label",
					component: "label",
					color: "secondary",
					className: "mb-1",
					htmlFor: emailId,
					children: "Email"
				}), /* @__PURE__ */ jsx(TextField, {
					placeholder: "you@example.com",
					id: emailId,
					className: loginFieldClasses,
					autoFocus: true,
					value: email ?? "",
					disabled: authController.initialLoading,
					type: "email",
					size: "medium",
					onChange: (event) => setEmail(event.target.value)
				})]
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "w-full mb-1",
				children: [/* @__PURE__ */ jsx(Typography, {
					variant: "label",
					component: "label",
					color: "secondary",
					className: "mb-1",
					htmlFor: passwordId,
					children: "Password"
				}), /* @__PURE__ */ jsx(TextField, {
					placeholder: "••••••••",
					id: passwordId,
					className: loginFieldClasses,
					value: password ?? "",
					disabled: authController.initialLoading,
					inputRef: passwordRef,
					type: "password",
					size: "medium",
					onChange: (event) => setPassword(event.target.value)
				})]
			}),
			registrationMode && /* @__PURE__ */ jsx(Typography, {
				variant: "caption",
				color: "secondary",
				className: "mb-3",
				children: "Password must be 8+ characters with uppercase, lowercase, and a number"
			}),
			!registrationMode && onForgotPassword && /* @__PURE__ */ jsx("div", {
				className: "w-full text-right mb-3",
				children: /* @__PURE__ */ jsx("button", {
					type: "button",
					className: "text-xs font-medium hover:underline cursor-pointer text-primary dark:text-primary-light",
					onClick: onForgotPassword,
					children: "Forgot password?"
				})
			}),
			showNewsletterOptIn && /* @__PURE__ */ jsxs("label", {
				className: "flex items-center gap-2 cursor-pointer mt-1 mb-1",
				children: [/* @__PURE__ */ jsx(Checkbox, {
					checked: newsletterOptIn,
					onCheckedChange: (checked) => setNewsletterOptIn?.(checked === true),
					size: "small"
				}), /* @__PURE__ */ jsx(Typography, {
					variant: "caption",
					color: "secondary",
					className: "select-none",
					children: t("join_newsletter")
				})]
			}),
			/* @__PURE__ */ jsx(LoadingButton, {
				type: "submit",
				variant: "filled",
				color: "primary",
				className: "w-full mt-1",
				size: "large",
				loading: authController.authLoading,
				disabled: authController.authLoading || !email || !password,
				children: buttonLabel
			}),
			switchToRegister && /* @__PURE__ */ jsx("div", {
				className: "mt-4 text-center",
				children: /* @__PURE__ */ jsxs(Typography, {
					variant: "body2",
					color: "secondary",
					children: [
						"Don't have an account?",
						" ",
						/* @__PURE__ */ jsx("button", {
							type: "button",
							className: "font-semibold hover:underline cursor-pointer text-primary dark:text-primary-light",
							onClick: switchToRegister,
							children: "Create one"
						})
					]
				})
			}),
			switchToLogin && /* @__PURE__ */ jsx("div", {
				className: "mt-4 text-center",
				children: /* @__PURE__ */ jsxs(Typography, {
					variant: "body2",
					color: "secondary",
					children: [
						"Already have an account?",
						" ",
						/* @__PURE__ */ jsx("button", {
							type: "button",
							className: "font-semibold hover:underline cursor-pointer text-primary dark:text-primary-light",
							onClick: switchToLogin,
							children: "Sign in"
						})
					]
				})
			})
		]
	});
}
function ForgotPasswordForm({ onClose, authController }) {
	const [email, setEmail] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [error, setError] = useState(null);
	const emailId = useId();
	useEffect(() => {
		if (!document) return;
		const escFunction = (event) => {
			if (event.key === "Escape") onClose();
		};
		document.addEventListener("keydown", escFunction, false);
		return () => {
			document.removeEventListener("keydown", escFunction, false);
		};
	}, [onClose]);
	const handleSubmit = async (event) => {
		event.preventDefault();
		setError(null);
		if (!email) {
			setError("Please enter your email address");
			return;
		}
		if (!authController.forgotPassword) {
			setError("Password reset is not available with this auth provider.");
			return;
		}
		try {
			await authController.forgotPassword(email);
			setSubmitted(true);
		} catch (err) {
			if (err instanceof Error && err.code === "EMAIL_NOT_CONFIGURED") setError("Password reset is not available. Please contact your administrator.");
			else setSubmitted(true);
		}
	};
	if (submitted) return /* @__PURE__ */ jsxs("div", {
		className: "flex flex-col w-full gap-4 mt-2",
		children: [
			/* @__PURE__ */ jsx("div", {
				className: "w-full -ml-2.5",
				children: /* @__PURE__ */ jsx(IconButton, {
					onClick: onClose,
					children: /* @__PURE__ */ jsx(ArrowLeftIcon, {})
				})
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "text-center rounded-xl p-6 bg-surface-raised",
				children: [
					/* @__PURE__ */ jsx("div", {
						className: "text-3xl mb-3",
						children: "📧"
					}),
					/* @__PURE__ */ jsx(Typography, {
						variant: "subtitle1",
						className: "mb-2",
						children: "Check your email"
					}),
					/* @__PURE__ */ jsxs(Typography, {
						variant: "body2",
						color: "secondary",
						children: [
							"If an account exists for ",
							/* @__PURE__ */ jsx("strong", { children: email }),
							", you'll receive a password reset link shortly."
						]
					})
				]
			}),
			/* @__PURE__ */ jsx(Button, {
				onClick: onClose,
				variant: "text",
				className: "mt-2",
				children: "Back to sign in"
			})
		]
	});
	return /* @__PURE__ */ jsxs("form", {
		onSubmit: handleSubmit,
		className: "flex flex-col w-full gap-1 mt-2",
		children: [
			/* @__PURE__ */ jsx("div", {
				className: "w-full mb-2 -ml-2.5",
				children: /* @__PURE__ */ jsx(IconButton, {
					onClick: onClose,
					children: /* @__PURE__ */ jsx(ArrowLeftIcon, {})
				})
			}),
			/* @__PURE__ */ jsx(Typography, {
				variant: "h6",
				className: "mb-0.5",
				children: "Reset password"
			}),
			/* @__PURE__ */ jsx(Typography, {
				variant: "body2",
				color: "secondary",
				className: "mb-5",
				children: "Enter your email and we'll send you a reset link."
			}),
			error && /* @__PURE__ */ jsx("div", {
				className: "w-full mb-3",
				children: /* @__PURE__ */ jsx(ErrorView, { error })
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "w-full mb-3",
				children: [/* @__PURE__ */ jsx(Typography, {
					variant: "label",
					component: "label",
					color: "secondary",
					className: "mb-1",
					htmlFor: emailId,
					children: "Email"
				}), /* @__PURE__ */ jsx(TextField, {
					placeholder: "you@example.com",
					id: emailId,
					className: loginFieldClasses,
					autoFocus: true,
					value: email,
					type: "email",
					size: "medium",
					onChange: (event) => setEmail(event.target.value)
				})]
			}),
			/* @__PURE__ */ jsx(LoadingButton, {
				type: "submit",
				variant: "filled",
				className: "w-full",
				size: "large",
				loading: authController.authLoading,
				disabled: authController.authLoading || !email,
				children: "Send reset link"
			})
		]
	});
}
//#endregion
//#region src/components/RebaseAuth.tsx
/**
* Declarative component to configure authentication in Rebase.
* Renders nothing — purely registers config into the RebaseRegistry.
*
* This is a framework-level component that lives in core since
* authentication is cross-cutting (admin, Studio, any app area).
* @group Core
*/
function RebaseAuth({ loginView }) {
	const dispatch = useRebaseRegistryDispatch();
	const registeredRef = useRef(false);
	useLayoutEffect(() => {
		dispatch.registerAuth({ loginView });
		registeredRef.current = true;
		return () => {
			registeredRef.current = false;
			dispatch.unregisterAuth();
		};
	}, [dispatch, loginView]);
	return null;
}
//#endregion
//#region src/locales/en.ts
/**
* English baseline translations for @rebasepro/app.
*
* This is the single source of truth for all default UI strings.
* Override any key via the `translations` prop on Rebase entry-point components.
*
* @group Core
*/
var en = {
	save: "Save",
	create: "Create",
	create_copy: "Create copy",
	save_and_close: "Save and close",
	create_copy_and_close: "Create copy and close",
	create_and_close: "Create and close",
	discard: "Discard",
	clear: "Clear",
	cancel: "Cancel",
	edit: "Edit",
	copy: "Copy",
	delete: "Delete",
	delete_not_allowed: "You have selected at least one entity you cannot delete",
	edit_entity: "Edit",
	back_to_detail: "Back to details",
	delete_confirmation_title: "Delete?",
	delete_confirmation_body: "This will delete the entity. Are you sure?",
	delete_multiple_confirmation_body: "This will delete the selected entities. Are you sure?",
	unsaved_changes_title: "Unsaved changes",
	unsaved_changes_body: "You have unsaved changes. Do you want to discard them?",
	discard_changes: "Discard changes",
	keep_editing: "Keep editing",
	clear_form: "Clear form?",
	discard_changes_confirmation: "All unsaved changes will be lost. This cannot be undone.",
	clear_form_confirmation: "All entered values will be cleared. This cannot be undone.",
	search: "Search",
	find_by_id: "Find by ID",
	find_entity_by_id: "Find entity by ID",
	filter: "Filter",
	filters: "Filters",
	records_in_view: "Records in this view",
	clear_filter: "Clear filter",
	clear_filter_sort: "Clear filter/sort",
	clear_sort: "Clear sort",
	sort: "Sort",
	sort_by: "Sort by",
	sort_then_by: "Then by",
	sort_ascending: "Sort ascending",
	sort_descending: "Sort descending",
	sort_remove: "Remove sort",
	sort_move_up: "Make this key more important",
	sort_move_down: "Make this key less important",
	sort_remove_key: "Remove this sort key",
	sort_key_position: "Sort key {{position}}",
	sort_shift_click_hint: "Shift-click to add a column under the current sort",
	save_entity_before_subcollections: "You need to save your entity before adding additional collections",
	clear_all: "Clear all",
	no_items: "No items",
	no_entries_found: "No entries found",
	all_entries_loaded: "All {{count}} entries loaded",
	create_your_first_entry: "Create your first entry",
	no_results_filter_sort: "No results with the applied filter/sort",
	no_results_search: "No results found for \"{{search}}\"",
	add: "Add",
	remove: "Remove",
	multiple_entities: "Multiple entities",
	unsaved_changes: "You have unsaved changes in this {{collectionName}}.",
	so_empty: "So empty...",
	no_results: "No results with the applied filter/sort",
	refresh_data: "Refresh data",
	dark_mode: "Dark",
	light_mode: "Light",
	system_mode: "System",
	join_newsletter: "Join the newsletter, no spam",
	ok: "Ok",
	save_collection_config: "Save collection structure",
	search_for_more_icons: "Search for more icons…",
	ai_modified: "AI modified",
	size_label: "Size",
	group_by: "Group by",
	initialize_kanban_order: "Initialize Kanban Order",
	copy_id: "Copy ID",
	add_specific: "Add {{name}}",
	add_named: "Add \"{{name}}\"",
	select_specific: "Select {{name}}",
	select_from: "Select from {{name}}",
	done: "Done",
	log_out: "Log out",
	column_cannot_be_edited: "This column can't be edited directly",
	close: "Close",
	hide_list: "Hide list",
	show_list: "Show list",
	unsaved_local_changes: "Unsaved Local changes",
	unsaved_local_changes_description: "This document was edited locally and has unsaved changes. These local changes will be lost if you don't apply them.",
	preview_changes: "Preview Changes",
	apply_changes: "Apply Changes",
	discard_local_changes: "Discard Local Changes",
	preview_local_changes: "Preview Local Changes",
	preview_local_changes_description: "These are the local changes that will be applied to the form.",
	type: "Type",
	string: "string",
	number: "number",
	boolean: "boolean",
	date: "date",
	map: "map",
	array: "array",
	arrays_of_arrays_not_supported: "Arrays of arrays are not supported.",
	data_type_not_supported: "Data type {{dataType}} not supported yet",
	passkey_error_unsupported: "Your device or browser doesn't support passkeys.",
	saved_correctly: "Saved successfully",
	error_before_saving: "Error preparing data",
	error_after_saving: "Error in post-save hook",
	error_saving_entity: "Error saving entity",
	entity_does_not_exist: "This entity does not exist in the database",
	form_modified: "This form has been modified",
	form_in_sync: "The current form is in sync with the database",
	fix_errors_before_saving: "Fix highlighted errors before saving",
	admin: "Admin",
	home: "Home",
	this_form_has_errors: "This form has errors",
	error_loading_navigation: "Error loading navigation",
	error_loading_auth: "Error loading auth",
	this_form_has_been_modified: "This form has been modified",
	current_form_in_sync: "The current form is in sync with the database",
	open_in_console: "Open in the console",
	collection_does_not_exist: "The specified collection does not exist. Check console",
	unexpected_value: "Unexpected value",
	unexpected_value_click_to_edit: "Unexpected value. Click to edit",
	unexpected_reference_value: "Unexpected reference value. Click to edit",
	copy_url_to_clipboard: "Copy url to clipboard",
	open_image_in_new_tab: "Open image in new tab",
	reference_not_set: "Reference not set",
	reference_does_not_exist: "Reference does not exist",
	entity_not_found: "Entity not found",
	entity_not_found_body: "This record does not exist, or it may be hidden from you by row-level security.",
	back_to_collection: "Back to {{name}}",
	file_not_found: "File not found",
	unsaved_changes_in_entity: "You have unsaved changes in this entity.",
	delete_this_role: "Delete this role",
	no_roles_yet: "You don't have any roles yet.",
	create_default_roles: "Create default roles",
	delete_role_confirmation: "Are you sure you want to delete this role?",
	delete_this_user: "Delete this user",
	no_users_yet: "There are no users yet",
	add_logged_user_as_admin: "Add the logged user as an admin",
	add_current_user_as_admin: "Add current user as admin",
	create_default_roles_and_add_admin: "Create default roles and add current user as admin",
	delete_user_confirmation: "Are you sure you want to delete this user?",
	create_your_users_and_roles: "Create your users and roles",
	no_users_or_roles_defined: "You have no users or roles defined. You can create default roles and add the current user as admin.",
	save_before_changing_schema: "You need to save the document before changing the schema",
	edit_schema_for_this_form: "Edit schema for this form",
	no_permissions_to_edit_collection: "You don't have permissions to edit this collection",
	browser_does_not_support_audio: "Your browser does not support the audio element.",
	user_not_found: "User not found: {{value}}",
	delete_selected: "Delete",
	cannot_delete_selected: "You have selected at least one entity you cannot delete",
	add_entry: "Add",
	add_on_top: "Add on top",
	add_below: "Add below",
	add_to_field: "Add to {{fieldName}}",
	value: "Value",
	key: "Key",
	drag_drop_multiple: "Drag 'n' drop some files here, or click to select files. Drag to reorder.",
	drag_drop_single: "Drag 'n' drop a file here, or click to select one",
	open_menu: "Open menu",
	close_drawer: "Close drawer",
	navigation_drawer: "Navigation drawer",
	collapse: "Collapse",
	expand: "Expand",
	change_language: "Change language",
	toggle_theme: "Toggle theme",
	user_menu: "User menu",
	error: "Error",
	error_loading_data: "Could not load data",
	error_check_server_logs: "Check server logs for details.",
	error_technical_details: "Technical details",
	error_uploading_file: "Error uploading file",
	error_deleting: "Error deleting",
	error_before_delete: "Error before delete",
	error_updating_asset: "Error updating asset",
	error_deleting_asset: "Error deleting asset",
	error_firestore_index: "A Firestore index is required for this query.",
	create_index: "Create Index",
	value_is_not_reference: "Value is not a reference.",
	click_to_edit: "Click to edit",
	data_is_not_array_of_references: "Data is not an array of references",
	loading: "Loading",
	local_changes_applied: "Local changes applied to the form",
	local_changes_discarded: "Local changes discarded",
	are_you_sure_leave: "Are you sure you want to leave this page?",
	see_console_details: "See console for more details.",
	drop_here_create_group: "Drop here to create a new group",
	filter_for_null_values: "Filter for null values",
	value_updated_elsewhere: "This value has been updated elsewhere",
	add_property: "Add property",
	edit_name: "Edit {{name}}",
	this_entity_not_exist: "This entity does not exist in the database",
	internal_error: "Internal ERROR",
	rename_group: "Rename Group",
	group_name_label: "Group Name",
	group_name_empty_error: "Group name cannot be empty.",
	group_name_exists_error: "This group name already exists.",
	search_collections: "Search collections",
	views_group: "Views",
	youd_need_to_save_before_additional_collections: "You need to save your entity before adding additional collections",
	page_not_found: "Page not found",
	page_not_found_body: "This page does not exist or you may not have access to it",
	back_to_home: "Back to home",
	default_collection_view: "Default collection view",
	table_view: "Table",
	cards_view: "Cards",
	kanban_view: "Kanban",
	choose_how_entities_displayed_default: "Choose how entities should be displayed by default",
	document_view: "Document view",
	side_panel: "Side panel",
	full_screen: "Full screen",
	should_documents_opened_full_screen: "Should documents be opened full screen or in an inline side dialog",
	select_custom_view: "Select custom view",
	no_custom_views_defined: "No custom views defined. Define your custom views in the customization settings, before using this dialog.",
	select_custom_action: "Select custom action",
	no_custom_actions_defined: "No custom actions defined. Define your custom actions in the customization settings, before using this dialog.",
	no_collections_found: "No collections found",
	start_building_collections: "Start building collections in Rebase easily. Map them to your existing database data, import from files, or use our templates.",
	create_first_collection: "Create your first collection",
	define_collections_programmatically: "You can also define collections programmatically.",
	edit_collection: "Edit collection",
	no_permissions_edit_collection: "You don't have permissions to edit this collection",
	no_permissions_create_collection: "You don't have permissions to create collections",
	create_collection: "Create collection",
	update_collection: "Update collection",
	new_collection: "New collection",
	add_new_collection: "Add new collection",
	collection_with_name: "{{name}} collection",
	change_icon: "Change icon",
	is_subcollection_of: "This is a subcollection of",
	name: "Name",
	collection_name_description: "Name of this collection, usually a plural name (e.g. Products)",
	path: "Path",
	relative_path_to_parent: "Relative path to the parent (no need to include the parent path)",
	path_in_database: "Path or table name where this collection is stored in the database",
	singular_name: "Singular name",
	singular_name_description: "Optionally define a singular name for your entities",
	description: "Description",
	description_of_collection: "Description of the collection, you can use markdown",
	collection_id: "Collection ID",
	collection_id_description: "This ID identifies this collection. Typically the same as the path or table name.",
	collection_group: "Collection group",
	collection_group_description: "A collection group consists of all collections with the same path. This allows you to query over multiple collections at once.",
	advanced_settings: "Advanced settings",
	doc_history_global: "Document history revisions enabled if enabled globally",
	doc_history_enabled: "Document history revisions ENABLED",
	doc_history_not_enabled: "Document history revisions NOT enabled",
	doc_history_description: "When enabled, each document in this collection will have a history of changes. This is useful for auditing purposes. The data is stored in a subcollection of the document in your database, called __history.",
	document_id_generation: "Document IDs generation",
	code_defined: "Code defined",
	users_must_define_id: "Users must define an ID",
	users_can_define_id: "Users can define an ID, but it is not required",
	doc_id_auto_generated: "Document ID is generated automatically",
	config_doc_id_generation: "Configure how document IDs are generated when creating new entities.",
	enable_text_search: "Enable text search for this collection",
	text_search_description: "Allow text search for this collection. If you have not specified a text search delegate, this will use the built-in local text search. This is not recommended for large collections, as it may incur in performance and cost issues.",
	database_id: "Database ID",
	default_text: "(default)",
	custom_actions: "Custom actions",
	more_info: "More info",
	define_custom_actions_cli: "Define your own custom actions by uploading them with the CLI.",
	action_defined_in_code: "This action is defined in code with key",
	add_custom_entity_action: "Add custom entity action",
	remove_this_action: "Remove this action?",
	remove_action_warning: "This will not delete any data, only the action in the admin panel",
	subcollections_of: "Subcollections of",
	add_subcollection: "Add subcollection",
	custom_views: "Custom views",
	define_custom_views_cli: "Define your own custom views by uploading them with the CLI.",
	view_defined_in_code: "This view is defined in code with key",
	add_custom_entity_view: "Add custom entity view",
	delete_this_subcollection: "Delete this subcollection?",
	remove_collection_warning: "This will not delete any data, only the collection in the admin panel",
	remove_this_view: "Remove this view?",
	remove_view_warning: "This will not delete any data, only the view in the admin panel",
	no_collection_selected: "No collection selected",
	code_for_collection: "Code for",
	use_config_define_json: "Use this config to define the collection in JSON format.",
	customise_collection_code: "If you want to customise the collection in code, you can add this collection code to your admin app configuration.",
	copied: "Copied",
	property_cant_be_edited: "This property can't be edited",
	property_not_editable_description: "You may not have permission to edit it or it is defined in code and cannot be modified.",
	delete_this_property: "Delete this property?",
	delete_property_warning: "This will not delete any data, only modify the collection.",
	error_must_specify_id: "You must specify an id for the field",
	error_id_format: "The id can only contain letters, numbers and underscores (_), and not start with a number",
	error_id_already_exists: "There is another field with this ID already",
	error_must_specify_title: "You must specify a title for the field",
	custom_or_other: "Custom/Other",
	select_property_widget: "Select a property widget",
	error_changing_data_type: "This widget uses a different data type than the initially selected widget. This can cause errors with existing data.",
	required: "Required",
	enum_form_dialog: "Enum form dialog",
	imported_data_preview: "Imported data preview",
	entities_with_same_id_overwritten: "Entities with the same id will be overwritten",
	collection_editor: "Collection editor",
	properties_in_this_group: "Properties in this group",
	data_property_mapping: "Data property mapping",
	property_edit_view: "Property edit view",
	all_of_these: "All of these",
	any_of_these: "Any of these",
	only_admins_edit_roles: "Only admins can edit roles",
	error_user_not_found: "User not found",
	all_roles: "All Roles",
	role: "Role",
	name_of_this_role: "Name of this role",
	id_of_this_role: "ID of this role",
	create_entities: "Create entities",
	read_entities: "Read entities",
	update_entities: "Update entities",
	delete_entities: "Delete entities",
	all_collections: "All collections",
	create_entities_in_collections: "Create entities in collections",
	access_all_data_in_every_collection: "Access all data in every collection",
	update_data_in_any_collection: "Update data in any collection",
	delete_data_in_any_collection: "Delete data in any collection",
	allow_all_permissions_in_this_collections: "Allow all permissions in this collections",
	all: "All",
	customise_permissions_description: "You can customise the permissions that the users related to this role can perform in the entities of each collection",
	create_collections: "Create collections",
	yes: "Yes",
	no: "No",
	can_user_create_collections: "Can the user create collections",
	edit_collections: "Edit collections",
	only_own_collections: "Only his/her own",
	own: "Own",
	can_user_edit_collections: "Can the user edit collections",
	delete_collections: "Delete collections",
	can_user_delete_collections: "Can the user delete collections",
	error_saving_role: "There was an error saving this role",
	create_role: "Create role",
	update: "Update",
	only_admins_change_roles: "Only admins can change roles",
	must_be_at_least_one_admin: "There must be at least one admin",
	logged_user_not_found: "Logged user not found",
	user: "User",
	user_id: "User ID",
	name_of_this_user: "Name of this user",
	email_of_this_user: "Email of this user",
	roles: "Roles",
	create_user: "Create user",
	users: "Users",
	add_user: "Add user",
	add_role: "Add role",
	is_admin: "Is Admin",
	default_permissions: "Default permissions",
	created_on: "Created on",
	email: "Email",
	id: "ID",
	read: "Read",
	column_in_file: "Column in file",
	map_to_property: "Map to Property",
	default_values: "Default values",
	default_values_description: "You can select a default value for unmapped columns and empty values:",
	property: "Property",
	default_value: "Default value",
	autogenerate_id: "Autogenerate ID",
	id_column_description: "Column that will be used as ID for each document",
	do_not_set_value: "Do not set value",
	set_value_to_true: "Set value to true",
	set_value_to_false: "Set value to false",
	drag_and_drop_file: "Drag and drop a file here or click to upload",
	error_saving_data: "Error saving data",
	retry: "Retry",
	saving_data: "Saving data",
	entities_saved: "entities saved",
	do_not_close_tab: "Do not close this tab or the import will be interrupted",
	import: "Import",
	import_data: "Import data",
	upload_file_description: "Upload a CSV, Excel or JSON file and map it to your existing schema",
	back: "Back",
	next: "Next",
	save_data: "Save data",
	use_column_as_id: "Use this column as ID",
	do_not_import_property: "Do not import this property",
	entities_will_be_overwritten: "Entities with the same id will be overwritten",
	data_imported_successfully: "Data imported successfully",
	export: "Export",
	export_data: "Export data",
	download_table_csv: "Download the content of this table as a CSV",
	csv: "CSV",
	json: "JSON",
	dates_as_timestamps: "Dates as timestamps",
	dates_as_strings: "Dates as strings",
	flatten_arrays: "Flatten arrays",
	download: "Download",
	large_number_of_documents: "This collection has a large number of documents ({{count}}).",
	too_many_documents_to_export: "This collection has more documents ({{count}}) than the browser can export in a single file (limit {{limit}}). Filter it down before exporting.",
	include_undefined_values: "Include undefined values",
	submit: "Submit",
	no_filterable_properties: "No filterable properties available",
	apply_filters: "Apply filters",
	filter_presets: "Presets",
	filter_preset_apply: "Apply preset",
	filter_preset_active: "Active: {{label}}",
	list: "List",
	table_view_mode: "Table",
	cards: "Cards",
	board: "Board",
	initialize_kanban_order_desc: "This will assign sequential order values to all items that don't have one. Items will maintain their current order within each column.",
	kanban_view_not_available: "Kanban view is not available",
	kanban_view_requires_enum: "Kanban view requires a string property with enum values to group entities into columns. Please add an enum property to your collection schema to use this view.",
	no_enum_values_configured: "No enum values configured for property \"{{property}}\"",
	items_need_backfill: "Some items don't have order values. Initialize to enable drag-and-drop reordering.",
	kanban_order_not_configured: "Drag-and-drop ordering is not configured. Set an order property in the collection settings to persist card positions.",
	initialize: "Initialize",
	confirm_multiple_delete: "Confirm multiple delete?",
	delete_entity_confirm_title: "Would you like to delete this {{entityName}}?",
	add_existing: "Add existing",
	unlink_entity_confirm_title: "Remove this {{entityName}} from this record?",
	unlink_entity_confirm_body: "It stays in {{collectionName}} and remains available to other records.",
	confirm_multiple_unlink: "remove these from this record?",
	no_filter: "No filter",
	is_true: "is true",
	is_false: "is false",
	error_uploading_file_size: "Error uploading file: File is larger than {{maxSize}} bytes",
	error_uploading_file_type: "Error uploading file: File type is not supported",
	drag_drop_files: "Drag 'n' drop some files here, or click here to edit",
	drag_drop_file: "Drag 'n' drop a file here, or click here to edit",
	multiple_deleted: "Multiple deleted",
	some_entities_deleted: "Some of the entities have been deleted, but not all",
	error_deleting_entities: "Error deleting entities",
	deleted: "Deleted",
	selection_options: "Selection options",
	selection_menu_all_loaded: "All on this page",
	selection_menu_all_matching: "All {{total}} {{collection}}",
	selection_menu_none: "None",
	selection_select_all_loaded: "Select all loaded rows",
	selection_deselect_all: "Deselect all",
	selection_reading_rows: "Reading rows… {{loaded}} of {{total}}",
	selection_reading_rows_unknown: "Reading rows… {{loaded}} so far",
	selection_deleting_progress: "Deleted {{completed}} of {{total}}",
	confirm_delete_selection: "Delete {{total}} {{collection}}?",
	confirm_delete_selection_unknown: "Delete every row matching the current filter?",
	confirm_delete_selection_body: "This cannot be undone. Each row is deleted individually, so this may take a while.",
	export_selection_count: "Download the {{total}} selected rows as a {{format}}",
	export_selection_all_matching: "Download every {{collection}} matching the current filter",
	select_reference: "Select reference",
	select_references: "Select references",
	account_settings: "Account Settings",
	profile: "Profile",
	security: "Security",
	sessions: "Sessions",
	change_password: "Change Password",
	current_password: "Current Password",
	new_password: "New Password",
	confirm_password: "Confirm New Password",
	password_changed: "Password changed successfully. You will be logged out.",
	passwords_dont_match: "Passwords don't match",
	password_too_short: "Password must be at least 8 characters",
	password_change_not_available: "Password change is not available for accounts using external sign-in providers.",
	changing_password: "Changing...",
	display_name: "Display Name",
	photo_url: "Photo URL",
	save_profile: "Save Profile",
	saving: "Saving...",
	no_active_sessions: "No active sessions found.",
	revoking: "Revoking...",
	revoke_all_sessions: "Revoke All Sessions",
	unknown_device: "Unknown Device",
	current: "Current",
	role_id: "Role ID",
	role_name: "Role Name",
	add_reference: "Add reference",
	add_reference_to: "Add reference to {{name}}",
	/** RolesView — table & permissions */
	role_deleted_successfully: "Role deleted successfully",
	error_deleting_role: "Error deleting role",
	collection_permissions: "Collection permissions",
	collection: "Collection",
	no_collections_configured: "No collections configured",
	no_security_rules_defined: "No security rules defined — all operations unrestricted",
	no_rules: "no rules",
	/** UsersView — snackbar */
	bootstrap_admin_success: "You are now an admin! Refreshing...",
	failed_to_bootstrap_admin: "Failed to bootstrap admin",
	user_deleted_successfully: "User deleted successfully",
	error_deleting_user: "Error deleting user",
	user_created_successfully: "User created successfully",
	invitation_sent: "An invitation email has been sent to {{email}}. They can use the link to set their password.",
	invitation_sent_title: "Invitation Sent",
	temporary_password: "Temporary Password",
	temporary_password_description: "Email is not configured. Share this temporary password with the user securely. It will not be shown again.",
	temporary_password_email_failed_description: "The email could not be delivered. Share this temporary password with the user securely. It will not be shown again.",
	copy_password: "Copy password",
	password_copied: "Password copied to clipboard",
	/** UsersView — pagination & search */
	search_users: "Search users",
	created: "Created",
	actions: "Actions",
	no_users_found: "No users found",
	reset_password: "Reset Password",
	reset_password_success: "Password reset successfully",
	reset_password_confirmation: "Are you sure you want to reset this user's password?",
	reset_password_send_email: "Send a password reset email",
	reset_password_set_manually: "Set a password manually",
	reset_password_set_manually_description: "The password is updated immediately and no email is sent. Share it with the user securely.",
	/** Permission-denied empty states */
	no_permission_to_view_users: "You don't have permission to view users",
	no_permission_to_view_roles: "You don't have permission to view roles",
	no_permission_description: "Contact an administrator if you need access to this section.",
	error_resetting_password: "Error resetting password",
	/** Editor table-bubble */
	add_row_before: "Add row before",
	add_row_after: "Add row after",
	delete_row: "Delete row",
	add_column_before: "Add column before",
	add_column_after: "Add column after",
	delete_column: "Delete column",
	delete_table: "Delete table",
	/** Editor image-bubble */
	alt_text: "Alt text",
	title: "Title",
	/** MapFieldBinding */
	a_property_of_this_map_has_error: "A property of this map has an error",
	/** AI Collection Generator Popover */
	generate_collection_with_ai: "Generate Collection with AI",
	modify_collection_with_ai: "Modify Collection with AI",
	describe_collection_to_create: "Describe the collection you want to create.",
	describe_changes_to_make: "Describe the changes you want to make to this collection.",
	ai_placeholder_create: "e.g., Create a products collection with name, price, description, and category...",
	ai_placeholder_modify: "e.g., Add a thumbnail image field with storage, make price required...",
	ai_assist: "AI Assist",
	generating: "Generating...",
	/** Recently extracted strings for collection editor */
	this_is_subcollection_of: "This is a subcollection of",
	use_existing_paths_database: "Use one of the existing paths in your database:",
	describe_collection_ai: "Describe your collection to AI:",
	generate_with_ai: "Generate with AI",
	create_from_json_config: "Create from JSON configuration:",
	paste_json_config: "Paste JSON Configuration",
	create_collection_from_file_formats: "Create a collection from a file (csv, json, xls, xslx...)",
	select_template: "Select a template:",
	products: "Products",
	collection_products_subtitle: "A collection of products with images, prices and stock",
	collection_users_subtitle: "A collection of users with emails, names and roles",
	blog_posts: "Blog posts",
	collection_blog_posts_subtitle: "A collection of blog posts with images, authors and complex content",
	pages: "Pages",
	collection_pages_subtitle: "A collection of pages with images, authors and complex content",
	continue_from_scratch: "Continue from scratch",
	/** Admin views config */
	admin_users: "Admin Users",
	roles_menu: "Roles",
	project_settings: "Project settings",
	by_signing_in_you_agree_to_our: "By signing in you agree to our",
	terms_and_conditions: "Terms and Conditions",
	and_our: "and our",
	privacy_policy: "Privacy policy",
	email_password: "Email/password",
	sign_in_with_google: "Sign in with Google",
	auth_user_not_found: "User not found",
	auth_wrong_password: "Wrong password. Please try again.",
	auth_user_disabled: "User disabled. Please contact support.",
	auth_account_exists_with_different_credential: "An account with this email already exists. Sign in the way you did before.",
	auth_account_exists_sign_in_with_password: "An account with this email already exists. Sign in with your email and password instead.",
	auth_email_already_in_use: "The email is already in use",
	auth_google_permissions_required: "You need to grant additional permissions in order to manage your Google Cloud projects",
	auth_invalid_email_password: "Please enter both email and password",
	auth_enter_email_first: "Please enter your email first",
	auth_password_reset_sent: "Password reset email sent",
	auth_sign_in_account: "Sign in to your account",
	auth_create_new_account: "Create a new account",
	auth_password: "Password",
	auth_reset_password: "Reset password",
	auth_new_user: "New user?",
	auth_have_account: "Have an account?",
	auth_sign_in: "Sign In",
	auth_sign_up: "Sign Up",
	auto_setup_collections_button: "Automatically set up collections",
	auto_setup_collections_title: "Automatically set up collections?",
	auto_setup_collections_desc: "This will automatically create collection configs for collections that are <b>NOT</b> already mapped",
	this_can_take_a_minute: "This can take a minute or two",
	no_collections_found_to_setup: "No collections found to setup.",
	collections_have_been_setup: "Collections have been automatically setup.",
	error_setting_up_collections: "Error automatically setting up collections",
	admin_panel_ready_bring_data: "Your admin panel is ready. Let's bring in your data.",
	admin_panel_ready_get_started: "Your admin panel is ready. Here's how to get started.",
	auto_detect_collections: "Auto-detect collections",
	auto_detect_collections_desc: "Let AI scan your database and automatically generate collection schemas.",
	create_a_collection: "Create a collection",
	create_collection_desc: "Manually define your first collection from scratch using the visual editor.",
	read_the_docs: "Read the docs",
	read_the_docs_desc: "Learn how to customize fields, views, actions and more.",
	explore_docs: "Explore docs",
	want_to_customize_with_code: "Want to customize with code? Run",
	to_scaffold_a_local_project: "to scaffold a local project.",
	validation: "Validation",
	unique: "Unique",
	required_message: "Required message",
	required_tooltip: "You won't be able to save this entity if this value is not set",
	unique_tooltip: "There cannot be multiple entities with the same value",
	lowercase: "Lowercase",
	uppercase: "Uppercase",
	trim: "Trim",
	exact_length: "Exact length",
	min_length: "Min length",
	max_length: "Max length",
	matches_regex: "Matches regex",
	not_valid_regexp: "Not a valid regexp",
	regex_helper: "e.g. /^\\d+$/ for digits only",
	min_value: "Min value",
	max_value: "Max value",
	less_than: "Less than",
	more_than: "More than",
	positive_value: "Positive value",
	negative_value: "Negative value",
	integer_value: "Integer value",
	invalid_regular_expression: "Invalid regular expression",
	must_specify_target_collection: "You must specify a target collection for the field",
	need_specify_repeat_field: "You need to specify a repeat field",
	need_specify_block_properties: "You need to specify the properties of this block",
	incomplete_condition: "Incomplete condition - please select a field",
	field_name: "Field name",
	kanban_column_property: "Kanban Column Property",
	select_a_property: "Select a property",
	kanban_property_not_found: "Property \"{{property}}\" does not exist or is not an enum string property. Please select a valid property or clear the selection.",
	no_enum_string_properties: "No enum string properties found. Add a string property with enumValues to use Kanban view.",
	kanban_column_description: "Select a string property with enum values to group entities into columns",
	create_property: "+ Create \"{{property}}\" property",
	order_property: "Order Property",
	order_property_not_found: "Property \"{{property}}\" does not exist or is not a text property. Please select a valid property or clear the selection.",
	no_number_properties: "No text properties found. Add a text property to enable ordering.",
	order_property_description: "Select a text property to persist the order of items",
	display_settings: "Display settings",
	default_row_size: "Default row size",
	side_dialog_width: "Side dialog width",
	side_dialog_width_description: "Optionally define the width (in pixels) of entities side dialog. Default is 768px",
	inline_editing_enabled: "Data can be edited directly in the table view",
	inline_editing_disabled: "Data can be edited only in the form view",
	inline_editing_description: "Allow editing data directly in the table view, without opening the form view.",
	include_json_view: "Include JSON view",
	no_json_view: "Do not include JSON view",
	json_view_description: "Include the JSON representation of the document.",
	not_found_suffix: "not found",
	editor_text: "Text",
	editor_text_description: "Just start typing with plain text.",
	editor_heading_1: "Heading 1",
	editor_heading_1_description: "Big section heading.",
	editor_heading_2: "Heading 2",
	editor_heading_2_description: "Medium section heading.",
	editor_heading_3: "Heading 3",
	editor_heading_3_description: "Small section heading.",
	editor_todo_list: "To-do List",
	editor_todo_list_description: "Track tasks with a to-do list.",
	editor_bullet_list: "Bullet List",
	editor_bullet_list_description: "Create a simple bulleted list.",
	editor_numbered_list: "Numbered List",
	editor_numbered_list_description: "Create a list with numbering.",
	editor_quote: "Quote",
	editor_quote_description: "Capture a quote.",
	editor_code: "Code",
	editor_code_description: "Capture a code snippet.",
	editor_image: "Image",
	editor_image_description: "Upload an image from your computer.",
	editor_multiple: "Multiple",
	editor_link: "Link",
	editor_save: "Save",
	editor_cancel: "Cancel",
	editor_remove_link: "Remove link",
	editor_paste_or_type_link: "Paste or type link",
	editor_open_in_new_window: "Open in new window",
	editor_bold: "Bold",
	editor_italic: "Italic",
	editor_underline: "Underline",
	editor_strikethrough: "Strikethrough",
	editor_autocomplete: "Autocomplete",
	editor_autocomplete_description: "Add text based on the context.",
	text_search_dialog_title: "Enable text search",
	text_search_local_not_recommended: "Local text search is not recommended for large collections.",
	text_search_local_fetch_warning: "Note that enabling local text search will need to fetch all documents from your collection and store them in the browser. This can be inefficient for large collections. It can also incur in additional costs.",
	text_search_external_suggestion: "For larger collections, you are encouraged to use an external search engine such as Algolia or Elastic Search, and assign a search delegate to your config.",
	text_search_local_description: "Local text search is the simplest way to enable text search in your collection. It loads all documents in the collection in the browser and performs the search locally. This is the recommended option for small collections.",
	text_search_own_implementation: "You have implemented your own text search controller. You enable text search for your collection.",
	text_search_enable_for_collection: "Enable for this collection",
	text_search_enable_for_project: "Enable for project",
	text_search_enabled_snackbar: "Local text search enabled",
	settings_heading: "Settings",
	settings_project_name: "Project name",
	settings_default_language: "Default language",
	settings_default_language_caption: "Select the base language for this project. Users can override this preference in their personal settings.",
	settings_enable_local_text_search: "Enable local text search",
	settings_local_text_search_caption: "Enable local text search for all collections. This will allow you to search text fields in your collections using the Rebase search bar. Note that this feature can incur in higher read counts, as it will index all text fields in your collections.",
	settings_doc_history_all_collections: "Document history enabled for all collections",
	settings_doc_history_caption: "When true, all collections will have the history enabled by default. You can override this setting in each collection. History will be saved in the __history subcollection of each document.",
	settings_theme: "Theme",
	settings_primary_color: "Primary color",
	settings_secondary_color: "Secondary color",
	settings_sample_theme_components: "Sample theme components",
	settings_drag_drop_logo: "Drag and drop your logo here",
	settings_security_rules: "Security rules",
	settings_security_rules_description: "Rebase uses granular security rules to restrict access to data. When creating a new user through Rebase, custom claims are added to the user. By adding the following security rules to your project, you ensure Rebase users can access data securely.",
	settings_security_rules_add_domain: "Remember to add the domain {{domain}} to your provider allowed domains",
	settings_security_rules_caption: "These rules restrict access to data to Rebase users only, but do not enforce the permissions at the database level. The permissions are enforced in the frontend though, which will work fine for most projects. If you need to enforce the permissions at the database level, you can modify these security rules yourself to suit your needs. The roles assigned to a user are set as custom claims in the auth token, so you can use them in your security rules.",
	studio_sql_executing_query: "EXECUTING QUERY...",
	studio_sql_query_error: "Query Error",
	studio_sql_run_query_placeholder: "Run a query to see results",
	studio_sql_visual_execution_plan: "Visual Execution Plan",
	studio_sql_success: "SUCCESS",
	studio_sql_no_results: "Query executed successfully but returned no results.",
	studio_sql_rows: "Rows:",
	studio_sql_time: "Time:",
	studio_sql_copy_markdown: "Copy Markdown",
	studio_sql_export_json: "Export JSON",
	studio_sql_export_csv: "Export CSV",
	studio_sql_format_sql: "Format SQL",
	studio_sql_explain: "Explain",
	studio_sql_limit_1000: "Limit 1000",
	studio_sql_remove_from_favorites: "Remove from Favorites",
	studio_sql_add_to_favorites: "Add to Favorites",
	studio_sql_save: "Save",
	studio_sql_run: "Run",
	studio_sql_database: "Database",
	studio_sql_role: "Role",
	studio_sql_admin: "(Admin)",
	studio_sql_select_db: "Select DB",
	studio_sql_query_results: "Query Results",
	studio_sql_save_snippet: "Save SQL Snippet",
	studio_sql_snippet_name: "Snippet Name",
	studio_sql_snippet_name_placeholder: "e.g., Get All Users",
	studio_sql_snippet_saved_local: "This will be saved to your local storage.",
	studio_sql_cancel: "Cancel",
	studio_sql_dangerous_operation: "Dangerous Operation Detected",
	studio_sql_dangerous_operation_body: "The query you are about to run contains potentially destructive operations (DELETE, DROP, TRUNCATE, or UPDATE without WHERE). Are you sure you want to proceed?",
	studio_sql_snippet_saved: "Snippet \"{{name}}\" saved.",
	studio_sql_markdown_copied: "Results copied as Markdown!",
	studio_sql_markdown_copy_failed: "Failed to copy Markdown to clipboard.",
	studio_sql_row_updated: "Row updated successfully.",
	studio_sql_cannot_edit_missing_query: "Cannot edit rows: The underlying query is missing.",
	studio_sql_cannot_resolve_table: "Could not resolve table/primary key",
	studio_sql_missing_pk: "Row is missing primary key column(s): {{columns}}. Cannot safely update.",
	studio_sql_update_failed: "Failed to update: {{message}}",
	studio_sql_execution_not_supported: "SQL execution is not supported by the current data source.",
	studio_sql_error_executing: "An error occurred while executing the query.",
	studio_sql_error_explaining: "An error occurred while explaining the query.",
	studio_sql_save_first_to_favorite: "Please save the snippet first before favoriting.",
	studio_sql_collections_label: "Collections:",
	studio_sql_admin_collections_tooltip: "Tables in this query that are mapped to collections",
	studio_sql_edit_entity: "Open {{name}} #{{id}}",
	studio_sql_sql_not_supported: "SQL execution not supported by this data source",
	studio_sql_fetch_error: "Failed to fetch connection options: {{message}}",
	studio_sql_unexpected_format: "Unexpected data format: {{type}}",
	studio_sql_no_tables: "No tables found in this database.",
	studio_sql_schema_fetch_error: "Failed to fetch schema: {{message}}",
	studio_sql_sidebar_snippets: "Snippets",
	studio_sql_sidebar_history: "History",
	studio_sql_sidebar_schema: "Schema",
	studio_sql_sidebar_no_snippets: "No saved snippets",
	studio_sql_sidebar_save_snippet_hint: "Save a query to access it here.",
	studio_sql_sidebar_no_history: "No history",
	studio_sql_sidebar_history_hint: "Executed queries will appear here.",
	studio_sql_sidebar_delete_snippet: "Delete snippet",
	studio_sql_close_tab: "Close this query tab",
	studio_sql_new_tab: "New query tab",
	studio_schema_tables: "Tables",
	studio_schema_loading: "Loading schema...",
	studio_schema_no_tables: "No tables found",
	studio_schema_retry: "Retry",
	studio_schema_primary_key: "Primary Key",
	studio_schema_select_all: "SELECT * FROM",
	studio_schema_insert_into: "INSERT INTO",
	studio_schema_update: "UPDATE",
	studio_schema_delete_from: "DELETE FROM",
	studio_schema_columns: "{{count}} columns",
	studio_rls_title: "Row Level Security",
	studio_rls_description: "Manage RLS policies for your tables",
	studio_rls_enabled: "RLS Enabled",
	studio_rls_disabled: "RLS Disabled",
	studio_rls_no_rls: "No RLS",
	studio_rls_enable_rls: "Enable RLS",
	studio_rls_disable_rls: "Disable RLS",
	studio_rls_create_policy: "Create Policy",
	studio_rls_policies: "Policies",
	studio_rls_no_policies: "No policies defined",
	studio_rls_no_policies_desc: "This table has no RLS policies. All rows are accessible based on the RLS status.",
	studio_rls_add_first_policy: "Add your first policy",
	studio_rls_force_rls: "Force RLS",
	studio_rls_force_rls_desc: "When enabled, RLS policies apply to the table owner as well.",
	studio_rls_enable_force_rls: "Enable Force RLS",
	studio_rls_disable_force_rls: "Disable Force RLS",
	studio_rls_edit: "Edit",
	studio_rls_delete: "Delete",
	studio_rls_confirm_delete_title: "Delete Policy",
	studio_rls_confirm_delete_body: "Are you sure you want to delete this policy? This action cannot be undone.",
	studio_rls_confirm_enable_title: "Enable Row Level Security",
	studio_rls_confirm_enable_body: "Enabling RLS will restrict access to rows in this table based on your policies. Make sure you have appropriate policies in place.",
	studio_rls_confirm_disable_title: "Disable Row Level Security",
	studio_rls_confirm_disable_body: "Disabling RLS will make all rows accessible to all roles. Are you sure you want to continue?",
	studio_rls_select_table: "Select a table",
	studio_rls_no_tables: "No tables available",
	studio_rls_error: "Error loading policies",
	studio_rls_retry: "Retry",
	studio_rls_loading: "Loading policies...",
	studio_policy_edit: "Edit Policy",
	studio_policy_create: "Create New Policy",
	studio_policy_defining_rules: "Defining access rules for",
	studio_policy_cancel: "Cancel",
	studio_policy_save: "Save Policy",
	studio_policy_template: "Start from a template (Optional)",
	studio_policy_select_template: "Select a template...",
	studio_policy_name: "Policy Name",
	studio_policy_name_placeholder: "Give your policy a descriptive name",
	studio_policy_behavior: "Policy Behavior",
	studio_policy_permissive: "Permissive",
	studio_policy_permissive_desc: "Combined using \"OR\"",
	studio_policy_restrictive: "Restrictive",
	studio_policy_restrictive_desc: "Combined using \"AND\"",
	studio_policy_command: "Policy Command",
	studio_policy_target_roles: "Target Roles",
	studio_policy_roles_placeholder: "Select roles (defaults to public)",
	studio_policy_using_expr: "USING expression",
	studio_policy_using_expr_desc: "Applies to existing rows (SELECT, UPDATE, DELETE)",
	studio_policy_check_expr: "WITH CHECK expression",
	studio_policy_check_expr_desc: "Applies to new or modified rows (INSERT, UPDATE)",
	studio_policy_help_title: "Row Level Security (RLS) Guide",
	studio_policy_help_intro: "Row Level Security (RLS) allows you to restrict which rows a user can access or modify based on their authentication status or roles.",
	studio_policy_help_step1_title: "1. Select a Template",
	studio_policy_help_step1_desc: "If you're new to RLS, the easiest way to start is by selecting a predefined template from the dropdown. It will automatically configure the policy for common use cases like \"Users can update their own rows\".",
	studio_policy_help_step2_title: "2. Target Roles",
	studio_policy_help_step2_desc: "Choose which users the policy applies to:",
	studio_policy_help_role_public: "Applies to everyone (logged in or not).",
	studio_policy_help_role_authenticated: "Only applies to users who are logged in.",
	studio_policy_help_role_anon: "Only applies to users who are NOT logged in.",
	studio_policy_help_step3_title: "3. USING Expression",
	studio_policy_help_step3_desc: "A SQL condition that must evaluate to true for existing rows to be visible or updatable (SELECT, UPDATE, DELETE).",
	studio_policy_help_step3_example: "Ensures users can only see rows where they are the owner.",
	studio_policy_help_step4_title: "4. WITH CHECK Expression",
	studio_policy_help_step4_desc: "A SQL condition that must evaluate to true for new or modified rows to be saved (INSERT, UPDATE).",
	studio_policy_help_step4_example: "Usually the same as the USING expression when users are only allowed to insert/edit their own data.",
	studio_policy_help_auth_vars_title: "Rebase Auth Variables",
	studio_policy_help_auth_vars_desc: "Rebase provides native helper functions that you can use in your expressions:",
	studio_policy_help_auth_uid: "Returns the current user's ID as text. Example:",
	studio_policy_help_auth_jwt: "Returns the full JWT payload as JSONB so you can check custom claims. Example:",
	studio_policy_help_auth_roles: "Returns the user's role IDs as a comma-separated string. Best used with:",
	studio_policy_help_docs_cta: "To learn more about Postgres policies, check out the official documentation.",
	studio_policy_help_read_docs: "Read Docs",
	studio_policy_help_got_it: "Got it!",
	studio_add_kanban_column: "Add column",
	studio_add_kanban_column_title: "Add Kanban Column",
	studio_add_kanban_column_name: "Column name",
	studio_add_kanban_column_name_placeholder: "New column name",
	studio_add_kanban_column_add: "Add",
	studio_add_kanban_column_cancel: "Cancel",
	studio_collection_view_sql: "SQL Editor",
	studio_collection_view_admin: "Admin View",
	studio_editor_collection_tooltip: "Edit collection schema",
	studio_editor_collection_no_permission: "You don't have permission to edit this collection",
	studio_editor_collection_start_tooltip: "Copy path or edit schema",
	studio_editor_collection_start_no_permission: "You don't have permission to modify this collection",
	studio_editor_collection_start_copied: "Copied!",
	studio_editor_entity_tooltip: "Edit entity schema",
	studio_editor_entity_no_permission: "You don't have permission to edit this entity",
	studio_home_edit_collection: "Edit collection",
	studio_home_delete_collection: "Delete collection",
	studio_home_confirm_delete_title: "Delete collection?",
	studio_home_confirm_delete_body: "Are you sure you want to delete the collection \"{{name}}\"? This action cannot be undone.",
	studio_kanban_setup: "Kanban Setup",
	studio_missing_reference: "Missing reference: {{path}}",
	studio_missing_reference_create: "Create",
	studio_new_collection: "New collection",
	studio_new_collection_card: "New collection",
	studio_new_collection_no_permission: "You don't have permissions to create collections",
	studio_property_add_column: "Add new property",
	studio_property_add_column_no_permission: "You don't have permission to add new properties",
	studio_add_kanban_column_desc: "Add a new option to the \"{{property}}\" property. This will create a new column in the Kanban board.",
	studio_add_kanban_column_adding: "Adding...",
	studio_collection_view_edit: "Edit",
	studio_editor_collection_disabled: "Collection editing is disabled",
	studio_editor_collection_edit: "Edit collection",
	studio_editor_entity_save_first: "You need to save the document before changing the schema",
	studio_editor_entity_edit_schema: "Edit schema for this form",
	studio_editor_collection_start_save_filter: "Save default filter and sort",
	studio_editor_collection_start_clear_filter: "Clear default filter and sort",
	studio_editor_collection_start_reset_filter: "Reset to default filter and sort",
	studio_editor_collection_start_saved: "Default config saved",
	studio_home_duplicate_collection: "Duplicate",
	studio_home_delete: "Delete",
	studio_home_confirm_delete_no_data: "This will not delete any data, only the collection in the admin panel",
	studio_home_collection_deleted: "Collection deleted",
	studio_kanban_configure: "Configure Kanban",
	studio_missing_reference_error: "No collection for path: {{path}}",
	studio_new_collection_add: "Add new collection",
	db_column_type: "Database Column Type",
	primary_key_unique_id: "Primary Key / Unique ID",
	spread_children_as_columns: "Spread children as columns",
	mode: "Mode",
	timezone: "Timezone",
	target_collection: "Target collection",
	storage_file_name: "File name",
	storage_path: "Storage path",
	storage_max_size: "Max size (in bytes)",
	storage_resize_mode: "Resize mode",
	storage_output_format: "Output format",
	storage_max_width: "Max width (px)",
	storage_max_height: "Max height (px)",
	storage_quality: "Quality (0-100)",
	storage_file_upload_config: "File upload config",
	storage_image_resize_config: "Image Resize Configuration",
	storage_all_file_types: "All file types allowed",
	storage_allowed_file_types: "Allowed file types",
	storage_include_bucket_url: "Include bucket URL (s3://...) in saved value",
	storage_save_url: "Save URL instead of storage path",
	datetime_automatic_value: "Automatic value",
	markdown_paste_behavior: "Paste behavior",
	entity_saved: "Saved",
	more_actions: "More actions",
	record_history: "History",
	inspect_record: "Inspect record",
	inspect: "Inspect",
	close_inspector: "Close inspector",
	open_in_live_site: "Open in the live site",
	history_revert: "Revert to this version",
	history_revert_title: "Revert data to this version?",
	history_reverted: "Reverted to selected version",
	history_revert_error: "Error reverting entity",
	history_revert_dirty: "Please save or discard your changes before reverting",
	history_empty: "No history available",
	history_empty_description: "When you save a record, a new version is created and stored in the history.",
	history_new_entity: "History is only available for existing records",
	history_no_more: "No more history",
	studio_group_views: "Views",
	studio_group_admin: "Admin",
	studio_group_settings: "Settings",
	studio_group_database: "Database",
	studio_group_compute: "Compute",
	studio_group_storage: "Storage",
	studio_group_api: "API",
	studio_group_access_control: "Access Control",
	studio_tool_sql: "SQL Console",
	studio_tool_sql_description: "Execute SQL queries",
	studio_tool_js: "JS Console",
	studio_tool_js_description: "Execute JavaScript",
	studio_tool_rls: "RLS Policies",
	studio_tool_rls_description: "Row Level Security",
	studio_tool_storage: "Storage",
	studio_tool_storage_description: "Manage storage files",
	studio_tool_cron: "Cron Jobs",
	studio_tool_cron_description: "Manage scheduled tasks",
	studio_tool_schema_visualizer: "Schema Visualizer",
	studio_tool_schema_visualizer_description: "Interactive database ERD",
	studio_tool_branches: "Branches",
	studio_tool_branches_description: "Create and manage database branches",
	studio_tool_backups: "Backups",
	studio_tool_backups_description: "Download database backups",
	studio_tool_api: "API Explorer",
	studio_tool_api_description: "Interactive API documentation and testing",
	studio_tool_logs: "Logs Explorer",
	studio_tool_logs_description: "Real-time system and query logs",
	studio_tool_api_keys: "API Keys",
	studio_tool_api_keys_description: "Create and manage scoped API keys",
	studio_backups_denied_title: "You cannot list this project's backups",
	studio_backups_denied_hint: "Nothing is wrong with the project. Listing backups needs an admin connection, and the signed-in account was refused.",
	studio_backups_read_failed: "Could not read this project's backups",
	studio_backups_downloading: "Downloading…",
	studio_backups_roles_file: "Roles file",
	studio_backups_roles_file_hint: "The .globals.sql saved with this backup. It recreates the database roles that the dump's grants and row-level security refer to, and `rebase db restore` reads it from next to the .dump, so keep the two files together.",
	studio_backups_no_roles_file: "No roles file",
	studio_backups_no_roles_file_hint: "No .globals.sql was saved with this backup. Restoring it into a new Postgres stops at the first grant to a database role that does not exist there.",
	studio_cron_denied_title: "You cannot list this project's cron jobs",
	studio_cron_denied_hint: "Nothing is wrong with the project. The cron API is admin-only, and the signed-in account was refused.",
	studio_cron_read_failed: "Could not read this project's cron jobs",
	studio_api_keys_denied_title: "You cannot list this project's API keys",
	studio_api_keys_denied_hint: "Nothing is wrong with the project. Managing API keys is an admin operation, and the signed-in account was refused.",
	studio_api_keys_read_failed: "Could not read this project's API keys",
	studio_api_keys_empty_title: "No API keys yet",
	studio_api_keys_empty_hint: "Create a key to enable machine-to-machine authentication",
	studio_api_keys_revoke: "Revoke",
	studio_api_keys_select_hint: "Select an API key to view details",
	studio_api_keys_permissions: "Permissions",
	studio_api_keys_no_permissions: "No permissions configured",
	studio_api_keys_revoke_confirmation: "Revoke confirmation",
	studio_api_keys_name_label: "Name:",
	studio_api_keys_access_label: "Access:",
	studio_api_keys_admin_granted: "Admin role granted",
	studio_api_keys_admin_granted_hint: "the admin routes and the default_admin policies",
	studio_api_keys_done: "Done",
	studio_api_keys_status_active: "Active",
	studio_api_keys_status_expired: "Expired",
	studio_api_keys_status_revoked: "Revoked",
	studio_api_keys_stat_status: "Status",
	studio_api_keys_stat_last_used: "Last used",
	studio_api_keys_stat_expires: "Expires",
	studio_api_keys_never: "Never",
	studio_api_keys_role_service: "Service",
	studio_api_keys_stat_rate_limit: "Rate limit",
	studio_api_keys_rate_limit_default: "Default (1000/15min)",
	studio_api_keys_stat_created_by: "Created by",
	studio_api_keys_copy_failed: "Failed to copy",
	studio_branches_denied_title: "You cannot list this project's branches",
	studio_branches_denied_hint: "Nothing is wrong with the project. Branching runs on the admin connection, and the signed-in account was refused.",
	studio_branches_read_failed: "Could not read this project's branches",
	studio_schema_no_collections: "No collections declared",
	studio_schema_no_collections_body: "There is nothing to draw yet. Add a collection under config/collections/, or open Edit collections to add one here.",
	studio_schema_introspect_hint: "Against a database that already has tables, rebase schema introspect writes the collection files for you.",
	studio_rls_enable_confirm_title: "Enable Row Level Security?",
	studio_rls_enable_confirm_body: "Until a policy allows it, every row of {{table}} becomes unreadable. Postgres denies by default.",
	studio_rls_disable_confirm_title: "Disable Row Level Security?",
	studio_rls_disable_confirm_body: "Every policy on {{table}} stops being enforced. Anyone who can reach the table can read and write every row.",
	studio_rls_drop_policy_title: "Drop this policy?",
	studio_rls_drop_policy_body: "{{policy}} is removed from {{table}} in the database. Nothing is written to your code, so a deploy can restore it.",
	studio_rls_unapplied: "Unapplied",
	studio_rls_unapplied_tooltip: "Declared in your code, not yet in the database. Save it here to plan and apply the change, or run `rebase db push`.",
	studio_branches_unavailable_title: "Database branching is not available",
	studio_branches_unavailable_body: "Branching needs a Postgres backend with an admin connection. Set ADMIN_CONNECTION_STRING in the backend's environment.",
	studio_backups_not_configured_title: "Backups are not configured",
	studio_backups_not_configured_body: "Set BACKUP_DESTINATION to a local path or an s3:// or gs:// URL, then create a backup with `rebase db backup` or a scheduled cron.",
	studio_cron_empty_title: "No cron jobs registered",
	studio_cron_empty_body: "Add a file under backend/crons/ that default-exports a CronJobDefinition.",
	studio_read_the_docs: "Read the documentation"
};
//#endregion
//#region src/locales/es.ts
/**
* Spanish baseline translations for @rebasepro/app.
*
* This is the single source of truth for all default UI strings in Spanish.
* Override any key via the `translations` prop on Rebase entry-point components.
*
* @group Core
*/
var es = {
	save: "Guardar",
	create: "Crear",
	create_copy: "Crear copia",
	save_and_close: "Guardar y cerrar",
	create_copy_and_close: "Crear copia y cerrar",
	create_and_close: "Crear y cerrar",
	discard: "Descartar",
	clear: "Limpiar",
	cancel: "Cancelar",
	edit: "Editar",
	copy: "Copiar",
	delete: "Eliminar",
	delete_not_allowed: "Has seleccionado al menos una entidad que no puedes eliminar",
	edit_entity: "Editar",
	back_to_detail: "Volver a los detalles",
	delete_confirmation_title: "¿Eliminar?",
	delete_confirmation_body: "Esto eliminará la entidad. ¿Estás seguro?",
	delete_multiple_confirmation_body: "Esto eliminará las entidades seleccionadas. ¿Estás seguro?",
	unsaved_changes_title: "Cambios sin guardar",
	unsaved_changes_body: "Tienes cambios sin guardar. ¿Quieres descartarlos?",
	discard_changes: "Descartar cambios",
	keep_editing: "Seguir editando",
	clear_form: "¿Vaciar formulario?",
	discard_changes_confirmation: "Todos los cambios no guardados se perderán. Esta acción no se puede deshacer.",
	clear_form_confirmation: "Todos los valores introducidos se borrarán. Esta acción no se puede deshacer.",
	search: "Buscar",
	find_by_id: "Buscar por ID",
	find_entity_by_id: "Buscar entidad por ID",
	filter: "Filtrar",
	filters: "Filtros",
	records_in_view: "Registros en esta vista",
	clear_filter: "Borrar filtro",
	clear_filter_sort: "Borrar filtro/orden",
	clear_sort: "Borrar orden",
	sort: "Ordenar",
	sort_by: "Ordenar por",
	sort_then_by: "Luego por",
	sort_ascending: "Ordenar ascendente",
	sort_descending: "Ordenar descendente",
	sort_remove: "Quitar orden",
	sort_move_up: "Dar más importancia a esta clave",
	sort_move_down: "Dar menos importancia a esta clave",
	sort_remove_key: "Quitar esta clave de orden",
	sort_key_position: "Clave de orden {{position}}",
	sort_shift_click_hint: "Mayús+clic para añadir una columna bajo el orden actual",
	save_entity_before_subcollections: "Debes guardar la entidad antes de añadir colecciones adicionales",
	clear_all: "Borrar todo",
	no_items: "Sin elementos",
	no_entries_found: "No se encontraron entradas",
	all_entries_loaded: "Todas las {{count}} entradas cargadas",
	create_your_first_entry: "Crea tu primera entrada",
	no_results_filter_sort: "No hay resultados con el filtro/orden aplicado",
	no_results_search: "No se encontraron resultados para \"{{search}}\"",
	add: "Añadir",
	remove: "Quitar",
	multiple_entities: "Múltiples entidades",
	unsaved_changes: "Tienes cambios sin guardar en esta colección {{collectionName}}.",
	so_empty: "Tan vacío...",
	no_results: "No hay resultados con el filtro/orden aplicado",
	refresh_data: "Actualizar datos",
	dark_mode: "Oscuro",
	light_mode: "Claro",
	system_mode: "Sistema",
	join_newsletter: "Únete a la newsletter, sin spam",
	ok: "Aceptar",
	save_collection_config: "Guardar la estructura de la colección",
	search_for_more_icons: "Buscar más iconos…",
	ai_modified: "Modificado por IA",
	size_label: "Tamaño",
	group_by: "Agrupar por",
	initialize_kanban_order: "Inicializar el orden para Kanban",
	copy_id: "Copiar ID",
	add_specific: "Añadir {{name}}",
	add_named: "Añadir \"{{name}}\"",
	select_specific: "Seleccionar {{name}}",
	select_from: "Seleccionar de {{name}}",
	done: "Hecho",
	log_out: "Cerrar sesión",
	column_cannot_be_edited: "Esta columna no se puede editar directamente",
	close: "Cerrar",
	hide_list: "Ocultar lista",
	show_list: "Mostrar lista",
	unsaved_local_changes: "Cambios locales sin guardar",
	unsaved_local_changes_description: "Este documento ha sido editado localmente y tiene cambios sin guardar. Estos cambios locales se perderán si no los aplicas.",
	preview_changes: "Previsualizar cambios",
	apply_changes: "Aplicar cambios",
	discard_local_changes: "Descartar cambios locales",
	preview_local_changes: "Previsualizar cambios locales",
	preview_local_changes_description: "Estos son los cambios locales que se aplicarán al formulario.",
	type: "Tipo",
	string: "cadena",
	number: "número",
	boolean: "booleano",
	date: "fecha",
	map: "mapa",
	array: "lista",
	arrays_of_arrays_not_supported: "Las listas de listas no están soportadas.",
	data_type_not_supported: "Tipo de datos {{dataType}} no soportado aún",
	passkey_error_unsupported: "Tu dispositivo o navegador no soporta passkeys.",
	saved_correctly: "Guardado correctamente",
	error_before_saving: "Error al preparar los datos",
	error_after_saving: "Error en el proceso posterior al guardado",
	error_saving_entity: "Error al guardar la entidad",
	entity_does_not_exist: "Esta entidad no existe en la base de datos",
	form_modified: "Este formulario ha sido modificado",
	form_in_sync: "El formulario actual está sincronizado con la base de datos",
	fix_errors_before_saving: "Corrija los errores resaltados antes de guardar",
	admin: "Admin",
	home: "Inicio",
	this_form_has_errors: "Este formulario tiene errores",
	error_loading_navigation: "Error cargando la navegación",
	error_loading_auth: "Error cargando la autenticación",
	this_form_has_been_modified: "Este formulario ha sido modificado",
	current_form_in_sync: "El formulario actual está sincronizado con la base de datos",
	open_in_console: "Abrir en consola",
	collection_does_not_exist: "La colección especificada no existe. Revisa la consola",
	unexpected_value: "Valor inesperado",
	unexpected_value_click_to_edit: "Valor inesperado. Haz clic para editar",
	unexpected_reference_value: "Valor de referencia inesperado. Haz clic para editar",
	copy_url_to_clipboard: "Copiar URL al portapapeles",
	open_image_in_new_tab: "Abrir imagen en una nueva pestaña",
	reference_not_set: "Referencia no establecida",
	reference_does_not_exist: "La referencia no existe",
	entity_not_found: "Entidad no encontrada",
	entity_not_found_body: "Este registro no existe, o puede estar oculto para ti por la seguridad a nivel de fila.",
	back_to_collection: "Volver a {{name}}",
	file_not_found: "Archivo no encontrado",
	unsaved_changes_in_entity: "Tienes cambios sin guardar en esta entidad.",
	delete_this_role: "Eliminar este rol",
	no_roles_yet: "Aún no tienes roles.",
	create_default_roles: "Crear roles por defecto",
	delete_role_confirmation: "¿Estás seguro de que quieres eliminar este rol?",
	delete_this_user: "Eliminar este usuario",
	no_users_yet: "Aún no hay usuarios",
	add_logged_user_as_admin: "Añadir al usuario autenticado como administrador",
	add_current_user_as_admin: "Añadir usuario actual como administrador",
	create_default_roles_and_add_admin: "Crear roles por defecto y añadir usuario actual como administrador",
	delete_user_confirmation: "¿Estás seguro de que quieres eliminar a este usuario?",
	create_your_users_and_roles: "Crea tus usuarios y roles",
	no_users_or_roles_defined: "No tienes usuarios ni roles. Puedes crear los roles por defecto y añadirte a ti mismo como administrador.",
	save_before_changing_schema: "Debes guardar el documento antes de cambiar el esquema",
	edit_schema_for_this_form: "Editar esquema para este formulario",
	no_permissions_to_edit_collection: "No tienes permisos para editar esta colección",
	browser_does_not_support_audio: "Tu navegador no soporta el elemento de audio.",
	user_not_found: "Usuario no encontrado: {{value}}",
	delete_selected: "Eliminar",
	cannot_delete_selected: "Has seleccionado al menos una entidad que no puedes eliminar",
	add_entry: "Añadir",
	add_on_top: "Añadir arriba",
	add_below: "Añadir debajo",
	add_to_field: "Añadir a {{fieldName}}",
	value: "Valor",
	key: "Clave",
	drag_drop_multiple: "Arrastra y suelta algunos archivos aquí, o haz clic para seleccionar archivos. Arrastra para reordenar.",
	drag_drop_single: "Arrastra y suelta un archivo aquí, o haz clic para seleccionar uno",
	open_menu: "Abrir menú",
	close_drawer: "Cerrar panel",
	navigation_drawer: "Panel de navegación",
	collapse: "Contraer",
	expand: "Expandir",
	change_language: "Cambiar idioma",
	toggle_theme: "Cambiar tema",
	user_menu: "Menú de usuario",
	error: "Error",
	error_loading_data: "No se pudieron cargar los datos",
	error_check_server_logs: "Consulta los registros del servidor para más detalles.",
	error_technical_details: "Detalles técnicos",
	error_uploading_file: "Error al subir archivo",
	error_deleting: "Error al eliminar",
	error_before_delete: "Error antes de eliminar",
	error_updating_asset: "Error al actualizar recurso",
	error_deleting_asset: "Error al eliminar recurso",
	error_firestore_index: "Se requiere un índice de Firestore para esta consulta.",
	create_index: "Crear índice",
	value_is_not_reference: "El valor no es una referencia.",
	click_to_edit: "Haz clic para editar",
	data_is_not_array_of_references: "Los datos no son un array de referencias",
	loading: "Cargando",
	local_changes_applied: "Cambios locales aplicados al formulario",
	local_changes_discarded: "Cambios locales descartados",
	are_you_sure_leave: "¿Estás seguro de que quieres salir de esta página?",
	see_console_details: "Consulta la consola para más detalles.",
	drop_here_create_group: "Suelta aquí para crear un nuevo grupo",
	filter_for_null_values: "Filtrar por valores nulos",
	value_updated_elsewhere: "Este valor ha sido actualizado en otro lugar",
	add_property: "Añadir propiedad",
	edit_name: "Editar {{name}}",
	this_entity_not_exist: "Esta entidad no existe en la base de datos",
	internal_error: "ERROR Interno",
	rename_group: "Renombrar grupo",
	group_name_label: "Nombre del grupo",
	group_name_empty_error: "El nombre del grupo no puede estar vacío.",
	group_name_exists_error: "Este nombre de grupo ya existe.",
	search_collections: "Buscar colecciones",
	views_group: "Vistas",
	youd_need_to_save_before_additional_collections: "Debes guardar la entidad antes de añadir colecciones adicionales",
	page_not_found: "Página no encontrada",
	page_not_found_body: "Esta página no existe o podrías no tener acceso a ella",
	back_to_home: "Volver al inicio",
	default_collection_view: "Vista por defecto de la colección",
	table_view: "Tabla",
	cards_view: "Tarjetas",
	kanban_view: "Kanban",
	choose_how_entities_displayed_default: "Elige cómo se deben mostrar por defecto las entidades",
	document_view: "Vista de documento",
	side_panel: "Panel lateral",
	full_screen: "Pantalla completa",
	should_documents_opened_full_screen: "¿Quieres que los documentos se abran en pantalla completa o en un panel lateral?",
	select_custom_view: "Seleccionar vista personalizada",
	no_custom_views_defined: "No hay vistas personalizadas definidas. Define tus vistas en los ajustes de personalización, antes de usar este diálogo.",
	select_custom_action: "Seleccionar acción personalizada",
	no_custom_actions_defined: "No hay acciones personalizadas definidas. Define tus acciones en los ajustes de personalización, antes de usar este diálogo.",
	no_collections_found: "No se encontraron colecciones",
	start_building_collections: "Empieza a crear colecciones en Rebase fácilmente. Mapea la información a tu base de datos existente, importa desde archivos o utiliza nuestras plantillas.",
	create_first_collection: "Crea tu primera colección",
	define_collections_programmatically: "También puedes definir colecciones programáticamente.",
	edit_collection: "Editar colección",
	no_permissions_edit_collection: "No tienes permisos para editar esta colección",
	no_permissions_create_collection: "No tienes permisos para crear colecciones",
	create_collection: "Crear colección",
	update_collection: "Actualizar colección",
	new_collection: "Nueva colección",
	add_new_collection: "Añadir nueva colección",
	collection_with_name: "Colección {{name}}",
	change_icon: "Cambiar icono",
	is_subcollection_of: "Esta es una subcolección de",
	name: "Nombre",
	collection_name_description: "Nombre de esta colección (usualmente un nombre en plural, ej. 'Productos')",
	path: "Ruta",
	relative_path_to_parent: "Ruta relativa al nivel superior (no es necesario incluir la ruta padre)",
	path_in_database: "La ruta donde está almacenada esta colección en la base de datos",
	singular_name: "Nombre en singular",
	singular_name_description: "Opcionalmente define un nombre en singular para tus entidades",
	description: "Descripción",
	description_of_collection: "Descripción de la colección, puedes usar markdown",
	collection_id: "ID de la Colección",
	collection_id_description: "Este ID identifica a esta colección. Típicamente es el mismo que la ruta.",
	collection_group: "Grupo de colección",
	collection_group_description: "Un grupo de colección consiste en todas las colecciones con la misma ruta. Esto te permite realizar consultas sobre múltiples colecciones a la vez.",
	advanced_settings: "Ajustes avanzados",
	doc_history_global: "Historial de revisiones de documentos habilitado si está activado globalmente",
	doc_history_enabled: "Historial de revisiones de documentos HABILITADO",
	doc_history_not_enabled: "Historial de revisiones de documentos NO habilitado",
	doc_history_description: "Al estar habilitado, cada documento de esta colección tendrá un registro de cambios. Esto es útil para fines de auditoría. Los datos se almacenan en una subcolección del documento en tu base de datos __history.",
	document_id_generation: "Generación de IDs de documentos",
	code_defined: "Definido por código",
	users_must_define_id: "Los usuarios deben definir un ID",
	users_can_define_id: "Los usuarios pueden definir un ID, pero no es obligatorio",
	doc_id_auto_generated: "El ID del documento se genera automáticamente",
	config_doc_id_generation: "Configura cómo se generan los IDs de los documentos al crear nuevas entidades.",
	enable_text_search: "Habilitar búsqueda de texto para esta colección",
	text_search_description: "Permite buscar texto en esta colección. Si no has especificado un mecanismo de búsqueda de texto, utilizará la búsqueda local incorporada. Esto no se recomienda para colecciones grandes, ya que puede incidir en el rendimiento.",
	database_id: "ID de la base de datos",
	default_text: "(por defecto)",
	custom_actions: "Acciones personalizadas",
	more_info: "Más información",
	define_custom_actions_cli: "Define tus propias acciones personalizadas subiéndolas con la CLI.",
	action_defined_in_code: "Esta acción está definida en el código con la clave",
	add_custom_entity_action: "Añadir acción personalizada de entidad",
	remove_this_action: "¿Eliminar esta acción?",
	remove_action_warning: "Esto no eliminará ningún dato, sólo se retirará del panel de administración",
	subcollections_of: "Subcolecciones de",
	add_subcollection: "Añadir subcolección",
	custom_views: "Vistas personalizadas",
	define_custom_views_cli: "Define tus propias vistas personalizadas subiéndolas con la CLI.",
	view_defined_in_code: "Esta vista se define en el código con la clave",
	add_custom_entity_view: "Añadir vista de entidad personalizada",
	delete_this_subcollection: "¿Eliminar esta subcolección?",
	remove_collection_warning: "Esto no eliminará ningún dato, sólo la colección en el panel de administración",
	remove_this_view: "¿Eliminar esta vista?",
	remove_view_warning: "Esto no eliminará ningún dato, sólo la vista en el panel de administración",
	no_collection_selected: "Ninguna colección seleccionada",
	code_for_collection: "Código para",
	use_config_define_json: "Usa esta configuración para definir la colección en formato JSON.",
	customise_collection_code: "Si deseas personalizar la colección en código, puedes añadir este código a la configuración de tu aplicación de administración.",
	copied: "Copiado",
	property_cant_be_edited: "Esta propiedad no se puede editar",
	property_not_editable_description: "Puede que no tengas permisos para editarla o que esté definida en código y no se pueda modificar.",
	delete_this_property: "¿Eliminar esta propiedad?",
	delete_property_warning: "Esto no eliminará datos, sólo modificará la configuración de la colección.",
	error_must_specify_id: "Debes especificar un id para el campo",
	error_id_format: "El id sólo puede contener letras, números y guiones bajos (_), y no puede empezar con un número",
	error_id_already_exists: "Ya existe otro campo con este ID",
	error_must_specify_title: "Debes especificar un título para el campo",
	custom_or_other: "Personalizado/Otro",
	select_property_widget: "Selecciona un widget de propiedad",
	error_changing_data_type: "Este widget utiliza un tipo de dato diferente al seleccionado inicialmente. Esto puede generar errores con los datos existentes.",
	required: "Requerido",
	enum_form_dialog: "Diálogo de formulario Enum",
	imported_data_preview: "Vista previa de datos importados",
	entities_with_same_id_overwritten: "Las entidades con el mismo id se sobrescribirán",
	collection_editor: "Editor de colecciones",
	properties_in_this_group: "Propiedades en este grupo",
	data_property_mapping: "Mapeo de propiedades",
	property_edit_view: "Vista de edición de propiedad",
	all_of_these: "Todos estos",
	any_of_these: "Cualquiera de estos",
	only_admins_edit_roles: "Sólo los administradores pueden editar roles",
	error_user_not_found: "Usuario no encontrado",
	all_roles: "Todos los roles",
	role: "Rol",
	name_of_this_role: "Nombre del rol",
	id_of_this_role: "ID del rol",
	create_entities: "Crear entidades",
	read_entities: "Leer entidades",
	update_entities: "Actualizar entidades",
	delete_entities: "Eliminar entidades",
	all_collections: "Todas las colecciones",
	create_entities_in_collections: "Crear entidades en colecciones",
	access_all_data_in_every_collection: "Acceder a todos los datos en cada colección",
	update_data_in_any_collection: "Actualizar datos en cualquier colección",
	delete_data_in_any_collection: "Eliminar datos en cualquier colección",
	allow_all_permissions_in_this_collections: "Permitir todos los permisos en estas colecciones",
	all: "Todo",
	customise_permissions_description: "Puedes personalizar los permisos de los usuarios para este rol en cada colección",
	create_collections: "Crear colecciones",
	yes: "Sí",
	no: "No",
	can_user_create_collections: "¿Puede el usuario crear colecciones?",
	edit_collections: "Editar colecciones",
	only_own_collections: "Sólo las propias",
	own: "Propias",
	can_user_edit_collections: "¿Puede el usuario editar colecciones?",
	delete_collections: "Eliminar colecciones",
	can_user_delete_collections: "¿Puede el usuario eliminar colecciones?",
	error_saving_role: "Hubo un error al guardar este rol",
	create_role: "Crear rol",
	update: "Actualizar",
	only_admins_change_roles: "Sólo los administradores pueden cambiar roles",
	must_be_at_least_one_admin: "Tiene que haber al menos un administrador",
	logged_user_not_found: "Usuario autenticado no encontrado",
	user: "Usuario",
	user_id: "ID del usuario",
	name_of_this_user: "Nombre de este usuario",
	email_of_this_user: "Correo electrónico de este usuario",
	roles: "Roles",
	create_user: "Crear usuario",
	users: "Usuarios",
	add_user: "Añadir usuario",
	add_role: "Añadir rol",
	is_admin: "Es Administrador",
	default_permissions: "Permisos predeterminados",
	created_on: "Creado en",
	email: "Email",
	id: "ID",
	read: "Leer",
	column_in_file: "Columna en el archivo",
	map_to_property: "Mapear a Propiedad",
	default_values: "Valores por defecto",
	default_values_description: "Puedes seleccionar un valor por defecto para columnas no mapeadas o valores en blanco:",
	property: "Propiedad",
	default_value: "Valor predeterminado",
	autogenerate_id: "Autogenerar ID",
	id_column_description: "Columna que se utilizará como ID de cada documento",
	do_not_set_value: "No asignar valor",
	set_value_to_true: "Asignar valor verdadero (true)",
	set_value_to_false: "Asignar valor falso (false)",
	drag_and_drop_file: "Arrastra y suelta un archivo aquí, o haz clic para subirlo",
	error_saving_data: "Error al guardar los datos",
	retry: "Reintentar",
	saving_data: "Guardando datos",
	entities_saved: "entidades guardadas",
	do_not_close_tab: "No cierres esta pestaña o se interrumpirá la importación",
	import: "Importar",
	import_data: "Importar datos",
	upload_file_description: "Sube un archivo CSV, Excel o JSON y mapéalo a tu esquema actual",
	back: "Atrás",
	next: "Siguiente",
	save_data: "Guardar datos",
	use_column_as_id: "Usar esta columna como ID",
	do_not_import_property: "No importar esta propiedad",
	entities_will_be_overwritten: "Las entidades con el mismo ID se sobrescribirán",
	data_imported_successfully: "Datos importados correctamente",
	export: "Exportar",
	export_data: "Exportar datos",
	download_table_csv: "Descargar el contenido de esta tabla como CSV",
	csv: "CSV",
	json: "JSON",
	dates_as_timestamps: "Fechas como marcas de tiempo (timestamps)",
	dates_as_strings: "Fechas como cadenas (strings)",
	flatten_arrays: "Aplanar arrays",
	download: "Descargar",
	large_number_of_documents: "Esta colección posee un gran número de documentos ({{count}}).",
	too_many_documents_to_export: "Esta colección tiene más documentos ({{count}}) de los que el navegador puede exportar en un solo archivo (límite {{limit}}). Fíltrala antes de exportar.",
	include_undefined_values: "Incluir valores omitidos (undefined)",
	submit: "Enviar",
	no_filterable_properties: "No hay propiedades filtrables disponibles",
	apply_filters: "Aplicar filtros",
	filter_presets: "Preajustes",
	filter_preset_apply: "Aplicar preajuste",
	filter_preset_active: "Activo: {{label}}",
	list: "Lista",
	table_view_mode: "Tabla",
	cards: "Tarjetas",
	board: "Tablero",
	initialize_kanban_order_desc: "Esto asignará valores secuenciales para todos los elementos sin ordenación. Los elementos mantendrán su orden actual en cada columna.",
	kanban_view_not_available: "La vista Kanban no está disponible",
	kanban_view_requires_enum: "La vista Kanban requiere una propiedad de tipo texto con opciones (enum) para poder agrupar. Por favor, asegúrate de añadir una propiedad de este tipo a la colección para utilizar esta vista.",
	no_enum_values_configured: "No hay opciones (enum) configuradas para la propiedad \"{{property}}\"",
	items_need_backfill: "Existen elementos sin valores de ordenación. Inicialízalos para habilitar la reorganización con arrastrar y soltar.",
	kanban_order_not_configured: "El ordenamiento por arrastrar y soltar no está configurado. Establece una propiedad de orden en la configuración de la colección para persistir las posiciones de las tarjetas.",
	initialize: "Inicializar",
	confirm_multiple_delete: "¿Confirmar eliminación múltiple?",
	delete_entity_confirm_title: "¿Confirmas que quieres borrar el elemento '{{entityName}}'?",
	add_existing: "Añadir existente",
	unlink_entity_confirm_title: "¿Quitar este/a {{entityName}} de este registro?",
	unlink_entity_confirm_body: "Seguirá en {{collectionName}} y disponible para otros registros.",
	confirm_multiple_unlink: "¿quitarlos de este registro?",
	no_filter: "Sin filtro",
	is_true: "es verdadero",
	is_false: "es falso",
	error_uploading_file_size: "Error al subir el archivo: supera los {{maxSize}} bytes",
	error_uploading_file_type: "Error al subir el archivo: el tipo de archivo no es compatible",
	drag_drop_files: "Arrastra archivos aquí o haz clic para editarlos",
	drag_drop_file: "Arrastra un archivo aquí o haz clic para editarlo",
	multiple_deleted: "Múltiples eliminados",
	some_entities_deleted: "Algunas entidades han sido eliminadas, pero no todas",
	error_deleting_entities: "Error al eliminar entidades",
	deleted: "eliminado",
	selection_options: "Opciones de selección",
	selection_menu_all_loaded: "Todo en esta página",
	selection_menu_all_matching: "Los {{total}} {{collection}}",
	selection_menu_none: "Ninguno",
	selection_select_all_loaded: "Seleccionar las filas cargadas",
	selection_deselect_all: "Deseleccionar todo",
	selection_reading_rows: "Leyendo filas… {{loaded}} de {{total}}",
	selection_reading_rows_unknown: "Leyendo filas… {{loaded}} hasta ahora",
	selection_deleting_progress: "Eliminados {{completed}} de {{total}}",
	confirm_delete_selection: "¿Eliminar {{total}} {{collection}}?",
	confirm_delete_selection_unknown: "¿Eliminar todas las filas que coinciden con el filtro actual?",
	confirm_delete_selection_body: "Esta acción no se puede deshacer. Cada fila se elimina por separado, así que puede tardar un rato.",
	export_selection_count: "Descargar las {{total}} filas seleccionadas en {{format}}",
	export_selection_all_matching: "Descargar todos los {{collection}} que coinciden con el filtro actual",
	select_reference: "Seleccionar referencia",
	select_references: "Seleccionar referencias",
	account_settings: "Configuración de la cuenta",
	profile: "Profile",
	security: "Seguridad",
	sessions: "Sesiones",
	change_password: "Cambiar Contraseña",
	current_password: "Contraseña Actual",
	new_password: "Nueva Contraseña",
	confirm_password: "Confirmar Nueva Contraseña",
	password_changed: "Contraseña cambiada con éxito. Se cerrará tu sesión.",
	passwords_dont_match: "Las contraseñas no coinciden",
	password_too_short: "La contraseña debe tener al menos 8 caracteres",
	password_change_not_available: "El cambio de contraseña no está disponible para cuentas que usan proveedores de inicio de sesión externos.",
	changing_password: "Cambiando...",
	display_name: "Nombre visible",
	photo_url: "URL de la foto",
	save_profile: "Guardar perfil",
	saving: "Saving...",
	no_active_sessions: "No se encontraron sesiones activas.",
	revoking: "Revoking...",
	revoke_all_sessions: "Revocar todas las sesiones",
	unknown_device: "Dispositivo desconocido",
	current: "Current",
	role_id: "ID del rol",
	role_name: "Nombre del rol",
	add_reference: "Añadir referencia",
	add_reference_to: "Añadir referencia a {{name}}",
	/** RolesView — table & permissions */
	role_deleted_successfully: "Rol eliminado correctamente",
	error_deleting_role: "Error al eliminar el rol",
	collection_permissions: "Permisos de la colección",
	collection: "Collection",
	no_collections_configured: "No hay colecciones configuradas",
	no_security_rules_defined: "No hay reglas de seguridad definidas: todas las operaciones están sin restricciones",
	no_rules: "sin reglas",
	/** UsersView — snackbar */
	bootstrap_admin_success: "¡Ya eres administrador! Actualizando...",
	failed_to_bootstrap_admin: "No se pudo crear el administrador inicial",
	user_deleted_successfully: "Usuario eliminado correctamente",
	error_deleting_user: "Error al eliminar el usuario",
	user_created_successfully: "Usuario creado correctamente",
	invitation_sent: "Se ha enviado un correo de invitación a {{email}}. Puede usar el enlace para establecer su contraseña.",
	invitation_sent_title: "Invitación enviada",
	temporary_password: "Contraseña temporal",
	temporary_password_description: "El correo no está configurado. Comparte esta contraseña temporal con el usuario de forma segura. No se volverá a mostrar.",
	temporary_password_email_failed_description: "No se pudo entregar el correo. Comparte esta contraseña temporal con el usuario de forma segura. No se volverá a mostrar.",
	copy_password: "Copiar contraseña",
	password_copied: "Contraseña copiada al portapapeles",
	/** UsersView — pagination & search */
	search_users: "Buscar usuarios",
	created: "Creado",
	actions: "Acciones",
	no_users_found: "No se encontraron usuarios",
	reset_password: "Restablecer contraseña",
	reset_password_success: "Contraseña restablecida correctamente",
	reset_password_confirmation: "¿Seguro que quieres restablecer la contraseña de este usuario?",
	reset_password_send_email: "Enviar un correo para restablecer la contraseña",
	reset_password_set_manually: "Establecer una contraseña manualmente",
	reset_password_set_manually_description: "La contraseña se actualiza de inmediato y no se envía ningún correo. Compártela con el usuario de forma segura.",
	/** Permission-denied empty states */
	no_permission_to_view_users: "No tienes permisos para ver los usuarios",
	no_permission_to_view_roles: "No tienes permisos para ver los roles",
	no_permission_description: "Contacta a un administrador si necesitas acceso a esta sección.",
	error_resetting_password: "Error al restablecer la contraseña",
	/** Editor table-bubble */
	add_row_before: "Insertar fila antes",
	add_row_after: "Insertar fila después",
	delete_row: "Eliminar fila",
	add_column_before: "Insertar columna antes",
	add_column_after: "Insertar columna después",
	delete_column: "Eliminar columna",
	delete_table: "Eliminar tabla",
	/** Editor image-bubble */
	alt_text: "Texto alternativo",
	title: "Title",
	/** MapFieldBinding */
	a_property_of_this_map_has_error: "Una propiedad de este mapa contiene un error",
	/** AI Collection Generator Popover */
	generate_collection_with_ai: "Generar colección con IA",
	modify_collection_with_ai: "Modificar colección con IA",
	describe_collection_to_create: "Describe la colección que quieres crear.",
	describe_changes_to_make: "Describe los cambios que quieres hacer en esta colección.",
	ai_placeholder_create: "p. ej., Crear una colección de productos con nombre, precio, descripción y categoría...",
	ai_placeholder_modify: "p. ej., Agregar un campo de imagen en miniatura con almacenamiento, hacer el precio obligatorio...",
	ai_assist: "Asistente IA",
	generating: "Generando...",
	/** Recently extracted strings for collection editor */
	this_is_subcollection_of: "Esta es una subcolección de",
	use_existing_paths_database: "Usa una de las rutas existentes en tu base de datos:",
	describe_collection_ai: "Describe tu colección a la IA:",
	generate_with_ai: "Generar con IA",
	create_from_json_config: "Crear desde configuración JSON:",
	paste_json_config: "Pegar configuración JSON",
	create_collection_from_file_formats: "Crear una colección desde un archivo (csv, json, xls, xslx...)",
	select_template: "Seleccionar una plantilla:",
	products: "Productos",
	collection_products_subtitle: "Una colección de productos con imágenes, precios y existencias",
	collection_users_subtitle: "Una colección de usuarios con correos electrónicos, nombres y roles",
	blog_posts: "Entradas de blog",
	collection_blog_posts_subtitle: "Una colección de artículos con imágenes, autores y contenido complejo",
	pages: "Páginas",
	collection_pages_subtitle: "Una colección de páginas con imágenes, autores y contenido complejo",
	continue_from_scratch: "Continuar desde cero",
	/** Admin views config */
	admin_users: "Usuarios del panel de administración",
	roles_menu: "Roles",
	project_settings: "Ajustes del proyecto",
	by_signing_in_you_agree_to_our: "Al iniciar sesión aceptas nuestros",
	terms_and_conditions: "Términos y Condiciones",
	and_our: "y nuestra",
	privacy_policy: "Política de Privacidad",
	email_password: "Email/Contraseña",
	sign_in_with_google: "Iniciar sesión con Google",
	auth_user_not_found: "Usuario no encontrado",
	auth_wrong_password: "Contraseña incorrecta. Por favor, inténtalo de nuevo.",
	auth_user_disabled: "Usuario deshabilitado. Por favor, contacta con soporte.",
	auth_account_exists_with_different_credential: "Ya existe una cuenta con este correo electrónico. Inicia sesión como lo hiciste la última vez.",
	auth_account_exists_sign_in_with_password: "Ya existe una cuenta con este correo electrónico. Inicia sesión con tu correo electrónico y contraseña.",
	auth_email_already_in_use: "El correo electrónico ya está en uso",
	auth_google_permissions_required: "Necesitas conceder permisos adicionales para gestionar tus proyectos de Google Cloud",
	auth_invalid_email_password: "Por favor introduce tanto el correo electrónico como la contraseña",
	auth_enter_email_first: "Por favor introduce tu correo electrónico primero",
	auth_password_reset_sent: "Correo electrónico de restablecimiento de contraseña enviado",
	auth_sign_in_account: "Inicia sesión en tu cuenta",
	auth_create_new_account: "Crea una cuenta nueva",
	auth_password: "Contraseña",
	auth_reset_password: "Restablecer contraseña",
	auth_new_user: "¿Usuario nuevo?",
	auth_have_account: "¿Tienes una cuenta?",
	auth_sign_in: "Iniciar Sesión",
	auth_sign_up: "Regístrate",
	auto_setup_collections_button: "Configurar colecciones automáticamente",
	auto_setup_collections_title: "¿Configurar colecciones automáticamente?",
	auto_setup_collections_desc: "Esto creará automáticamente la configuración de las colecciones que <b>NO</b> estén mapeadas",
	this_can_take_a_minute: "Esto puede tardar un minuto o dos",
	no_collections_found_to_setup: "No se encontraron colecciones para configurar",
	collections_have_been_setup: "¡Tus colecciones han sido configuradas!",
	error_setting_up_collections: "Error al configurar colecciones",
	admin_panel_ready_bring_data: "Tu panel de administración está listo. Traigamos tus datos.",
	admin_panel_ready_get_started: "Tu panel de administración está listo. Así es como puedes empezar.",
	auto_detect_collections: "Autodetectar colecciones",
	auto_detect_collections_desc: "Deja que la IA escanee tu base de datos y genere esquemas de colección automáticamente.",
	create_a_collection: "Crear una colección",
	create_collection_desc: "Define manualmente tu primera colección desde cero usando el editor visual.",
	read_the_docs: "Consulta la documentación",
	read_the_docs_desc: "Aprende cómo personalizar campos, vistas, acciones y más.",
	explore_docs: "Explorar la documentación",
	want_to_customize_with_code: "¿Quieres personalizar con código? Ejecuta",
	to_scaffold_a_local_project: "para crear un proyecto local.",
	validation: "Validación",
	unique: "Único",
	required_message: "Mensaje de campo obligatorio",
	required_tooltip: "No podrás guardar esta entidad si este valor no está establecido",
	unique_tooltip: "No puede haber múltiples entidades con el mismo valor",
	lowercase: "Minúsculas",
	uppercase: "Mayúsculas",
	trim: "Recortar espacios",
	exact_length: "Longitud exacta",
	min_length: "Longitud mínima",
	max_length: "Longitud máxima",
	matches_regex: "Coincide con regex",
	not_valid_regexp: "No es una expresión regular válida",
	regex_helper: "ej. /^\\d+$/ solo para dígitos",
	min_value: "Valor mínimo",
	max_value: "Valor máximo",
	less_than: "Menor que",
	more_than: "Mayor que",
	positive_value: "Valor positivo",
	negative_value: "Valor negativo",
	integer_value: "Valor entero",
	invalid_regular_expression: "Expresión regular no válida",
	must_specify_target_collection: "Debes especificar una colección de destino para el campo",
	need_specify_repeat_field: "Debes especificar un campo de repetición",
	need_specify_block_properties: "Debes especificar las propiedades de este bloque",
	incomplete_condition: "Condición incompleta - por favor selecciona un campo",
	field_name: "Nombre del campo",
	kanban_column_property: "Propiedad de columna Kanban",
	select_a_property: "Seleccionar una propiedad",
	kanban_property_not_found: "La propiedad \"{{property}}\" no existe o no es una propiedad de tipo string con enum. Selecciona una propiedad válida o borra la selección.",
	no_enum_string_properties: "No se encontraron propiedades de tipo string con enum. Agrega una propiedad string con enumValues para usar la vista Kanban.",
	kanban_column_description: "Selecciona una propiedad de tipo string con valores enum para agrupar entidades en columnas",
	create_property: "+ Crear propiedad \"{{property}}\"",
	order_property: "Propiedad de orden",
	order_property_not_found: "La propiedad \"{{property}}\" no existe o no es una propiedad numérica. Selecciona una propiedad válida o borra la selección.",
	no_number_properties: "No se encontraron propiedades numéricas. Agrega una propiedad numérica para habilitar el ordenamiento.",
	order_property_description: "Selecciona una propiedad numérica para mantener el orden de los elementos",
	display_settings: "Configuración de visualización",
	default_row_size: "Tamaño de fila predeterminado",
	side_dialog_width: "Ancho del diálogo lateral",
	side_dialog_width_description: "Opcionalmente define el ancho (en píxeles) del diálogo lateral de entidades. El valor predeterminado es 768px",
	inline_editing_enabled: "Los datos se pueden editar directamente en la vista de tabla",
	inline_editing_disabled: "Los datos solo se pueden editar en la vista de formulario",
	inline_editing_description: "Permitir editar datos directamente en la vista de tabla, sin abrir la vista de formulario.",
	include_json_view: "Incluir vista JSON",
	no_json_view: "No incluir vista JSON",
	json_view_description: "Incluir la representación JSON del documento.",
	not_found_suffix: "no encontrado",
	editor_text: "Texto",
	editor_text_description: "Empieza a escribir con texto plano.",
	editor_heading_1: "Encabezado 1",
	editor_heading_1_description: "Encabezado de sección grande.",
	editor_heading_2: "Encabezado 2",
	editor_heading_2_description: "Encabezado de sección mediano.",
	editor_heading_3: "Encabezado 3",
	editor_heading_3_description: "Encabezado de sección pequeño.",
	editor_todo_list: "Lista de tareas",
	editor_todo_list_description: "Sigue tus tareas con una lista de tareas.",
	editor_bullet_list: "Lista con viñetas",
	editor_bullet_list_description: "Crea una lista simple con viñetas.",
	editor_numbered_list: "Lista numerada",
	editor_numbered_list_description: "Crea una lista con numeración.",
	editor_quote: "Cita",
	editor_quote_description: "Captura una cita.",
	editor_code: "Código",
	editor_code_description: "Captura un fragmento de código.",
	editor_image: "Imagen",
	editor_image_description: "Sube una imagen desde tu ordenador.",
	editor_multiple: "Múltiple",
	editor_link: "Enlace",
	editor_save: "Guardar",
	editor_cancel: "Cancelar",
	editor_remove_link: "Eliminar enlace",
	editor_paste_or_type_link: "Pega o escribe el enlace",
	editor_open_in_new_window: "Abrir en una ventana nueva",
	editor_bold: "Negrita",
	editor_italic: "Cursiva",
	editor_underline: "Subrayado",
	editor_strikethrough: "Tachado",
	editor_autocomplete: "Autocompletar",
	editor_autocomplete_description: "Añade texto basado en el contexto.",
	text_search_dialog_title: "Activar búsqueda de texto",
	text_search_local_not_recommended: "La búsqueda de texto local no es recomendable para colecciones grandes.",
	text_search_local_fetch_warning: "Ten en cuenta que activar la búsqueda de texto local requiere descargar todos los documentos de la colección y almacenarlos en el navegador. Esto puede ser ineficiente para colecciones grandes y generar costes adicionales.",
	text_search_external_suggestion: "Para colecciones más grandes, te recomendamos usar un motor de búsqueda externo como Algolia o Elastic Search, y asignar un delegado de búsqueda a tu configuración.",
	text_search_local_description: "La búsqueda de texto local es la forma más sencilla de activar la búsqueda de texto en tu colección. Carga todos los documentos de la colección en el navegador y realiza la búsqueda de forma local. Es la opción recomendada para colecciones pequeñas.",
	text_search_own_implementation: "Has implementado tu propio controlador de búsqueda de texto. Puedes activar la búsqueda de texto para tu colección.",
	text_search_enable_for_collection: "Activar para esta colección",
	text_search_enable_for_project: "Activar para el proyecto",
	text_search_enabled_snackbar: "Búsqueda de texto local activada",
	settings_heading: "Ajustes",
	settings_project_name: "Nombre del proyecto",
	settings_default_language: "Idioma predeterminado",
	settings_default_language_caption: "Selecciona el idioma base para este proyecto. Los usuarios pueden modificar esta preferencia en sus ajustes personales.",
	settings_enable_local_text_search: "Habilitar búsqueda de texto local",
	settings_local_text_search_caption: "Habilita la búsqueda de texto local para todas las colecciones. Esto te permitirá buscar campos de texto en tus colecciones usando la barra de búsqueda de Rebase. Ten en cuenta que esta función puede implicar un mayor número de lecturas, ya que indexará todos los campos de texto de tus colecciones.",
	settings_doc_history_all_collections: "Historial de documentos habilitado para todas las colecciones",
	settings_doc_history_caption: "Cuando está activado, todas las colecciones tendrán el historial habilitado por defecto. Puedes cambiar este ajuste en cada colección. El historial se guardará en la subcolección __history de cada documento.",
	settings_theme: "Tema",
	settings_primary_color: "Color primario",
	settings_secondary_color: "Color secundario",
	settings_sample_theme_components: "Componentes de ejemplo del tema",
	settings_drag_drop_logo: "Arrastra y suelta tu logotipo aquí",
	settings_security_rules: "Reglas de seguridad",
	settings_security_rules_description: "Rebase utiliza las reglas de seguridad de Firebase para restringir el acceso a los datos. Al crear un nuevo usuario a través de Rebase, se añade el atributo personalizado rebaseUser al usuario en el proyecto cliente. Al añadir las siguientes reglas de seguridad a tu proyecto, te aseguras de que los usuarios de Rebase puedan acceder a los datos a través de Rebase.",
	settings_security_rules_add_domain: "Recuerda añadir el dominio {{domain}} a los dominios permitidos de tu proveedor",
	settings_security_rules_caption: "Estas reglas restringen el acceso a los datos solo para usuarios de Rebase, pero no aplican los permisos a nivel de base de datos. Los permisos se aplican en el frontend, lo que funciona bien en la mayoría de los proyectos. Si necesitas aplicar los permisos a nivel de base de datos, puedes modificar estas reglas de seguridad. Los roles asignados a un usuario se configuran como atributos personalizados en el token de autenticación de Firebase, por lo que puedes usarlos en tus reglas de seguridad.",
	studio_sql_executing_query: "EJECUTANDO CONSULTA...",
	studio_sql_query_error: "Error en la consulta",
	studio_sql_run_query_placeholder: "Ejecuta una consulta para ver los resultados",
	studio_sql_visual_execution_plan: "Plan de ejecución visual",
	studio_sql_success: "SUCCESS",
	studio_sql_no_results: "La consulta se ejecutó correctamente pero no devolvió resultados.",
	studio_sql_rows: "Rows:",
	studio_sql_time: "Time:",
	studio_sql_copy_markdown: "Copiar como Markdown",
	studio_sql_export_json: "Exportar JSON",
	studio_sql_export_csv: "Exportar CSV",
	studio_sql_format_sql: "Formatear SQL",
	studio_sql_explain: "Explain",
	studio_sql_limit_1000: "Limit 1000",
	studio_sql_remove_from_favorites: "Quitar de favoritos",
	studio_sql_add_to_favorites: "Añadir a favoritos",
	studio_sql_save: "Save",
	studio_sql_run: "Run",
	studio_sql_database: "Database",
	studio_sql_role: "Role",
	studio_sql_admin: "(Admin)",
	studio_sql_select_db: "Elegir base de datos",
	studio_sql_query_results: "Resultados de la consulta",
	studio_sql_save_snippet: "Guardar fragmento SQL",
	studio_sql_snippet_name: "Nombre del fragmento",
	studio_sql_snippet_name_placeholder: "p. ej., Obtener todos los usuarios",
	studio_sql_snippet_saved_local: "Se guardará en tu almacenamiento local.",
	studio_sql_cancel: "Cancel",
	studio_sql_dangerous_operation: "Operación peligrosa detectada",
	studio_sql_dangerous_operation_body: "La consulta que vas a ejecutar contiene operaciones potencialmente destructivas (DELETE, DROP, TRUNCATE o UPDATE sin WHERE). ¿Seguro que quieres continuar?",
	studio_sql_snippet_saved: "Fragmento «{{name}}» guardado.",
	studio_sql_markdown_copied: "¡Resultados copiados como Markdown!",
	studio_sql_markdown_copy_failed: "No se pudo copiar el Markdown al portapapeles.",
	studio_sql_row_updated: "Fila actualizada correctamente.",
	studio_sql_cannot_edit_missing_query: "No se pueden editar las filas: falta la consulta subyacente.",
	studio_sql_cannot_resolve_table: "No se pudo determinar la tabla ni la clave primaria",
	studio_sql_missing_pk: "A la fila le faltan columnas de clave primaria: {{columns}}. No se puede actualizar con seguridad.",
	studio_sql_update_failed: "Error al actualizar: {{message}}",
	studio_sql_execution_not_supported: "La fuente de datos actual no admite la ejecución de SQL.",
	studio_sql_error_executing: "Se produjo un error al ejecutar la consulta.",
	studio_sql_error_explaining: "Se produjo un error al explicar la consulta.",
	studio_sql_save_first_to_favorite: "Guarda el fragmento antes de marcarlo como favorito.",
	studio_sql_collections_label: "Collections:",
	studio_sql_admin_collections_tooltip: "Tables in this query that are mapped as admin collections",
	studio_sql_edit_entity: "Edit {{name}} #{{id}}",
	studio_sql_sql_not_supported: "Esta fuente de datos no admite la ejecución de SQL",
	studio_sql_fetch_error: "No se pudieron obtener las opciones de conexión: {{message}}",
	studio_sql_unexpected_format: "Formato de datos inesperado: {{type}}",
	studio_sql_no_tables: "No se encontraron tablas en esta base de datos.",
	studio_sql_schema_fetch_error: "No se pudo obtener el esquema: {{message}}",
	studio_sql_sidebar_snippets: "Snippets",
	studio_sql_sidebar_history: "History",
	studio_sql_sidebar_schema: "Schema",
	studio_sql_sidebar_no_snippets: "No hay fragmentos guardados",
	studio_sql_sidebar_save_snippet_hint: "Guarda una consulta para acceder a ella aquí.",
	studio_sql_sidebar_no_history: "Sin historial",
	studio_sql_sidebar_history_hint: "Las consultas ejecutadas aparecerán aquí.",
	studio_sql_sidebar_delete_snippet: "Eliminar fragmento",
	studio_sql_close_tab: "Cerrar esta pestaña de consulta",
	studio_sql_new_tab: "Nueva pestaña de consulta",
	studio_schema_tables: "Tables",
	studio_schema_loading: "Cargando esquema...",
	studio_schema_no_tables: "No se encontraron tablas",
	studio_schema_retry: "Retry",
	studio_schema_primary_key: "Clave primaria",
	studio_schema_select_all: "SELECT * FROM",
	studio_schema_insert_into: "INSERT INTO",
	studio_schema_update: "UPDATE",
	studio_schema_delete_from: "DELETE FROM",
	studio_schema_columns: "{{count}} columnas",
	studio_rls_title: "Seguridad a nivel de fila",
	studio_rls_description: "Gestiona las políticas RLS de tus tablas",
	studio_rls_enabled: "RLS activada",
	studio_rls_disabled: "RLS desactivada",
	studio_rls_no_rls: "Sin RLS",
	studio_rls_enable_rls: "Activar RLS",
	studio_rls_disable_rls: "Desactivar RLS",
	studio_rls_create_policy: "Crear política",
	studio_rls_policies: "Policies",
	studio_rls_no_policies: "No hay políticas definidas",
	studio_rls_no_policies_desc: "Esta tabla no tiene políticas RLS. El acceso a todas las filas depende del estado de RLS.",
	studio_rls_add_first_policy: "Añade tu primera política",
	studio_rls_force_rls: "Forzar RLS",
	studio_rls_force_rls_desc: "Si se activa, las políticas RLS se aplican también al propietario de la tabla.",
	studio_rls_enable_force_rls: "Activar RLS forzada",
	studio_rls_disable_force_rls: "Desactivar RLS forzada",
	studio_rls_edit: "Edit",
	studio_rls_delete: "Delete",
	studio_rls_confirm_delete_title: "Eliminar política",
	studio_rls_confirm_delete_body: "¿Seguro que quieres eliminar esta política? Esta acción no se puede deshacer.",
	studio_rls_confirm_enable_title: "Activar la seguridad a nivel de fila",
	studio_rls_confirm_enable_body: "Al activar RLS, el acceso a las filas de esta tabla quedará restringido por tus políticas. Asegúrate de tener políticas adecuadas.",
	studio_rls_confirm_disable_title: "Desactivar la seguridad a nivel de fila",
	studio_rls_confirm_disable_body: "Al desactivar RLS, todas las filas quedarán accesibles para todos los roles. ¿Seguro que quieres continuar?",
	studio_rls_select_table: "Selecciona una tabla",
	studio_rls_no_tables: "No hay tablas disponibles",
	studio_rls_error: "Error al cargar las políticas",
	studio_rls_retry: "Retry",
	studio_rls_loading: "Cargando políticas...",
	studio_policy_edit: "Editar política",
	studio_policy_create: "Crear una política nueva",
	studio_policy_defining_rules: "Definiendo reglas de acceso para",
	studio_policy_cancel: "Cancel",
	studio_policy_save: "Guardar política",
	studio_policy_template: "Empezar desde una plantilla (opcional)",
	studio_policy_select_template: "Selecciona una plantilla...",
	studio_policy_name: "Nombre de la política",
	studio_policy_name_placeholder: "Da a tu política un nombre descriptivo",
	studio_policy_behavior: "Comportamiento de la política",
	studio_policy_permissive: "Permissive",
	studio_policy_permissive_desc: "Se combina con «OR»",
	studio_policy_restrictive: "Restrictive",
	studio_policy_restrictive_desc: "Se combina con «AND»",
	studio_policy_command: "Comando de la política",
	studio_policy_target_roles: "Roles destinatarios",
	studio_policy_roles_placeholder: "Selecciona roles (por defecto, public)",
	studio_policy_using_expr: "Expresión USING",
	studio_policy_using_expr_desc: "Se aplica a las filas existentes (SELECT, UPDATE, DELETE)",
	studio_policy_check_expr: "Expresión WITH CHECK",
	studio_policy_check_expr_desc: "Se aplica a las filas nuevas o modificadas (INSERT, UPDATE)",
	studio_policy_help_title: "Guía de seguridad a nivel de fila (RLS)",
	studio_policy_help_intro: "La seguridad a nivel de fila (RLS) te permite restringir a qué filas puede acceder o qué filas puede modificar cada usuario según su estado de autenticación o sus roles.",
	studio_policy_help_step1_title: "1. Selecciona una plantilla",
	studio_policy_help_step1_desc: "Si es tu primera vez con RLS, lo más fácil es empezar eligiendo una plantilla predefinida en el desplegable. Configurará la política automáticamente para casos habituales como «los usuarios pueden actualizar sus propias filas».",
	studio_policy_help_step2_title: "2. Roles destinatarios",
	studio_policy_help_step2_desc: "Elige a qué usuarios se aplica la política:",
	studio_policy_help_role_public: "Se aplica a todo el mundo (con sesión iniciada o sin ella).",
	studio_policy_help_role_authenticated: "Solo se aplica a los usuarios con sesión iniciada.",
	studio_policy_help_role_anon: "Solo se aplica a los usuarios que NO han iniciado sesión.",
	studio_policy_help_step3_title: "3. Expresión USING",
	studio_policy_help_step3_desc: "Una condición SQL que debe evaluarse como verdadera para que las filas existentes sean visibles o actualizables (SELECT, UPDATE, DELETE).",
	studio_policy_help_step3_example: "Garantiza que los usuarios solo vean las filas de las que son propietarios.",
	studio_policy_help_step4_title: "4. Expresión WITH CHECK",
	studio_policy_help_step4_desc: "Una condición SQL que debe evaluarse como verdadera para que se guarden las filas nuevas o modificadas (INSERT, UPDATE).",
	studio_policy_help_step4_example: "Suele coincidir con la expresión USING cuando los usuarios solo pueden insertar o editar sus propios datos.",
	studio_policy_help_auth_vars_title: "Variables de autenticación de Rebase",
	studio_policy_help_auth_vars_desc: "Rebase ofrece funciones auxiliares nativas que puedes usar en tus expresiones:",
	studio_policy_help_auth_uid: "Devuelve el ID del usuario actual como texto. Ejemplo:",
	studio_policy_help_auth_jwt: "Devuelve la carga útil completa del JWT como JSONB para que puedas comprobar reclamaciones personalizadas. Ejemplo:",
	studio_policy_help_auth_roles: "Devuelve los ID de rol del usuario como una cadena separada por comas. Se usa mejor con:",
	studio_policy_help_docs_cta: "Para saber más sobre las políticas de Postgres, consulta la documentación oficial.",
	studio_policy_help_read_docs: "Leer la documentación",
	studio_policy_help_got_it: "¡Entendido!",
	studio_add_kanban_column: "Añadir columna",
	studio_add_kanban_column_title: "Añadir columna al Kanban",
	studio_add_kanban_column_name: "Nombre de la columna",
	studio_add_kanban_column_name_placeholder: "Nombre de la nueva columna",
	studio_add_kanban_column_add: "Add",
	studio_add_kanban_column_cancel: "Cancel",
	studio_collection_view_sql: "Editor SQL",
	studio_collection_view_admin: "Vista de administración",
	studio_editor_collection_tooltip: "Editar el esquema de la colección",
	studio_editor_collection_no_permission: "No tienes permiso para editar esta colección",
	studio_editor_collection_start_tooltip: "Copiar la ruta o editar el esquema",
	studio_editor_collection_start_no_permission: "No tienes permiso para modificar esta colección",
	studio_editor_collection_start_copied: "Copied!",
	studio_editor_entity_tooltip: "Editar el esquema de la entidad",
	studio_editor_entity_no_permission: "No tienes permiso para editar esta entidad",
	studio_home_edit_collection: "Editar colección",
	studio_home_delete_collection: "Eliminar colección",
	studio_home_confirm_delete_title: "¿Eliminar la colección?",
	studio_home_confirm_delete_body: "¿Seguro que quieres eliminar la colección «{{name}}»? Esta acción no se puede deshacer.",
	studio_kanban_setup: "Configuración del Kanban",
	studio_missing_reference: "Referencia inexistente: {{path}}",
	studio_missing_reference_create: "Create",
	studio_new_collection: "Colección nueva",
	studio_new_collection_card: "Colección nueva",
	studio_new_collection_no_permission: "No tienes permisos para crear colecciones",
	studio_property_add_column: "Añadir una propiedad nueva",
	studio_property_add_column_no_permission: "No tienes permiso para añadir propiedades nuevas",
	studio_add_kanban_column_desc: "Añade una opción nueva a la propiedad «{{property}}». Se creará una columna nueva en el tablero Kanban.",
	studio_add_kanban_column_adding: "Adding...",
	studio_collection_view_edit: "Edit",
	studio_editor_collection_disabled: "La edición de colecciones está desactivada",
	studio_editor_collection_edit: "Editar colección",
	studio_editor_entity_save_first: "Debes guardar el documento antes de cambiar el esquema",
	studio_editor_entity_edit_schema: "Editar el esquema de este formulario",
	studio_editor_collection_start_save_filter: "Guardar el filtro y el orden por defecto",
	studio_editor_collection_start_clear_filter: "Borrar el filtro y el orden por defecto",
	studio_editor_collection_start_reset_filter: "Restablecer el filtro y el orden por defecto",
	studio_editor_collection_start_saved: "Configuración por defecto guardada",
	studio_home_duplicate_collection: "Duplicate",
	studio_home_delete: "Delete",
	studio_home_confirm_delete_no_data: "No se eliminará ningún dato, solo la colección en el panel de administración",
	studio_home_collection_deleted: "Colección eliminada",
	studio_kanban_configure: "Configurar el Kanban",
	studio_missing_reference_error: "No hay ninguna colección para la ruta: {{path}}",
	studio_new_collection_add: "Añadir nueva colección",
	db_column_type: "Tipo de columna de base de datos",
	primary_key_unique_id: "Clave primaria / ID único",
	spread_children_as_columns: "Expandir hijos como columnas",
	mode: "Modo",
	timezone: "Zona horaria",
	target_collection: "Colección destino",
	storage_file_name: "Nombre del archivo",
	storage_path: "Ruta de almacenamiento",
	storage_max_size: "Tamaño máximo (en bytes)",
	storage_resize_mode: "Modo de redimensionamiento",
	storage_output_format: "Formato de salida",
	storage_max_width: "Ancho máximo (px)",
	storage_max_height: "Alto máximo (px)",
	storage_quality: "Calidad (0-100)",
	storage_file_upload_config: "Configuración de subida de archivos",
	storage_image_resize_config: "Configuración de redimensionamiento de imagen",
	storage_all_file_types: "Todos los tipos de archivo permitidos",
	storage_allowed_file_types: "Tipos de archivo permitidos",
	storage_include_bucket_url: "Incluir URL del bucket (s3://...) en el valor guardado",
	storage_save_url: "Guardar URL en lugar de la ruta de almacenamiento",
	datetime_automatic_value: "Valor automático",
	markdown_paste_behavior: "Comportamiento al pegar",
	entity_saved: "Guardado",
	more_actions: "Más acciones",
	record_history: "Historial",
	inspect_record: "Inspeccionar registro",
	inspect: "Inspeccionar",
	close_inspector: "Cerrar el inspector",
	open_in_live_site: "Abrir en el sitio publicado",
	history_revert: "Volver a esta versión",
	history_revert_title: "¿Volver a esta versión de los datos?",
	history_reverted: "Se volvió a la versión seleccionada",
	history_revert_error: "Error al revertir el registro",
	history_revert_dirty: "Guarda o descarta tus cambios antes de revertir",
	history_empty: "No hay historial disponible",
	history_empty_description: "Al guardar un registro se crea una nueva versión y se almacena en el historial.",
	history_new_entity: "El historial solo está disponible para registros existentes",
	history_no_more: "No hay más historial",
	studio_group_views: "Vistas",
	studio_group_admin: "Administración",
	studio_group_settings: "Ajustes",
	studio_group_database: "Base de datos",
	studio_group_compute: "Cómputo",
	studio_group_storage: "Almacenamiento",
	studio_group_api: "API",
	studio_group_access_control: "Control de acceso",
	studio_tool_sql: "Consola SQL",
	studio_tool_sql_description: "Ejecuta consultas SQL",
	studio_tool_js: "Consola JS",
	studio_tool_js_description: "Ejecuta JavaScript",
	studio_tool_rls: "Políticas RLS",
	studio_tool_rls_description: "Seguridad a nivel de fila",
	studio_tool_storage: "Almacenamiento",
	studio_tool_storage_description: "Gestiona los archivos de almacenamiento",
	studio_tool_cron: "Tareas cron",
	studio_tool_cron_description: "Gestiona las tareas programadas",
	studio_tool_schema_visualizer: "Visualizador de esquema",
	studio_tool_schema_visualizer_description: "Diagrama ER interactivo de la base de datos",
	studio_tool_branches: "Ramas",
	studio_tool_branches_description: "Crea y gestiona ramas de la base de datos",
	studio_tool_backups: "Copias de seguridad",
	studio_tool_backups_description: "Descarga copias de seguridad de la base de datos",
	studio_tool_api: "Explorador de API",
	studio_tool_api_description: "Documentación de API interactiva y pruebas",
	studio_tool_logs: "Explorador de registros",
	studio_tool_logs_description: "Registros de sistema y consultas en tiempo real",
	studio_tool_api_keys: "Claves de API",
	studio_tool_api_keys_description: "Crea y gestiona claves de API con alcance",
	studio_backups_denied_title: "No puedes listar las copias de seguridad de este proyecto",
	studio_backups_denied_hint: "El proyecto está bien. Listar copias de seguridad requiere una conexión de administrador, y la cuenta con la sesión iniciada fue rechazada.",
	studio_backups_read_failed: "No se han podido leer las copias de seguridad de este proyecto",
	studio_backups_downloading: "Descargando…",
	studio_backups_roles_file: "Archivo de roles",
	studio_backups_roles_file_hint: "El .globals.sql guardado con esta copia. Recrea los roles de base de datos a los que se refieren los permisos y la seguridad a nivel de fila del volcado, y `rebase db restore` lo lee junto al .dump, así que guarda los dos archivos juntos.",
	studio_backups_no_roles_file: "Sin archivo de roles",
	studio_backups_no_roles_file_hint: "Esta copia no tiene ningún .globals.sql. Al restaurarla en un Postgres nuevo, se detiene en el primer permiso concedido a un rol de base de datos que allí no existe.",
	studio_cron_denied_title: "No puedes listar las tareas cron de este proyecto",
	studio_cron_denied_hint: "El proyecto está bien. La API de cron es solo para administradores, y la cuenta con la sesión iniciada fue rechazada.",
	studio_cron_read_failed: "No se han podido leer las tareas cron de este proyecto",
	studio_api_keys_denied_title: "No puedes listar las claves de API de este proyecto",
	studio_api_keys_denied_hint: "El proyecto está bien. Gestionar claves de API es una operación de administrador, y la cuenta con la sesión iniciada fue rechazada.",
	studio_api_keys_read_failed: "No se han podido leer las claves de API de este proyecto",
	studio_api_keys_empty_title: "Aún no hay claves de API",
	studio_api_keys_empty_hint: "Cree una clave para habilitar la autenticación entre servicios",
	studio_api_keys_revoke: "Revocar",
	studio_api_keys_select_hint: "Seleccione una clave de API para ver los detalles",
	studio_api_keys_permissions: "Permisos",
	studio_api_keys_no_permissions: "No hay permisos configurados",
	studio_api_keys_revoke_confirmation: "Confirmación de revocación",
	studio_api_keys_name_label: "Nombre:",
	studio_api_keys_access_label: "Acceso:",
	studio_api_keys_admin_granted: "Rol de administrador concedido",
	studio_api_keys_admin_granted_hint: "las rutas de administración y las políticas default_admin",
	studio_api_keys_done: "Listo",
	studio_api_keys_status_active: "Activa",
	studio_api_keys_status_expired: "Caducada",
	studio_api_keys_status_revoked: "Revocada",
	studio_api_keys_stat_status: "Estado",
	studio_api_keys_stat_last_used: "Último uso",
	studio_api_keys_stat_expires: "Caduca",
	studio_api_keys_never: "Nunca",
	studio_api_keys_role_service: "Servicio",
	studio_api_keys_stat_rate_limit: "Límite de peticiones",
	studio_api_keys_rate_limit_default: "Predeterminado (1000/15 min)",
	studio_api_keys_stat_created_by: "Creada por",
	studio_api_keys_copy_failed: "No se pudo copiar",
	studio_branches_denied_title: "No puedes listar las ramas de este proyecto",
	studio_branches_denied_hint: "El proyecto está bien. El uso de ramas va por la conexión de administrador, y la cuenta con la sesión iniciada fue rechazada.",
	studio_branches_read_failed: "No se han podido leer las ramas de este proyecto",
	studio_schema_no_collections: "No hay colecciones declaradas",
	studio_schema_no_collections_body: "Todavía no hay nada que dibujar. Añade una colección en config/collections/, o abre «Editar colecciones» para añadir una aquí.",
	studio_schema_introspect_hint: "Contra una base de datos que ya tiene tablas, rebase schema introspect escribe los archivos de colección por ti.",
	studio_rls_enable_confirm_title: "¿Activar la seguridad a nivel de fila?",
	studio_rls_enable_confirm_body: "Hasta que una política lo permita, ninguna fila de {{table}} será legible. Postgres deniega por defecto.",
	studio_rls_disable_confirm_title: "¿Desactivar la seguridad a nivel de fila?",
	studio_rls_disable_confirm_body: "Todas las políticas de {{table}} dejan de aplicarse. Quien pueda alcanzar la tabla podrá leer y escribir todas las filas.",
	studio_rls_drop_policy_title: "¿Eliminar esta política?",
	studio_rls_drop_policy_body: "{{policy}} se elimina de {{table}} en la base de datos. No se escribe nada en tu código, así que un despliegue puede restaurarla.",
	studio_rls_unapplied: "Sin aplicar",
	studio_rls_unapplied_tooltip: "Declarada en tu código, todavía no en la base de datos. Guárdala aquí para planificar y aplicar el cambio, o ejecuta `rebase db push`.",
	studio_branches_unavailable_title: "Las ramas de base de datos no están disponibles",
	studio_branches_unavailable_body: "Las ramas necesitan un backend Postgres con una conexión de administrador. Define ADMIN_CONNECTION_STRING en el entorno del backend.",
	studio_backups_not_configured_title: "Las copias de seguridad no están configuradas",
	studio_backups_not_configured_body: "Define BACKUP_DESTINATION como una ruta local o una URL s3:// o gs://, y crea una copia con `rebase db backup` o un cron programado.",
	studio_cron_empty_title: "No hay tareas cron registradas",
	studio_cron_empty_body: "Añade un archivo en backend/crons/ que exporte por defecto una CronJobDefinition.",
	studio_read_the_docs: "Consulta la documentación"
};
//#endregion
//#region src/locales/de.ts
var de = {
	save: "Speichern",
	create: "Erstellen",
	create_copy: "Kopie erstellen",
	save_and_close: "Speichern und schließen",
	create_copy_and_close: "Kopie erstellen und schließen",
	create_and_close: "Erstellen und schließen",
	discard: "Verwerfen",
	clear: "Löschen",
	cancel: "Abbrechen",
	edit: "Bearbeiten",
	copy: "Kopieren",
	delete: "Löschen",
	delete_not_allowed: "Sie haben mindestens eine Entität ausgewählt, die Sie nicht löschen können",
	edit_entity: "Bearbeiten",
	back_to_detail: "Zurück zu den Details",
	delete_confirmation_title: "Löschen?",
	delete_confirmation_body: "Dadurch wird die Entität gelöscht. Sind Sie sicher?",
	delete_multiple_confirmation_body: "Dadurch werden die ausgewählten Entitäten gelöscht. Sind Sie sicher?",
	unsaved_changes_title: "Ungespeicherte Änderungen",
	unsaved_changes_body: "Sie haben ungespeicherte Änderungen. Möchten Sie diese verwerfen?",
	discard_changes: "Änderungen verwerfen",
	keep_editing: "Weiter bearbeiten",
	clear_form: "Formular leeren?",
	discard_changes_confirmation: "Alle nicht gespeicherten Änderungen gehen verloren. Dies kann nicht rückgängig gemacht werden.",
	clear_form_confirmation: "Alle eingegebenen Werte werden gelöscht. Dies kann nicht rückgängig gemacht werden.",
	search: "Suchen",
	find_by_id: "Nach ID suchen",
	find_entity_by_id: "Entität nach ID suchen",
	filter: "Filter",
	filters: "Filter",
	records_in_view: "Datensätze in dieser Ansicht",
	clear_filter: "Filter löschen",
	clear_filter_sort: "Filter/Sortierung löschen",
	clear_sort: "Sortierung löschen",
	sort: "Sortieren",
	sort_by: "Sortieren nach",
	sort_then_by: "Dann nach",
	sort_ascending: "Aufsteigend sortieren",
	sort_descending: "Absteigend sortieren",
	sort_remove: "Sortierung entfernen",
	sort_move_up: "Diesen Schlüssel wichtiger machen",
	sort_move_down: "Diesen Schlüssel weniger wichtig machen",
	sort_remove_key: "Diesen Sortierschlüssel entfernen",
	sort_key_position: "Sortierschlüssel {{position}}",
	sort_shift_click_hint: "Umschalt+Klick, um eine Spalte unter der aktuellen Sortierung hinzuzufügen",
	save_entity_before_subcollections: "Du musst den Eintrag speichern, bevor du weitere Sammlungen hinzufügst",
	clear_all: "Alle löschen",
	no_items: "Keine Elemente",
	no_entries_found: "Keine Einträge gefunden",
	all_entries_loaded: "Alle {{count}} Einträge geladen",
	create_your_first_entry: "Erstellen Sie Ihren ersten Eintrag",
	no_results_filter_sort: "Keine Ergebnisse mit angewendetem Filter/Sortierung",
	no_results_search: "Keine Ergebnisse gefunden für \"{{search}}\"",
	add: "Hinzufügen",
	remove: "Entfernen",
	multiple_entities: "Mehrere Entitäten",
	unsaved_changes: "Sie haben ungespeicherte Änderungen in {{collectionName}}.",
	so_empty: "So leer...",
	no_results: "Keine Ergebnisse mit angewendetem Filter/Sortierung",
	refresh_data: "Daten aktualisieren",
	dark_mode: "Dunkel",
	light_mode: "Hell",
	system_mode: "System",
	join_newsletter: "Newsletter abonnieren, kein Spam",
	ok: "Ok",
	save_collection_config: "Sammlungsstruktur speichern",
	search_for_more_icons: "Nach weiteren Symbolen suchen…",
	ai_modified: "KI-modifiziert",
	size_label: "Größe",
	group_by: "Gruppieren nach",
	initialize_kanban_order: "Kanban-Reihenfolge initialisieren",
	copy_id: "ID kopieren",
	add_specific: "{{name}} hinzufügen",
	add_named: "\"{{name}}\" hinzufügen",
	select_specific: "{{name}} auswählen",
	select_from: "Aus {{name}} auswählen",
	done: "Fertig",
	log_out: "Abmelden",
	column_cannot_be_edited: "Diese Spalte kann nicht direkt bearbeitet werden",
	close: "Schließen",
	hide_list: "Liste ausblenden",
	show_list: "Liste einblenden",
	unsaved_local_changes: "Ungespeicherte lokale Änderungen",
	unsaved_local_changes_description: "Dieses Dokument wurde lokal bearbeitet und weist ungespeicherte Änderungen auf. Diese lokalen Änderungen gehen verloren, wenn Sie sie nicht anwenden.",
	preview_changes: "Änderungen in der Vorschau anzeigen",
	apply_changes: "Änderungen anwenden",
	discard_local_changes: "Lokale Änderungen verwerfen",
	preview_local_changes: "Lokale Änderungen in der Vorschau anzeigen",
	preview_local_changes_description: "Dies sind die lokalen Änderungen, die auf das Formular angewendet werden.",
	type: "Typ",
	string: "Zeichenfolge",
	number: "Zahl",
	boolean: "Boolescher Wert",
	date: "Datum",
	map: "Karte",
	array: "Array",
	arrays_of_arrays_not_supported: "Arrays von Arrays werden nicht unterstützt.",
	data_type_not_supported: "Datentyp {{dataType}} wird noch nicht unterstützt",
	passkey_error_unsupported: "Ihr Gerät oder Browser unterstützt keine Passkeys.",
	saved_correctly: "Erfolgreich gespeichert",
	error_before_saving: "Fehler bei der Datenvorbereitung",
	error_after_saving: "Fehler im Nachspeichervorgang",
	error_saving_entity: "Fehler beim Speichern des Eintrags",
	entity_does_not_exist: "Dieser Eintrag existiert nicht in der Datenbank",
	form_modified: "Dieses Formular wurde geändert",
	form_in_sync: "Das aktuelle Formular ist mit der Datenbank synchron",
	fix_errors_before_saving: "Markierte Fehler vor dem Speichern beheben",
	admin: "Admin",
	home: "Startseite",
	this_form_has_errors: "Dieses Formular enthält Fehler",
	error_loading_navigation: "Fehler beim Laden der Navigation",
	error_loading_auth: "Fehler beim Laden der Authentifizierung",
	this_form_has_been_modified: "Dieses Formular wurde geändert",
	current_form_in_sync: "Das aktuelle Formular ist mit der Datenbank synchron",
	open_in_console: "In der Konsole öffnen",
	collection_does_not_exist: "Die angegebene Sammlung existiert nicht. Überprüfen Sie die Konsole",
	unexpected_value: "Unerwarteter Wert",
	unexpected_value_click_to_edit: "Unerwarteter Wert. Klicken zum Bearbeiten",
	unexpected_reference_value: "Unerwarteter Referenzwert. Klicken zum Bearbeiten",
	copy_url_to_clipboard: "URL in Zwischenablage kopieren",
	open_image_in_new_tab: "Bild in neuem Tab öffnen",
	reference_not_set: "Referenz nicht gesetzt",
	reference_does_not_exist: "Referenz existiert nicht",
	entity_not_found: "Entität nicht gefunden",
	entity_not_found_body: "Dieser Datensatz existiert nicht, oder er ist durch die Sicherheit auf Zeilenebene für Sie nicht sichtbar.",
	back_to_collection: "Zurück zu {{name}}",
	file_not_found: "Datei nicht gefunden",
	unsaved_changes_in_entity: "Sie haben ungespeicherte Änderungen in dieser Entität.",
	delete_this_role: "Diese Rolle löschen",
	no_roles_yet: "Sie haben noch keine Rollen.",
	create_default_roles: "Standardrollen erstellen",
	delete_role_confirmation: "Sind Sie sicher, dass Sie diese Rolle löschen möchten?",
	delete_this_user: "Diesen Benutzer löschen",
	no_users_yet: "Es gibt noch keine Benutzer",
	add_logged_user_as_admin: "Den angemeldeten Benutzer als Administrator hinzufügen",
	add_current_user_as_admin: "Aktuellen Benutzer als Administrator hinzufügen",
	create_default_roles_and_add_admin: "Standardrollen erstellen und aktuellen Benutzer als Administrator hinzufügen",
	delete_user_confirmation: "Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?",
	create_your_users_and_roles: "Erstellen Sie Ihre Benutzer und Rollen",
	no_users_or_roles_defined: "Sie haben keine Benutzer oder Rollen definiert. Sie können Standardrollen erstellen und den aktuellen Benutzer als Administrator hinzufügen.",
	save_before_changing_schema: "Sie müssen das Dokument speichern, bevor Sie das Schema ändern können",
	edit_schema_for_this_form: "Schema für dieses Formular bearbeiten",
	no_permissions_to_edit_collection: "Sie haben keine Berechtigung, diese Sammlung zu bearbeiten",
	browser_does_not_support_audio: "Ihr Browser unterstützt das Audioelement nicht.",
	user_not_found: "Benutzer nicht gefunden: {{value}}",
	delete_selected: "Löschen",
	cannot_delete_selected: "Sie haben mindestens eine Entität ausgewählt, die Sie nicht löschen können",
	add_entry: "Hinzufügen",
	add_on_top: "Oben hinzufügen",
	add_below: "Unten hinzufügen",
	add_to_field: "Zu {{fieldName}} hinzufügen",
	value: "Wert",
	key: "Schlüssel",
	drag_drop_multiple: "Ziehen Sie einige Dateien hierher oder klicken Sie, um Dateien auszuwählen. Ziehen zum Neuordnen.",
	drag_drop_single: "Ziehen Sie eine Datei hierher oder klicken Sie, um eine auszuwählen",
	open_menu: "Menü öffnen",
	close_drawer: "Schublade schließen",
	navigation_drawer: "Navigationsschublade",
	collapse: "Einklappen",
	expand: "Ausklappen",
	change_language: "Sprache ändern",
	toggle_theme: "Design umschalten",
	user_menu: "Benutzermenü",
	error: "Fehler",
	error_loading_data: "Daten konnten nicht geladen werden",
	error_check_server_logs: "Weitere Details finden Sie in den Serverprotokollen.",
	error_technical_details: "Technische Details",
	error_uploading_file: "Fehler beim Hochladen der Datei",
	error_deleting: "Fehler beim Löschen",
	error_before_delete: "Fehler vor dem Löschen",
	error_updating_asset: "Fehler beim Aktualisieren des Assets",
	error_deleting_asset: "Fehler beim Löschen des Assets",
	error_firestore_index: "Für diese Abfrage ist ein Firestore-Index erforderlich.",
	create_index: "Index erstellen",
	value_is_not_reference: "Wert ist keine Referenz.",
	click_to_edit: "Klicken zum Bearbeiten",
	data_is_not_array_of_references: "Daten sind kein Array von Referenzen",
	loading: "Wird geladen",
	local_changes_applied: "Lokale Änderungen auf das Formular angewendet",
	local_changes_discarded: "Lokale Änderungen verworfen",
	are_you_sure_leave: "Sind Sie sicher, dass Sie diese Seite verlassen möchten?",
	see_console_details: "Weitere Details siehe Konsole.",
	drop_here_create_group: "Hier ablegen, um eine neue Gruppe zu erstellen",
	filter_for_null_values: "Nach Nullwerten filtern",
	value_updated_elsewhere: "Dieser Wert wurde anderswo aktualisiert",
	add_property: "Eigenschaft hinzufügen",
	edit_name: "Bearbeiten {{name}}",
	this_entity_not_exist: "Diese Entität existiert nicht in der Datenbank",
	internal_error: "Interner FEHLER",
	rename_group: "Gruppe umbenennen",
	group_name_label: "Gruppenname",
	group_name_empty_error: "Gruppenname darf nicht leer sein.",
	group_name_exists_error: "Dieser Gruppenname existiert bereits.",
	search_collections: "Sammlungen durchsuchen",
	views_group: "Ansichten",
	youd_need_to_save_before_additional_collections: "Sie müssen Ihre Entität speichern, bevor Sie weitere Sammlungen hinzufügen können",
	page_not_found: "Seite nicht gefunden",
	page_not_found_body: "Diese Seite existiert nicht oder Sie haben möglicherweise keinen Zugriff darauf",
	back_to_home: "Zurück zur Startseite",
	default_collection_view: "Standard-Sammlungsansicht",
	table_view: "Tabelle",
	cards_view: "Karten",
	kanban_view: "Kanban",
	choose_how_entities_displayed_default: "Wählen Sie aus, wie Entitäten standardmäßig angezeigt werden sollen",
	document_view: "Dokumentenansicht",
	side_panel: "Seitenbereich",
	full_screen: "Vollbild",
	should_documents_opened_full_screen: "Sollen Dokumente im Vollbildmodus oder in einem Inline-Seitendialog geöffnet werden",
	select_custom_view: "Benutzerdefinierte Ansicht auswählen",
	no_custom_views_defined: "Keine benutzerdefinierten Ansichten definiert. Definieren Sie Ihre benutzerdefinierten Ansichten in den Anpassungseinstellungen, bevor Sie diesen Dialog verwenden.",
	select_custom_action: "Benutzerdefinierte Aktion auswählen",
	no_custom_actions_defined: "Keine benutzerdefinierten Aktionen definiert. Definieren Sie Ihre benutzerdefinierten Aktionen in den Anpassungseinstellungen, bevor Sie diesen Dialog verwenden.",
	no_collections_found: "Keine Sammlungen gefunden",
	start_building_collections: "Beginnen Sie einfach mit dem Erstellen von Sammlungen in Rebase. Ordnen Sie sie Ihren bestehenden Datenbankdaten zu, importieren Sie sie aus Dateien oder verwenden Sie unsere Vorlagen.",
	create_first_collection: "Erstellen Sie Ihre erste Sammlung",
	define_collections_programmatically: "Sie können Sammlungen auch programmgesteuert definieren.",
	edit_collection: "Sammlung bearbeiten",
	no_permissions_edit_collection: "Sie haben keine Berechtigung, diese Sammlung zu bearbeiten",
	no_permissions_create_collection: "Sie haben keine Berechtigung, Sammlungen zu erstellen",
	create_collection: "Sammlung erstellen",
	update_collection: "Sammlung aktualisieren",
	new_collection: "Neue Sammlung",
	add_new_collection: "Neue Sammlung hinzufügen",
	collection_with_name: "Sammlung {{name}}",
	change_icon: "Symbol ändern",
	is_subcollection_of: "Dies ist eine Untersammlung von",
	name: "Name",
	collection_name_description: "Name dieser Sammlung, normalerweise im Plural (z. B. Produkte)",
	path: "Pfad",
	relative_path_to_parent: "Relativer Pfad zum übergeordneten Element (der übergeordnete Pfad muss nicht eingeschlossen werden)",
	path_in_database: "Pfad, in dem diese Sammlung in der Datenbank gespeichert ist",
	singular_name: "Bezeichnung im Singular",
	singular_name_description: "Definieren Sie optional einen Singularnamen für Ihre Entitäten",
	description: "Beschreibung",
	description_of_collection: "Beschreibung der Sammlung, Sie können Markdown verwenden",
	collection_id: "Sammlungs-ID",
	collection_id_description: "Diese ID identifiziert diese Sammlung. Typischerweise identisch mit dem Pfad.",
	collection_group: "Sammlungsgruppe",
	collection_group_description: "Eine Sammlungsgruppe besteht aus allen Sammlungen mit demselben Pfad. Auf diese Weise können Sie über mehrere Sammlungen gleichzeitig abfragen.",
	advanced_settings: "Erweiterte Einstellungen",
	doc_history_global: "Dokumentverlaufsrevisionen aktiviert, falls global aktiviert",
	doc_history_enabled: "Dokumentverlaufsrevisionen AKTIVIERT",
	doc_history_not_enabled: "Dokumentverlaufsrevisionen NICHT aktiviert",
	doc_history_description: "Wenn diese Option aktiviert ist, verfügt jedes Dokument in dieser Sammlung über einen Änderungsverlauf. Dies ist nützlich für Überwachungszwecke. Die Daten werden in einer Untersammlung des Dokuments in Ihrer Datenbank namens __history gespeichert.",
	document_id_generation: "Generierung von Dokument-IDs",
	code_defined: "Code definiert",
	users_must_define_id: "Benutzer müssen eine ID definieren",
	users_can_define_id: "Benutzer können eine ID definieren, dies ist jedoch nicht erforderlich",
	doc_id_auto_generated: "Dokument-ID wird automatisch generiert",
	config_doc_id_generation: "Konfigurieren Sie, wie Dokument-IDs beim Erstellen neuer Entitäten generiert werden.",
	enable_text_search: "Textsuche für diese Sammlung aktivieren",
	text_search_description: "Erlauben Sie die Textsuche für diese Sammlung. Wenn Sie keinen Delegaten für die Textsuche angegeben haben, wird die integrierte lokale Textsuche verwendet. Dies wird für große Sammlungen nicht empfohlen, da dies zu Leistungs- und Kostenproblemen führen kann.",
	database_id: "Datenbank-ID",
	default_text: "(Standard)",
	custom_actions: "Benutzerdefinierte Aktionen",
	more_info: "Mehr Infos",
	define_custom_actions_cli: "Definieren Sie Ihre eigenen benutzerdefinierten Aktionen, indem Sie sie mit der CLI hochladen.",
	action_defined_in_code: "Diese Aktion ist im Code definiert mit dem Schlüssel",
	add_custom_entity_action: "Benutzerdefinierte Entitätsaktion hinzufügen",
	remove_this_action: "Diese Aktion entfernen?",
	remove_action_warning: "Dadurch werden keine Daten gelöscht, sondern nur die Aktion im Admin-Panel",
	subcollections_of: "Untersammlungen von",
	add_subcollection: "Untersammlung hinzufügen",
	custom_views: "Benutzerdefinierte Ansichten",
	define_custom_views_cli: "Definieren Sie Ihre eigenen benutzerdefinierten Ansichten, indem Sie sie mit der CLI hochladen.",
	view_defined_in_code: "Diese Ansicht ist im Code definiert mit dem Schlüssel",
	add_custom_entity_view: "Benutzerdefinierte Entitätsansicht hinzufügen",
	delete_this_subcollection: "Diese Untersammlung löschen?",
	remove_collection_warning: "Dadurch werden keine Daten gelöscht, sondern nur die Sammlung im Admin-Panel",
	remove_this_view: "Diese Ansicht entfernen?",
	remove_view_warning: "Dadurch werden keine Daten gelöscht, sondern nur die Ansicht im Admin-Panel",
	no_collection_selected: "Keine Sammlung ausgewählt",
	code_for_collection: "Code für",
	use_config_define_json: "Verwenden Sie diese Konfiguration, um die Sammlung im JSON-Format zu definieren.",
	customise_collection_code: "Wenn Sie die Sammlung im Code anpassen möchten, können Sie diesen Sammlungscode zu Ihrer Admin-App-Konfiguration hinzufügen.",
	copied: "Kopiert",
	property_cant_be_edited: "Diese Eigenschaft kann nicht bearbeitet werden",
	property_not_editable_description: "Sie haben möglicherweise keine Berechtigung zum Bearbeiten oder es ist im Code definiert und kann nicht geändert werden.",
	delete_this_property: "Diese Eigenschaft löschen?",
	delete_property_warning: "Dadurch werden keine Daten gelöscht, nur die Sammlung wird geändert.",
	error_must_specify_id: "Sie müssen eine ID für das Feld angeben",
	error_id_format: "Die ID darf nur Buchstaben, Zahlen und Unterstriche (_) enthalten und nicht mit einer Zahl beginnen",
	error_id_already_exists: "Es gibt bereits ein anderes Feld mit dieser ID",
	error_must_specify_title: "Sie müssen einen Titel für das Feld angeben",
	custom_or_other: "Benutzerdefiniert/Andere",
	select_property_widget: "Wählen Sie ein Eigenschafts-Widget aus",
	error_changing_data_type: "Dieses Widget verwendet einen anderen Datentyp als das ursprünglich ausgewählte Widget. Dies kann zu Fehlern bei vorhandenen Daten führen.",
	required: "Erforderlich",
	enum_form_dialog: "Enum-Formulardialog",
	imported_data_preview: "Vorschau der importierten Daten",
	entities_with_same_id_overwritten: "Entitäten mit derselben ID werden überschrieben",
	collection_editor: "Sammlungseditor",
	properties_in_this_group: "Eigenschaften in dieser Gruppe",
	data_property_mapping: "Daten-Eigenschafts-Zuordnung",
	property_edit_view: "Ansicht Eigenschaft bearbeiten",
	all_of_these: "Alle von diesen",
	any_of_these: "Eine von diesen",
	only_admins_edit_roles: "Nur Administratoren können Rollen bearbeiten",
	error_user_not_found: "Benutzer nicht gefunden",
	all_roles: "Alle Rollen",
	role: "Rolle",
	name_of_this_role: "Name dieser Rolle",
	id_of_this_role: "ID dieser Rolle",
	create_entities: "Entitäten erstellen",
	read_entities: "Entitäten lesen",
	update_entities: "Entitäten aktualisieren",
	delete_entities: "Entitäten löschen",
	all_collections: "Alle Sammlungen",
	create_entities_in_collections: "Entitäten in Sammlungen erstellen",
	access_all_data_in_every_collection: "Auf alle Daten in jeder Sammlung zugreifen",
	update_data_in_any_collection: "Daten in einer beliebigen Sammlung aktualisieren",
	delete_data_in_any_collection: "Daten in einer beliebigen Sammlung löschen",
	allow_all_permissions_in_this_collections: "Alle Berechtigungen in diesen Sammlungen zulassen",
	all: "Alle",
	customise_permissions_description: "Sie können die Berechtigungen anpassen, die die mit dieser Rolle verknüpften Benutzer für die Entitäten jeder Sammlung ausführen können",
	create_collections: "Sammlungen erstellen",
	yes: "Ja",
	no: "Nein",
	can_user_create_collections: "Kann der Benutzer Sammlungen erstellen",
	edit_collections: "Sammlungen bearbeiten",
	only_own_collections: "Nur seine/ihre eigenen",
	own: "Eigene",
	can_user_edit_collections: "Kann der Benutzer Sammlungen bearbeiten",
	delete_collections: "Sammlungen löschen",
	can_user_delete_collections: "Kann der Benutzer Sammlungen löschen",
	error_saving_role: "Beim Speichern dieser Rolle ist ein Fehler aufgetreten",
	create_role: "Rolle erstellen",
	update: "Aktualisieren",
	only_admins_change_roles: "Nur Administratoren können Rollen ändern",
	must_be_at_least_one_admin: "Es muss mindestens einen Administrator geben",
	logged_user_not_found: "Angemeldeter Benutzer nicht gefunden",
	user: "Benutzer",
	user_id: "Benutzer-ID",
	name_of_this_user: "Name dieses Benutzers",
	email_of_this_user: "E-Mail dieses Benutzers",
	roles: "Rollen",
	create_user: "Benutzer erstellen",
	users: "Benutzer",
	add_user: "Benutzer hinzufügen",
	add_role: "Rolle hinzufügen",
	is_admin: "Ist Administrator",
	default_permissions: "Standardberechtigungen",
	created_on: "Erstellt am",
	email: "E-Mail",
	id: "ID",
	read: "Lesen",
	column_in_file: "Spalte in der Datei",
	map_to_property: "Zuordnung zur Eigenschaft",
	default_values: "Standardwerte",
	default_values_description: "Sie können einen Standardwert für nicht zugeordnete Spalten und leere Werte auswählen:",
	property: "Eigenschaft",
	default_value: "Standardwert",
	autogenerate_id: "ID automatisch generieren",
	id_column_description: "Spalte, die als ID für jedes Dokument verwendet wird",
	do_not_set_value: "Wert nicht festlegen",
	set_value_to_true: "Wert auf true setzen",
	set_value_to_false: "Wert auf false setzen",
	drag_and_drop_file: "Datei hierher ziehen und ablegen oder zum Hochladen klicken",
	error_saving_data: "Fehler beim Speichern der Daten",
	retry: "Wiederholen",
	saving_data: "Daten speichern",
	entities_saved: "Entitäten gespeichert",
	do_not_close_tab: "Schließen Sie diesen Tab nicht, da sonst der Import unterbrochen wird",
	import: "Importieren",
	import_data: "Daten importieren",
	upload_file_description: "Laden Sie eine CSV-, Excel- oder JSON-Datei hoch und ordnen Sie sie Ihrem vorhandenen Schema zu",
	back: "Zurück",
	next: "Weiter",
	save_data: "Daten speichern",
	use_column_as_id: "Diese Spalte als ID verwenden",
	do_not_import_property: "Diese Eigenschaft nicht importieren",
	entities_will_be_overwritten: "Entitäten mit derselben ID werden überschrieben",
	data_imported_successfully: "Daten erfolgreich importiert",
	export: "Exportieren",
	export_data: "Daten exportieren",
	download_table_csv: "Laden Sie den Inhalt dieser Tabelle als CSV-Datei herunter",
	csv: "CSV",
	json: "JSON",
	dates_as_timestamps: "Daten als Zeitstempel",
	dates_as_strings: "Daten als Zeichenfolgen",
	flatten_arrays: "Arrays glätten",
	download: "Herunterladen",
	large_number_of_documents: "Diese Sammlung hat eine große Anzahl von Dokumenten ({{count}}).",
	too_many_documents_to_export: "Diese Sammlung enthält mehr Dokumente ({{count}}), als der Browser in einer einzigen Datei exportieren kann (Grenze {{limit}}). Filtern Sie sie vor dem Export.",
	include_undefined_values: "Undefinierte Werte einschließen",
	submit: "Einreichen",
	no_filterable_properties: "Keine filterbaren Eigenschaften verfügbar",
	apply_filters: "Filter anwenden",
	filter_presets: "Vorlagen",
	filter_preset_apply: "Vorlage anwenden",
	filter_preset_active: "Aktiv: {{label}}",
	list: "Liste",
	table_view_mode: "Tabelle",
	cards: "Karten",
	board: "Board",
	initialize_kanban_order_desc: "Dadurch werden allen Elementen, die keine haben, fortlaufende Auftragswerte zugewiesen. Die Elemente behalten ihre aktuelle Reihenfolge innerhalb jeder Spalte bei.",
	kanban_view_not_available: "Kanban-Ansicht ist nicht verfügbar",
	kanban_view_requires_enum: "Die Kanban-Ansicht erfordert eine String-Eigenschaft mit Enum-Werten, um Entitäten in Spalten zu gruppieren. Bitte fügen Sie dem Schema Ihrer Sammlung eine Enum-Eigenschaft hinzu, um diese Ansicht zu verwenden.",
	no_enum_values_configured: "Keine Enum-Werte für Eigenschaft \"{{property}}\" konfiguriert",
	items_need_backfill: "Einige Elemente haben keine Reihenfolge. Initialisieren Sie sie, um das Verschieben per Drag-and-Drop zu aktivieren.",
	kanban_order_not_configured: "Die Drag-and-Drop-Sortierung ist nicht konfiguriert. Legen Sie eine Ordnungseigenschaft in den Sammlungseinstellungen fest, um die Kartenpositionen zu speichern.",
	initialize: "Initialisieren",
	confirm_multiple_delete: "Mehrfaches Löschen bestätigen?",
	delete_entity_confirm_title: "Möchten Sie dieses {{entityName}} löschen?",
	add_existing: "Vorhandenes hinzufügen",
	unlink_entity_confirm_title: "Dieses {{entityName}} aus diesem Datensatz entfernen?",
	unlink_entity_confirm_body: "Es bleibt in {{collectionName}} und steht anderen Datensätzen weiterhin zur Verfügung.",
	confirm_multiple_unlink: "diese aus diesem Datensatz entfernen?",
	no_filter: "Kein Filter",
	is_true: "ist wahr",
	is_false: "ist falsch",
	error_uploading_file_size: "Fehler beim Hochladen: Die Datei ist größer als {{maxSize}} Bytes",
	error_uploading_file_type: "Fehler beim Hochladen: Dieser Dateityp wird nicht unterstützt",
	drag_drop_files: "Dateien hierher ziehen oder hier klicken, um sie zu bearbeiten",
	drag_drop_file: "Datei hierher ziehen oder hier klicken, um sie zu bearbeiten",
	multiple_deleted: "Mehrere gelöscht",
	some_entities_deleted: "Einige der Entitäten wurden gelöscht, aber nicht alle",
	error_deleting_entities: "Fehler beim Löschen der Entitäten",
	deleted: "gelöscht",
	selection_options: "Auswahloptionen",
	selection_menu_all_loaded: "Alle auf dieser Seite",
	selection_menu_all_matching: "Alle {{total}} {{collection}}",
	selection_menu_none: "Keine",
	selection_select_all_loaded: "Geladene Zeilen auswählen",
	selection_deselect_all: "Alle abwählen",
	selection_reading_rows: "Zeilen werden gelesen… {{loaded}} von {{total}}",
	selection_reading_rows_unknown: "Zeilen werden gelesen… bisher {{loaded}}",
	selection_deleting_progress: "{{completed}} von {{total}} gelöscht",
	confirm_delete_selection: "{{total}} {{collection}} löschen?",
	confirm_delete_selection_unknown: "Alle Zeilen löschen, die dem aktuellen Filter entsprechen?",
	confirm_delete_selection_body: "Dies kann nicht rückgängig gemacht werden. Jede Zeile wird einzeln gelöscht, das kann eine Weile dauern.",
	export_selection_count: "Die {{total}} ausgewählten Zeilen als {{format}} herunterladen",
	export_selection_all_matching: "Alle {{collection}}, die dem aktuellen Filter entsprechen, herunterladen",
	select_reference: "Referenz auswählen",
	select_references: "Referenzen auswählen",
	account_settings: "Kontoeinstellungen",
	profile: "Profile",
	security: "Sicherheit",
	sessions: "Sessions",
	change_password: "Passwort ändern",
	current_password: "Aktuelles Passwort",
	new_password: "Neues Passwort",
	confirm_password: "Neues Passwort bestätigen",
	password_changed: "Passwort erfolgreich geändert. Sie werden abgemeldet.",
	passwords_dont_match: "Die Passwörter stimmen nicht überein",
	password_too_short: "Das Passwort muss mindestens 8 Zeichen lang sein",
	password_change_not_available: "Für Konten mit externem Anmeldeanbieter ist die Passwortänderung nicht verfügbar.",
	changing_password: "Wird geändert …",
	display_name: "Anzeigename",
	photo_url: "Foto-URL",
	save_profile: "Profil speichern",
	saving: "Saving...",
	no_active_sessions: "Keine aktiven Sitzungen gefunden.",
	revoking: "Revoking...",
	revoke_all_sessions: "Alle Sitzungen widerrufen",
	unknown_device: "Unbekanntes Gerät",
	current: "Current",
	role_id: "Rollen-ID",
	role_name: "Rollenname",
	add_reference: "Referenz hinzufügen",
	add_reference_to: "Referenz zu {{name}} hinzufügen",
	/** RolesView — table & permissions */
	role_deleted_successfully: "Rolle erfolgreich gelöscht",
	error_deleting_role: "Fehler beim Löschen der Rolle",
	collection_permissions: "Berechtigungen der Sammlung",
	collection: "Collection",
	no_collections_configured: "Keine Sammlungen konfiguriert",
	no_security_rules_defined: "Keine Sicherheitsregeln definiert — alle Vorgänge sind uneingeschränkt",
	no_rules: "keine Regeln",
	/** UsersView — snackbar */
	bootstrap_admin_success: "Sie sind jetzt Administrator! Wird aktualisiert …",
	failed_to_bootstrap_admin: "Administrator konnte nicht eingerichtet werden",
	user_deleted_successfully: "Benutzer erfolgreich gelöscht",
	error_deleting_user: "Fehler beim Löschen des Benutzers",
	user_created_successfully: "Benutzer erfolgreich erstellt",
	invitation_sent: "Eine Einladungs-E-Mail wurde an {{email}} gesendet. Über den Link kann das Passwort gesetzt werden.",
	invitation_sent_title: "Einladung gesendet",
	temporary_password: "Temporäres Passwort",
	temporary_password_description: "E-Mail ist nicht konfiguriert. Geben Sie dieses temporäre Passwort sicher an den Benutzer weiter. Es wird nicht erneut angezeigt.",
	temporary_password_email_failed_description: "Die E-Mail konnte nicht zugestellt werden. Geben Sie dieses temporäre Passwort sicher an den Benutzer weiter. Es wird nicht erneut angezeigt.",
	copy_password: "Passwort kopieren",
	password_copied: "Passwort in die Zwischenablage kopiert",
	/** UsersView — pagination & search */
	search_users: "Benutzer suchen",
	created: "Erstellt",
	actions: "Aktionen",
	no_users_found: "Keine Benutzer gefunden",
	reset_password: "Passwort zurücksetzen",
	reset_password_success: "Passwort erfolgreich zurückgesetzt",
	reset_password_confirmation: "Möchten Sie das Passwort dieses Benutzers wirklich zurücksetzen?",
	reset_password_send_email: "E-Mail zum Zurücksetzen des Passworts senden",
	reset_password_set_manually: "Passwort manuell setzen",
	reset_password_set_manually_description: "Das Passwort wird sofort geändert und es wird keine E-Mail gesendet. Geben Sie es sicher an den Benutzer weiter.",
	/** Permission-denied empty states */
	no_permission_to_view_users: "Sie haben keine Berechtigung, Benutzer anzuzeigen",
	no_permission_to_view_roles: "Sie haben keine Berechtigung, Rollen anzuzeigen",
	no_permission_description: "Wenden Sie sich an einen Administrator, wenn Sie Zugang zu diesem Bereich benötigen.",
	error_resetting_password: "Fehler beim Zurücksetzen des Passworts",
	/** Editor table-bubble */
	add_row_before: "Zeile davor einfügen",
	add_row_after: "Zeile danach einfügen",
	delete_row: "Zeile löschen",
	add_column_before: "Spalte davor einfügen",
	add_column_after: "Spalte danach einfügen",
	delete_column: "Spalte löschen",
	delete_table: "Tabelle löschen",
	/** Editor image-bubble */
	alt_text: "Alternativtext",
	title: "Title",
	/** MapFieldBinding */
	a_property_of_this_map_has_error: "Eine Eigenschaft dieser Zuordnung enthält einen Fehler",
	/** AI Collection Generator Popover */
	generate_collection_with_ai: "Sammlung mit KI generieren",
	modify_collection_with_ai: "Sammlung mit KI ändern",
	describe_collection_to_create: "Beschreibe die Sammlung, die du erstellen möchtest.",
	describe_changes_to_make: "Beschreibe die Änderungen, die du an dieser Sammlung vornehmen möchtest.",
	ai_placeholder_create: "z. B. Erstelle eine Produktsammlung mit Name, Preis, Beschreibung und Kategorie...",
	ai_placeholder_modify: "z. B. Füge ein Miniaturbild-Feld mit Speicher hinzu, mache den Preis erforderlich...",
	ai_assist: "KI-Assistent",
	generating: "Generiere...",
	/** Recently extracted strings for collection editor */
	this_is_subcollection_of: "Dies ist eine Untersammlung von",
	use_existing_paths_database: "Verwenden Sie einen der vorhandenen Pfade in Ihrer Datenbank:",
	describe_collection_ai: "Beschreiben Sie Ihre Sammlung für KI:",
	generate_with_ai: "Mit KI generieren",
	create_from_json_config: "Aus JSON-Konfiguration erstellen:",
	paste_json_config: "JSON-Konfiguration einfügen",
	create_collection_from_file_formats: "Eine Sammlung aus einer Datei erstellen (csv, json, xls, xslx...)",
	select_template: "Wählen Sie eine Vorlage:",
	products: "Produkte",
	collection_products_subtitle: "Eine Sammlung von Produkten mit Bildern, Preisen und Lagerbestand",
	collection_users_subtitle: "Eine Sammlung von Benutzern mit E-Mails, Namen und Rollen",
	blog_posts: "Blogbeiträge",
	collection_blog_posts_subtitle: "Eine Sammlung von Blogbeiträgen mit Bildern, Autoren und komplexen Inhalten",
	pages: "Seiten",
	collection_pages_subtitle: "Eine Sammlung von Seiten mit Bildern, Autoren und komplexen Inhalten",
	continue_from_scratch: "Von vorne beginnen",
	/** Admin views config */
	admin_users: "Admin-Benutzer",
	roles_menu: "Rollen",
	project_settings: "Projekteinstellungen",
	by_signing_in_you_agree_to_our: "Mit der Anmeldung stimmen Sie unseren",
	terms_and_conditions: "Allgemeinen Geschäftsbedingungen",
	and_our: "und unserer",
	privacy_policy: "Datenschutzerklärung",
	email_password: "E-Mail/Passwort",
	sign_in_with_google: "Über Google anmelden",
	auth_user_not_found: "Benutzer nicht gefunden",
	auth_wrong_password: "Falsches Passwort. Bitte versuchen Sie es erneut.",
	auth_user_disabled: "Benutzer deaktiviert. Bitte kontaktieren Sie den Support.",
	auth_account_exists_with_different_credential: "Mit dieser E-Mail-Adresse existiert bereits ein Konto. Melden Sie sich so an wie beim letzten Mal.",
	auth_account_exists_sign_in_with_password: "Mit dieser E-Mail-Adresse existiert bereits ein Konto. Melden Sie sich stattdessen mit E-Mail und Passwort an.",
	auth_email_already_in_use: "Die E-Mail wird bereits verwendet",
	auth_google_permissions_required: "Sie müssen zusätzliche Berechtigungen erteilen, um Ihre Google Cloud-Projekte verwalten zu können",
	auth_invalid_email_password: "Bitte geben Sie sowohl E-Mail als auch Passwort ein",
	auth_enter_email_first: "Bitte geben Sie zuerst Ihre E-Mail ein",
	auth_password_reset_sent: "E-Mail zum Zurücksetzen des Passworts gesendet",
	auth_sign_in_account: "Melden Sie sich in Ihrem Konto an",
	auth_create_new_account: "Neues Konto erstellen",
	auth_password: "Passwort",
	auth_reset_password: "Passwort zurücksetzen",
	auth_new_user: "Neuer Benutzer?",
	auth_have_account: "Haben Sie bereits ein Konto?",
	auth_sign_in: "Anmelden",
	auth_sign_up: "Registrieren",
	auto_setup_collections_button: "Sammlungen automatisch einrichten",
	auto_setup_collections_title: "Sammlungen automatisch einrichten?",
	auto_setup_collections_desc: "Dadurch werden automatisch Sammlungskonfigurationen für Sammlungen erstellt, die noch <b>NICHT</b> zugeordnet sind.",
	this_can_take_a_minute: "Dies kann ein bis zwei Minuten dauern",
	no_collections_found_to_setup: "Keine einzurichtenden Sammlungen gefunden",
	collections_have_been_setup: "Sammlungen wurden automatisch eingerichtet",
	error_setting_up_collections: "Fehler beim automatischen Einrichten der Sammlungen",
	admin_panel_ready_bring_data: "Ihr Admin-Panel ist bereit. Bringen Sie Ihre Daten ein.",
	admin_panel_ready_get_started: "Ihr Admin-Panel ist bereit. So können Sie loslegen.",
	auto_detect_collections: "Sammlungen automatisch erkennen",
	auto_detect_collections_desc: "Lassen Sie die KI Ihre Datenbank scannen und Sammlungs-Schemata automatisch generieren.",
	create_a_collection: "Eine Sammlung erstellen",
	create_collection_desc: "Definieren Sie Ihre erste Sammlung manuell mit dem visuellen Editor.",
	read_the_docs: "Dokumentation lesen",
	read_the_docs_desc: "Erfahren Sie, wie Sie Felder, Ansichten, Aktionen und mehr anpassen können.",
	explore_docs: "Dokumentation erkunden",
	want_to_customize_with_code: "Möchten Sie mit Code anpassen? Führen Sie aus:",
	to_scaffold_a_local_project: "um ein lokales Projekt zu erstellen",
	validation: "Validierung",
	unique: "Einzigartig",
	required_message: "Pflichtfeld-Nachricht",
	required_tooltip: "Sie können diese Entität nicht speichern, wenn dieser Wert nicht gesetzt ist",
	unique_tooltip: "Es darf nicht mehrere Entitäten mit demselben Wert geben",
	lowercase: "Kleinbuchstaben",
	uppercase: "Großbuchstaben",
	trim: "Leerzeichen entfernen",
	exact_length: "Exakte Länge",
	min_length: "Mindestlänge",
	max_length: "Maximale Länge",
	matches_regex: "Entspricht Regex",
	not_valid_regexp: "Kein gültiger regulärer Ausdruck",
	regex_helper: "z.B. /^\\d+$/ nur für Ziffern",
	min_value: "Minimalwert",
	max_value: "Maximalwert",
	less_than: "Kleiner als",
	more_than: "Größer als",
	positive_value: "Positiver Wert",
	negative_value: "Negativer Wert",
	integer_value: "Ganzzahl",
	invalid_regular_expression: "Ungültiger regulärer Ausdruck",
	must_specify_target_collection: "Sie müssen eine Ziel-Sammlung für das Feld angeben",
	need_specify_repeat_field: "Sie müssen ein Wiederholungsfeld angeben",
	need_specify_block_properties: "Sie müssen die Eigenschaften dieses Blocks angeben",
	incomplete_condition: "Unvollständige Bedingung – bitte wählen Sie ein Feld",
	field_name: "Feldname",
	kanban_column_property: "Kanban-Spalten-Eigenschaft",
	select_a_property: "Eigenschaft auswählen",
	kanban_property_not_found: "Eigenschaft \"{{property}}\" existiert nicht oder ist keine Enum-String-Eigenschaft. Bitte wählen Sie eine gültige Eigenschaft oder löschen Sie die Auswahl.",
	no_enum_string_properties: "Keine Enum-String-Eigenschaften gefunden. Fügen Sie eine String-Eigenschaft mit enumValues hinzu, um die Kanban-Ansicht zu verwenden.",
	kanban_column_description: "Wählen Sie eine String-Eigenschaft mit Enum-Werten, um Entitäten in Spalten zu gruppieren",
	create_property: "+ Eigenschaft \"{{property}}\" erstellen",
	order_property: "Reihenfolge-Eigenschaft",
	order_property_not_found: "Eigenschaft \"{{property}}\" existiert nicht oder ist keine Zahlen-Eigenschaft. Bitte wählen Sie eine gültige Eigenschaft oder löschen Sie die Auswahl.",
	no_number_properties: "Keine Zahlen-Eigenschaften gefunden. Fügen Sie eine Zahlen-Eigenschaft hinzu, um die Sortierung zu aktivieren.",
	order_property_description: "Wählen Sie eine Zahlen-Eigenschaft, um die Reihenfolge der Elemente beizubehalten",
	display_settings: "Anzeigeeinstellungen",
	default_row_size: "Standard-Zeilengröße",
	side_dialog_width: "Seitendialog-Breite",
	side_dialog_width_description: "Optional die Breite (in Pixeln) des Seitendialogs für Entitäten definieren. Standard ist 768px",
	inline_editing_enabled: "Daten können direkt in der Tabellenansicht bearbeitet werden",
	inline_editing_disabled: "Daten können nur in der Formularansicht bearbeitet werden",
	inline_editing_description: "Ermöglicht das direkte Bearbeiten von Daten in der Tabellenansicht, ohne die Formularansicht zu öffnen.",
	include_json_view: "JSON-Ansicht einbeziehen",
	no_json_view: "JSON-Ansicht nicht einbeziehen",
	json_view_description: "Die JSON-Darstellung des Dokuments einbeziehen.",
	not_found_suffix: "nicht gefunden",
	editor_text: "Text",
	editor_text_description: "Fangen Sie einfach an, Klartext zu tippen.",
	editor_heading_1: "Überschrift 1",
	editor_heading_1_description: "Große Bereichsüberschrift.",
	editor_heading_2: "Überschrift 2",
	editor_heading_2_description: "Mittlere Bereichsüberschrift.",
	editor_heading_3: "Überschrift 3",
	editor_heading_3_description: "Kleine Bereichsüberschrift.",
	editor_todo_list: "To-Do Liste",
	editor_todo_list_description: "Verfolgen Sie Aufgaben mit einer To-Do Liste.",
	editor_bullet_list: "Aufzählungsliste",
	editor_bullet_list_description: "Erstellen Sie eine einfache Aufzählungsliste.",
	editor_numbered_list: "Nummerierte Liste",
	editor_numbered_list_description: "Erstellen Sie eine nummerierte Liste.",
	editor_quote: "Zitat",
	editor_quote_description: "Erfassen Sie ein Zitat.",
	editor_code: "Code",
	editor_code_description: "Erfassen Sie einen Code-Ausschnitt.",
	editor_image: "Bild",
	editor_image_description: "Laden Sie ein Bild hoch.",
	editor_multiple: "Mehrere",
	editor_link: "Link",
	editor_save: "Speichern",
	editor_cancel: "Abbrechen",
	editor_remove_link: "Link entfernen",
	editor_paste_or_type_link: "Link einfügen oder eintippen",
	editor_open_in_new_window: "In neuem Fenster öffnen",
	editor_bold: "Fett",
	editor_italic: "Kursiv",
	editor_underline: "Unterstrichen",
	editor_strikethrough: "Durchgestrichen",
	editor_autocomplete: "Autovervollständigung",
	editor_autocomplete_description: "Fügen Sie Text basierend auf dem Kontext hinzu.",
	text_search_dialog_title: "Textsuche aktivieren",
	text_search_local_not_recommended: "Die lokale Textsuche wird für große Sammlungen nicht empfohlen.",
	text_search_local_fetch_warning: "Beachten Sie, dass die lokale Textsuche alle Dokumente Ihrer Sammlung abrufen und im Browser speichern muss. Dies kann bei großen Sammlungen ineffizient sein und zusätzliche Kosten verursachen.",
	text_search_external_suggestion: "Für größere Sammlungen wird empfohlen, eine externe Suchmaschine wie Algolia oder Elastic Search zu verwenden und einen Suchdelegaten in Ihrer Konfiguration zuzuweisen.",
	text_search_local_description: "Die lokale Textsuche ist der einfachste Weg, die Textsuche in Ihrer Sammlung zu aktivieren. Sie lädt alle Dokumente der Sammlung im Browser und führt die Suche lokal durch. Dies ist die empfohlene Option für kleine Sammlungen.",
	text_search_own_implementation: "Sie haben Ihren eigenen Textsuch-Controller implementiert. Sie können die Textsuche für Ihre Sammlung aktivieren.",
	text_search_enable_for_collection: "Für diese Sammlung aktivieren",
	text_search_enable_for_project: "Für das Projekt aktivieren",
	text_search_enabled_snackbar: "Lokale Textsuche aktiviert",
	settings_heading: "Einstellungen",
	settings_project_name: "Projektname",
	settings_default_language: "Standardsprache",
	settings_default_language_caption: "Wählen Sie die Basissprache für dieses Projekt. Benutzer können diese Einstellung in ihren persönlichen Einstellungen überschreiben.",
	settings_enable_local_text_search: "Lokale Textsuche aktivieren",
	settings_local_text_search_caption: "Aktivieren Sie die lokale Textsuche für alle Sammlungen. Dadurch können Sie Textfelder in Ihren Sammlungen über die Rebase-Suchleiste durchsuchen. Beachten Sie, dass diese Funktion zu höheren Lesezahlen führen kann, da alle Textfelder in Ihren Sammlungen indexiert werden.",
	settings_doc_history_all_collections: "Dokumentverlauf für alle Sammlungen aktiviert",
	settings_doc_history_caption: "Wenn aktiviert, wird der Verlauf standardmäßig für alle Sammlungen aktiviert. Sie können diese Einstellung für jede Sammlung überschreiben. Der Verlauf wird in der Untersammlung __history jedes Dokuments gespeichert.",
	settings_theme: "Design",
	settings_primary_color: "Primärfarbe",
	settings_secondary_color: "Sekundärfarbe",
	settings_sample_theme_components: "Beispiel-Designkomponenten",
	settings_drag_drop_logo: "Ziehen Sie Ihr Logo hierher",
	settings_security_rules: "Sicherheitsregeln",
	settings_security_rules_description: "Rebase verwendet Firebase-Sicherheitsregeln, um den Zugriff auf Daten einzuschränken. Beim Erstellen eines neuen Benutzers über Rebase wird dem Benutzer im Client-Projekt das benutzerdefinierte Attribut rebaseUser hinzugefügt. Durch Hinzufügen der folgenden Sicherheitsregeln stellen Sie sicher, dass Rebase-Benutzer über Rebase auf Daten zugreifen können.",
	settings_security_rules_add_domain: "Denken Sie daran, die Domain {{domain}} zu den erlaubten Domains Ihres Anbieters hinzuzufügen",
	settings_security_rules_caption: "Diese Regeln beschränken den Datenzugriff nur auf Rebase-Benutzer, setzen die Berechtigungen jedoch nicht auf Datenbankebene durch. Die Berechtigungen werden im Frontend durchgesetzt, was für die meisten Projekte ausreichend ist. Wenn Sie die Berechtigungen auf Datenbankebene durchsetzen müssen, können Sie diese Sicherheitsregeln entsprechend anpassen. Die einem Benutzer zugewiesenen Rollen werden als benutzerdefinierte Attribute im Firebase-Authentifizierungstoken gespeichert, sodass Sie sie in Ihren Sicherheitsregeln verwenden können.",
	studio_sql_executing_query: "ABFRAGE WIRD AUSGEFÜHRT …",
	studio_sql_query_error: "Abfragefehler",
	studio_sql_run_query_placeholder: "Führen Sie eine Abfrage aus, um Ergebnisse zu sehen",
	studio_sql_visual_execution_plan: "Visueller Ausführungsplan",
	studio_sql_success: "SUCCESS",
	studio_sql_no_results: "Die Abfrage wurde ausgeführt, lieferte aber keine Ergebnisse.",
	studio_sql_rows: "Rows:",
	studio_sql_time: "Time:",
	studio_sql_copy_markdown: "Als Markdown kopieren",
	studio_sql_export_json: "JSON exportieren",
	studio_sql_export_csv: "CSV exportieren",
	studio_sql_format_sql: "SQL formatieren",
	studio_sql_explain: "Explain",
	studio_sql_limit_1000: "Limit 1000",
	studio_sql_remove_from_favorites: "Aus Favoriten entfernen",
	studio_sql_add_to_favorites: "Zu Favoriten hinzufügen",
	studio_sql_save: "Save",
	studio_sql_run: "Run",
	studio_sql_database: "Database",
	studio_sql_role: "Role",
	studio_sql_admin: "(Admin)",
	studio_sql_select_db: "Datenbank wählen",
	studio_sql_query_results: "Abfrageergebnisse",
	studio_sql_save_snippet: "SQL-Snippet speichern",
	studio_sql_snippet_name: "Name des Snippets",
	studio_sql_snippet_name_placeholder: "z. B. Alle Benutzer abrufen",
	studio_sql_snippet_saved_local: "Dies wird in Ihrem lokalen Speicher abgelegt.",
	studio_sql_cancel: "Cancel",
	studio_sql_dangerous_operation: "Gefährlicher Vorgang erkannt",
	studio_sql_dangerous_operation_body: "Die Abfrage, die Sie ausführen möchten, enthält potenziell zerstörerische Vorgänge (DELETE, DROP, TRUNCATE oder UPDATE ohne WHERE). Möchten Sie wirklich fortfahren?",
	studio_sql_snippet_saved: "Snippet „{{name}}“ gespeichert.",
	studio_sql_markdown_copied: "Ergebnisse als Markdown kopiert!",
	studio_sql_markdown_copy_failed: "Markdown konnte nicht in die Zwischenablage kopiert werden.",
	studio_sql_row_updated: "Zeile erfolgreich aktualisiert.",
	studio_sql_cannot_edit_missing_query: "Zeilen können nicht bearbeitet werden: Die zugrunde liegende Abfrage fehlt.",
	studio_sql_cannot_resolve_table: "Tabelle bzw. Primärschlüssel konnte nicht ermittelt werden",
	studio_sql_missing_pk: "In der Zeile fehlen die Primärschlüsselspalten: {{columns}}. Eine sichere Aktualisierung ist nicht möglich.",
	studio_sql_update_failed: "Aktualisierung fehlgeschlagen: {{message}}",
	studio_sql_execution_not_supported: "Die aktuelle Datenquelle unterstützt keine SQL-Ausführung.",
	studio_sql_error_executing: "Beim Ausführen der Abfrage ist ein Fehler aufgetreten.",
	studio_sql_error_explaining: "Beim Erklären der Abfrage ist ein Fehler aufgetreten.",
	studio_sql_save_first_to_favorite: "Bitte speichern Sie das Snippet, bevor Sie es zu den Favoriten hinzufügen.",
	studio_sql_collections_label: "Collections:",
	studio_sql_admin_collections_tooltip: "Tables in this query that are mapped as admin collections",
	studio_sql_edit_entity: "Edit {{name}} #{{id}}",
	studio_sql_sql_not_supported: "Diese Datenquelle unterstützt keine SQL-Ausführung",
	studio_sql_fetch_error: "Verbindungsoptionen konnten nicht geladen werden: {{message}}",
	studio_sql_unexpected_format: "Unerwartetes Datenformat: {{type}}",
	studio_sql_no_tables: "In dieser Datenbank wurden keine Tabellen gefunden.",
	studio_sql_schema_fetch_error: "Schema konnte nicht geladen werden: {{message}}",
	studio_sql_sidebar_snippets: "Snippets",
	studio_sql_sidebar_history: "History",
	studio_sql_sidebar_schema: "Schema",
	studio_sql_sidebar_no_snippets: "Keine gespeicherten Snippets",
	studio_sql_sidebar_save_snippet_hint: "Speichern Sie eine Abfrage, um sie hier wiederzufinden.",
	studio_sql_sidebar_no_history: "Kein Verlauf",
	studio_sql_sidebar_history_hint: "Ausgeführte Abfragen erscheinen hier.",
	studio_sql_sidebar_delete_snippet: "Snippet löschen",
	studio_sql_close_tab: "Diesen Abfrage-Tab schließen",
	studio_sql_new_tab: "Neuer Abfrage-Tab",
	studio_schema_tables: "Tables",
	studio_schema_loading: "Schema wird geladen …",
	studio_schema_no_tables: "Keine Tabellen gefunden",
	studio_schema_retry: "Retry",
	studio_schema_primary_key: "Primärschlüssel",
	studio_schema_select_all: "SELECT * FROM",
	studio_schema_insert_into: "INSERT INTO",
	studio_schema_update: "UPDATE",
	studio_schema_delete_from: "DELETE FROM",
	studio_schema_columns: "{{count}} Spalten",
	studio_rls_title: "Sicherheit auf Zeilenebene",
	studio_rls_description: "RLS-Richtlinien für Ihre Tabellen verwalten",
	studio_rls_enabled: "RLS aktiviert",
	studio_rls_disabled: "RLS deaktiviert",
	studio_rls_no_rls: "Ohne RLS",
	studio_rls_enable_rls: "RLS aktivieren",
	studio_rls_disable_rls: "RLS deaktivieren",
	studio_rls_create_policy: "Richtlinie erstellen",
	studio_rls_policies: "Policies",
	studio_rls_no_policies: "Keine Richtlinien definiert",
	studio_rls_no_policies_desc: "Für diese Tabelle gibt es keine RLS-Richtlinien. Der Zugriff auf alle Zeilen richtet sich nach dem RLS-Status.",
	studio_rls_add_first_policy: "Erste Richtlinie hinzufügen",
	studio_rls_force_rls: "RLS erzwingen",
	studio_rls_force_rls_desc: "Wenn aktiviert, gelten die RLS-Richtlinien auch für den Eigentümer der Tabelle.",
	studio_rls_enable_force_rls: "Erzwungene RLS aktivieren",
	studio_rls_disable_force_rls: "Erzwungene RLS deaktivieren",
	studio_rls_edit: "Edit",
	studio_rls_delete: "Delete",
	studio_rls_confirm_delete_title: "Richtlinie löschen",
	studio_rls_confirm_delete_body: "Möchten Sie diese Richtlinie wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.",
	studio_rls_confirm_enable_title: "Sicherheit auf Zeilenebene aktivieren",
	studio_rls_confirm_enable_body: "Mit aktivierter RLS wird der Zugriff auf Zeilen dieser Tabelle durch Ihre Richtlinien eingeschränkt. Stellen Sie sicher, dass passende Richtlinien vorhanden sind.",
	studio_rls_confirm_disable_title: "Sicherheit auf Zeilenebene deaktivieren",
	studio_rls_confirm_disable_body: "Ohne RLS sind alle Zeilen für alle Rollen zugänglich. Möchten Sie wirklich fortfahren?",
	studio_rls_select_table: "Tabelle auswählen",
	studio_rls_no_tables: "Keine Tabellen verfügbar",
	studio_rls_error: "Fehler beim Laden der Richtlinien",
	studio_rls_retry: "Retry",
	studio_rls_loading: "Richtlinien werden geladen …",
	studio_policy_edit: "Richtlinie bearbeiten",
	studio_policy_create: "Neue Richtlinie erstellen",
	studio_policy_defining_rules: "Zugriffsregeln festlegen für",
	studio_policy_cancel: "Cancel",
	studio_policy_save: "Richtlinie speichern",
	studio_policy_template: "Mit einer Vorlage beginnen (optional)",
	studio_policy_select_template: "Vorlage auswählen …",
	studio_policy_name: "Name der Richtlinie",
	studio_policy_name_placeholder: "Geben Sie der Richtlinie einen aussagekräftigen Namen",
	studio_policy_behavior: "Verhalten der Richtlinie",
	studio_policy_permissive: "Permissive",
	studio_policy_permissive_desc: "Wird mit „OR“ kombiniert",
	studio_policy_restrictive: "Restrictive",
	studio_policy_restrictive_desc: "Wird mit „AND“ kombiniert",
	studio_policy_command: "Befehl der Richtlinie",
	studio_policy_target_roles: "Zielrollen",
	studio_policy_roles_placeholder: "Rollen auswählen (Standard: public)",
	studio_policy_using_expr: "USING-Ausdruck",
	studio_policy_using_expr_desc: "Gilt für vorhandene Zeilen (SELECT, UPDATE, DELETE)",
	studio_policy_check_expr: "WITH CHECK-Ausdruck",
	studio_policy_check_expr_desc: "Gilt für neue oder geänderte Zeilen (INSERT, UPDATE)",
	studio_policy_help_title: "Leitfaden zur Sicherheit auf Zeilenebene (RLS)",
	studio_policy_help_intro: "Mit der Sicherheit auf Zeilenebene (RLS) legen Sie fest, welche Zeilen ein Benutzer je nach Anmeldestatus oder Rollen lesen oder ändern darf.",
	studio_policy_help_step1_title: "1. Vorlage auswählen",
	studio_policy_help_step1_desc: "Wenn RLS neu für Sie ist, beginnen Sie am einfachsten mit einer vordefinierten Vorlage aus der Liste. Sie richtet die Richtlinie für häufige Fälle wie „Benutzer dürfen ihre eigenen Zeilen ändern“ automatisch ein.",
	studio_policy_help_step2_title: "2. Zielrollen",
	studio_policy_help_step2_desc: "Wählen Sie, für welche Benutzer die Richtlinie gilt:",
	studio_policy_help_role_public: "Gilt für alle (angemeldet oder nicht).",
	studio_policy_help_role_authenticated: "Gilt nur für angemeldete Benutzer.",
	studio_policy_help_role_anon: "Gilt nur für Benutzer, die NICHT angemeldet sind.",
	studio_policy_help_step3_title: "3. USING-Ausdruck",
	studio_policy_help_step3_desc: "Eine SQL-Bedingung, die wahr sein muss, damit vorhandene Zeilen sichtbar oder änderbar sind (SELECT, UPDATE, DELETE).",
	studio_policy_help_step3_example: "Stellt sicher, dass Benutzer nur die Zeilen sehen, die ihnen gehören.",
	studio_policy_help_step4_title: "4. WITH CHECK-Ausdruck",
	studio_policy_help_step4_desc: "Eine SQL-Bedingung, die wahr sein muss, damit neue oder geänderte Zeilen gespeichert werden (INSERT, UPDATE).",
	studio_policy_help_step4_example: "Meist identisch mit dem USING-Ausdruck, wenn Benutzer nur ihre eigenen Daten anlegen oder bearbeiten dürfen.",
	studio_policy_help_auth_vars_title: "Rebase-Auth-Variablen",
	studio_policy_help_auth_vars_desc: "Rebase stellt native Hilfsfunktionen bereit, die Sie in Ihren Ausdrücken verwenden können:",
	studio_policy_help_auth_uid: "Gibt die ID des aktuellen Benutzers als Text zurück. Beispiel:",
	studio_policy_help_auth_jwt: "Gibt die vollständige JWT-Nutzlast als JSONB zurück, sodass Sie eigene Claims prüfen können. Beispiel:",
	studio_policy_help_auth_roles: "Gibt die Rollen-IDs des Benutzers als kommagetrennten Text zurück. Am besten verwendet mit:",
	studio_policy_help_docs_cta: "Mehr über Postgres-Richtlinien erfahren Sie in der offiziellen Dokumentation.",
	studio_policy_help_read_docs: "Dokumentation lesen",
	studio_policy_help_got_it: "Verstanden!",
	studio_add_kanban_column: "Spalte hinzufügen",
	studio_add_kanban_column_title: "Kanban-Spalte hinzufügen",
	studio_add_kanban_column_name: "Spaltenname",
	studio_add_kanban_column_name_placeholder: "Name der neuen Spalte",
	studio_add_kanban_column_add: "Add",
	studio_add_kanban_column_cancel: "Cancel",
	studio_collection_view_sql: "SQL-Editor",
	studio_collection_view_admin: "Admin-Ansicht",
	studio_editor_collection_tooltip: "Schema der Sammlung bearbeiten",
	studio_editor_collection_no_permission: "Sie haben keine Berechtigung, diese Sammlung zu bearbeiten",
	studio_editor_collection_start_tooltip: "Pfad kopieren oder Schema bearbeiten",
	studio_editor_collection_start_no_permission: "Sie haben keine Berechtigung, diese Sammlung zu ändern",
	studio_editor_collection_start_copied: "Copied!",
	studio_editor_entity_tooltip: "Schema des Eintrags bearbeiten",
	studio_editor_entity_no_permission: "Sie haben keine Berechtigung, diesen Eintrag zu bearbeiten",
	studio_home_edit_collection: "Sammlung bearbeiten",
	studio_home_delete_collection: "Sammlung löschen",
	studio_home_confirm_delete_title: "Sammlung löschen?",
	studio_home_confirm_delete_body: "Möchten Sie die Sammlung „{{name}}“ wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.",
	studio_kanban_setup: "Kanban einrichten",
	studio_missing_reference: "Fehlende Referenz: {{path}}",
	studio_missing_reference_create: "Create",
	studio_new_collection: "Neue Sammlung",
	studio_new_collection_card: "Neue Sammlung",
	studio_new_collection_no_permission: "Sie haben keine Berechtigung, Sammlungen zu erstellen",
	studio_property_add_column: "Neue Eigenschaft hinzufügen",
	studio_property_add_column_no_permission: "Sie haben keine Berechtigung, neue Eigenschaften hinzuzufügen",
	studio_add_kanban_column_desc: "Fügt der Eigenschaft „{{property}}“ eine neue Option hinzu. Dadurch entsteht eine neue Spalte im Kanban-Board.",
	studio_add_kanban_column_adding: "Adding...",
	studio_collection_view_edit: "Edit",
	studio_editor_collection_disabled: "Das Bearbeiten von Sammlungen ist deaktiviert",
	studio_editor_collection_edit: "Sammlung bearbeiten",
	studio_editor_entity_save_first: "Sie müssen das Dokument speichern, bevor Sie das Schema ändern",
	studio_editor_entity_edit_schema: "Schema dieses Formulars bearbeiten",
	studio_editor_collection_start_save_filter: "Standardfilter und -sortierung speichern",
	studio_editor_collection_start_clear_filter: "Standardfilter und -sortierung entfernen",
	studio_editor_collection_start_reset_filter: "Auf Standardfilter und -sortierung zurücksetzen",
	studio_editor_collection_start_saved: "Standardkonfiguration gespeichert",
	studio_home_duplicate_collection: "Duplicate",
	studio_home_delete: "Delete",
	studio_home_confirm_delete_no_data: "Es werden keine Daten gelöscht, nur die Sammlung im Adminbereich",
	studio_home_collection_deleted: "Sammlung gelöscht",
	studio_kanban_configure: "Kanban konfigurieren",
	studio_missing_reference_error: "Keine Sammlung für den Pfad: {{path}}",
	studio_new_collection_add: "Neue Sammlung hinzufügen",
	db_column_type: "Datenbank-Spaltentyp",
	primary_key_unique_id: "Primärschlüssel / Eindeutige ID",
	spread_children_as_columns: "Kinder als Spalten aufteilen",
	mode: "Modus",
	timezone: "Zeitzone",
	target_collection: "Zielsammlung",
	storage_file_name: "Dateiname",
	storage_path: "Speicherpfad",
	storage_max_size: "Maximale Größe (in Bytes)",
	storage_resize_mode: "Größenanpassungsmodus",
	storage_output_format: "Ausgabeformat",
	storage_max_width: "Maximale Breite (px)",
	storage_max_height: "Maximale Höhe (px)",
	storage_quality: "Qualität (0-100)",
	storage_file_upload_config: "Datei-Upload-Konfiguration",
	storage_image_resize_config: "Bildgrößenanpassung",
	storage_all_file_types: "Alle Dateitypen erlaubt",
	storage_allowed_file_types: "Erlaubte Dateitypen",
	storage_include_bucket_url: "Bucket-URL (s3://...) im gespeicherten Wert einschließen",
	storage_save_url: "URL anstelle des Speicherpfads speichern",
	datetime_automatic_value: "Automatischer Wert",
	markdown_paste_behavior: "Einfügeverhalten",
	entity_saved: "Gespeichert",
	more_actions: "Weitere Aktionen",
	record_history: "Verlauf",
	inspect_record: "Datensatz inspizieren",
	inspect: "Inspizieren",
	close_inspector: "Inspektor schließen",
	open_in_live_site: "Auf der Live-Website öffnen",
	history_revert: "Auf diese Version zurücksetzen",
	history_revert_title: "Daten auf diese Version zurücksetzen?",
	history_reverted: "Auf die gewählte Version zurückgesetzt",
	history_revert_error: "Fehler beim Zurücksetzen des Datensatzes",
	history_revert_dirty: "Bitte speichere oder verwirf deine Änderungen vor dem Zurücksetzen",
	history_empty: "Kein Verlauf vorhanden",
	history_empty_description: "Beim Speichern eines Datensatzes wird eine neue Version erstellt und im Verlauf abgelegt.",
	history_new_entity: "Der Verlauf ist nur für vorhandene Datensätze verfügbar",
	history_no_more: "Kein weiterer Verlauf",
	studio_group_views: "Ansichten",
	studio_group_admin: "Verwaltung",
	studio_group_settings: "Einstellungen",
	studio_group_database: "Datenbank",
	studio_group_compute: "Rechenleistung",
	studio_group_storage: "Speicher",
	studio_group_api: "API",
	studio_group_access_control: "Zugriffskontrolle",
	studio_tool_sql: "SQL-Konsole",
	studio_tool_sql_description: "SQL-Abfragen ausführen",
	studio_tool_js: "JS-Konsole",
	studio_tool_js_description: "JavaScript ausführen",
	studio_tool_rls: "RLS-Richtlinien",
	studio_tool_rls_description: "Sicherheit auf Zeilenebene",
	studio_tool_storage: "Speicher",
	studio_tool_storage_description: "Dateien im Speicher verwalten",
	studio_tool_cron: "Cron-Jobs",
	studio_tool_cron_description: "Geplante Aufgaben verwalten",
	studio_tool_schema_visualizer: "Schema-Visualisierung",
	studio_tool_schema_visualizer_description: "Interaktives ER-Diagramm der Datenbank",
	studio_tool_branches: "Branches",
	studio_tool_branches_description: "Datenbank-Branches erstellen und verwalten",
	studio_tool_backups: "Sicherungen",
	studio_tool_backups_description: "Datenbanksicherungen herunterladen",
	studio_tool_api: "API-Explorer",
	studio_tool_api_description: "Interaktive API-Dokumentation und Tests",
	studio_tool_logs: "Log-Explorer",
	studio_tool_logs_description: "System- und Abfrage-Logs in Echtzeit",
	studio_tool_api_keys: "API-Schlüssel",
	studio_tool_api_keys_description: "Bereichsbezogene API-Schlüssel erstellen und verwalten",
	studio_backups_denied_title: "Du darfst die Sicherungen dieses Projekts nicht auflisten",
	studio_backups_denied_hint: "Mit dem Projekt ist alles in Ordnung. Das Auflisten von Sicherungen erfordert eine Admin-Verbindung, und das angemeldete Konto wurde abgelehnt.",
	studio_backups_read_failed: "Die Sicherungen dieses Projekts konnten nicht gelesen werden",
	studio_backups_downloading: "Wird heruntergeladen…",
	studio_backups_roles_file: "Rollendatei",
	studio_backups_roles_file_hint: "Die .globals.sql, die mit dieser Sicherung gespeichert wurde. Sie legt die Datenbankrollen neu an, auf die sich die Berechtigungen und die Sicherheit auf Zeilenebene des Dumps beziehen, und `rebase db restore` liest sie neben der .dump – bewahre beide Dateien zusammen auf.",
	studio_backups_no_roles_file: "Keine Rollendatei",
	studio_backups_no_roles_file_hint: "Zu dieser Sicherung wurde keine .globals.sql gespeichert. Eine Wiederherstellung in ein neues Postgres bricht bei der ersten Berechtigung für eine Datenbankrolle ab, die dort nicht existiert.",
	studio_cron_denied_title: "Du darfst die Cron-Jobs dieses Projekts nicht auflisten",
	studio_cron_denied_hint: "Mit dem Projekt ist alles in Ordnung. Die Cron-API ist nur für Admins, und das angemeldete Konto wurde abgelehnt.",
	studio_cron_read_failed: "Die Cron-Jobs dieses Projekts konnten nicht gelesen werden",
	studio_api_keys_denied_title: "Du darfst die API-Schlüssel dieses Projekts nicht auflisten",
	studio_api_keys_denied_hint: "Mit dem Projekt ist alles in Ordnung. Das Verwalten von API-Schlüsseln ist eine Admin-Aktion, und das angemeldete Konto wurde abgelehnt.",
	studio_api_keys_read_failed: "Die API-Schlüssel dieses Projekts konnten nicht gelesen werden",
	studio_api_keys_empty_title: "Noch keine API-Schlüssel",
	studio_api_keys_empty_hint: "Erstellen Sie einen Schlüssel, um die Authentifizierung zwischen Diensten zu ermöglichen",
	studio_api_keys_revoke: "Widerrufen",
	studio_api_keys_select_hint: "Wählen Sie einen API-Schlüssel, um Details anzuzeigen",
	studio_api_keys_permissions: "Berechtigungen",
	studio_api_keys_no_permissions: "Keine Berechtigungen konfiguriert",
	studio_api_keys_revoke_confirmation: "Widerruf bestätigen",
	studio_api_keys_name_label: "Name:",
	studio_api_keys_access_label: "Zugriff:",
	studio_api_keys_admin_granted: "Admin-Rolle gewährt",
	studio_api_keys_admin_granted_hint: "die Admin-Routen und die default_admin-Richtlinien",
	studio_api_keys_done: "Fertig",
	studio_api_keys_status_active: "Aktiv",
	studio_api_keys_status_expired: "Abgelaufen",
	studio_api_keys_status_revoked: "Widerrufen",
	studio_api_keys_stat_status: "Status",
	studio_api_keys_stat_last_used: "Zuletzt verwendet",
	studio_api_keys_stat_expires: "Läuft ab",
	studio_api_keys_never: "Nie",
	studio_api_keys_role_service: "Dienst",
	studio_api_keys_stat_rate_limit: "Ratenlimit",
	studio_api_keys_rate_limit_default: "Standard (1000/15 Min.)",
	studio_api_keys_stat_created_by: "Erstellt von",
	studio_api_keys_copy_failed: "Kopieren fehlgeschlagen",
	studio_branches_denied_title: "Du darfst die Branches dieses Projekts nicht auflisten",
	studio_branches_denied_hint: "Mit dem Projekt ist alles in Ordnung. Branching läuft über die Admin-Verbindung, und das angemeldete Konto wurde abgelehnt.",
	studio_branches_read_failed: "Die Branches dieses Projekts konnten nicht gelesen werden",
	studio_schema_no_collections: "Keine Collections deklariert",
	studio_schema_no_collections_body: "Es gibt noch nichts zu zeichnen. Lege eine Collection unter config/collections/ an oder öffne „Collections bearbeiten“, um hier eine hinzuzufügen.",
	studio_schema_introspect_hint: "Bei einer Datenbank, die bereits Tabellen hat, schreibt rebase schema introspect die Collection-Dateien für dich.",
	studio_rls_enable_confirm_title: "Sicherheit auf Zeilenebene aktivieren?",
	studio_rls_enable_confirm_body: "Bis eine Richtlinie es erlaubt, ist jede Zeile von {{table}} unlesbar. Postgres verweigert standardmäßig.",
	studio_rls_disable_confirm_title: "Sicherheit auf Zeilenebene deaktivieren?",
	studio_rls_disable_confirm_body: "Alle Richtlinien für {{table}} werden nicht mehr durchgesetzt. Wer die Tabelle erreicht, kann jede Zeile lesen und schreiben.",
	studio_rls_drop_policy_title: "Diese Richtlinie löschen?",
	studio_rls_drop_policy_body: "{{policy}} wird in der Datenbank aus {{table}} entfernt. In den Code wird nichts geschrieben, ein Deploy kann sie also zurückbringen.",
	studio_rls_unapplied: "Nicht angewendet",
	studio_rls_unapplied_tooltip: "Im Code deklariert, aber noch nicht in der Datenbank. Hier speichern, um die Änderung zu planen und anzuwenden, oder `rebase db push` ausführen.",
	studio_branches_unavailable_title: "Datenbank-Branching ist nicht verfügbar",
	studio_branches_unavailable_body: "Branching benötigt ein Postgres-Backend mit einer Admin-Verbindung. Setze ADMIN_CONNECTION_STRING in der Umgebung des Backends.",
	studio_backups_not_configured_title: "Sicherungen sind nicht konfiguriert",
	studio_backups_not_configured_body: "Setze BACKUP_DESTINATION auf einen lokalen Pfad oder eine s3:// bzw. gs://-URL und erstelle dann eine Sicherung mit `rebase db backup` oder einem geplanten Cron-Job.",
	studio_cron_empty_title: "Keine Cron-Jobs registriert",
	studio_cron_empty_body: "Lege unter backend/crons/ eine Datei an, die eine CronJobDefinition als Default exportiert.",
	studio_read_the_docs: "Zur Dokumentation"
};
//#endregion
//#region src/locales/fr.ts
var fr = {
	save: "Enregistrer",
	create: "Créer",
	create_copy: "Créer une copie",
	save_and_close: "Enregistrer et fermer",
	create_copy_and_close: "Créer une copie et fermer",
	create_and_close: "Créer et fermer",
	discard: "Ignorer",
	clear: "Effacer",
	cancel: "Annuler",
	edit: "Modifier",
	copy: "Copier",
	delete: "Supprimer",
	delete_not_allowed: "Vous avez sélectionné au moins une entité que vous ne pouvez pas supprimer",
	edit_entity: "Modifier",
	back_to_detail: "Retour aux détails",
	delete_confirmation_title: "Supprimer ?",
	delete_confirmation_body: "Cela supprimera l'entité. Êtes-vous sûr ?",
	delete_multiple_confirmation_body: "Cela supprimera les entités sélectionnées. Êtes-vous sûr ?",
	unsaved_changes_title: "Modifications non enregistrées",
	unsaved_changes_body: "Vous avez des modifications non enregistrées. Voulez-vous les ignorer ?",
	discard_changes: "Ignorer les modifications",
	keep_editing: "Continuer l'édition",
	clear_form: "Vider le formulaire ?",
	discard_changes_confirmation: "Toutes les modifications non enregistrées seront perdues. Cette action est irréversible.",
	clear_form_confirmation: "Toutes les valeurs saisies seront effacées. Cette action est irréversible.",
	search: "Rechercher",
	find_by_id: "Rechercher par ID",
	find_entity_by_id: "Rechercher l'entité par ID",
	filter: "Filtrer",
	filters: "Filtres",
	records_in_view: "Enregistrements dans cette vue",
	clear_filter: "Effacer le filtre",
	clear_filter_sort: "Effacer le filtre/tri",
	clear_sort: "Effacer le tri",
	sort: "Trier",
	sort_by: "Trier par",
	sort_then_by: "Puis par",
	sort_ascending: "Trier par ordre croissant",
	sort_descending: "Trier par ordre décroissant",
	sort_remove: "Supprimer le tri",
	sort_move_up: "Rendre cette clé plus importante",
	sort_move_down: "Rendre cette clé moins importante",
	sort_remove_key: "Supprimer cette clé de tri",
	sort_key_position: "Clé de tri {{position}}",
	sort_shift_click_hint: "Maj+clic pour ajouter une colonne sous le tri actuel",
	save_entity_before_subcollections: "Vous devez enregistrer l'entité avant d'ajouter des collections supplémentaires",
	clear_all: "Tout effacer",
	no_items: "Aucun élément",
	no_entries_found: "Aucune entrée trouvée",
	all_entries_loaded: "Toutes les {{count}} entrées chargées",
	create_your_first_entry: "Créez votre première entrée",
	no_results_filter_sort: "Aucun résultat avec le filtre/tri appliqué",
	no_results_search: "Aucun résultat trouvé pour \"{{search}}\"",
	add: "Ajouter",
	remove: "Supprimer",
	multiple_entities: "Entités multiples",
	unsaved_changes: "Vous avez des modifications non enregistrées dans {{collectionName}}.",
	so_empty: "C'est bien vide...",
	no_results: "Aucun résultat avec le filtre/tri appliqué",
	refresh_data: "Actualiser les données",
	dark_mode: "Sombre",
	light_mode: "Clair",
	system_mode: "Système",
	join_newsletter: "Rejoignez la newsletter, pas de spam",
	ok: "Ok",
	save_collection_config: "Enregistrer la structure de la collection",
	search_for_more_icons: "Rechercher d'autres icônes…",
	ai_modified: "Modifié par l'IA",
	size_label: "Taille",
	group_by: "Grouper par",
	initialize_kanban_order: "Initialiser l'ordre Kanban",
	copy_id: "Copier l'ID",
	add_specific: "Ajouter {{name}}",
	add_named: "Ajouter « {{name}} »",
	select_specific: "Sélectionner {{name}}",
	select_from: "Sélectionner depuis {{name}}",
	done: "Terminé",
	log_out: "Se déconnecter",
	column_cannot_be_edited: "Cette colonne ne peut pas être modifiée directement",
	close: "Fermer",
	hide_list: "Masquer la liste",
	show_list: "Afficher la liste",
	unsaved_local_changes: "Modifications locales non enregistrées",
	unsaved_local_changes_description: "Ce document a été modifié localement et contient des modifications non enregistrées. Ces modifications locales seront perdues si vous ne les appliquez pas.",
	preview_changes: "Aperçu des modifications",
	apply_changes: "Appliquer les modifications",
	discard_local_changes: "Ignorer les modifications locales",
	preview_local_changes: "Aperçu des modifications locales",
	preview_local_changes_description: "Voici les modifications locales qui seront appliquées au formulaire.",
	type: "Type",
	string: "chaîne de caractères",
	number: "nombre",
	boolean: "booléen",
	date: "date",
	map: "carte (map)",
	array: "tableau",
	arrays_of_arrays_not_supported: "Les tableaux de tableaux ne sont pas pris en charge.",
	data_type_not_supported: "Type de données {{dataType}} non pris en charge pour le moment",
	passkey_error_unsupported: "Votre appareil ou navigateur ne prend pas en charge les passkeys.",
	saved_correctly: "Enregistré avec succès",
	error_before_saving: "Erreur lors de la préparation des données",
	error_after_saving: "Erreur dans le traitement post-sauvegarde",
	error_saving_entity: "Erreur lors de l'enregistrement de l'entité",
	entity_does_not_exist: "Cette entité n'existe pas dans la base de données",
	form_modified: "Ce formulaire a été modifié",
	form_in_sync: "Le formulaire actuel est synchronisé avec la base de données",
	fix_errors_before_saving: "Corrigez les erreurs signalées avant d'enregistrer",
	admin: "Admin",
	home: "Accueil",
	this_form_has_errors: "Ce formulaire contient des erreurs",
	error_loading_navigation: "Erreur lors du chargement de la navigation",
	error_loading_auth: "Erreur lors du chargement de l'authentification",
	this_form_has_been_modified: "Ce formulaire a été modifié",
	current_form_in_sync: "Le formulaire actuel est synchronisé avec la base de données",
	open_in_console: "Ouvrir dans la console",
	collection_does_not_exist: "La collection spécifiée n'existe pas. Vérifiez la console",
	unexpected_value: "Valeur inattendue",
	unexpected_value_click_to_edit: "Valeur inattendue. Cliquez pour modifier",
	unexpected_reference_value: "Valeur de référence inattendue. Cliquez pour modifier",
	copy_url_to_clipboard: "Copier l'URL dans le presse-papiers",
	open_image_in_new_tab: "Ouvrir l'image dans un nouvel onglet",
	reference_not_set: "Référence non définie",
	reference_does_not_exist: "La référence n'existe pas",
	entity_not_found: "Entité non trouvée",
	entity_not_found_body: "Cet enregistrement n'existe pas, ou il vous est masqué par la sécurité au niveau des lignes.",
	back_to_collection: "Retour à {{name}}",
	file_not_found: "Fichier non trouvé",
	unsaved_changes_in_entity: "Vous avez des modifications non enregistrées dans cette entité.",
	delete_this_role: "Supprimer ce rôle",
	no_roles_yet: "Vous n'avez pas encore de rôles.",
	create_default_roles: "Créer les rôles par défaut",
	delete_role_confirmation: "Êtes-vous sûr de vouloir supprimer ce rôle ?",
	delete_this_user: "Supprimer cet utilisateur",
	no_users_yet: "Il n'y a pas encore d'utilisateurs",
	add_logged_user_as_admin: "Ajouter l'utilisateur connecté en tant qu'administrateur",
	add_current_user_as_admin: "Ajouter l'utilisateur actuel en tant qu'administrateur",
	create_default_roles_and_add_admin: "Créer les rôles par défaut et ajouter l'utilisateur actuel en tant qu'administrateur",
	delete_user_confirmation: "Êtes-vous sûr de vouloir supprimer cet utilisateur ?",
	create_your_users_and_roles: "Créez vos utilisateurs et rôles",
	no_users_or_roles_defined: "Vous n'avez défini aucun utilisateur ni rôle. Vous pouvez créer les rôles par défaut et ajouter l'utilisateur actuel en tant qu'administrateur.",
	save_before_changing_schema: "Vous devez enregistrer le document avant de modifier le schéma",
	edit_schema_for_this_form: "Modifier le schéma de ce formulaire",
	no_permissions_to_edit_collection: "Vous n'avez pas l'autorisation de modifier cette collection",
	browser_does_not_support_audio: "Votre navigateur ne prend pas en charge l'élément audio.",
	user_not_found: "Utilisateur introuvable : {{value}}",
	delete_selected: "Supprimer la sélection",
	cannot_delete_selected: "Vous avez sélectionné au moins une entité que vous ne pouvez pas supprimer",
	add_entry: "Ajouter",
	add_on_top: "Ajouter en haut",
	add_below: "Ajouter en dessous",
	add_to_field: "Ajouter à {{fieldName}}",
	value: "Valeur",
	key: "Clé",
	drag_drop_multiple: "Faites glisser et déposez des fichiers ici, ou cliquez pour sélectionner des fichiers. Faites glisser pour réorganiser.",
	drag_drop_single: "Faites glisser et déposez un fichier ici, ou cliquez pour en sélectionner un",
	open_menu: "Ouvrir le menu",
	close_drawer: "Fermer le menu latéral",
	navigation_drawer: "Tiroir de navigation",
	collapse: "Réduire",
	expand: "Développer",
	change_language: "Changer de langue",
	toggle_theme: "Changer de thème",
	user_menu: "Menu utilisateur",
	error: "Erreur",
	error_loading_data: "Impossible de charger les données",
	error_check_server_logs: "Consultez les journaux du serveur pour plus de détails.",
	error_technical_details: "Détails techniques",
	error_uploading_file: "Erreur lors du téléchargement du fichier",
	error_deleting: "Erreur lors de la suppression",
	error_before_delete: "Erreur avant la suppression",
	error_updating_asset: "Erreur lors de la mise à jour de l'actif",
	error_deleting_asset: "Erreur lors de la suppression de l'actif",
	error_firestore_index: "Un index Firestore est requis pour cette requête.",
	create_index: "Créer un index",
	value_is_not_reference: "La valeur n'est pas une référence.",
	click_to_edit: "Cliquez pour modifier",
	data_is_not_array_of_references: "Les données ne sont pas un tableau de références",
	loading: "Chargement...",
	local_changes_applied: "Modifications locales appliquées au formulaire",
	local_changes_discarded: "Modifications locales ignorées",
	are_you_sure_leave: "Êtes-vous sûr de vouloir quitter cette page ?",
	see_console_details: "Consultez la console pour plus de détails.",
	drop_here_create_group: "Déposez ici pour créer un nouveau groupe",
	filter_for_null_values: "Filtrer les valeurs nulles",
	value_updated_elsewhere: "Cette valeur a été mise à jour ailleurs",
	add_property: "Ajouter une propriété",
	edit_name: "Modifier {{name}}",
	this_entity_not_exist: "Cette entité n'existe pas dans la base de données",
	internal_error: "ERREUR interne",
	rename_group: "Renommer le groupe",
	group_name_label: "Nom du groupe",
	group_name_empty_error: "Le nom du groupe ne peut pas être vide.",
	group_name_exists_error: "Ce nom de groupe existe déjà.",
	search_collections: "Rechercher dans les collections",
	views_group: "Vues",
	youd_need_to_save_before_additional_collections: "Vous devez enregistrer votre entité avant d'ajouter des collections supplémentaires",
	page_not_found: "Page introuvable",
	page_not_found_body: "Cette page n'existe pas ou vous n'y avez peut-être pas accès",
	back_to_home: "Retour à l'accueil",
	default_collection_view: "Vue de collection par défaut",
	table_view: "Tableau",
	cards_view: "Cartes",
	kanban_view: "Kanban",
	choose_how_entities_displayed_default: "Choisissez comment les entités doivent être affichées par défaut",
	document_view: "Vue de document",
	side_panel: "Panneau latéral",
	full_screen: "Plein écran",
	should_documents_opened_full_screen: "Les documents doivent-ils être ouverts en plein écran ou dans une boîte de dialogue latérale intégrée",
	select_custom_view: "Sélectionner une vue personnalisée",
	no_custom_views_defined: "Aucune vue personnalisée n'est définie. Définissez vos vues personnalisées dans les paramètres de personnalisation, avant d'utiliser cette boîte de dialogue.",
	select_custom_action: "Sélectionner une action personnalisée",
	no_custom_actions_defined: "Aucune action personnalisée définie. Définissez vos actions personnalisées dans les paramètres de personnalisation, avant d'utiliser cette boîte de dialogue.",
	no_collections_found: "Aucune collection trouvée",
	start_building_collections: "Commencez à créer des collections dans Rebase facilement. Mappez-les à vos données de base de données existantes, importez-les à partir de fichiers ou utilisez nos modèles.",
	create_first_collection: "Créez votre première collection",
	define_collections_programmatically: "Vous pouvez également définir des collections par programmation.",
	edit_collection: "Modifier la collection",
	no_permissions_edit_collection: "Vous n'avez pas les autorisations pour modifier cette collection",
	no_permissions_create_collection: "Vous n'avez pas les autorisations pour créer des collections",
	create_collection: "Créer une collection",
	update_collection: "Mettre à jour la collection",
	new_collection: "Nouvelle collection",
	add_new_collection: "Ajouter une nouvelle collection",
	collection_with_name: "Collection {{name}}",
	change_icon: "Changer l'icône",
	is_subcollection_of: "Ceci est une sous-collection de",
	name: "Nom",
	collection_name_description: "Nom de cette collection, généralement un nom au pluriel (par ex., Produits)",
	path: "Chemin",
	relative_path_to_parent: "Chemin relatif vers le parent (il n'est pas nécessaire d'inclure le chemin parent)",
	path_in_database: "Chemin dans lequel cette collection est stockée, dans la base de données",
	singular_name: "Nom au singulier",
	singular_name_description: "Définissez éventuellement un nom au singulier pour vos entités",
	description: "Description",
	description_of_collection: "Description de la collection, vous pouvez utiliser le format markdown",
	collection_id: "ID de la collection",
	collection_id_description: "Cet ID identifie cette collection. Généralement le même que le chemin.",
	collection_group: "Groupe de collections",
	collection_group_description: "Un groupe de collections se compose de toutes les collections ayant le même chemin. Cela vous permet d'effectuer des requêtes sur plusieurs collections à la fois.",
	advanced_settings: "Paramètres avancés",
	doc_history_global: "Révisions de l'historique des documents activées si activées globalement",
	doc_history_enabled: "Révisions de l'historique des documents ACTIVÉES",
	doc_history_not_enabled: "Révisions de l'historique des documents NON activées",
	doc_history_description: "Lorsqu'il est activé, chaque document de cette collection conservera un historique des modifications. Ceci est utile à des fins d'audit. Les données sont stockées dans une sous-collection du document dans votre base de données, appelée __history.",
	document_id_generation: "Génération des ID de document",
	code_defined: "Défini par le code",
	users_must_define_id: "Les utilisateurs doivent définir un ID",
	users_can_define_id: "Les utilisateurs peuvent définir un ID, mais ce n'est pas obligatoire",
	doc_id_auto_generated: "L'ID du document est généré automatiquement",
	config_doc_id_generation: "Configurez comment les ID de document sont générés lors de la création de nouvelles entités.",
	enable_text_search: "Activer la recherche de texte pour cette collection",
	text_search_description: "Autoriser la recherche de texte pour cette collection. Si vous n'avez pas spécifié de délégué de recherche de texte, la recherche de texte locale intégrée sera utilisée. Ceci n'est pas recommandé pour les grandes collections, car cela peut entraîner des problèmes de performances et de coûts.",
	database_id: "ID de base de données",
	default_text: "(par défaut)",
	custom_actions: "Actions personnalisées",
	more_info: "Plus d'informations",
	define_custom_actions_cli: "Définissez vos propres actions personnalisées en les téléchargeant via la CLI.",
	action_defined_in_code: "Cette action est définie dans le code avec la clé",
	add_custom_entity_action: "Ajouter une action d'entité personnalisée",
	remove_this_action: "Supprimer cette action ?",
	remove_action_warning: "Cela ne supprimera aucune donnée, uniquement l'action dans le panneau d'administration",
	subcollections_of: "Sous-collections de",
	add_subcollection: "Ajouter une sous-collection",
	custom_views: "Vues personnalisées",
	define_custom_views_cli: "Définissez vos propres vues personnalisées en les téléchargeant via la CLI.",
	view_defined_in_code: "Cette vue est définie dans le code avec la clé",
	add_custom_entity_view: "Ajouter une vue d'entité personnalisée",
	delete_this_subcollection: "Supprimer cette sous-collection ?",
	remove_collection_warning: "Cela ne supprimera aucune donnée, uniquement la collection dans le panneau d'administration",
	remove_this_view: "Supprimer cette vue ?",
	remove_view_warning: "Cela ne supprimera aucune donnée, uniquement la vue dans le panneau d'administration",
	no_collection_selected: "Aucune collection sélectionnée",
	code_for_collection: "Code pour",
	use_config_define_json: "Utilisez cette configuration pour définir la collection au format JSON.",
	customise_collection_code: "Si vous souhaitez personnaliser la collection dans le code, vous pouvez ajouter ce code de collection à la configuration de votre application d'administration.",
	copied: "Copié",
	property_cant_be_edited: "Cette propriété ne peut pas être modifiée",
	property_not_editable_description: "Vous n'avez peut-être pas l'autorisation de la modifier ou elle est définie dans le code et ne peut pas être modifiée.",
	delete_this_property: "Supprimer cette propriété ?",
	delete_property_warning: "Cela ne supprimera aucune donnée, mais modifiera uniquement la collection.",
	error_must_specify_id: "Vous devez spécifier un ID pour le champ",
	error_id_format: "L'ID ne peut contenir que des lettres, des chiffres et des traits de soulignement (_), et ne doit pas commencer par un chiffre",
	error_id_already_exists: "Il existe déjà un autre champ avec cet ID",
	error_must_specify_title: "Vous devez spécifier un titre pour le champ",
	custom_or_other: "Personnalisé/Autre",
	select_property_widget: "Sélectionnez un widget de propriété",
	error_changing_data_type: "Ce widget utilise un type de données différent de celui initialement sélectionné. Cela peut provoquer des erreurs avec les données existantes.",
	required: "Requis",
	enum_form_dialog: "Boîte de dialogue du formulaire Enum",
	imported_data_preview: "Aperçu des données importées",
	entities_with_same_id_overwritten: "Les entités avec le même identifiant seront écrasées",
	collection_editor: "Éditeur de collection",
	properties_in_this_group: "Propriétés de ce groupe",
	data_property_mapping: "Mappage de propriétés de données",
	property_edit_view: "Vue d'édition de propriété",
	all_of_these: "Tous ces éléments",
	any_of_these: "N'importe lequel de ces éléments",
	only_admins_edit_roles: "Seuls les administrateurs peuvent modifier les rôles",
	error_user_not_found: "Utilisateur introuvable",
	all_roles: "Tous les rôles",
	role: "Rôle",
	name_of_this_role: "Nom de ce rôle",
	id_of_this_role: "ID de ce rôle",
	create_entities: "Créer des entités",
	read_entities: "Lire des entités",
	update_entities: "Mettre à jour des entités",
	delete_entities: "Supprimer des entités",
	all_collections: "Toutes les collections",
	create_entities_in_collections: "Créer des entités dans les collections",
	access_all_data_in_every_collection: "Accéder à toutes les données de chaque collection",
	update_data_in_any_collection: "Mettre à jour les données dans n'importe quelle collection",
	delete_data_in_any_collection: "Supprimer des données dans n'importe quelle collection",
	allow_all_permissions_in_this_collections: "Autoriser toutes les autorisations dans ces collections",
	all: "Tout",
	customise_permissions_description: "Vous pouvez personnaliser les autorisations que les utilisateurs liés à ce rôle peuvent exécuter dans les entités de chaque collection",
	create_collections: "Créer des collections",
	yes: "Oui",
	no: "Non",
	can_user_create_collections: "L'utilisateur peut-il créer des collections",
	edit_collections: "Modifier les collections",
	only_own_collections: "Uniquement les siennes",
	own: "Propre",
	can_user_edit_collections: "L'utilisateur peut-il modifier les collections",
	delete_collections: "Supprimer les collections",
	can_user_delete_collections: "L'utilisateur peut-il supprimer des collections",
	error_saving_role: "Une erreur s'est produite lors de l'enregistrement de ce rôle",
	create_role: "Créer un rôle",
	update: "Mettre à jour",
	only_admins_change_roles: "Seuls les administrateurs peuvent modifier les rôles",
	must_be_at_least_one_admin: "Il doit y avoir au moins un administrateur",
	logged_user_not_found: "Utilisateur connecté introuvable",
	user: "Utilisateur",
	user_id: "ID de l'utilisateur",
	name_of_this_user: "Nom de cet utilisateur",
	email_of_this_user: "Email de cet utilisateur",
	roles: "Rôles",
	create_user: "Créer un utilisateur",
	users: "Utilisateurs",
	add_user: "Ajouter un utilisateur",
	add_role: "Ajouter un rôle",
	is_admin: "Est Admin",
	default_permissions: "Autorisations par défaut",
	created_on: "Créé le",
	email: "E-mail",
	id: "ID",
	read: "Lire",
	column_in_file: "Colonne dans le fichier",
	map_to_property: "Mappage à la propriété",
	default_values: "Valeurs par défaut",
	default_values_description: "Vous pouvez sélectionner une valeur par défaut pour les colonnes non mappées et les valeurs vides :",
	property: "Propriété",
	default_value: "Valeur par défaut",
	autogenerate_id: "Générer automatiquement l'ID",
	id_column_description: "Colonne qui sera utilisée comme ID pour chaque document",
	do_not_set_value: "Ne pas définir de valeur",
	set_value_to_true: "Définir la valeur sur Vrai (true)",
	set_value_to_false: "Définir la valeur sur Faux (false)",
	drag_and_drop_file: "Glissez et déposez un fichier ici ou cliquez pour télécharger",
	error_saving_data: "Erreur lors de l'enregistrement des données",
	retry: "Réessayer",
	saving_data: "Enregistrement des données",
	entities_saved: "entités enregistrées",
	do_not_close_tab: "Ne fermez pas cet onglet, sinon l'importation sera interrompue",
	import: "Importer",
	import_data: "Importer des données",
	upload_file_description: "Téléchargez un fichier CSV, Excel ou JSON et mappez-le à votre schéma existant",
	back: "Retour",
	next: "Suivant",
	save_data: "Enregistrer les données",
	use_column_as_id: "Utiliser cette colonne comme ID",
	do_not_import_property: "Ne pas importer cette propriété",
	entities_will_be_overwritten: "Les entités avec le même identifiant seront écrasées",
	data_imported_successfully: "Données importées avec succès",
	export: "Exporter",
	export_data: "Exporter des données",
	download_table_csv: "Télécharger le contenu de cette table au format CSV",
	csv: "CSV",
	json: "JSON",
	dates_as_timestamps: "Dates sous forme d'horodatages",
	dates_as_strings: "Dates sous forme de chaînes",
	flatten_arrays: "Aplatir les tableaux",
	download: "Télécharger",
	large_number_of_documents: "Cette collection contient un grand nombre de documents ({{count}}).",
	too_many_documents_to_export: "Cette collection contient plus de documents ({{count}}) que le navigateur ne peut en exporter dans un seul fichier (limite {{limit}}). Filtrez-la avant d'exporter.",
	include_undefined_values: "Inclure les valeurs non définies",
	submit: "Soumettre",
	no_filterable_properties: "Aucune propriété filtrable disponible",
	apply_filters: "Appliquer les filtres",
	filter_presets: "Préréglages",
	filter_preset_apply: "Appliquer le préréglage",
	filter_preset_active: "Actif : {{label}}",
	list: "Liste",
	table_view_mode: "Tableau",
	cards: "Cartes",
	board: "Tableau",
	initialize_kanban_order_desc: "Ceci attribuera des valeurs d'ordre séquentiel à tous les éléments qui n'en ont pas. Les articles conserveront leur ordre actuel dans chaque colonne.",
	kanban_view_not_available: "La vue Kanban n'est pas disponible",
	kanban_view_requires_enum: "La vue Kanban requiert une propriété de chaîne de caractères avec des valeurs Enumération (enum) pour regrouper les entités en colonnes. Veuillez ajouter une propriété Enum à votre schéma de collection pour utiliser cette vue.",
	no_enum_values_configured: "Aucune valeur enum configurée pour la propriété \"{{property}}\"",
	items_need_backfill: "Certains éléments n'ont pas de valeurs d'ordre. Initialisez-les pour permettre la réorganisation par glisser-déposer.",
	kanban_order_not_configured: "L'ordonnancement par glisser-déposer n'est pas configuré. Définissez une propriété d'ordre dans les paramètres de la collection pour conserver les positions des cartes.",
	initialize: "Initialiser",
	confirm_multiple_delete: "Confirmez la suppression multiple ?",
	delete_entity_confirm_title: "Souhaitez-vous supprimer {{entityName}} ?",
	add_existing: "Ajouter un existant",
	unlink_entity_confirm_title: "Retirer ce/cette {{entityName}} de cet enregistrement ?",
	unlink_entity_confirm_body: "Il reste dans {{collectionName}} et disponible pour d'autres enregistrements.",
	confirm_multiple_unlink: "les retirer de cet enregistrement ?",
	no_filter: "Aucun filtre",
	is_true: "est vrai",
	is_false: "est faux",
	error_uploading_file_size: "Erreur lors de l'envoi : le fichier dépasse {{maxSize}} octets",
	error_uploading_file_type: "Erreur lors de l'envoi : ce type de fichier n'est pas pris en charge",
	drag_drop_files: "Glissez-déposez des fichiers ici, ou cliquez pour les modifier",
	drag_drop_file: "Glissez-déposez un fichier ici, ou cliquez pour le modifier",
	multiple_deleted: "Suppression multiple terminée",
	some_entities_deleted: "Certaines des entités ont été supprimées, mais pas toutes",
	error_deleting_entities: "Erreur lors de la suppression des entités",
	deleted: "supprimé",
	selection_options: "Options de sélection",
	selection_menu_all_loaded: "Tout sur cette page",
	selection_menu_all_matching: "Les {{total}} {{collection}}",
	selection_menu_none: "Aucun",
	selection_select_all_loaded: "Sélectionner les lignes chargées",
	selection_deselect_all: "Tout désélectionner",
	selection_reading_rows: "Lecture des lignes… {{loaded}} sur {{total}}",
	selection_reading_rows_unknown: "Lecture des lignes… {{loaded}} jusqu’à présent",
	selection_deleting_progress: "{{completed}} supprimés sur {{total}}",
	confirm_delete_selection: "Supprimer {{total}} {{collection}} ?",
	confirm_delete_selection_unknown: "Supprimer toutes les lignes correspondant au filtre actuel ?",
	confirm_delete_selection_body: "Cette action est irréversible. Chaque ligne est supprimée séparément, l’opération peut donc prendre du temps.",
	export_selection_count: "Télécharger les {{total}} lignes sélectionnées au format {{format}}",
	export_selection_all_matching: "Télécharger tous les {{collection}} correspondant au filtre actuel",
	select_reference: "Sélectionner une référence",
	select_references: "Sélectionner des références",
	account_settings: "Paramètres du compte",
	profile: "Profile",
	security: "Sécurité",
	sessions: "Sessions",
	change_password: "Changer le mot de passe",
	current_password: "Mot de passe actuel",
	new_password: "Nouveau mot de passe",
	confirm_password: "Confirmer le nouveau mot de passe",
	password_changed: "Mot de passe modifié. Vous allez être déconnecté.",
	passwords_dont_match: "Les mots de passe ne correspondent pas",
	password_too_short: "Le mot de passe doit comporter au moins 8 caractères",
	password_change_not_available: "Le changement de mot de passe n'est pas disponible pour les comptes utilisant un fournisseur de connexion externe.",
	changing_password: "Modification…",
	display_name: "Nom affiché",
	photo_url: "URL de la photo",
	save_profile: "Enregistrer le profil",
	saving: "Saving...",
	no_active_sessions: "Aucune session active trouvée.",
	revoking: "Revoking...",
	revoke_all_sessions: "Révoquer toutes les sessions",
	unknown_device: "Appareil inconnu",
	current: "Current",
	role_id: "ID du rôle",
	role_name: "Nom du rôle",
	add_reference: "Ajouter une référence",
	add_reference_to: "Ajouter une référence à {{name}}",
	/** RolesView — table & permissions */
	role_deleted_successfully: "Rôle supprimé",
	error_deleting_role: "Erreur lors de la suppression du rôle",
	collection_permissions: "Permissions de la collection",
	collection: "Collection",
	no_collections_configured: "Aucune collection configurée",
	no_security_rules_defined: "Aucune règle de sécurité définie — toutes les opérations sont autorisées",
	no_rules: "aucune règle",
	/** UsersView — snackbar */
	bootstrap_admin_success: "Vous êtes désormais administrateur ! Actualisation…",
	failed_to_bootstrap_admin: "Impossible de créer l'administrateur initial",
	user_deleted_successfully: "Utilisateur supprimé",
	error_deleting_user: "Erreur lors de la suppression de l'utilisateur",
	user_created_successfully: "Utilisateur créé",
	invitation_sent: "Un e-mail d'invitation a été envoyé à {{email}}. Le lien permet de définir le mot de passe.",
	invitation_sent_title: "Invitation envoyée",
	temporary_password: "Mot de passe temporaire",
	temporary_password_description: "L'e-mail n'est pas configuré. Transmettez ce mot de passe temporaire à l'utilisateur de manière sécurisée. Il ne sera plus affiché.",
	temporary_password_email_failed_description: "L'e-mail n'a pas pu être remis. Transmettez ce mot de passe temporaire à l'utilisateur de manière sécurisée. Il ne sera plus affiché.",
	copy_password: "Copier le mot de passe",
	password_copied: "Mot de passe copié dans le presse-papiers",
	/** UsersView — pagination & search */
	search_users: "Rechercher des utilisateurs",
	created: "Créé le",
	actions: "Actions",
	no_users_found: "Aucun utilisateur trouvé",
	reset_password: "Réinitialiser le mot de passe",
	reset_password_success: "Mot de passe réinitialisé",
	reset_password_confirmation: "Voulez-vous vraiment réinitialiser le mot de passe de cet utilisateur ?",
	reset_password_send_email: "Envoyer un e-mail de réinitialisation",
	reset_password_set_manually: "Définir un mot de passe manuellement",
	reset_password_set_manually_description: "Le mot de passe est mis à jour immédiatement et aucun e-mail n'est envoyé. Transmettez-le à l'utilisateur de manière sécurisée.",
	/** Permission-denied empty states */
	no_permission_to_view_users: "Vous n'avez pas la permission de voir les utilisateurs",
	no_permission_to_view_roles: "Vous n'avez pas la permission de voir les rôles",
	no_permission_description: "Contactez un administrateur si vous avez besoin d'accéder à cette section.",
	error_resetting_password: "Erreur lors de la réinitialisation du mot de passe",
	/** Editor table-bubble */
	add_row_before: "Insérer une ligne avant",
	add_row_after: "Insérer une ligne après",
	delete_row: "Supprimer la ligne",
	add_column_before: "Insérer une colonne avant",
	add_column_after: "Insérer une colonne après",
	delete_column: "Supprimer la colonne",
	delete_table: "Supprimer le tableau",
	/** Editor image-bubble */
	alt_text: "Texte alternatif",
	title: "Title",
	/** MapFieldBinding */
	a_property_of_this_map_has_error: "Une propriété de cette table d'association contient une erreur",
	/** AI Collection Generator Popover */
	generate_collection_with_ai: "Générer une collection avec l'IA",
	modify_collection_with_ai: "Modifier la collection avec l'IA",
	describe_collection_to_create: "Décrivez la collection que vous souhaitez créer.",
	describe_changes_to_make: "Décrivez les modifications que vous souhaitez apporter à cette collection.",
	ai_placeholder_create: "ex. : Créer une collection de produits avec nom, prix, description et catégorie...",
	ai_placeholder_modify: "ex. : Ajouter un champ d'image miniature avec stockage, rendre le prix obligatoire...",
	ai_assist: "Assistance IA",
	generating: "Génération en cours...",
	/** Recently extracted strings for collection editor */
	this_is_subcollection_of: "Ceci est une sous-collection de",
	use_existing_paths_database: "Utiliser l'un des chemins existants dans votre base de données :",
	describe_collection_ai: "Décrivez votre collection à l'IA :",
	generate_with_ai: "Générer avec l'IA",
	create_from_json_config: "Créer à partir d'une configuration JSON :",
	paste_json_config: "Coller la configuration JSON",
	create_collection_from_file_formats: "Créer une collection à partir d'un fichier (csv, json, xls, xslx...)",
	select_template: "Sélectionner un modèle :",
	products: "Produits",
	collection_products_subtitle: "Une collection de produits avec images, prix et stock",
	collection_users_subtitle: "Une collection d'utilisateurs avec e-mails, noms et rôles",
	blog_posts: "Articles de blog",
	collection_blog_posts_subtitle: "Une collection d'articles de blog avec images, auteurs et contenu complexe",
	pages: "Pages",
	collection_pages_subtitle: "Une collection de pages avec images, auteurs et contenu complexe",
	continue_from_scratch: "Continuer à partir de zéro",
	/** Admin views config */
	admin_users: "Utilisateurs du panneau d'administration",
	roles_menu: "Rôles",
	project_settings: "Paramètres du projet",
	by_signing_in_you_agree_to_our: "En vous connectant, vous acceptez nos",
	terms_and_conditions: "Conditions générales",
	and_our: "et notre",
	privacy_policy: "Politique de confidentialité",
	email_password: "E-mail/Mot de passe",
	sign_in_with_google: "Se connecter avec Google",
	auth_user_not_found: "Utilisateur non trouvé",
	auth_wrong_password: "Mot de passe erroné. Veuillez réessayer.",
	auth_user_disabled: "Utilisateur désactivé. Veuillez contacter le support.",
	auth_account_exists_with_different_credential: "Un compte existe déjà avec cet e-mail. Connectez-vous comme la dernière fois.",
	auth_account_exists_sign_in_with_password: "Un compte existe déjà avec cet e-mail. Connectez-vous plutôt avec votre e-mail et votre mot de passe.",
	auth_email_already_in_use: "L'e-mail est déjà utilisé",
	auth_google_permissions_required: "Vous devez accorder des autorisations supplémentaires afin de gérer vos projets Google Cloud",
	auth_invalid_email_password: "Veuillez entrer l'e-mail et le mot de passe",
	auth_enter_email_first: "Veuillez d'abord entrer votre e-mail",
	auth_password_reset_sent: "E-mail de réinitialisation du mot de passe envoyé",
	auth_sign_in_account: "Connectez-vous à votre compte",
	auth_create_new_account: "Créer un nouveau compte",
	auth_password: "Mot de passe",
	auth_reset_password: "Réinitialiser le mot de passe",
	auth_new_user: "Nouvel utilisateur ?",
	auth_have_account: "Vous avez déjà un compte ?",
	auth_sign_in: "Se connecter",
	auth_sign_up: "S'inscrire",
	auto_setup_collections_button: "Configurer les collections automatiquement",
	auto_setup_collections_title: "Configurer les collections automatiquement ?",
	auto_setup_collections_desc: "Cela créera automatiquement des configurations de collection pour les collections qui ne sont <b>PAS</b> déjà mappées",
	this_can_take_a_minute: "Cela peut prendre une minute",
	no_collections_found_to_setup: "Aucune collection à configurer trouvée",
	collections_have_been_setup: "Les collections ont été configurées",
	error_setting_up_collections: "Erreur lors de la configuration des collections",
	admin_panel_ready_bring_data: "Votre panneau d'administration est prêt. Ajoutons vos données.",
	admin_panel_ready_get_started: "Votre panneau d'administration est prêt. Voici comment démarrer.",
	auto_detect_collections: "Détecter automatiquement les collections",
	auto_detect_collections_desc: "Laissez l'IA analyser votre base de données et générer automatiquement des schémas de collection.",
	create_a_collection: "Créer une collection",
	create_collection_desc: "Définissez manuellement votre première collection à partir de zéro à l'aide de l'éditeur visuel.",
	read_the_docs: "Lire la documentation",
	read_the_docs_desc: "Apprenez à personnaliser les champs, les vues, les actions et bien plus encore.",
	explore_docs: "Explorer la documentation",
	want_to_customize_with_code: "Vous souhaitez personnaliser avec du code ? Exécutez",
	to_scaffold_a_local_project: "pour générer un projet local.",
	validation: "Validation",
	unique: "Unique",
	required_message: "Message de champ obligatoire",
	required_tooltip: "Vous ne pourrez pas enregistrer cette entité si cette valeur n'est pas définie",
	unique_tooltip: "Il ne peut pas y avoir plusieurs entités avec la même valeur",
	lowercase: "Minuscules",
	uppercase: "Majuscules",
	trim: "Supprimer les espaces",
	exact_length: "Longueur exacte",
	min_length: "Longueur min.",
	max_length: "Longueur max.",
	matches_regex: "Correspond à une regex",
	not_valid_regexp: "Expression régulière non valide",
	regex_helper: "ex. /^\\d+$/ pour les chiffres uniquement",
	min_value: "Valeur min.",
	max_value: "Valeur max.",
	less_than: "Inférieur à",
	more_than: "Supérieur à",
	positive_value: "Valeur positive",
	negative_value: "Valeur négative",
	integer_value: "Valeur entière",
	invalid_regular_expression: "Expression régulière non valide",
	must_specify_target_collection: "Vous devez spécifier une collection cible pour le champ",
	need_specify_repeat_field: "Vous devez spécifier un champ de répétition",
	need_specify_block_properties: "Vous devez spécifier les propriétés de ce bloc",
	incomplete_condition: "Condition incomplète - veuillez sélectionner un champ",
	field_name: "Nom du champ",
	kanban_column_property: "Propriété de colonne Kanban",
	select_a_property: "Sélectionner une propriété",
	kanban_property_not_found: "La propriété \"{{property}}\" n'existe pas ou n'est pas une propriété de type string avec enum. Veuillez sélectionner une propriété valide ou effacer la sélection.",
	no_enum_string_properties: "Aucune propriété de type string avec enum trouvée. Ajoutez une propriété string avec enumValues pour utiliser la vue Kanban.",
	kanban_column_description: "Sélectionnez une propriété de type string avec des valeurs enum pour regrouper les entités en colonnes",
	create_property: "+ Créer la propriété \"{{property}}\"",
	order_property: "Propriété d'ordre",
	order_property_not_found: "La propriété \"{{property}}\" n'existe pas ou n'est pas une propriété numérique. Veuillez sélectionner une propriété valide ou effacer la sélection.",
	no_number_properties: "Aucune propriété numérique trouvée. Ajoutez une propriété numérique pour activer le tri.",
	order_property_description: "Sélectionnez une propriété numérique pour conserver l'ordre des éléments",
	display_settings: "Paramètres d'affichage",
	default_row_size: "Taille de ligne par défaut",
	side_dialog_width: "Largeur du dialogue latéral",
	side_dialog_width_description: "Définissez optionnellement la largeur (en pixels) du dialogue latéral des entités. La valeur par défaut est 768px",
	inline_editing_enabled: "Les données peuvent être modifiées directement dans la vue tableau",
	inline_editing_disabled: "Les données ne peuvent être modifiées que dans la vue formulaire",
	inline_editing_description: "Autoriser la modification des données directement dans la vue tableau, sans ouvrir la vue formulaire.",
	include_json_view: "Inclure la vue JSON",
	no_json_view: "Ne pas inclure la vue JSON",
	json_view_description: "Inclure la représentation JSON du document.",
	not_found_suffix: "introuvable",
	editor_text: "Texte",
	editor_text_description: "Commencez simplement à taper du texte brut.",
	editor_heading_1: "Titre 1",
	editor_heading_1_description: "Grand titre de section.",
	editor_heading_2: "Titre 2",
	editor_heading_2_description: "Titre de section moyen.",
	editor_heading_3: "Titre 3",
	editor_heading_3_description: "Petit titre de section.",
	editor_todo_list: "Liste de tâches",
	editor_todo_list_description: "Suivez les tâches avec une liste de tâches.",
	editor_bullet_list: "Liste à puces",
	editor_bullet_list_description: "Créez une simple liste à puces.",
	editor_numbered_list: "Liste numérotée",
	editor_numbered_list_description: "Créez une liste avec numérotation.",
	editor_quote: "Citation",
	editor_quote_description: "Capturez une citation.",
	editor_code: "Code",
	editor_code_description: "Capturez un extrait de code.",
	editor_image: "Image",
	editor_image_description: "Téléchargez une image depuis votre ordinateur.",
	editor_multiple: "Multiple",
	editor_link: "Lien",
	editor_save: "Enregistrer",
	editor_cancel: "Annuler",
	editor_remove_link: "Supprimer le lien",
	editor_paste_or_type_link: "Coller ou taper le lien",
	editor_open_in_new_window: "Ouvrir dans une nouvelle fenêtre",
	editor_bold: "Gras",
	editor_italic: "Italique",
	editor_underline: "Souligné",
	editor_strikethrough: "Barré",
	editor_autocomplete: "Saisie automatique",
	editor_autocomplete_description: "Ajoutez du texte basé sur le contexte.",
	text_search_dialog_title: "Activer la recherche de texte",
	text_search_local_not_recommended: "La recherche de texte locale n'est pas recommandée pour les grandes collections.",
	text_search_local_fetch_warning: "Notez que l'activation de la recherche de texte locale nécessite de récupérer tous les documents de votre collection et de les stocker dans le navigateur. Cela peut être inefficace pour les grandes collections et entraîner des coûts supplémentaires.",
	text_search_external_suggestion: "Pour les collections plus grandes, il est recommandé d'utiliser un moteur de recherche externe comme Algolia ou Elastic Search, et d'attribuer un délégué de recherche à votre configuration.",
	text_search_local_description: "La recherche de texte locale est le moyen le plus simple d'activer la recherche de texte dans votre collection. Elle charge tous les documents de la collection dans le navigateur et effectue la recherche localement. C'est l'option recommandée pour les petites collections.",
	text_search_own_implementation: "Vous avez implémenté votre propre contrôleur de recherche de texte. Vous pouvez activer la recherche de texte pour votre collection.",
	text_search_enable_for_collection: "Activer pour cette collection",
	text_search_enable_for_project: "Activer pour le projet",
	text_search_enabled_snackbar: "Recherche de texte locale activée",
	settings_heading: "Paramètres",
	settings_project_name: "Nom du projet",
	settings_default_language: "Langue par défaut",
	settings_default_language_caption: "Sélectionnez la langue de base pour ce projet. Les utilisateurs peuvent modifier cette préférence dans leurs paramètres personnels.",
	settings_enable_local_text_search: "Activer la recherche de texte locale",
	settings_local_text_search_caption: "Activez la recherche de texte locale pour toutes les collections. Cela vous permettra de rechercher des champs textuels à l'aide de la barre de recherche Rebase. Notez que cette fonctionnalité peut entraîner un nombre de lectures plus élevé, car tous les champs textuels de vos collections seront indexés.",
	settings_doc_history_all_collections: "Historique des documents activé pour toutes les collections",
	settings_doc_history_caption: "Lorsque activé, toutes les collections auront l'historique activé par défaut. Vous pouvez modifier ce paramètre pour chaque collection. L'historique sera enregistré dans la sous-collection __history de chaque document.",
	settings_theme: "Thème",
	settings_primary_color: "Couleur primaire",
	settings_secondary_color: "Couleur secondaire",
	settings_sample_theme_components: "Exemples de composants du thème",
	settings_drag_drop_logo: "Glissez-déposez votre logo ici",
	settings_security_rules: "Règles de sécurité",
	settings_security_rules_description: "Rebase utilise les règles de sécurité Firebase pour restreindre l'accès aux données. Lors de la création d'un utilisateur via Rebase, l'attribut personnalisé rebaseUser est ajouté à l'utilisateur dans le projet client. En ajoutant les règles de sécurité suivantes, vous garantissez que les utilisateurs Rebase peuvent accéder aux données via Rebase.",
	settings_security_rules_add_domain: "N'oubliez pas d'ajouter le domaine {{domain}} aux domaines autorisés de votre fournisseur",
	settings_security_rules_caption: "Ces règles restreignent l'accès aux données aux seuls utilisateurs Rebase, mais n'appliquent pas les permissions au niveau de la base de données. Les permissions sont appliquées côté frontend, ce qui convient à la plupart des projets. Si vous devez appliquer les permissions au niveau de la base de données, vous pouvez modifier ces règles de sécurité. Les rôles attribués à un utilisateur sont définis comme attributs personnalisés dans le jeton d'authentification Firebase, vous pouvez donc les utiliser dans vos règles de sécurité.",
	studio_sql_executing_query: "EXÉCUTION DE LA REQUÊTE…",
	studio_sql_query_error: "Erreur de requête",
	studio_sql_run_query_placeholder: "Exécutez une requête pour voir les résultats",
	studio_sql_visual_execution_plan: "Plan d'exécution visuel",
	studio_sql_success: "SUCCESS",
	studio_sql_no_results: "La requête s'est exécutée correctement mais n'a renvoyé aucun résultat.",
	studio_sql_rows: "Rows:",
	studio_sql_time: "Time:",
	studio_sql_copy_markdown: "Copier en Markdown",
	studio_sql_export_json: "Exporter en JSON",
	studio_sql_export_csv: "Exporter en CSV",
	studio_sql_format_sql: "Formater le SQL",
	studio_sql_explain: "Explain",
	studio_sql_limit_1000: "Limit 1000",
	studio_sql_remove_from_favorites: "Retirer des favoris",
	studio_sql_add_to_favorites: "Ajouter aux favoris",
	studio_sql_save: "Save",
	studio_sql_run: "Run",
	studio_sql_database: "Database",
	studio_sql_role: "Role",
	studio_sql_admin: "(Admin)",
	studio_sql_select_db: "Choisir la base",
	studio_sql_query_results: "Résultats de la requête",
	studio_sql_save_snippet: "Enregistrer l'extrait SQL",
	studio_sql_snippet_name: "Nom de l'extrait",
	studio_sql_snippet_name_placeholder: "p. ex. Récupérer tous les utilisateurs",
	studio_sql_snippet_saved_local: "Cet extrait sera enregistré dans votre stockage local.",
	studio_sql_cancel: "Cancel",
	studio_sql_dangerous_operation: "Opération dangereuse détectée",
	studio_sql_dangerous_operation_body: "La requête que vous allez exécuter contient des opérations potentiellement destructrices (DELETE, DROP, TRUNCATE ou UPDATE sans WHERE). Voulez-vous vraiment continuer ?",
	studio_sql_snippet_saved: "Extrait « {{name}} » enregistré.",
	studio_sql_markdown_copied: "Résultats copiés en Markdown !",
	studio_sql_markdown_copy_failed: "Impossible de copier le Markdown dans le presse-papiers.",
	studio_sql_row_updated: "Ligne mise à jour.",
	studio_sql_cannot_edit_missing_query: "Impossible de modifier les lignes : la requête sous-jacente est absente.",
	studio_sql_cannot_resolve_table: "Impossible de déterminer la table ou la clé primaire",
	studio_sql_missing_pk: "La ligne n'a pas les colonnes de clé primaire : {{columns}}. Mise à jour impossible en toute sécurité.",
	studio_sql_update_failed: "Échec de la mise à jour : {{message}}",
	studio_sql_execution_not_supported: "La source de données actuelle ne prend pas en charge l'exécution de SQL.",
	studio_sql_error_executing: "Une erreur s'est produite lors de l'exécution de la requête.",
	studio_sql_error_explaining: "Une erreur s'est produite lors de l'analyse de la requête.",
	studio_sql_save_first_to_favorite: "Enregistrez l'extrait avant de l'ajouter aux favoris.",
	studio_sql_collections_label: "Collections:",
	studio_sql_admin_collections_tooltip: "Tables in this query that are mapped as admin collections",
	studio_sql_edit_entity: "Edit {{name}} #{{id}}",
	studio_sql_sql_not_supported: "Cette source de données ne prend pas en charge l'exécution de SQL",
	studio_sql_fetch_error: "Impossible de récupérer les options de connexion : {{message}}",
	studio_sql_unexpected_format: "Format de données inattendu : {{type}}",
	studio_sql_no_tables: "Aucune table trouvée dans cette base de données.",
	studio_sql_schema_fetch_error: "Impossible de récupérer le schéma : {{message}}",
	studio_sql_sidebar_snippets: "Snippets",
	studio_sql_sidebar_history: "History",
	studio_sql_sidebar_schema: "Schema",
	studio_sql_sidebar_no_snippets: "Aucun extrait enregistré",
	studio_sql_sidebar_save_snippet_hint: "Enregistrez une requête pour la retrouver ici.",
	studio_sql_sidebar_no_history: "Aucun historique",
	studio_sql_sidebar_history_hint: "Les requêtes exécutées apparaîtront ici.",
	studio_sql_sidebar_delete_snippet: "Supprimer l'extrait",
	studio_sql_close_tab: "Fermer cet onglet de requête",
	studio_sql_new_tab: "Nouvel onglet de requête",
	studio_schema_tables: "Tables",
	studio_schema_loading: "Chargement du schéma…",
	studio_schema_no_tables: "Aucune table trouvée",
	studio_schema_retry: "Retry",
	studio_schema_primary_key: "Clé primaire",
	studio_schema_select_all: "SELECT * FROM",
	studio_schema_insert_into: "INSERT INTO",
	studio_schema_update: "UPDATE",
	studio_schema_delete_from: "DELETE FROM",
	studio_schema_columns: "{{count}} colonnes",
	studio_rls_title: "Sécurité au niveau des lignes",
	studio_rls_description: "Gérez les politiques RLS de vos tables",
	studio_rls_enabled: "RLS activée",
	studio_rls_disabled: "RLS désactivée",
	studio_rls_no_rls: "Sans RLS",
	studio_rls_enable_rls: "Activer la RLS",
	studio_rls_disable_rls: "Désactiver la RLS",
	studio_rls_create_policy: "Créer une politique",
	studio_rls_policies: "Policies",
	studio_rls_no_policies: "Aucune politique définie",
	studio_rls_no_policies_desc: "Cette table n'a aucune politique RLS. L'accès à toutes les lignes dépend de l'état de la RLS.",
	studio_rls_add_first_policy: "Ajouter votre première politique",
	studio_rls_force_rls: "Forcer la RLS",
	studio_rls_force_rls_desc: "Une fois activé, les politiques RLS s'appliquent aussi au propriétaire de la table.",
	studio_rls_enable_force_rls: "Activer la RLS forcée",
	studio_rls_disable_force_rls: "Désactiver la RLS forcée",
	studio_rls_edit: "Edit",
	studio_rls_delete: "Delete",
	studio_rls_confirm_delete_title: "Supprimer la politique",
	studio_rls_confirm_delete_body: "Voulez-vous vraiment supprimer cette politique ? Cette action est irréversible.",
	studio_rls_confirm_enable_title: "Activer la sécurité au niveau des lignes",
	studio_rls_confirm_enable_body: "Activer la RLS restreindra l'accès aux lignes de cette table selon vos politiques. Assurez-vous d'avoir des politiques appropriées.",
	studio_rls_confirm_disable_title: "Désactiver la sécurité au niveau des lignes",
	studio_rls_confirm_disable_body: "Désactiver la RLS rendra toutes les lignes accessibles à tous les rôles. Voulez-vous vraiment continuer ?",
	studio_rls_select_table: "Sélectionnez une table",
	studio_rls_no_tables: "Aucune table disponible",
	studio_rls_error: "Erreur lors du chargement des politiques",
	studio_rls_retry: "Retry",
	studio_rls_loading: "Chargement des politiques…",
	studio_policy_edit: "Modifier la politique",
	studio_policy_create: "Créer une nouvelle politique",
	studio_policy_defining_rules: "Définition des règles d'accès pour",
	studio_policy_cancel: "Cancel",
	studio_policy_save: "Enregistrer la politique",
	studio_policy_template: "Partir d'un modèle (facultatif)",
	studio_policy_select_template: "Sélectionnez un modèle…",
	studio_policy_name: "Nom de la politique",
	studio_policy_name_placeholder: "Donnez à votre politique un nom explicite",
	studio_policy_behavior: "Comportement de la politique",
	studio_policy_permissive: "Permissive",
	studio_policy_permissive_desc: "Combinée avec « OR »",
	studio_policy_restrictive: "Restrictive",
	studio_policy_restrictive_desc: "Combinée avec « AND »",
	studio_policy_command: "Commande de la politique",
	studio_policy_target_roles: "Rôles ciblés",
	studio_policy_roles_placeholder: "Sélectionnez des rôles (public par défaut)",
	studio_policy_using_expr: "Expression USING",
	studio_policy_using_expr_desc: "S'applique aux lignes existantes (SELECT, UPDATE, DELETE)",
	studio_policy_check_expr: "Expression WITH CHECK",
	studio_policy_check_expr_desc: "S'applique aux lignes nouvelles ou modifiées (INSERT, UPDATE)",
	studio_policy_help_title: "Guide de la sécurité au niveau des lignes (RLS)",
	studio_policy_help_intro: "La sécurité au niveau des lignes (RLS) permet de restreindre les lignes qu'un utilisateur peut consulter ou modifier selon son état d'authentification ou ses rôles.",
	studio_policy_help_step1_title: "1. Sélectionnez un modèle",
	studio_policy_help_step1_desc: "Si vous débutez avec la RLS, le plus simple est de choisir un modèle prédéfini dans la liste. Il configure automatiquement la politique pour des cas courants comme « les utilisateurs peuvent modifier leurs propres lignes ».",
	studio_policy_help_step2_title: "2. Rôles ciblés",
	studio_policy_help_step2_desc: "Choisissez les utilisateurs auxquels la politique s'applique :",
	studio_policy_help_role_public: "S'applique à tout le monde (connecté ou non).",
	studio_policy_help_role_authenticated: "S'applique uniquement aux utilisateurs connectés.",
	studio_policy_help_role_anon: "S'applique uniquement aux utilisateurs qui NE SONT PAS connectés.",
	studio_policy_help_step3_title: "3. Expression USING",
	studio_policy_help_step3_desc: "Une condition SQL qui doit être vraie pour que les lignes existantes soient visibles ou modifiables (SELECT, UPDATE, DELETE).",
	studio_policy_help_step3_example: "Garantit que les utilisateurs ne voient que les lignes dont ils sont propriétaires.",
	studio_policy_help_step4_title: "4. Expression WITH CHECK",
	studio_policy_help_step4_desc: "Une condition SQL qui doit être vraie pour que les lignes nouvelles ou modifiées soient enregistrées (INSERT, UPDATE).",
	studio_policy_help_step4_example: "Généralement identique à l'expression USING lorsque les utilisateurs ne peuvent insérer ou modifier que leurs propres données.",
	studio_policy_help_auth_vars_title: "Variables d'authentification Rebase",
	studio_policy_help_auth_vars_desc: "Rebase fournit des fonctions natives que vous pouvez utiliser dans vos expressions :",
	studio_policy_help_auth_uid: "Renvoie l'identifiant de l'utilisateur courant sous forme de texte. Exemple :",
	studio_policy_help_auth_jwt: "Renvoie l'intégralité de la charge utile du JWT en JSONB, ce qui permet de vérifier des revendications personnalisées. Exemple :",
	studio_policy_help_auth_roles: "Renvoie les identifiants de rôle de l'utilisateur sous forme de chaîne séparée par des virgules. À utiliser de préférence avec :",
	studio_policy_help_docs_cta: "Pour en savoir plus sur les politiques Postgres, consultez la documentation officielle.",
	studio_policy_help_read_docs: "Lire la documentation",
	studio_policy_help_got_it: "J'ai compris !",
	studio_add_kanban_column: "Ajouter une colonne",
	studio_add_kanban_column_title: "Ajouter une colonne Kanban",
	studio_add_kanban_column_name: "Nom de la colonne",
	studio_add_kanban_column_name_placeholder: "Nom de la nouvelle colonne",
	studio_add_kanban_column_add: "Add",
	studio_add_kanban_column_cancel: "Cancel",
	studio_collection_view_sql: "Éditeur SQL",
	studio_collection_view_admin: "Vue d'administration",
	studio_editor_collection_tooltip: "Modifier le schéma de la collection",
	studio_editor_collection_no_permission: "Vous n'avez pas la permission de modifier cette collection",
	studio_editor_collection_start_tooltip: "Copier le chemin ou modifier le schéma",
	studio_editor_collection_start_no_permission: "Vous n'avez pas la permission de modifier cette collection",
	studio_editor_collection_start_copied: "Copied!",
	studio_editor_entity_tooltip: "Modifier le schéma de l'entité",
	studio_editor_entity_no_permission: "Vous n'avez pas la permission de modifier cette entité",
	studio_home_edit_collection: "Modifier la collection",
	studio_home_delete_collection: "Supprimer la collection",
	studio_home_confirm_delete_title: "Supprimer la collection ?",
	studio_home_confirm_delete_body: "Voulez-vous vraiment supprimer la collection « {{name}} » ? Cette action est irréversible.",
	studio_kanban_setup: "Configuration du Kanban",
	studio_missing_reference: "Référence manquante : {{path}}",
	studio_missing_reference_create: "Create",
	studio_new_collection: "Nouvelle collection",
	studio_new_collection_card: "Nouvelle collection",
	studio_new_collection_no_permission: "Vous n'avez pas les permissions pour créer des collections",
	studio_property_add_column: "Ajouter une nouvelle propriété",
	studio_property_add_column_no_permission: "Vous n'avez pas la permission d'ajouter de nouvelles propriétés",
	studio_add_kanban_column_desc: "Ajoute une nouvelle option à la propriété « {{property}} ». Une nouvelle colonne apparaîtra dans le tableau Kanban.",
	studio_add_kanban_column_adding: "Adding...",
	studio_collection_view_edit: "Edit",
	studio_editor_collection_disabled: "La modification des collections est désactivée",
	studio_editor_collection_edit: "Modifier la collection",
	studio_editor_entity_save_first: "Vous devez enregistrer le document avant de modifier le schéma",
	studio_editor_entity_edit_schema: "Modifier le schéma de ce formulaire",
	studio_editor_collection_start_save_filter: "Enregistrer le filtre et le tri par défaut",
	studio_editor_collection_start_clear_filter: "Effacer le filtre et le tri par défaut",
	studio_editor_collection_start_reset_filter: "Rétablir le filtre et le tri par défaut",
	studio_editor_collection_start_saved: "Configuration par défaut enregistrée",
	studio_home_duplicate_collection: "Duplicate",
	studio_home_delete: "Delete",
	studio_home_confirm_delete_no_data: "Aucune donnée ne sera supprimée, seulement la collection dans l'interface d'administration",
	studio_home_collection_deleted: "Collection supprimée",
	studio_kanban_configure: "Configurer le Kanban",
	studio_missing_reference_error: "Aucune collection pour le chemin : {{path}}",
	studio_new_collection_add: "Ajouter une nouvelle collection",
	db_column_type: "Type de colonne de base de données",
	primary_key_unique_id: "Clé primaire / ID unique",
	spread_children_as_columns: "Étendre les enfants en colonnes",
	mode: "Mode",
	timezone: "Fuseau horaire",
	target_collection: "Collection cible",
	storage_file_name: "Nom du fichier",
	storage_path: "Chemin de stockage",
	storage_max_size: "Taille maximale (en octets)",
	storage_resize_mode: "Mode de redimensionnement",
	storage_output_format: "Format de sortie",
	storage_max_width: "Largeur maximale (px)",
	storage_max_height: "Hauteur maximale (px)",
	storage_quality: "Qualité (0-100)",
	storage_file_upload_config: "Configuration de téléchargement de fichier",
	storage_image_resize_config: "Configuration du redimensionnement d'image",
	storage_all_file_types: "Tous les types de fichiers autorisés",
	storage_allowed_file_types: "Types de fichiers autorisés",
	storage_include_bucket_url: "Inclure l'URL du bucket (s3://...) dans la valeur enregistrée",
	storage_save_url: "Enregistrer l'URL au lieu du chemin de stockage",
	datetime_automatic_value: "Valeur automatique",
	markdown_paste_behavior: "Comportement au collage",
	entity_saved: "Enregistré",
	more_actions: "Plus d'actions",
	record_history: "Historique",
	inspect_record: "Inspecter l'enregistrement",
	inspect: "Inspecter",
	close_inspector: "Fermer l'inspecteur",
	open_in_live_site: "Ouvrir sur le site en ligne",
	history_revert: "Revenir à cette version",
	history_revert_title: "Revenir à cette version des données ?",
	history_reverted: "Retour à la version sélectionnée",
	history_revert_error: "Erreur lors du retour en arrière",
	history_revert_dirty: "Enregistrez ou annulez vos modifications avant de revenir en arrière",
	history_empty: "Aucun historique disponible",
	history_empty_description: "À chaque enregistrement, une nouvelle version est créée et conservée dans l'historique.",
	history_new_entity: "L'historique n'est disponible que pour les enregistrements existants",
	history_no_more: "Plus d'historique",
	studio_group_views: "Vues",
	studio_group_admin: "Administration",
	studio_group_settings: "Paramètres",
	studio_group_database: "Base de données",
	studio_group_compute: "Calcul",
	studio_group_storage: "Stockage",
	studio_group_api: "API",
	studio_group_access_control: "Contrôle d'accès",
	studio_tool_sql: "Console SQL",
	studio_tool_sql_description: "Exécuter des requêtes SQL",
	studio_tool_js: "Console JS",
	studio_tool_js_description: "Exécuter du JavaScript",
	studio_tool_rls: "Politiques RLS",
	studio_tool_rls_description: "Sécurité au niveau des lignes",
	studio_tool_storage: "Stockage",
	studio_tool_storage_description: "Gérer les fichiers de stockage",
	studio_tool_cron: "Tâches cron",
	studio_tool_cron_description: "Gérer les tâches planifiées",
	studio_tool_schema_visualizer: "Visualiseur de schéma",
	studio_tool_schema_visualizer_description: "Diagramme ER interactif de la base de données",
	studio_tool_branches: "Branches",
	studio_tool_branches_description: "Créer et gérer des branches de base de données",
	studio_tool_backups: "Sauvegardes",
	studio_tool_backups_description: "Télécharger les sauvegardes de la base de données",
	studio_tool_api: "Explorateur d'API",
	studio_tool_api_description: "Documentation d'API interactive et tests",
	studio_tool_logs: "Explorateur de journaux",
	studio_tool_logs_description: "Journaux système et de requêtes en temps réel",
	studio_tool_api_keys: "Clés d'API",
	studio_tool_api_keys_description: "Créer et gérer des clés d'API à portée limitée",
	studio_backups_denied_title: "Vous ne pouvez pas lister les sauvegardes de ce projet",
	studio_backups_denied_hint: "Le projet n'a rien d'anormal. Lister les sauvegardes nécessite une connexion administrateur, et le compte connecté a été refusé.",
	studio_backups_read_failed: "Impossible de lire les sauvegardes de ce projet",
	studio_backups_downloading: "Téléchargement…",
	studio_backups_roles_file: "Fichier des rôles",
	studio_backups_roles_file_hint: "Le .globals.sql enregistré avec cette sauvegarde. Il recrée les rôles de base de données auxquels se réfèrent les autorisations et la sécurité au niveau des lignes du dump, et `rebase db restore` le lit à côté du .dump : gardez les deux fichiers ensemble.",
	studio_backups_no_roles_file: "Aucun fichier des rôles",
	studio_backups_no_roles_file_hint: "Aucun .globals.sql n'a été enregistré avec cette sauvegarde. Une restauration dans un nouveau Postgres s'arrête à la première autorisation accordée à un rôle de base de données qui n'y existe pas.",
	studio_cron_denied_title: "Vous ne pouvez pas lister les tâches cron de ce projet",
	studio_cron_denied_hint: "Le projet n'a rien d'anormal. L'API cron est réservée aux administrateurs, et le compte connecté a été refusé.",
	studio_cron_read_failed: "Impossible de lire les tâches cron de ce projet",
	studio_api_keys_denied_title: "Vous ne pouvez pas lister les clés d'API de ce projet",
	studio_api_keys_denied_hint: "Le projet n'a rien d'anormal. Gérer les clés d'API est une opération d'administrateur, et le compte connecté a été refusé.",
	studio_api_keys_read_failed: "Impossible de lire les clés d'API de ce projet",
	studio_api_keys_empty_title: "Aucune clé d'API pour l'instant",
	studio_api_keys_empty_hint: "Créez une clé pour permettre l'authentification de service à service",
	studio_api_keys_revoke: "Révoquer",
	studio_api_keys_select_hint: "Sélectionnez une clé d'API pour voir les détails",
	studio_api_keys_permissions: "Autorisations",
	studio_api_keys_no_permissions: "Aucune autorisation configurée",
	studio_api_keys_revoke_confirmation: "Confirmation de révocation",
	studio_api_keys_name_label: "Nom :",
	studio_api_keys_access_label: "Accès :",
	studio_api_keys_admin_granted: "Rôle d'administrateur accordé",
	studio_api_keys_admin_granted_hint: "les routes d'administration et les politiques default_admin",
	studio_api_keys_done: "Terminé",
	studio_api_keys_status_active: "Active",
	studio_api_keys_status_expired: "Expirée",
	studio_api_keys_status_revoked: "Révoquée",
	studio_api_keys_stat_status: "Statut",
	studio_api_keys_stat_last_used: "Dernière utilisation",
	studio_api_keys_stat_expires: "Expire",
	studio_api_keys_never: "Jamais",
	studio_api_keys_role_service: "Service",
	studio_api_keys_stat_rate_limit: "Limite de débit",
	studio_api_keys_rate_limit_default: "Par défaut (1000/15 min)",
	studio_api_keys_stat_created_by: "Créée par",
	studio_api_keys_copy_failed: "La copie a échoué",
	studio_branches_denied_title: "Vous ne pouvez pas lister les branches de ce projet",
	studio_branches_denied_hint: "Le projet n'a rien d'anormal. Le branchement passe par la connexion administrateur, et le compte connecté a été refusé.",
	studio_branches_read_failed: "Impossible de lire les branches de ce projet",
	studio_schema_no_collections: "Aucune collection déclarée",
	studio_schema_no_collections_body: "Il n'y a encore rien à dessiner. Ajoutez une collection dans config/collections/, ou ouvrez « Modifier les collections » pour en ajouter une ici.",
	studio_schema_introspect_hint: "Sur une base de données qui a déjà des tables, rebase schema introspect écrit les fichiers de collection pour vous.",
	studio_rls_enable_confirm_title: "Activer la sécurité au niveau des lignes ?",
	studio_rls_enable_confirm_body: "Tant qu'aucune politique ne l'autorise, aucune ligne de {{table}} n'est lisible. Postgres refuse par défaut.",
	studio_rls_disable_confirm_title: "Désactiver la sécurité au niveau des lignes ?",
	studio_rls_disable_confirm_body: "Toutes les politiques de {{table}} cessent de s'appliquer. Quiconque atteint la table peut lire et écrire chaque ligne.",
	studio_rls_drop_policy_title: "Supprimer cette politique ?",
	studio_rls_drop_policy_body: "{{policy}} est retirée de {{table}} dans la base de données. Rien n'est écrit dans votre code, un déploiement peut donc la rétablir.",
	studio_rls_unapplied: "Non appliquée",
	studio_rls_unapplied_tooltip: "Déclarée dans votre code, pas encore dans la base de données. Enregistrez-la ici pour planifier et appliquer le changement, ou lancez `rebase db push`.",
	studio_branches_unavailable_title: "Le branchement de base de données n'est pas disponible",
	studio_branches_unavailable_body: "Le branchement requiert un backend Postgres avec une connexion administrateur. Définissez ADMIN_CONNECTION_STRING dans l'environnement du backend.",
	studio_backups_not_configured_title: "Les sauvegardes ne sont pas configurées",
	studio_backups_not_configured_body: "Définissez BACKUP_DESTINATION sur un chemin local ou une URL s3:// ou gs://, puis créez une sauvegarde avec `rebase db backup` ou un cron planifié.",
	studio_cron_empty_title: "Aucune tâche cron enregistrée",
	studio_cron_empty_body: "Ajoutez un fichier dans backend/crons/ qui exporte par défaut une CronJobDefinition.",
	studio_read_the_docs: "Consulter la documentation"
};
//#endregion
//#region src/locales/it.ts
var it = {
	save: "Salva",
	create: "Crea",
	create_copy: "Crea copia",
	save_and_close: "Salva e chiudi",
	create_copy_and_close: "Crea copia e chiudi",
	create_and_close: "Crea e chiudi",
	discard: "Scarta",
	clear: "Cancella",
	cancel: "Annulla",
	edit: "Modifica",
	copy: "Copia",
	delete: "Elimina",
	delete_not_allowed: "Hai selezionato almeno un'entità che non puoi eliminare",
	edit_entity: "Modifica",
	back_to_detail: "Torna ai dettagli",
	delete_confirmation_title: "Eliminare?",
	delete_confirmation_body: "Questo eliminerà l'entità. Sei sicuro?",
	delete_multiple_confirmation_body: "Questo eliminerà le entità selezionate. Sei sicuro?",
	unsaved_changes_title: "Modifiche non salvate",
	unsaved_changes_body: "Hai modifiche non salvate. Vuoi scartarle?",
	discard_changes: "Scarta le modifiche",
	keep_editing: "Continua a modificare",
	clear_form: "Svuotare il modulo?",
	discard_changes_confirmation: "Tutte le modifiche non salvate andranno perse. Questa azione non può essere annullata.",
	clear_form_confirmation: "Tutti i valori inseriti verranno cancellati. Questa azione non può essere annullata.",
	search: "Cerca",
	find_by_id: "Cerca per ID",
	find_entity_by_id: "Cerca entità per ID",
	filter: "Filtra",
	filters: "Filtri",
	records_in_view: "Record in questa vista",
	clear_filter: "Cancella filtro",
	clear_filter_sort: "Cancella filtro/ordinamento",
	clear_sort: "Cancella ordinamento",
	sort: "Ordina",
	sort_by: "Ordina per",
	sort_then_by: "Poi per",
	sort_ascending: "Ordina in modo crescente",
	sort_descending: "Ordina in modo decrescente",
	sort_remove: "Rimuovi ordinamento",
	sort_move_up: "Rendi questa chiave più importante",
	sort_move_down: "Rendi questa chiave meno importante",
	sort_remove_key: "Rimuovi questa chiave di ordinamento",
	sort_key_position: "Chiave di ordinamento {{position}}",
	sort_shift_click_hint: "Maiusc+clic per aggiungere una colonna sotto l'ordinamento attuale",
	save_entity_before_subcollections: "Devi salvare l'entità prima di aggiungere altre raccolte",
	clear_all: "Cancella tutto",
	no_items: "Nessun elemento",
	no_entries_found: "Nessuna voce trovata",
	all_entries_loaded: "Tutte le {{count}} voci caricate",
	create_your_first_entry: "Crea la tua prima voce",
	no_results_filter_sort: "Nessun risultato con il filtro/ordinamento applicato",
	no_results_search: "Nessun risultato trovato per \"{{search}}\"",
	add: "Aggiungi",
	remove: "Rimuovi",
	multiple_entities: "Entità multiple",
	unsaved_changes: "Hai modifiche non salvate in {{collectionName}}.",
	so_empty: "Così vuoto...",
	no_results: "Nessun risultato con il filtro/ordinamento applicato",
	refresh_data: "Aggiorna i dati",
	dark_mode: "Scuro",
	light_mode: "Chiaro",
	system_mode: "Sistema",
	join_newsletter: "Iscriviti alla newsletter, niente spam",
	ok: "Ok",
	save_collection_config: "Salva struttura della collezione",
	search_for_more_icons: "Cerca altre icone…",
	ai_modified: "Modificato dall'IA",
	size_label: "Dimensioni",
	group_by: "Raggruppa per",
	initialize_kanban_order: "Inizializza ordine Kanban",
	copy_id: "Copia ID",
	add_specific: "Aggiungi {{name}}",
	add_named: "Aggiungi \"{{name}}\"",
	select_specific: "Seleziona {{name}}",
	select_from: "Seleziona da {{name}}",
	done: "Fatto",
	log_out: "Esci",
	column_cannot_be_edited: "Questa colonna non può essere modificata direttamente",
	close: "Chiudi",
	hide_list: "Nascondi elenco",
	show_list: "Mostra elenco",
	unsaved_local_changes: "Modifiche locali non salvate",
	unsaved_local_changes_description: "Questo documento è stato modificato localmente e ha modifiche non salvate. Queste modifiche locali andranno perse se non le applichi.",
	preview_changes: "Anteprima modifiche",
	apply_changes: "Applica modifiche",
	discard_local_changes: "Scarta modifiche locali",
	preview_local_changes: "Anteprima modifiche locali",
	preview_local_changes_description: "Queste sono le modifiche locali che verranno applicate al modulo.",
	type: "Tipo",
	string: "stringa",
	number: "numero",
	boolean: "booleano",
	date: "data",
	map: "mappa",
	array: "array",
	arrays_of_arrays_not_supported: "Array di array non sono supportati.",
	data_type_not_supported: "Tipo di dati {{dataType}} non ancora supportato",
	passkey_error_unsupported: "Il tuo dispositivo o browser non supporta le passkey.",
	saved_correctly: "Salvato correttamente",
	error_before_saving: "Errore nella preparazione dei dati",
	error_after_saving: "Errore nel processo post-salvataggio",
	error_saving_entity: "Errore durante il salvataggio dell'entità",
	entity_does_not_exist: "Questa entità non esiste nel database",
	form_modified: "Questo modulo è stato modificato",
	form_in_sync: "Il modulo attuale è sincronizzato con il database",
	fix_errors_before_saving: "Correggi gli errori evidenziati prima di salvare",
	admin: "Admin",
	home: "Home",
	this_form_has_errors: "Questo modulo contiene errori",
	error_loading_navigation: "Errore durante il caricamento della navigazione",
	error_loading_auth: "Errore durante il caricamento dell'autenticazione",
	this_form_has_been_modified: "Questo modulo è stato modificato",
	current_form_in_sync: "Il modulo corrente è sincronizzato con il database",
	open_in_console: "Apri nella console",
	collection_does_not_exist: "La collezione specificata non esiste. Controlla la console",
	unexpected_value: "Valore inaspettato",
	unexpected_value_click_to_edit: "Valore inaspettato. Fai clic per modificare",
	unexpected_reference_value: "Valore di riferimento inaspettato. Fai clic per modificare",
	copy_url_to_clipboard: "Copia url negli appunti",
	open_image_in_new_tab: "Apri immagine in una nuova scheda",
	reference_not_set: "Riferimento non impostato",
	reference_does_not_exist: "Il riferimento non esiste",
	entity_not_found: "Entità non trovata",
	entity_not_found_body: "Questo record non esiste, oppure è nascosto per te dalla sicurezza a livello di riga.",
	back_to_collection: "Torna a {{name}}",
	file_not_found: "File non trovato",
	unsaved_changes_in_entity: "Hai modifiche non salvate in questa entità.",
	delete_this_role: "Elimina questo ruolo",
	no_roles_yet: "Non hai ancora ruoli.",
	create_default_roles: "Crea ruoli predefiniti",
	delete_role_confirmation: "Sei sicuro di voler eliminare questo ruolo?",
	delete_this_user: "Elimina questo utente",
	no_users_yet: "Non ci sono ancora utenti",
	add_logged_user_as_admin: "Aggiungi l'utente registrato come amministratore",
	add_current_user_as_admin: "Aggiungi l'utente corrente come amministratore",
	create_default_roles_and_add_admin: "Crea i ruoli predefiniti e aggiungi l'utente corrente come amministratore",
	delete_user_confirmation: "Sei sicuro di voler eliminare questo utente?",
	create_your_users_and_roles: "Crea i tuoi utenti e ruoli",
	no_users_or_roles_defined: "Non hai definito né utenti né ruoli. Puoi creare ruoli predefiniti e aggiungere l'utente corrente come amministratore.",
	save_before_changing_schema: "Devi salvare il documento prima di modificare lo schema",
	edit_schema_for_this_form: "Modifica lo schema per questo modulo",
	no_permissions_to_edit_collection: "Non hai i permessi per modificare questa collezione",
	browser_does_not_support_audio: "Il tuo browser non supporta l'elemento audio.",
	user_not_found: "Utente non trovato: {{value}}",
	delete_selected: "Elimina selezionato",
	cannot_delete_selected: "Hai selezionato almeno un'entità che non puoi eliminare",
	add_entry: "Aggiungi",
	add_on_top: "Aggiungi in alto",
	add_below: "Aggiungi in basso",
	add_to_field: "Aggiungi a {{fieldName}}",
	value: "Valore",
	key: "Chiave",
	drag_drop_multiple: "Trascina qui alcuni file oppure fai clic per selezionarli. Trascina per riordinare.",
	drag_drop_single: "Trascina qui un file oppure fai clic per selezionarne uno",
	open_menu: "Apri menù",
	close_drawer: "Chiudi cassetto",
	navigation_drawer: "Cassetto di navigazione",
	collapse: "Comprimi",
	expand: "Espandi",
	change_language: "Cambia lingua",
	toggle_theme: "Cambia tema",
	user_menu: "Menu utente",
	error: "Errore",
	error_loading_data: "Impossibile caricare i dati",
	error_check_server_logs: "Consulta i log del server per i dettagli.",
	error_technical_details: "Dettagli tecnici",
	error_uploading_file: "Errore durante il caricamento del file",
	error_deleting: "Errore durante l'eliminazione",
	error_before_delete: "Errore prima dell'eliminazione",
	error_updating_asset: "Errore durante l'aggiornamento dell'asset",
	error_deleting_asset: "Errore durante l'eliminazione dell'asset",
	error_firestore_index: "Per questa query è richiesto un indice Firestore.",
	create_index: "Crea indice",
	value_is_not_reference: "Il valore non è un riferimento.",
	click_to_edit: "Fai clic per modificare",
	data_is_not_array_of_references: "I dati non sono un array di riferimenti",
	loading: "Caricamento in corso",
	local_changes_applied: "Modifiche locali applicate al modulo",
	local_changes_discarded: "Modifiche locali scartate",
	are_you_sure_leave: "Sei sicuro di voler uscire da questa pagina?",
	see_console_details: "Vedi la console per maggiori dettagli.",
	drop_here_create_group: "Rilascia qui per creare un nuovo gruppo",
	filter_for_null_values: "Filtra per valori nulli",
	value_updated_elsewhere: "Questo valore è stato aggiornato altrove",
	add_property: "Aggiungi proprietà",
	edit_name: "Modifica {{name}}",
	this_entity_not_exist: "Questa entità non esiste nel database",
	internal_error: "ERRORE interno",
	rename_group: "Rinomina gruppo",
	group_name_label: "Nome gruppo",
	group_name_empty_error: "Il nome del gruppo non può essere vuoto.",
	group_name_exists_error: "Questo nome del gruppo esiste già.",
	search_collections: "Cerca collezioni",
	views_group: "Viste",
	youd_need_to_save_before_additional_collections: "Devi salvare la tua entità prima di poter aggiungere altre collezioni",
	page_not_found: "Pagina non trovata",
	page_not_found_body: "Questa pagina non esiste o potresti non avere i permessi per accedervi",
	back_to_home: "Torna alla home",
	default_collection_view: "Vista collezione predefinita",
	table_view: "Tabella",
	cards_view: "Schede (card)",
	kanban_view: "Kanban",
	choose_how_entities_displayed_default: "Scegli come debbano essere visualizzate le entità di default",
	document_view: "Vista documento",
	side_panel: "Pannello laterale",
	full_screen: "Schermo intero",
	should_documents_opened_full_screen: "I documenti dovrebbero essere aperti a schermo intero o in una finestra di dialogo laterale integrata",
	select_custom_view: "Seleziona vista personalizzata",
	no_custom_views_defined: "Nessuna vista personalizzata definita. Definisci le viste personalizzate nelle impostazioni di personalizzazione prima di utilizzare questa finestra.",
	select_custom_action: "Seleziona azione personalizzata",
	no_custom_actions_defined: "Nessuna azione personalizzata definita. Definisci le azioni personalizzate nelle impostazioni di personalizzazione prima di utilizzare questa finestra.",
	no_collections_found: "Nessuna collezione trovata",
	start_building_collections: "Inizia facilmente a costruire collezioni in Rebase. Mapale comodamente ai dati del tuo database esistente, importali da file o usa i nostri modelli.",
	create_first_collection: "Crea la tua prima collezione",
	define_collections_programmatically: "Puoi anche definire le collezioni a livello di codice.",
	edit_collection: "Modifica collezione",
	no_permissions_edit_collection: "Non disponi dei permessi per modificare questa collezione",
	no_permissions_create_collection: "Non disponi dei permessi per creare collezioni",
	create_collection: "Crea collezione",
	update_collection: "Aggiorna collezione",
	new_collection: "Nuova collezione",
	add_new_collection: "Aggiungi nuova collezione",
	collection_with_name: "Collezione {{name}}",
	change_icon: "Cambia icona",
	is_subcollection_of: "Questa è una sottocollezione di",
	name: "Nome",
	collection_name_description: "Il nome di questa collezione, solitamente un nome al plurale (es. Prodotti)",
	path: "Percorso",
	relative_path_to_parent: "Percorso relativo al genitore (non è necessario includere il percorso genitore)",
	path_in_database: "Il percorso in cui questa collezione è archiviata nel database",
	singular_name: "Nome singolare",
	singular_name_description: "Facoltativamente definisci un nome singolare per le tue entità",
	description: "Descrizione",
	description_of_collection: "Descrizione della collezione, puoi utilizzare il markdown",
	collection_id: "ID della collezione",
	collection_id_description: "Questo ID identifica in modo univoco questa collezione. Spesso coincide con il percorso.",
	collection_group: "Gruppo di collezioni",
	collection_group_description: "Un gruppo di collezioni include tutte le collezioni con lo stesso percorso. Ciò consente di interrogare più collezioni simultaneamente.",
	advanced_settings: "Impostazioni avanzate",
	doc_history_global: "Revisioni della cronologia dei documenti abilitate se abilitate a livello globale",
	doc_history_enabled: "Revisioni della cronologia dei documenti ABILITATE",
	doc_history_not_enabled: "Revisioni della cronologia dei documenti NON abilitate",
	doc_history_description: "Se abilitata questa opzione, ogni documento in questa collezione avrà una cronologia delle modifiche per scopi di auditing. I dati vengono salvati in una sottocollezione del documento denominata __history.",
	document_id_generation: "Generazione ID documento",
	code_defined: "Definito dal codice",
	users_must_define_id: "Gli utenti devono definire un ID",
	users_can_define_id: "Gli utenti possono definire un ID, ma non è obbligatorio",
	doc_id_auto_generated: "L'ID del documento viene generato automaticamente",
	config_doc_id_generation: "Configura il modo in cui vengono generati gli ID dei documenti per le nuove entità.",
	enable_text_search: "Abilita la ricerca testuale in questa collezione",
	text_search_description: "Attiva la ricerca testuale per questa collezione. Se non è specificato un delegato di ricerca testuale, si utilizzerà la ricerca locale integrata. Si sconsiglia l'uso con collezioni grandi, essendo soggetto a problemi di prestazioni e costi.",
	database_id: "ID del Database",
	default_text: "(predefinito)",
	custom_actions: "Azioni personalizzate",
	more_info: "Ulteriori info",
	define_custom_actions_cli: "Definisci le tue azioni personalizzate ed eseguine l'upload tramite l'interfaccia a riga di comando (CLI).",
	action_defined_in_code: "Questa azione è definita dal codice con la chiave",
	add_custom_entity_action: "Aggiungi azione di entità personalizzata",
	remove_this_action: "Rimuovi questa azione?",
	remove_action_warning: "Questa operazione non eliminerà dati, ma solo l'azione dal pannello di amministrazione",
	subcollections_of: "Sottocollezioni di",
	add_subcollection: "Aggiungi sottocollezione",
	custom_views: "Viste personalizzate",
	define_custom_views_cli: "Definisci le tue viste personalizzate e caricale tramite l'interfaccia a riga di comando (CLI).",
	view_defined_in_code: "Questa vista è definita nel codice dalla chiave",
	add_custom_entity_view: "Aggiungi vista ad entità personalizzata",
	delete_this_subcollection: "Vuoi eliminare questa sottocollezione?",
	remove_collection_warning: "Attenzione: questo rimuoverà solo la collezione configurata nel pannello di amministrazione, senza eliminare i dati effettivi.",
	remove_this_view: "Desideri rimuovere questa vista?",
	remove_view_warning: "Questa azione rimuoverà solo la visualizzazione dal pannello di amministrazione; i dati rimarranno intatti.",
	no_collection_selected: "Nessuna collezione selezionata",
	code_for_collection: "Codice per",
	use_config_define_json: "Utilizza questa configurazione per definire la collezione in formato JSON.",
	customise_collection_code: "Se desideri personalizzare ulteriormente tramite codice la collezione, puoi aggiungere questo frammento di codice nel tuo pannello di amministrazione.",
	copied: "Copiato",
	property_cant_be_edited: "Non è possibile modificare questa proprietà",
	property_not_editable_description: "Potresti non disporre dei permessi per modificarla o potrebbe essere definita nel codice e non modificabile.",
	delete_this_property: "Desideri eliminare questa proprietà?",
	delete_property_warning: "Questo non cancellerà i dati associati, modificherà semplicemente la configurazione della collezione.",
	error_must_specify_id: "È d'obbligo specificare un id per questo campo",
	error_id_format: "L'id può contenere solo lettere, numeri e underscore (_) e non deve iniziare con un numero",
	error_id_already_exists: "Esiste già un altro campo utilizzando questo ID",
	error_must_specify_title: "Specifica un titolo obbligatorio per questo campo",
	custom_or_other: "Personalizzato/Altro",
	select_property_widget: "Scegli un widget per la proprietà",
	error_changing_data_type: "Il widget selezionato adotta un formato di dati diverso rispetto a quello iniziale. Questa modifica potrebbe causare errori sui dati già presenti.",
	required: "Richiesto",
	enum_form_dialog: "Finestra di dialogo modulo Enum",
	imported_data_preview: "Anteprima dei dati importati",
	entities_with_same_id_overwritten: "Le entità con lo stesso id verranno sovrascritte",
	collection_editor: "Editor della collezione",
	properties_in_this_group: "Proprietà di questo gruppo",
	data_property_mapping: "Mappatura dei dati alle proprietà",
	property_edit_view: "Vista di modifica delle proprietà",
	all_of_these: "Tutti questi",
	any_of_these: "Alcuni di questi",
	only_admins_edit_roles: "Solo gli amministratori possono modificare i ruoli",
	error_user_not_found: "Utente non trovato",
	all_roles: "Tutti i ruoli",
	role: "Ruolo",
	name_of_this_role: "Nome di questo ruolo",
	id_of_this_role: "ID di questo ruolo",
	create_entities: "Crea entità",
	read_entities: "Leggi entità",
	update_entities: "Aggiorna entità",
	delete_entities: "Elimina entità",
	all_collections: "Tutte le collezioni",
	create_entities_in_collections: "Crea entità nelle collezioni",
	access_all_data_in_every_collection: "Accedi a tutti i dati in ogni collezione",
	update_data_in_any_collection: "Aggiorna i dati in qualsiasi collezione",
	delete_data_in_any_collection: "Elimina i dati in qualsiasi collezione",
	allow_all_permissions_in_this_collections: "Consenti tutti i permessi in queste collezioni",
	all: "Tutto",
	customise_permissions_description: "Puoi personalizzare i permessi che gli utenti associati a questo ruolo possono eseguire nelle entità di ciascuna collezione",
	create_collections: "Crea collezioni",
	yes: "Sì",
	no: "No",
	can_user_create_collections: "L'utente può creare collezioni",
	edit_collections: "Modifica collezioni",
	only_own_collections: "Solo le proprie collezioni",
	own: "Propri",
	can_user_edit_collections: "L'utente può modificare le collezioni",
	delete_collections: "Elimina collezioni",
	can_user_delete_collections: "L'utente può eliminare le collezioni",
	error_saving_role: "Si è verificato un errore durante il salvataggio del ruolo",
	create_role: "Crea ruolo",
	update: "Aggiorna",
	only_admins_change_roles: "Solo gli amministratori possono modificare i ruoli",
	must_be_at_least_one_admin: "Deve esserci almeno un amministratore",
	logged_user_not_found: "Utente connesso non trovato",
	user: "Utente",
	user_id: "ID utente",
	name_of_this_user: "Nome dell'utente",
	email_of_this_user: "Email dell'utente",
	roles: "Ruoli",
	create_user: "Crea utente",
	users: "Utenti",
	add_user: "Aggiungi utente",
	add_role: "Aggiungi ruolo",
	is_admin: "È amministratore",
	default_permissions: "Permessi predefiniti",
	created_on: "Creato il",
	email: "Email",
	id: "ID",
	read: "Lettura",
	column_in_file: "Colonna nel file",
	map_to_property: "Mappa alla proprietà",
	default_values: "Valori predefiniti",
	default_values_description: "Non tutte le righe del file importato contengono dati per ogni colonna. Assegna valori predefiniti che Rebase utilizzerà per le colonne mancanti.",
	property: "Proprietà",
	default_value: "Valore predefinito",
	autogenerate_id: "Genera automaticamente ID",
	id_column_description: "Imposta la colonna che verrà utilizzata come ID dell'entità",
	do_not_set_value: "Non impostare il valore",
	set_value_to_true: "Imposta il valore su Vero",
	set_value_to_false: "Imposta il valore su Falso",
	drag_and_drop_file: "Trascina e rilascia un file CSV, Excel o JSON qui, oppure fai clic per caricare",
	error_saving_data: "Errore durante il salvataggio dei dati",
	retry: "Riprova",
	saving_data: "Salvataggio dei dati in corso...",
	entities_saved: "entità salvate",
	do_not_close_tab: "Non chiudere questa scheda durante l'importazione dei dati",
	import: "Importa",
	import_data: "Importa dati",
	upload_file_description: "Carica il tuo file CSV, Excel o foglio di calcolo per importare i dati",
	back: "Indietro",
	next: "Avanti",
	save_data: "Salva dati",
	use_column_as_id: "Usa questa colonna come ID dell'entità",
	do_not_import_property: "Non importare questa proprietà",
	entities_will_be_overwritten: "Le entità esistenti con lo stesso ID verranno sovrascritte",
	data_imported_successfully: "Dati importati con successo",
	export: "Esporta",
	export_data: "Esporta dati",
	download_table_csv: "Scarica la tabella come file CSV",
	csv: "CSV",
	json: "JSON",
	dates_as_timestamps: "Date come timestamp",
	dates_as_strings: "Date come stringhe",
	flatten_arrays: "Appiattisci gli array",
	download: "Scarica",
	large_number_of_documents: "Questa collezione contiene un numero elevato di documenti ({{count}})",
	too_many_documents_to_export: "Questa collezione contiene più documenti ({{count}}) di quanti il browser possa esportarne in un solo file (limite {{limit}}). Filtrala prima di esportare.",
	include_undefined_values: "Includi valori non definiti",
	submit: "Invia",
	no_filterable_properties: "Nessuna proprietà filtrabile disponibile",
	apply_filters: "Applica filtri",
	filter_presets: "Preimpostazioni",
	filter_preset_apply: "Applica preimpostazione",
	filter_preset_active: "Attivo: {{label}}",
	list: "Elenco",
	table_view_mode: "Tabella",
	cards: "Schede",
	board: "Bacheca",
	initialize_kanban_order_desc: "Verranno assegnati valori di ordine sequenziali a tutti gli elementi che ne sono privi. Gli elementi manterranno l'ordine corrente all'interno di ciascuna colonna.",
	kanban_view_not_available: "Vista Kanban non disponibile",
	kanban_view_requires_enum: "La vista Kanban richiede una proprietà stringa con valori enumerati per raggruppare le entità nelle colonne. Aggiungi una proprietà enum allo schema della tua collezione per utilizzare questa vista.",
	no_enum_values_configured: "Nessun valore enumerativo configurato per \"{{property}}\"",
	items_need_backfill: "Alcuni elementi non hanno un ordine definito. Fai clic su 'Inizializza' per impostare l'ordine.",
	kanban_order_not_configured: "L'ordinamento drag-and-drop non è configurato. Imposta una proprietà di ordinamento nelle impostazioni della collezione per mantenere le posizioni delle schede.",
	initialize: "Inizializza",
	confirm_multiple_delete: "Conferma eliminazione multipla",
	delete_entity_confirm_title: "Eliminare '{{entityName}}'?",
	add_existing: "Aggiungi esistente",
	unlink_entity_confirm_title: "Rimuovere questo/a {{entityName}} da questo record?",
	unlink_entity_confirm_body: "Resta in {{collectionName}} e disponibile per altri record.",
	confirm_multiple_unlink: "rimuoverli da questo record?",
	no_filter: "Nessun filtro",
	is_true: "è vero",
	is_false: "è falso",
	error_uploading_file_size: "Errore nel caricamento: il file supera {{maxSize}} byte",
	error_uploading_file_type: "Errore nel caricamento: tipo di file non supportato",
	drag_drop_files: "Trascina qui i file, oppure clicca per modificarli",
	drag_drop_file: "Trascina qui un file, oppure clicca per modificarlo",
	multiple_deleted: "Eliminazioni multiple",
	some_entities_deleted: "Alcune entità sono state eliminate, ma non tutte",
	error_deleting_entities: "Errore durante l'eliminazione delle entità",
	deleted: "eliminata",
	selection_options: "Opzioni di selezione",
	selection_menu_all_loaded: "Tutto in questa pagina",
	selection_menu_all_matching: "Tutti i {{total}} {{collection}}",
	selection_menu_none: "Nessuno",
	selection_select_all_loaded: "Seleziona le righe caricate",
	selection_deselect_all: "Deseleziona tutto",
	selection_reading_rows: "Lettura delle righe… {{loaded}} di {{total}}",
	selection_reading_rows_unknown: "Lettura delle righe… {{loaded}} finora",
	selection_deleting_progress: "Eliminati {{completed}} di {{total}}",
	confirm_delete_selection: "Eliminare {{total}} {{collection}}?",
	confirm_delete_selection_unknown: "Eliminare tutte le righe che corrispondono al filtro attuale?",
	confirm_delete_selection_body: "L’operazione non può essere annullata. Ogni riga viene eliminata singolarmente, quindi potrebbe richiedere del tempo.",
	export_selection_count: "Scarica le {{total}} righe selezionate in formato {{format}}",
	export_selection_all_matching: "Scarica tutti i {{collection}} che corrispondono al filtro attuale",
	select_reference: "Seleziona riferimento",
	select_references: "Seleziona riferimenti",
	account_settings: "Impostazioni account",
	profile: "Profile",
	security: "Sicurezza",
	sessions: "Sessions",
	change_password: "Cambia password",
	current_password: "Password attuale",
	new_password: "Nuova password",
	confirm_password: "Conferma la nuova password",
	password_changed: "Password modificata con successo. Verrai disconnesso.",
	passwords_dont_match: "Le password non coincidono",
	password_too_short: "La password deve contenere almeno 8 caratteri",
	password_change_not_available: "La modifica della password non è disponibile per gli account che usano provider di accesso esterni.",
	changing_password: "Modifica in corso...",
	display_name: "Nome visualizzato",
	photo_url: "URL della foto",
	save_profile: "Salva profilo",
	saving: "Saving...",
	no_active_sessions: "Nessuna sessione attiva trovata.",
	revoking: "Revoking...",
	revoke_all_sessions: "Revoca tutte le sessioni",
	unknown_device: "Dispositivo sconosciuto",
	current: "Current",
	role_id: "ID del ruolo",
	role_name: "Nome del ruolo",
	add_reference: "Aggiungi riferimento",
	add_reference_to: "Aggiungi riferimento a {{name}}",
	/** RolesView — table & permissions */
	role_deleted_successfully: "Ruolo eliminato con successo",
	error_deleting_role: "Errore durante l'eliminazione del ruolo",
	collection_permissions: "Permessi della collezione",
	collection: "Collection",
	no_collections_configured: "Nessuna collezione configurata",
	no_security_rules_defined: "Nessuna regola di sicurezza definita: tutte le operazioni sono senza restrizioni",
	no_rules: "nessuna regola",
	/** UsersView — snackbar */
	bootstrap_admin_success: "Ora sei amministratore! Aggiornamento in corso...",
	failed_to_bootstrap_admin: "Impossibile creare l'amministratore iniziale",
	user_deleted_successfully: "Utente eliminato con successo",
	error_deleting_user: "Errore durante l'eliminazione dell'utente",
	user_created_successfully: "Utente creato con successo",
	invitation_sent: "È stata inviata un'email di invito a {{email}}. Il link consente di impostare la password.",
	invitation_sent_title: "Invito inviato",
	temporary_password: "Password temporanea",
	temporary_password_description: "L'email non è configurata. Condividi questa password temporanea con l'utente in modo sicuro. Non verrà mostrata di nuovo.",
	temporary_password_email_failed_description: "Non è stato possibile consegnare l'email. Condividi questa password temporanea con l'utente in modo sicuro. Non verrà mostrata di nuovo.",
	copy_password: "Copia password",
	password_copied: "Password copiata negli appunti",
	/** UsersView — pagination & search */
	search_users: "Cerca utenti",
	created: "Creato",
	actions: "Azioni",
	no_users_found: "Nessun utente trovato",
	reset_password: "Reimposta password",
	reset_password_success: "Password reimpostata con successo",
	reset_password_confirmation: "Vuoi davvero reimpostare la password di questo utente?",
	reset_password_send_email: "Invia un'email di reimpostazione della password",
	reset_password_set_manually: "Imposta una password manualmente",
	reset_password_set_manually_description: "La password viene aggiornata subito e non viene inviata alcuna email. Condividila con l'utente in modo sicuro.",
	/** Permission-denied empty states */
	no_permission_to_view_users: "Non hai i permessi per visualizzare gli utenti",
	no_permission_to_view_roles: "Non hai i permessi per visualizzare i ruoli",
	no_permission_description: "Contatta un amministratore se hai bisogno di accedere a questa sezione.",
	error_resetting_password: "Errore durante la reimpostazione della password",
	/** Editor table-bubble */
	add_row_before: "Inserisci riga prima",
	add_row_after: "Inserisci riga dopo",
	delete_row: "Elimina riga",
	add_column_before: "Inserisci colonna prima",
	add_column_after: "Inserisci colonna dopo",
	delete_column: "Elimina colonna",
	delete_table: "Elimina tabella",
	/** Editor image-bubble */
	alt_text: "Testo alternativo",
	title: "Title",
	/** MapFieldBinding */
	a_property_of_this_map_has_error: "Una proprietà di questa mappa contiene un errore",
	/** AI Collection Generator Popover */
	generate_collection_with_ai: "Genera collezione con AI",
	modify_collection_with_ai: "Modifica collezione con AI",
	describe_collection_to_create: "Descrivi la collezione che vuoi creare.",
	describe_changes_to_make: "Descrivi le modifiche che vuoi apportare a questa collezione.",
	ai_placeholder_create: "es. Crea una collezione di prodotti con nome, prezzo, descrizione e categoria...",
	ai_placeholder_modify: "es. Aggiungi un campo immagine in miniatura con archiviazione, rendi il prezzo obbligatorio...",
	ai_assist: "Assistente AI",
	generating: "Generazione in corso...",
	/** Recently extracted strings for collection editor */
	this_is_subcollection_of: "Questa è una sottocollezione di",
	use_existing_paths_database: "Utilizza i percorsi esistenti nel database",
	describe_collection_ai: "Descrivi la collezione per generarla con l'IA",
	generate_with_ai: "Genera con IA",
	create_from_json_config: "Crea da configurazione JSON",
	paste_json_config: "Incolla la configurazione JSON",
	create_collection_from_file_formats: "Crea collezione da file (CSV, Excel, JSON)",
	select_template: "Seleziona un modello",
	products: "Prodotti",
	collection_products_subtitle: "Gestisci i tuoi prodotti con prezzi, inventario e immagini",
	collection_users_subtitle: "Gestisci gli utenti registrati con i loro profili e ruoli",
	blog_posts: "Articoli del blog",
	collection_blog_posts_subtitle: "Crea e gestisci gli articoli del tuo blog",
	pages: "Pagine",
	collection_pages_subtitle: "Gestisci le pagine statiche del tuo sito",
	continue_from_scratch: "Continua da zero",
	/** Admin views config */
	admin_users: "Utenti del pannello di amministrazione",
	roles_menu: "Ruoli",
	project_settings: "Impostazioni del progetto",
	by_signing_in_you_agree_to_our: "Accedendo accetti i nostri",
	terms_and_conditions: "Termini e Condizioni",
	and_our: "e la nostra",
	privacy_policy: "Informativa sulla privacy",
	email_password: "Email/password",
	sign_in_with_google: "Accedi con Google",
	auth_user_not_found: "Utente non trovato",
	auth_wrong_password: "Password errata",
	auth_user_disabled: "Account disabilitato",
	auth_account_exists_with_different_credential: "Esiste già un account con questo indirizzo email. Accedi come hai fatto l'ultima volta.",
	auth_account_exists_sign_in_with_password: "Esiste già un account con questo indirizzo email. Accedi con email e password.",
	auth_email_already_in_use: "Questo indirizzo email è già in uso",
	auth_google_permissions_required: "Sono necessarie le autorizzazioni di Google",
	auth_invalid_email_password: "Email o password non valide",
	auth_enter_email_first: "Inserisci prima l'indirizzo email",
	auth_password_reset_sent: "Email di reimpostazione della password inviata",
	auth_sign_in_account: "Accedi al tuo account",
	auth_create_new_account: "Crea un nuovo account",
	auth_password: "Password",
	auth_reset_password: "Reimposta password",
	auth_new_user: "Nuovo utente",
	auth_have_account: "Hai già un account?",
	auth_sign_in: "Accedi",
	auth_sign_up: "Registrati",
	auto_setup_collections_button: "Configura le collezioni automaticamente",
	auto_setup_collections_title: "Configurazione automatica delle collezioni",
	auto_setup_collections_desc: "Configura le collezioni automaticamente in base ai dati Firestore esistenti. Lascia che Rebase configuri il pannello di amministrazione perfetto per te.",
	this_can_take_a_minute: "Potrebbe richiedere qualche minuto",
	no_collections_found_to_setup: "Nessuna collezione trovata da configurare",
	collections_have_been_setup: "Le collezioni sono state configurate",
	error_setting_up_collections: "Errore durante la configurazione delle collezioni",
	admin_panel_ready_bring_data: "Il tuo pannello è pronto. Inizia aggiungendo i tuoi dati.",
	admin_panel_ready_get_started: "Il tuo pannello è pronto. Ecco come iniziare.",
	auto_detect_collections: "Rileva automaticamente le collezioni",
	auto_detect_collections_desc: "Rebase analizzerà i tuoi dati Firestore e configurerà automaticamente le collezioni per te",
	create_a_collection: "Crea una collezione",
	create_collection_desc: "Definisci manualmente la struttura della tua collezione",
	read_the_docs: "Leggi la documentazione",
	read_the_docs_desc: "Scopri come usare Rebase al meglio",
	explore_docs: "Esplora la documentazione",
	want_to_customize_with_code: "Vuoi personalizzare con il codice? Esegui",
	to_scaffold_a_local_project: "per creare un progetto locale",
	validation: "Validazione",
	unique: "Unico",
	required_message: "Messaggio campo obbligatorio",
	required_tooltip: "Non potrai salvare questa entità se questo valore non è impostato",
	unique_tooltip: "Non possono esistere più entità con lo stesso valore",
	lowercase: "Minuscolo",
	uppercase: "Maiuscolo",
	trim: "Rimuovi spazi",
	exact_length: "Lunghezza esatta",
	min_length: "Lunghezza min.",
	max_length: "Lunghezza max.",
	matches_regex: "Corrisponde a regex",
	not_valid_regexp: "Espressione regolare non valida",
	regex_helper: "es. /^\\d+$/ solo per cifre",
	min_value: "Valore min.",
	max_value: "Valore max.",
	less_than: "Minore di",
	more_than: "Maggiore di",
	positive_value: "Valore positivo",
	negative_value: "Valore negativo",
	integer_value: "Valore intero",
	invalid_regular_expression: "Espressione regolare non valida",
	must_specify_target_collection: "Devi specificare una collezione di destinazione per il campo",
	need_specify_repeat_field: "Devi specificare un campo di ripetizione",
	need_specify_block_properties: "Devi specificare le proprietà di questo blocco",
	incomplete_condition: "Condizione incompleta - seleziona un campo",
	field_name: "Nome del campo",
	kanban_column_property: "Proprietà colonna Kanban",
	select_a_property: "Seleziona una proprietà",
	kanban_property_not_found: "La proprietà \"{{property}}\" non esiste o non è una proprietà string con enum. Seleziona una proprietà valida o cancella la selezione.",
	no_enum_string_properties: "Nessuna proprietà string con enum trovata. Aggiungi una proprietà string con enumValues per usare la vista Kanban.",
	kanban_column_description: "Seleziona una proprietà string con valori enum per raggruppare le entità in colonne",
	create_property: "+ Crea proprietà \"{{property}}\"",
	order_property: "Proprietà di ordinamento",
	order_property_not_found: "La proprietà \"{{property}}\" non esiste o non è una proprietà numerica. Seleziona una proprietà valida o cancella la selezione.",
	no_number_properties: "Nessuna proprietà numerica trovata. Aggiungi una proprietà numerica per abilitare l'ordinamento.",
	order_property_description: "Seleziona una proprietà numerica per mantenere l'ordine degli elementi",
	display_settings: "Impostazioni di visualizzazione",
	default_row_size: "Dimensione riga predefinita",
	side_dialog_width: "Larghezza del dialogo laterale",
	side_dialog_width_description: "Definisci opzionalmente la larghezza (in pixel) del dialogo laterale delle entità. Il valore predefinito è 768px",
	inline_editing_enabled: "I dati possono essere modificati direttamente nella vista tabella",
	inline_editing_disabled: "I dati possono essere modificati solo nella vista modulo",
	inline_editing_description: "Consenti la modifica dei dati direttamente nella vista tabella, senza aprire la vista modulo.",
	include_json_view: "Includi vista JSON",
	no_json_view: "Non includere vista JSON",
	json_view_description: "Includi la rappresentazione JSON del documento.",
	not_found_suffix: "non trovato",
	editor_text: "Testo",
	editor_text_description: "Inizia a digitare con testo semplice.",
	editor_heading_1: "Intestazione 1",
	editor_heading_1_description: "Intestazione di sezione grande.",
	editor_heading_2: "Intestazione 2",
	editor_heading_2_description: "Intestazione di sezione media.",
	editor_heading_3: "Intestazione 3",
	editor_heading_3_description: "Intestazione di sezione piccola.",
	editor_todo_list: "Lista di cose da fare",
	editor_todo_list_description: "Tieni traccia delle attività con una lista di cose da fare.",
	editor_bullet_list: "Elenco puntato",
	editor_bullet_list_description: "Crea un semplice elenco puntato.",
	editor_numbered_list: "Elenco numerato",
	editor_numbered_list_description: "Crea un elenco con numerazione.",
	editor_quote: "Citazione",
	editor_quote_description: "Cattura una citazione.",
	editor_code: "Codice",
	editor_code_description: "Cattura un frammento di codice.",
	editor_image: "Immagine",
	editor_image_description: "Carica un'immagine dal tuo computer.",
	editor_multiple: "Multiplo",
	editor_link: "Link",
	editor_save: "Salva",
	editor_cancel: "Annulla",
	editor_remove_link: "Rimuovi link",
	editor_paste_or_type_link: "Incolla o digita il link",
	editor_open_in_new_window: "Apri in una nuova finestra",
	editor_bold: "Grassetto",
	editor_italic: "Corsivo",
	editor_underline: "Sottolineato",
	editor_strikethrough: "Barrato",
	editor_autocomplete: "Completamento automatico",
	editor_autocomplete_description: "Aggiungi testo basato sul contesto.",
	text_search_dialog_title: "Abilita ricerca testuale",
	text_search_local_not_recommended: "La ricerca testuale locale non è consigliata per collezioni di grandi dimensioni.",
	text_search_local_fetch_warning: "Tieni presente che abilitando la ricerca testuale locale sarà necessario scaricare tutti i documenti dalla collezione e memorizzarli nel browser. Questo può essere inefficiente per collezioni grandi e può comportare costi aggiuntivi.",
	text_search_external_suggestion: "Per collezioni più grandi, ti consigliamo di utilizzare un motore di ricerca esterno come Algolia o Elastic Search e di assegnare un delegato di ricerca alla tua configurazione.",
	text_search_local_description: "La ricerca testuale locale è il modo più semplice per abilitare la ricerca testuale nella tua collezione. Carica tutti i documenti della collezione nel browser ed esegue la ricerca localmente. È l'opzione consigliata per le collezioni piccole.",
	text_search_own_implementation: "Hai implementato il tuo controller di ricerca testuale. Puoi abilitare la ricerca testuale per la tua collezione.",
	text_search_enable_for_collection: "Abilita per questa collezione",
	text_search_enable_for_project: "Abilita per il progetto",
	text_search_enabled_snackbar: "Ricerca testuale locale abilitata",
	settings_heading: "Impostazioni",
	settings_project_name: "Nome del progetto",
	settings_default_language: "Lingua predefinita",
	settings_default_language_caption: "Seleziona la lingua di base per questo progetto. Gli utenti possono modificare questa preferenza nelle impostazioni personali.",
	settings_enable_local_text_search: "Abilita la ricerca testuale locale",
	settings_local_text_search_caption: "Abilita la ricerca testuale locale per tutte le collezioni. Questo ti permetterà di cercare campi di testo nelle collezioni utilizzando la barra di ricerca di Rebase. Tieni presente che questa funzionalità può comportare un numero maggiore di letture, poiché verranno indicizzati tutti i campi di testo delle tue collezioni.",
	settings_doc_history_all_collections: "Cronologia documenti abilitata per tutte le collezioni",
	settings_doc_history_caption: "Quando attivato, tutte le collezioni avranno la cronologia abilitata per impostazione predefinita. Puoi modificare questa impostazione per ogni singola collezione. La cronologia verrà salvata nella sottocollezione __history di ogni documento.",
	settings_theme: "Tema",
	settings_primary_color: "Colore primario",
	settings_secondary_color: "Colore secondario",
	settings_sample_theme_components: "Componenti di esempio del tema",
	settings_drag_drop_logo: "Trascina e rilascia il tuo logo qui",
	settings_security_rules: "Regole di sicurezza",
	settings_security_rules_description: "Rebase utilizza le regole di sicurezza di Firebase per limitare l'accesso ai dati. Quando si crea un nuovo utente tramite Rebase, l'attributo personalizzato rebaseUser viene aggiunto all'utente nel progetto client. Aggiungendo le seguenti regole di sicurezza al tuo progetto, ti assicuri che gli utenti Rebase possano accedere ai dati tramite Rebase.",
	settings_security_rules_add_domain: "Ricorda di aggiungere il dominio {{domain}} ai domini consentiti del tuo provider",
	settings_security_rules_caption: "Queste regole limitano l'accesso ai dati solo agli utenti Rebase, ma non applicano i permessi a livello di database. I permessi vengono applicati nel frontend, il che funziona bene per la maggior parte dei progetti. Se hai bisogno di applicare i permessi a livello di database, puoi modificare queste regole di sicurezza. I ruoli assegnati a un utente vengono impostati come attributi personalizzati nel token di autenticazione Firebase, quindi puoi utilizzarli nelle tue regole di sicurezza.",
	studio_sql_executing_query: "ESECUZIONE DELLA QUERY...",
	studio_sql_query_error: "Errore nella query",
	studio_sql_run_query_placeholder: "Esegui una query per vedere i risultati",
	studio_sql_visual_execution_plan: "Piano di esecuzione visuale",
	studio_sql_success: "SUCCESS",
	studio_sql_no_results: "La query è stata eseguita correttamente ma non ha restituito risultati.",
	studio_sql_rows: "Rows:",
	studio_sql_time: "Time:",
	studio_sql_copy_markdown: "Copia come Markdown",
	studio_sql_export_json: "Esporta JSON",
	studio_sql_export_csv: "Esporta CSV",
	studio_sql_format_sql: "Formatta SQL",
	studio_sql_explain: "Explain",
	studio_sql_limit_1000: "Limit 1000",
	studio_sql_remove_from_favorites: "Rimuovi dai preferiti",
	studio_sql_add_to_favorites: "Aggiungi ai preferiti",
	studio_sql_save: "Save",
	studio_sql_run: "Run",
	studio_sql_database: "Database",
	studio_sql_role: "Role",
	studio_sql_admin: "(Admin)",
	studio_sql_select_db: "Scegli il database",
	studio_sql_query_results: "Risultati della query",
	studio_sql_save_snippet: "Salva frammento SQL",
	studio_sql_snippet_name: "Nome del frammento",
	studio_sql_snippet_name_placeholder: "es. Ottieni tutti gli utenti",
	studio_sql_snippet_saved_local: "Verrà salvato nella memoria locale del browser.",
	studio_sql_cancel: "Cancel",
	studio_sql_dangerous_operation: "Rilevata un'operazione pericolosa",
	studio_sql_dangerous_operation_body: "La query che stai per eseguire contiene operazioni potenzialmente distruttive (DELETE, DROP, TRUNCATE o UPDATE senza WHERE). Vuoi davvero continuare?",
	studio_sql_snippet_saved: "Frammento «{{name}}» salvato.",
	studio_sql_markdown_copied: "Risultati copiati come Markdown!",
	studio_sql_markdown_copy_failed: "Impossibile copiare il Markdown negli appunti.",
	studio_sql_row_updated: "Riga aggiornata con successo.",
	studio_sql_cannot_edit_missing_query: "Impossibile modificare le righe: manca la query sottostante.",
	studio_sql_cannot_resolve_table: "Impossibile determinare la tabella o la chiave primaria",
	studio_sql_missing_pk: "Alla riga mancano le colonne della chiave primaria: {{columns}}. Impossibile aggiornarla in sicurezza.",
	studio_sql_update_failed: "Aggiornamento non riuscito: {{message}}",
	studio_sql_execution_not_supported: "L'origine dati attuale non supporta l'esecuzione di SQL.",
	studio_sql_error_executing: "Si è verificato un errore durante l'esecuzione della query.",
	studio_sql_error_explaining: "Si è verificato un errore durante l'analisi della query.",
	studio_sql_save_first_to_favorite: "Salva il frammento prima di aggiungerlo ai preferiti.",
	studio_sql_collections_label: "Collections:",
	studio_sql_admin_collections_tooltip: "Tables in this query that are mapped as admin collections",
	studio_sql_edit_entity: "Edit {{name}} #{{id}}",
	studio_sql_sql_not_supported: "Questa origine dati non supporta l'esecuzione di SQL",
	studio_sql_fetch_error: "Impossibile recuperare le opzioni di connessione: {{message}}",
	studio_sql_unexpected_format: "Formato dei dati inatteso: {{type}}",
	studio_sql_no_tables: "Nessuna tabella trovata in questo database.",
	studio_sql_schema_fetch_error: "Impossibile recuperare lo schema: {{message}}",
	studio_sql_sidebar_snippets: "Snippets",
	studio_sql_sidebar_history: "History",
	studio_sql_sidebar_schema: "Schema",
	studio_sql_sidebar_no_snippets: "Nessun frammento salvato",
	studio_sql_sidebar_save_snippet_hint: "Salva una query per ritrovarla qui.",
	studio_sql_sidebar_no_history: "Nessuna cronologia",
	studio_sql_sidebar_history_hint: "Le query eseguite appariranno qui.",
	studio_sql_sidebar_delete_snippet: "Elimina frammento",
	studio_sql_close_tab: "Chiudi questa scheda di query",
	studio_sql_new_tab: "Nuova scheda di query",
	studio_schema_tables: "Tables",
	studio_schema_loading: "Caricamento dello schema...",
	studio_schema_no_tables: "Nessuna tabella trovata",
	studio_schema_retry: "Retry",
	studio_schema_primary_key: "Chiave primaria",
	studio_schema_select_all: "SELECT * FROM",
	studio_schema_insert_into: "INSERT INTO",
	studio_schema_update: "UPDATE",
	studio_schema_delete_from: "DELETE FROM",
	studio_schema_columns: "{{count}} colonne",
	studio_rls_title: "Sicurezza a livello di riga",
	studio_rls_description: "Gestisci i criteri RLS delle tue tabelle",
	studio_rls_enabled: "RLS attiva",
	studio_rls_disabled: "RLS disattivata",
	studio_rls_no_rls: "Senza RLS",
	studio_rls_enable_rls: "Attiva RLS",
	studio_rls_disable_rls: "Disattiva RLS",
	studio_rls_create_policy: "Crea criterio",
	studio_rls_policies: "Policies",
	studio_rls_no_policies: "Nessun criterio definito",
	studio_rls_no_policies_desc: "Questa tabella non ha criteri RLS. L'accesso a tutte le righe dipende dallo stato della RLS.",
	studio_rls_add_first_policy: "Aggiungi il tuo primo criterio",
	studio_rls_force_rls: "Forza RLS",
	studio_rls_force_rls_desc: "Se attivo, i criteri RLS si applicano anche al proprietario della tabella.",
	studio_rls_enable_force_rls: "Attiva RLS forzata",
	studio_rls_disable_force_rls: "Disattiva RLS forzata",
	studio_rls_edit: "Edit",
	studio_rls_delete: "Delete",
	studio_rls_confirm_delete_title: "Elimina criterio",
	studio_rls_confirm_delete_body: "Vuoi davvero eliminare questo criterio? L'operazione non può essere annullata.",
	studio_rls_confirm_enable_title: "Attiva la sicurezza a livello di riga",
	studio_rls_confirm_enable_body: "Attivando la RLS l'accesso alle righe di questa tabella sarà limitato dai tuoi criteri. Assicurati di avere criteri adeguati.",
	studio_rls_confirm_disable_title: "Disattiva la sicurezza a livello di riga",
	studio_rls_confirm_disable_body: "Disattivando la RLS tutte le righe saranno accessibili a tutti i ruoli. Vuoi davvero continuare?",
	studio_rls_select_table: "Seleziona una tabella",
	studio_rls_no_tables: "Nessuna tabella disponibile",
	studio_rls_error: "Errore nel caricamento dei criteri",
	studio_rls_retry: "Retry",
	studio_rls_loading: "Caricamento dei criteri...",
	studio_policy_edit: "Modifica criterio",
	studio_policy_create: "Crea un nuovo criterio",
	studio_policy_defining_rules: "Definizione delle regole di accesso per",
	studio_policy_cancel: "Cancel",
	studio_policy_save: "Salva criterio",
	studio_policy_template: "Parti da un modello (facoltativo)",
	studio_policy_select_template: "Seleziona un modello...",
	studio_policy_name: "Nome del criterio",
	studio_policy_name_placeholder: "Assegna al criterio un nome descrittivo",
	studio_policy_behavior: "Comportamento del criterio",
	studio_policy_permissive: "Permissive",
	studio_policy_permissive_desc: "Combinata con «OR»",
	studio_policy_restrictive: "Restrictive",
	studio_policy_restrictive_desc: "Combinata con «AND»",
	studio_policy_command: "Comando del criterio",
	studio_policy_target_roles: "Ruoli destinatari",
	studio_policy_roles_placeholder: "Seleziona i ruoli (predefinito: public)",
	studio_policy_using_expr: "Espressione USING",
	studio_policy_using_expr_desc: "Si applica alle righe esistenti (SELECT, UPDATE, DELETE)",
	studio_policy_check_expr: "Espressione WITH CHECK",
	studio_policy_check_expr_desc: "Si applica alle righe nuove o modificate (INSERT, UPDATE)",
	studio_policy_help_title: "Guida alla sicurezza a livello di riga (RLS)",
	studio_policy_help_intro: "La sicurezza a livello di riga (RLS) consente di limitare le righe che un utente può leggere o modificare in base al suo stato di autenticazione o ai suoi ruoli.",
	studio_policy_help_step1_title: "1. Seleziona un modello",
	studio_policy_help_step1_desc: "Se non hai mai usato la RLS, il modo più semplice è partire da un modello predefinito nell'elenco. Configurerà automaticamente il criterio per casi comuni come «gli utenti possono aggiornare le proprie righe».",
	studio_policy_help_step2_title: "2. Ruoli destinatari",
	studio_policy_help_step2_desc: "Scegli a quali utenti si applica il criterio:",
	studio_policy_help_role_public: "Si applica a tutti (autenticati o meno).",
	studio_policy_help_role_authenticated: "Si applica solo agli utenti autenticati.",
	studio_policy_help_role_anon: "Si applica solo agli utenti NON autenticati.",
	studio_policy_help_step3_title: "3. Espressione USING",
	studio_policy_help_step3_desc: "Una condizione SQL che deve risultare vera perché le righe esistenti siano visibili o modificabili (SELECT, UPDATE, DELETE).",
	studio_policy_help_step3_example: "Garantisce che gli utenti vedano solo le righe di cui sono proprietari.",
	studio_policy_help_step4_title: "4. Espressione WITH CHECK",
	studio_policy_help_step4_desc: "Una condizione SQL che deve risultare vera perché le righe nuove o modificate vengano salvate (INSERT, UPDATE).",
	studio_policy_help_step4_example: "Di solito coincide con l'espressione USING quando gli utenti possono inserire o modificare solo i propri dati.",
	studio_policy_help_auth_vars_title: "Variabili di autenticazione di Rebase",
	studio_policy_help_auth_vars_desc: "Rebase mette a disposizione funzioni native che puoi usare nelle tue espressioni:",
	studio_policy_help_auth_uid: "Restituisce l'ID dell'utente corrente come testo. Esempio:",
	studio_policy_help_auth_jwt: "Restituisce l'intero payload del JWT come JSONB, così puoi verificare claim personalizzati. Esempio:",
	studio_policy_help_auth_roles: "Restituisce gli ID dei ruoli dell'utente come stringa separata da virgole. Da usare preferibilmente con:",
	studio_policy_help_docs_cta: "Per saperne di più sui criteri di Postgres, consulta la documentazione ufficiale.",
	studio_policy_help_read_docs: "Leggi la documentazione",
	studio_policy_help_got_it: "Ho capito!",
	studio_add_kanban_column: "Aggiungi colonna",
	studio_add_kanban_column_title: "Aggiungi colonna Kanban",
	studio_add_kanban_column_name: "Nome della colonna",
	studio_add_kanban_column_name_placeholder: "Nome della nuova colonna",
	studio_add_kanban_column_add: "Add",
	studio_add_kanban_column_cancel: "Cancel",
	studio_collection_view_sql: "Editor SQL",
	studio_collection_view_admin: "Vista di amministrazione",
	studio_editor_collection_tooltip: "Modifica lo schema della collezione",
	studio_editor_collection_no_permission: "Non hai il permesso di modificare questa collezione",
	studio_editor_collection_start_tooltip: "Copia il percorso o modifica lo schema",
	studio_editor_collection_start_no_permission: "Non hai il permesso di modificare questa collezione",
	studio_editor_collection_start_copied: "Copied!",
	studio_editor_entity_tooltip: "Modifica lo schema dell'entità",
	studio_editor_entity_no_permission: "Non hai il permesso di modificare questa entità",
	studio_home_edit_collection: "Modifica collezione",
	studio_home_delete_collection: "Elimina collezione",
	studio_home_confirm_delete_title: "Eliminare la collezione?",
	studio_home_confirm_delete_body: "Vuoi davvero eliminare la collezione «{{name}}»? L'operazione non può essere annullata.",
	studio_kanban_setup: "Configurazione Kanban",
	studio_missing_reference: "Riferimento mancante: {{path}}",
	studio_missing_reference_create: "Create",
	studio_new_collection: "Nuova collezione",
	studio_new_collection_card: "Nuova collezione",
	studio_new_collection_no_permission: "Non hai i permessi per creare collezioni",
	studio_property_add_column: "Aggiungi una nuova proprietà",
	studio_property_add_column_no_permission: "Non hai il permesso di aggiungere nuove proprietà",
	studio_add_kanban_column_desc: "Aggiunge una nuova opzione alla proprietà «{{property}}». Verrà creata una nuova colonna nella board Kanban.",
	studio_add_kanban_column_adding: "Adding...",
	studio_collection_view_edit: "Edit",
	studio_editor_collection_disabled: "La modifica delle collezioni è disattivata",
	studio_editor_collection_edit: "Modifica collezione",
	studio_editor_entity_save_first: "Devi salvare il documento prima di modificare lo schema",
	studio_editor_entity_edit_schema: "Modifica lo schema di questo modulo",
	studio_editor_collection_start_save_filter: "Salva filtro e ordinamento predefiniti",
	studio_editor_collection_start_clear_filter: "Cancella filtro e ordinamento predefiniti",
	studio_editor_collection_start_reset_filter: "Ripristina filtro e ordinamento predefiniti",
	studio_editor_collection_start_saved: "Configurazione predefinita salvata",
	studio_home_duplicate_collection: "Duplicate",
	studio_home_delete: "Delete",
	studio_home_confirm_delete_no_data: "Non verrà eliminato alcun dato, solo la collezione nel pannello di amministrazione",
	studio_home_collection_deleted: "Collezione eliminata",
	studio_kanban_configure: "Configura Kanban",
	studio_missing_reference_error: "Nessuna collezione per il percorso: {{path}}",
	studio_new_collection_add: "Aggiungi nuova collezione",
	db_column_type: "Tipo di colonna del database",
	primary_key_unique_id: "Chiave primaria / ID univoco",
	spread_children_as_columns: "Espandi i figli come colonne",
	mode: "Modalità",
	timezone: "Fuso orario",
	target_collection: "Collezione di destinazione",
	storage_file_name: "Nome del file",
	storage_path: "Percorso di archiviazione",
	storage_max_size: "Dimensione massima (in byte)",
	storage_resize_mode: "Modalità di ridimensionamento",
	storage_output_format: "Formato di output",
	storage_max_width: "Larghezza massima (px)",
	storage_max_height: "Altezza massima (px)",
	storage_quality: "Qualità (0-100)",
	storage_file_upload_config: "Configurazione caricamento file",
	storage_image_resize_config: "Configurazione ridimensionamento immagine",
	storage_all_file_types: "Tutti i tipi di file consentiti",
	storage_allowed_file_types: "Tipi di file consentiti",
	storage_include_bucket_url: "Includi URL del bucket (s3://...) nel valore salvato",
	storage_save_url: "Salva URL invece del percorso di archiviazione",
	datetime_automatic_value: "Valore automatico",
	markdown_paste_behavior: "Comportamento incolla",
	entity_saved: "Salvato",
	more_actions: "Altre azioni",
	record_history: "Cronologia",
	inspect_record: "Ispeziona record",
	inspect: "Ispeziona",
	close_inspector: "Chiudi l'ispettore",
	open_in_live_site: "Apri nel sito pubblicato",
	history_revert: "Torna a questa versione",
	history_revert_title: "Ripristinare i dati a questa versione?",
	history_reverted: "Ripristinata la versione selezionata",
	history_revert_error: "Errore durante il ripristino del record",
	history_revert_dirty: "Salva o annulla le modifiche prima di ripristinare",
	history_empty: "Nessuna cronologia disponibile",
	history_empty_description: "Quando salvi un record viene creata una nuova versione e conservata nella cronologia.",
	history_new_entity: "La cronologia è disponibile solo per i record esistenti",
	history_no_more: "Nessun'altra cronologia",
	studio_group_views: "Viste",
	studio_group_admin: "Amministrazione",
	studio_group_settings: "Impostazioni",
	studio_group_database: "Database",
	studio_group_compute: "Elaborazione",
	studio_group_storage: "Archiviazione",
	studio_group_api: "API",
	studio_group_access_control: "Controllo accessi",
	studio_tool_sql: "Console SQL",
	studio_tool_sql_description: "Esegui query SQL",
	studio_tool_js: "Console JS",
	studio_tool_js_description: "Esegui JavaScript",
	studio_tool_rls: "Criteri RLS",
	studio_tool_rls_description: "Sicurezza a livello di riga",
	studio_tool_storage: "Archiviazione",
	studio_tool_storage_description: "Gestisci i file di archiviazione",
	studio_tool_cron: "Processi cron",
	studio_tool_cron_description: "Gestisci le attività pianificate",
	studio_tool_schema_visualizer: "Visualizzatore di schema",
	studio_tool_schema_visualizer_description: "Diagramma ER interattivo del database",
	studio_tool_branches: "Rami",
	studio_tool_branches_description: "Crea e gestisci i rami del database",
	studio_tool_backups: "Backup",
	studio_tool_backups_description: "Scarica i backup del database",
	studio_tool_api: "Esploratore API",
	studio_tool_api_description: "Documentazione API interattiva e test",
	studio_tool_logs: "Esploratore dei log",
	studio_tool_logs_description: "Log di sistema e delle query in tempo reale",
	studio_tool_api_keys: "Chiavi API",
	studio_tool_api_keys_description: "Crea e gestisci chiavi API con ambito",
	studio_backups_denied_title: "Non puoi elencare i backup di questo progetto",
	studio_backups_denied_hint: "Il progetto non ha nulla che non va. Elencare i backup richiede una connessione da amministratore, e l'account connesso è stato rifiutato.",
	studio_backups_read_failed: "Non è stato possibile leggere i backup di questo progetto",
	studio_backups_downloading: "Download in corso…",
	studio_backups_roles_file: "File dei ruoli",
	studio_backups_roles_file_hint: "Il .globals.sql salvato con questo backup. Ricrea i ruoli del database a cui fanno riferimento i permessi e la sicurezza a livello di riga del dump, e `rebase db restore` lo legge accanto al .dump, quindi tieni insieme i due file.",
	studio_backups_no_roles_file: "Nessun file dei ruoli",
	studio_backups_no_roles_file_hint: "Con questo backup non è stato salvato nessun .globals.sql. Ripristinarlo in un nuovo Postgres si ferma al primo permesso concesso a un ruolo del database che lì non esiste.",
	studio_cron_denied_title: "Non puoi elencare i processi cron di questo progetto",
	studio_cron_denied_hint: "Il progetto non ha nulla che non va. L'API cron è riservata agli amministratori, e l'account connesso è stato rifiutato.",
	studio_cron_read_failed: "Non è stato possibile leggere i processi cron di questo progetto",
	studio_api_keys_denied_title: "Non puoi elencare le chiavi API di questo progetto",
	studio_api_keys_denied_hint: "Il progetto non ha nulla che non va. Gestire le chiavi API è un'operazione da amministratore, e l'account connesso è stato rifiutato.",
	studio_api_keys_read_failed: "Non è stato possibile leggere le chiavi API di questo progetto",
	studio_api_keys_empty_title: "Ancora nessuna chiave API",
	studio_api_keys_empty_hint: "Crea una chiave per abilitare l'autenticazione tra servizi",
	studio_api_keys_revoke: "Revoca",
	studio_api_keys_select_hint: "Seleziona una chiave API per vederne i dettagli",
	studio_api_keys_permissions: "Permessi",
	studio_api_keys_no_permissions: "Nessun permesso configurato",
	studio_api_keys_revoke_confirmation: "Conferma della revoca",
	studio_api_keys_name_label: "Nome:",
	studio_api_keys_access_label: "Accesso:",
	studio_api_keys_admin_granted: "Ruolo di amministratore concesso",
	studio_api_keys_admin_granted_hint: "le route di amministrazione e le policy default_admin",
	studio_api_keys_done: "Fatto",
	studio_api_keys_status_active: "Attiva",
	studio_api_keys_status_expired: "Scaduta",
	studio_api_keys_status_revoked: "Revocata",
	studio_api_keys_stat_status: "Stato",
	studio_api_keys_stat_last_used: "Ultimo utilizzo",
	studio_api_keys_stat_expires: "Scade",
	studio_api_keys_never: "Mai",
	studio_api_keys_role_service: "Servizio",
	studio_api_keys_stat_rate_limit: "Limite di richieste",
	studio_api_keys_rate_limit_default: "Predefinito (1000/15 min)",
	studio_api_keys_stat_created_by: "Creata da",
	studio_api_keys_copy_failed: "Copia non riuscita",
	studio_branches_denied_title: "Non puoi elencare i rami di questo progetto",
	studio_branches_denied_hint: "Il progetto non ha nulla che non va. I rami passano dalla connessione da amministratore, e l'account connesso è stato rifiutato.",
	studio_branches_read_failed: "Non è stato possibile leggere i rami di questo progetto",
	studio_schema_no_collections: "Nessuna collection dichiarata",
	studio_schema_no_collections_body: "Non c'è ancora nulla da disegnare. Aggiungi una collection in config/collections/, oppure apri «Modifica collection» per aggiungerne una qui.",
	studio_schema_introspect_hint: "Su un database che ha già delle tabelle, rebase schema introspect scrive i file di collection al posto tuo.",
	studio_rls_enable_confirm_title: "Attivare la sicurezza a livello di riga?",
	studio_rls_enable_confirm_body: "Finché un criterio non lo consente, nessuna riga di {{table}} è leggibile. Postgres nega per impostazione predefinita.",
	studio_rls_disable_confirm_title: "Disattivare la sicurezza a livello di riga?",
	studio_rls_disable_confirm_body: "Tutti i criteri su {{table}} smettono di essere applicati. Chi raggiunge la tabella può leggere e scrivere ogni riga.",
	studio_rls_drop_policy_title: "Eliminare questo criterio?",
	studio_rls_drop_policy_body: "{{policy}} viene rimosso da {{table}} nel database. Nel codice non viene scritto nulla, quindi un deploy può ripristinarlo.",
	studio_rls_unapplied: "Non applicato",
	studio_rls_unapplied_tooltip: "Dichiarato nel codice, non ancora nel database. Salvalo qui per pianificare e applicare la modifica, oppure esegui `rebase db push`.",
	studio_branches_unavailable_title: "Il branching del database non è disponibile",
	studio_branches_unavailable_body: "Il branching richiede un backend Postgres con una connessione da amministratore. Imposta ADMIN_CONNECTION_STRING nell'ambiente del backend.",
	studio_backups_not_configured_title: "I backup non sono configurati",
	studio_backups_not_configured_body: "Imposta BACKUP_DESTINATION su un percorso locale o su un URL s3:// o gs://, poi crea un backup con `rebase db backup` o un cron pianificato.",
	studio_cron_empty_title: "Nessun processo cron registrato",
	studio_cron_empty_body: "Aggiungi un file in backend/crons/ che esporti di default una CronJobDefinition.",
	studio_read_the_docs: "Leggi la documentazione"
};
//#endregion
//#region src/locales/hi.ts
var hi = {
	save: "सहेजें",
	create: "बनाएं",
	create_copy: "प्रतिलिपि बनाएं",
	save_and_close: "सहेजें और बंद करें",
	create_copy_and_close: "प्रतिलिपि बनाएं और बंद करें",
	create_and_close: "बनाएं और बंद करें",
	discard: "रद्द करें (Discard)",
	clear: "साफ़ करें",
	cancel: "रद्द करें (Cancel)",
	edit: "संपादित करें",
	copy: "कॉपी करें",
	delete: "हटाएं",
	delete_not_allowed: "आपने कम से कम एक ऐसी इकाई चुनी है जिसे आप हटा नहीं सकते",
	edit_entity: "संपादित करें",
	back_to_detail: "विवरण पर वापस जाएँ",
	delete_confirmation_title: "हटाएं?",
	delete_confirmation_body: "इससे इकाई (entity) हटा दी जाएगी। क्या आप सुनिश्चित हैं?",
	delete_multiple_confirmation_body: "इससे चयनित इकाइयां (entities) हटा दी जाएंगी। क्या आप सुनिश्चित हैं?",
	unsaved_changes_title: "सहेजे नहीं गए परिवर्तन",
	unsaved_changes_body: "आपके पास सहेजे नहीं गए परिवर्तन हैं। क्या आप उन्हें रद्द करना चाहते हैं?",
	discard_changes: "परिवर्तन रद्द करें",
	keep_editing: "संपादन जारी रखें",
	clear_form: "फ़ॉर्म साफ़ करें?",
	discard_changes_confirmation: "सभी सहेजे नहीं गए परिवर्तन खो जाएँगे। इसे पूर्ववत नहीं किया जा सकता।",
	clear_form_confirmation: "सभी दर्ज किए गए मान साफ़ कर दिए जाएँगे। इसे पूर्ववत नहीं किया जा सकता।",
	search: "खोजें",
	find_by_id: "ID द्वारा खोजें",
	find_entity_by_id: "ID द्वारा इकाई खोजें",
	filter: "फ़िल्टर",
	filters: "फ़िल्टर",
	records_in_view: "इस दृश्य में रिकॉर्ड",
	clear_filter: "फ़िल्टर साफ़ करें",
	clear_filter_sort: "फ़िल्टर/सॉर्ट साफ़ करें",
	clear_sort: "सॉर्ट साफ़ करें",
	sort: "क्रमबद्ध करें",
	sort_by: "इसके अनुसार क्रमबद्ध करें",
	sort_then_by: "फिर इसके अनुसार",
	sort_ascending: "आरोही क्रम में लगाएँ",
	sort_descending: "अवरोही क्रम में लगाएँ",
	sort_remove: "क्रम हटाएँ",
	sort_move_up: "इस कुंजी को अधिक महत्व दें",
	sort_move_down: "इस कुंजी को कम महत्व दें",
	sort_remove_key: "यह क्रम कुंजी हटाएँ",
	sort_key_position: "क्रम कुंजी {{position}}",
	sort_shift_click_hint: "वर्तमान क्रम के नीचे कॉलम जोड़ने के लिए Shift+क्लिक करें",
	save_entity_before_subcollections: "अतिरिक्त संग्रह जोड़ने से पहले आपको एंटिटी सहेजनी होगी",
	clear_all: "सभी साफ़ करें",
	no_items: "कोई आइटम नहीं",
	no_entries_found: "कोई प्रविष्टि नहीं मिली",
	all_entries_loaded: "सभी {{count}} प्रविष्टियाँ लोड हो गईं",
	create_your_first_entry: "अपनी पहली प्रविष्टि बनाएं",
	no_results_filter_sort: "लागू किए गए फ़िल्टर/सॉर्ट के साथ कोई परिणाम नहीं",
	no_results_search: "\"{{search}}\" के लिए कोई परिणाम नहीं मिला",
	add: "जोड़ें",
	remove: "हटाएं",
	multiple_entities: "एकाधिक इकाइयां",
	unsaved_changes: "आपके पास {{collectionName}} में सहेजे नहीं गए परिवर्तन हैं।",
	so_empty: "इतना खाली...",
	no_results: "लागू किए गए फ़िल्टर/सॉर्ट के साथ कोई परिणाम नहीं",
	refresh_data: "डेटा रीफ़्रेश करें",
	dark_mode: "डार्क",
	light_mode: "लाइट",
	system_mode: "सिस्टम",
	join_newsletter: "न्यूज़लेटर से जुड़ें, कोई स्पैम नहीं",
	ok: "ठीक है",
	save_collection_config: "संग्रह संरचना सहेजें",
	search_for_more_icons: "और आइकन खोजें…",
	ai_modified: "AI द्वारा संशोधित",
	size_label: "आकार",
	group_by: "इसके द्वारा समूहित करें",
	initialize_kanban_order: "कानबन क्रम प्रारंभ करें",
	copy_id: "ID कॉपी करें",
	add_specific: "{{name}} जोड़ें",
	add_named: "\"{{name}}\" जोड़ें",
	select_specific: "विशिष्ट {{name}} चुनें",
	select_from: "{{name}} से चुनें",
	done: "हो गया",
	log_out: "लॉग आउट",
	column_cannot_be_edited: "इस कॉलम को सीधे संपादित नहीं किया जा सकता",
	close: "बंद करें",
	hide_list: "सूची छिपाएँ",
	show_list: "सूची दिखाएँ",
	unsaved_local_changes: "सहेजे नहीं गए स्थानीय परिवर्तन",
	unsaved_local_changes_description: "इस दस्तावेज़ को स्थानीय स्तर पर संपादित किया गया था और इसमें सहेजे नहीं गए परिवर्तन हैं। यदि आप इन्हें लागू नहीं करते हैं तो ये स्थानीय परिवर्तन खो जाएंगे।",
	preview_changes: "परिवर्तनों का पूर्वावलोकन करें",
	apply_changes: "परिवर्तन लागू करें",
	discard_local_changes: "स्थानीय परिवर्तन रद्द करें",
	preview_local_changes: "स्थानीय परिवर्तनों का पूर्वावलोकन करें",
	preview_local_changes_description: "ये वे स्थानीय परिवर्तन हैं जिन्हें फॉर्म पर लागू किया जाएगा।",
	type: "प्रकार",
	string: "स्ट्रिंग",
	number: "नंबर",
	boolean: "बूलियन",
	date: "दिनांक",
	map: "मैप",
	array: "ऐरे",
	arrays_of_arrays_not_supported: "ऐरे के ऐरे समर्थित नहीं हैं।",
	data_type_not_supported: "डेटा प्रकार {{dataType}} अभी समर्थित नहीं है",
	passkey_error_unsupported: "आपका डिवाइस या ब्राउज़र पासकी का समर्थन नहीं करता है।",
	saved_correctly: "सफलतापूर्वक सहेजा गया",
	error_before_saving: "डेटा तैयार करने में त्रुटि",
	error_after_saving: "सहेजने के बाद की प्रक्रिया में त्रुटि",
	error_saving_entity: "प्रविष्टि सहेजने में त्रुटि",
	entity_does_not_exist: "यह प्रविष्टि डेटाबेस में मौजूद नहीं है",
	form_modified: "इस फ़ॉर्म में बदलाव किए गए हैं",
	form_in_sync: "वर्तमान फ़ॉर्म डेटाबेस के साथ सिंक में है",
	fix_errors_before_saving: "सहेजने से पहले हाइलाइट की गई त्रुटियों को ठीक करें",
	admin: "एडमिन",
	home: "होम",
	this_form_has_errors: "इस फॉर्म में त्रुटियाँ हैं",
	error_loading_navigation: "नेविगेशन लोड करने में त्रुटि",
	error_loading_auth: "प्रमाणीकरण लोड करने में त्रुटि",
	this_form_has_been_modified: "इस फॉर्म को संशोधित किया गया है",
	current_form_in_sync: "वर्तमान फॉर्म डेटाबेस के साथ सिंक में है",
	open_in_console: "कंसोल में खोलें",
	collection_does_not_exist: "निर्दिष्ट संग्रह मौजूद नहीं है। कंसोल चेक करें",
	unexpected_value: "अप्रत्याशित मान",
	unexpected_value_click_to_edit: "अप्रत्याशित मान। संपादित करने के लिए क्लिक करें",
	unexpected_reference_value: "अप्रत्याशित संदर्भ मान। संपादित करने के लिए क्लिक करें",
	copy_url_to_clipboard: "क्लिपबोर्ड पर URL कॉपी करें",
	open_image_in_new_tab: "नई टैब में छवि खोलें",
	reference_not_set: "संदर्भ सेट नहीं है",
	reference_does_not_exist: "संदर्भ मौजूद नहीं है",
	entity_not_found: "इकाई नहीं मिली",
	entity_not_found_body: "यह रिकॉर्ड मौजूद नहीं है, या पंक्ति-स्तरीय सुरक्षा के कारण आपसे छिपा हो सकता है।",
	back_to_collection: "{{name}} पर वापस जाएँ",
	file_not_found: "फ़ाइल नहीं मिली",
	unsaved_changes_in_entity: "आपके पास इस इकाई में सहेजे नहीं गए परिवर्तन हैं।",
	delete_this_role: "यह भूमिका हटाएं",
	no_roles_yet: "आपके पास अभी तक कोई भूमिका नहीं है।",
	create_default_roles: "डिफ़ॉल्ट भूमिकाएँ बनाएँ",
	delete_role_confirmation: "क्या आप वाकई इस भूमिका को हटाना चाहते हैं?",
	delete_this_user: "इस उपयोगकर्ता को हटाएं",
	no_users_yet: "अभी तक कोई उपयोगकर्ता नहीं है",
	add_logged_user_as_admin: "लॉग इन उपयोगकर्ता को एडमिन के रूप में जोड़ें",
	add_current_user_as_admin: "वर्तमान उपयोगकर्ता को एडमिन के रूप में जोड़ें",
	create_default_roles_and_add_admin: "डिफ़ॉल्ट भूमिकाएँ बनाएं और वर्तमान उपयोगकर्ता को एडमिन के रूप में जोड़ें",
	delete_user_confirmation: "क्या आप वाकई इस उपयोगकर्ता को हटाना चाहते हैं?",
	create_your_users_and_roles: "अपने उपयोगकर्ता और भूमिकाएँ बनाएँ",
	no_users_or_roles_defined: "आपके पास कोई उपयोगकर्ता या भूमिका परिभाषित नहीं है। आप डिफ़ॉल्ट भूमिकाएँ बना सकते हैं और वर्तमान उपयोगकर्ता को एडमिन के रूप में जोड़ सकते हैं।",
	save_before_changing_schema: "स्कीमा बदलने से पहले आपको दस्तावेज़ सहेजना होगा",
	edit_schema_for_this_form: "इस फॉर्म के लिए स्कीमा संपादित करें",
	no_permissions_to_edit_collection: "आपके पास इस संग्रह को संपादित करने की अनुमति नहीं है",
	browser_does_not_support_audio: "आपका ब्राउज़र ऑडियो तत्व का समर्थन नहीं करता है।",
	user_not_found: "उपयोगकर्ता नहीं मिला: {{value}}",
	delete_selected: "चयनित हटाएं",
	cannot_delete_selected: "आपने कम से कम एक इकाई का चयन किया है जिसे आप हटा नहीं सकते",
	add_entry: "जोड़ें",
	add_on_top: "शीर्ष पर जोड़ें",
	add_below: "नीचे जोड़ें",
	add_to_field: "{{fieldName}} में जोड़ें",
	value: "मान",
	key: "कुंजी",
	drag_drop_multiple: "कुछ फ़ाइलों को यहां खींचें और छोड़ें, या फ़ाइलों का चयन करने के लिए क्लिक करें। पुन: क्रमबद्ध करने के लिए खींचें।",
	drag_drop_single: "किसी फ़ाइल को यहां खींचें और छोड़ें, या किसी एक का चयन करने के लिए क्लिक करें",
	open_menu: "मेनू खोलें",
	close_drawer: "ड्रॉअर बंद करें",
	navigation_drawer: "नेविगेशन ड्रॉअर",
	collapse: "संक्षिप्त करें",
	expand: "विस्तार करें",
	change_language: "भाषा बदलें",
	toggle_theme: "थीम बदलें",
	user_menu: "उपयोगकर्ता मेनू",
	error: "त्रुटि",
	error_loading_data: "डेटा लोड नहीं हो सका",
	error_check_server_logs: "विस्तृत जानकारी के लिए सर्वर लॉग देखें।",
	error_technical_details: "तकनीकी विवरण",
	error_uploading_file: "फ़ाइल अपलोड करने में त्रुटि",
	error_deleting: "हटाने में त्रुटि",
	error_before_delete: "हटाने से पहले त्रुटि",
	error_updating_asset: "एसेट अपडेट करने में त्रुटि",
	error_deleting_asset: "एसेट हटाने में त्रुटि",
	error_firestore_index: "इस क्वेरी के लिए Firestore इंडेक्स आवश्यक है।",
	create_index: "इंडेक्स बनाएं",
	value_is_not_reference: "मान एक संदर्भ नहीं है।",
	click_to_edit: "संपादित करने के लिए क्लिक करें",
	data_is_not_array_of_references: "डेटा संदर्भों का ऐरे नहीं है",
	loading: "लोड हो रहा है",
	local_changes_applied: "स्थानीय परिवर्तन फॉर्म पर लागू किए गए",
	local_changes_discarded: "स्थानीय परिवर्तन रद्द किए गए",
	are_you_sure_leave: "क्या आप वाकई इस पृष्ठ को छोड़ना चाहते हैं?",
	see_console_details: "अधिक विवरण के लिए कंसोल देखें।",
	drop_here_create_group: "नया समूह बनाने के लिए यहां छोड़ें",
	filter_for_null_values: "रिक्त (नल्ल) मानों के लिए फ़िल्टर करें",
	value_updated_elsewhere: "इस मान को कहीं और अपडेट किया गया है",
	add_property: "विशेषता (प्रॉपर्टी) जोड़ें",
	edit_name: "{{name}} संपादित करें",
	this_entity_not_exist: "यह इकाई डेटाबेस में मौजूद नहीं है",
	internal_error: "आंतरिक त्रुटि (Internal ERROR)",
	rename_group: "समूह का नाम बदलें",
	group_name_label: "समूह का नाम",
	group_name_empty_error: "समूह का नाम खाली नहीं हो सकता।",
	group_name_exists_error: "यह समूह नाम पहले से मौजूद है।",
	search_collections: "संग्रह खोजें",
	views_group: "दृश्य",
	youd_need_to_save_before_additional_collections: "अतिरिक्त संग्रह जोड़ने से पहले आपको अपनी इकाई को सहेजना होगा",
	page_not_found: "पृष्ठ नहीं मिला",
	page_not_found_body: "यह पृष्ठ मौजूद नहीं है या आपके पास इस तक पहुंच नहीं हो सकती है",
	back_to_home: "होम पर वापस जाएँ",
	default_collection_view: "डिफ़ॉल्ट संग्रह दृश्य",
	table_view: "तालिका",
	cards_view: "कार्ड्स",
	kanban_view: "कानबन",
	choose_how_entities_displayed_default: "चुनें कि डिफ़ॉल्ट रूप से इकाइयां कैसे प्रदर्शित होनी चाहिए",
	document_view: "दस्तावेज़ दृश्य",
	side_panel: "साइड पैनल",
	full_screen: "पूरी स्क्रीन",
	should_documents_opened_full_screen: "क्या दस्तावेज़ों को फ़ुल स्क्रीन में या इनलाइन साइड डायलॉग में खोला जाना चाहिए",
	select_custom_view: "कस्टम दृश्य चुनें",
	no_custom_views_defined: "कोई कस्टम दृश्य परिभाषित नहीं है। इस डायलॉग का उपयोग करने से पहले कस्टमाइज़ेशन सेटिंग्स में अपने कस्टम दृश्य परिभाषित करें।",
	select_custom_action: "कस्टम कार्रवाई चुनें",
	no_custom_actions_defined: "कोई कस्टम कार्रवाई परिभाषित नहीं है। इस डायलॉग का उपयोग करने से पहले कस्टमाइज़ेशन सेटिंग्स में अपनी कस्टम कार्रवाइयाँ परिभाषित करें।",
	no_collections_found: "कोई संग्रह नहीं मिला",
	start_building_collections: "Rebase में आसानी से संग्रह बनाना शुरू करें। उन्हें अपने मौजूदा डेटाबेस डेटा पर मैप करें, फ़ाइलों से आयात करें या हमारे टेम्प्लेट का उपयोग करें।",
	create_first_collection: "अपना पहला संग्रह बनाएं",
	define_collections_programmatically: "आप संग्रहों को प्रोग्रामेटिक रूप से भी परिभाषित कर सकते हैं।",
	edit_collection: "संग्रह संपादित करें",
	no_permissions_edit_collection: "आपके पास इस संग्रह को संपादित करने की अनुमति नहीं है",
	no_permissions_create_collection: "आपके पास संग्रह बनाने की अनुमति नहीं है",
	create_collection: "संग्रह बनाएं",
	update_collection: "संग्रह अपडेट करें",
	new_collection: "नया संग्रह",
	add_new_collection: "नया संग्रह जोड़ें",
	collection_with_name: "{{name}} संग्रह",
	change_icon: "आइकन बदलें",
	is_subcollection_of: "यह इसका उपसंग्रह (subcollection) है",
	name: "नाम",
	collection_name_description: "इस संग्रह का नाम, आमतौर पर एक बहुवचन नाम (जैसे उत्पाद)",
	path: "पथ (Path)",
	relative_path_to_parent: "मूल (parent) का सापेक्ष पथ (मूल पथ को शामिल करने की आवश्यकता नहीं है)",
	path_in_database: "वह पथ जिसमें यह संग्रह संग्रहीत है, डेटाबेस में",
	singular_name: "एकवचन नाम",
	singular_name_description: "वैकल्पिक रूप से अपनी इकाइयों के लिए एकवचन नाम परिभाषित करें",
	description: "विवरण",
	description_of_collection: "संग्रह का विवरण, आप मार्कडाउन का उपयोग कर सकते हैं",
	collection_id: "संग्रह ID",
	collection_id_description: "यह ID इस संग्रह की पहचान करता है। आम तौर पर पथ के समान।",
	collection_group: "संग्रह समूह",
	collection_group_description: "एक संग्रह समूह में एक ही पथ वाले सभी संग्रह होते हैं। यह आपको एक साथ कई संग्रहों पर क्वेरी करने की अनुमति देता है।",
	advanced_settings: "उन्नत सेटिंग्स",
	doc_history_global: "दस्तावेज़ इतिहास संशोधन सक्षम (यदि विश्व स्तर पर सक्षम हैं)",
	doc_history_enabled: "दस्तावेज़ इतिहास संशोधन सक्षम",
	doc_history_not_enabled: "दस्तावेज़ इतिहास संशोधन सक्षम नहीं",
	doc_history_description: "सक्षम होने पर, इस संग्रह के प्रत्येक दस्तावेज़ में परिवर्तनों का इतिहास होगा। यह ऑडिटिंग उद्देश्यों के लिए उपयोगी है। डेटा आपके डेटाबेस में दस्तावेज़ के एक उपसंग्रह में संग्रहीत होता है, जिसे __history कहा जाता है।",
	document_id_generation: "दस्तावेज़ ID जनरेशन",
	code_defined: "कोड परिभाषित",
	users_must_define_id: "उपयोगकर्ताओं को एक ID परिभाषित करनी होगी",
	users_can_define_id: "उपयोगकर्ता एक ID परिभाषित कर सकते हैं, लेकिन इसकी आवश्यकता नहीं है",
	doc_id_auto_generated: "दस्तावेज़ ID स्वचालित रूप से उत्पन्न होती है",
	config_doc_id_generation: "नई इकाइयां बनाते समय दस्तावेज़ ID कैसे उत्पन्न होती हैं, इसे कॉन्फ़िगर करें।",
	enable_text_search: "इस संग्रह के लिए पाठ खोज (text search) सक्षम करें",
	text_search_description: "इस संग्रह के लिए पाठ खोज की अनुमति दें। यदि आपने पाठ खोज प्रतिनिधि निर्दिष्ट नहीं किया है, तो यह अंतर्निहित स्थानीय पाठ खोज का उपयोग करेगा। बड़े संग्रहों के लिए इसकी अनुशंसा नहीं की जाती है, क्योंकि इससे प्रदर्शन और लागत संबंधी समस्याएं हो सकती हैं।",
	database_id: "डेटाबेस ID",
	default_text: "(डिफ़ॉल्ट)",
	custom_actions: "कस्टम कार्रवाइयां",
	more_info: "अतिरिक्त जानकारी",
	define_custom_actions_cli: "CLI के साथ अपलोड करके अपनी स्वयं की कस्टम कार्रवाइयां परिभाषित करें।",
	action_defined_in_code: "यह क्रिया कोड में कुंजी (key) के साथ परिभाषित की गई है",
	add_custom_entity_action: "कस्टम इकाई कार्रवाई जोड़ें",
	remove_this_action: "यह क्रिया हटाएँ?",
	remove_action_warning: "यह कोई डेटा नहीं हटाएगा, केवल एडमिन पैनल में कार्रवाई को हटाएगा",
	subcollections_of: "के उपसंग्रह",
	add_subcollection: "उपसंग्रह जोड़ें",
	custom_views: "कस्टम दृश्य",
	define_custom_views_cli: "CLI के साथ अपलोड करके अपने स्वयं के कस्टम दृश्य परिभाषित करें।",
	view_defined_in_code: "यह दृश्य कोड में कुंजी (key) के साथ परिभाषित किया गया है",
	add_custom_entity_view: "कस्टम इकाई दृश्य जोड़ें",
	delete_this_subcollection: "इस उपसंग्रह को हटाएं?",
	remove_collection_warning: "यह कोई डेटा नहीं हटाएगा, केवल एडमिन पैनल में संग्रह को हटाएगा",
	remove_this_view: "यह दृश्य हटाएँ?",
	remove_view_warning: "यह कोई डेटा नहीं हटाएगा, केवल एडमिन पैनल में दृश्य को हटाएगा",
	no_collection_selected: "कोई संग्रह नहीं चुना गया",
	code_for_collection: "संग्रह के लिए कोड",
	use_config_define_json: "संग्रह को JSON प्रारूप में परिभाषित करने के लिए इस कॉन्फ़िगरेशन का उपयोग करें।",
	customise_collection_code: "यदि आप कोड में संग्रह को अनुकूलित करना चाहते हैं, तो आप इस संग्रह कोड को अपने एडमिन ऐप कॉन्फ़िगरेशन में जोड़ सकते हैं।",
	copied: "कॉपी किया गया",
	property_cant_be_edited: "इस विशेषता को संपादित नहीं किया जा सकता",
	property_not_editable_description: "आपके पास इसे संपादित करने की अनुमति नहीं हो सकती है या इसे कोड में परिभाषित किया गया है और इसे संशोधित नहीं किया जा सकता।",
	delete_this_property: "यह विशेषता हटाएँ?",
	delete_property_warning: "यह कोई डेटा नहीं हटाएगा, केवल संग्रह को संशोधित करेगा।",
	error_must_specify_id: "आपको फ़ील्ड के लिए एक id निर्दिष्ट करना होगा",
	error_id_format: "id में केवल अक्षर, संख्याएं और अंडरस्कोर (_) हो सकते हैं, और यह किसी संख्या से शुरू नहीं हो सकता",
	error_id_already_exists: "इस ID के साथ पहले से ही एक और फ़ील्ड मौजूद है",
	error_must_specify_title: "आपको फ़ील्ड के लिए एक शीर्षक निर्दिष्ट करना होगा",
	custom_or_other: "कस्टम/अन्य",
	select_property_widget: "एक प्रॉपर्टी विजेट का चयन करें",
	error_changing_data_type: "यह विजेट प्रारंभ में चयनित विजेट की तुलना में एक अलग डेटा प्रकार का उपयोग करता है। यह मौजूदा डेटा के साथ त्रुटियों का कारण बन सकता है।",
	required: "आवश्यक",
	enum_form_dialog: "Enum फॉर्म डायलॉग",
	imported_data_preview: "आयात किए गए डेटा का पूर्वावलोकन",
	entities_with_same_id_overwritten: "समान id वाली इकाइयों को अधिलेखित (overwritten) किया जाएगा",
	collection_editor: "संग्रह संपादक",
	properties_in_this_group: "इस समूह में विशेषताएं",
	data_property_mapping: "डेटा संपत्ति मानचित्रण (मॅपिंग)",
	property_edit_view: "प्रॉपर्टी संपादन दृश्य",
	all_of_these: "ये सभी",
	any_of_these: "इनमें से कोई भी",
	only_admins_edit_roles: "केवल व्यवस्थापक (Admins) ही भूमिकाएँ संपादित कर सकते हैं",
	error_user_not_found: "उपयोगकर्ता नहीं मिला",
	all_roles: "सभी भूमिकाएँ",
	role: "भूमिका",
	name_of_this_role: "इस भूमिका का नाम",
	id_of_this_role: "इस भूमिका की ID",
	create_entities: "इकाइयाँ बनाएँ",
	read_entities: "इकाइयाँ पढ़ें",
	update_entities: "इकाइयाँ अपडेट करें",
	delete_entities: "इकाइयाँ हटाएँ",
	all_collections: "सभी संग्रह",
	create_entities_in_collections: "संग्रहों में इकाइयाँ बनाएँ",
	access_all_data_in_every_collection: "हर संग्रह में सभी डेटा तक पहुँचें",
	update_data_in_any_collection: "किसी भी संग्रह में डेटा अपडेट करें",
	delete_data_in_any_collection: "किसी भी संग्रह में डेटा हटाएँ",
	allow_all_permissions_in_this_collections: "इन संग्रहों में सभी अनुमतियों की अनुमति दें",
	all: "सभी",
	customise_permissions_description: "आप उन अनुमतियों को अनुकूलित कर सकते हैं जो इस भूमिका से संबंधित उपयोगकर्ता प्रत्येक संग्रह की इकाइयों में कर सकते हैं",
	create_collections: "संग्रह बनाएँ",
	yes: "हाँ",
	no: "नहीं",
	can_user_create_collections: "क्या उपयोगकर्ता संग्रह बना सकता है",
	edit_collections: "संग्रह संपादित करें",
	only_own_collections: "केवल उसकी/उसकी अपनी",
	own: "अपनी",
	can_user_edit_collections: "क्या उपयोगकर्ता संग्रह संपादित कर सकता है",
	delete_collections: "संग्रह हटाएँ",
	can_user_delete_collections: "क्या उपयोगकर्ता संग्रह हटा सकता है",
	error_saving_role: "इस भूमिका को सहेजने में कोई त्रुटि हुई",
	create_role: "भूमिका बनाएँ",
	update: "अपडेट करें",
	only_admins_change_roles: "केवल व्यवस्थापक ही भूमिकाएँ बदल सकते हैं",
	must_be_at_least_one_admin: "कम से कम एक एडमिन होना चाहिए",
	logged_user_not_found: "लॉग इन उपयोगकर्ता नहीं मिला",
	user: "उपयोगकर्ता (User)",
	user_id: "उपयोगकर्ता ID",
	name_of_this_user: "इस उपयोगकर्ता का नाम",
	email_of_this_user: "इस उपयोगकर्ता का ईमेल",
	roles: "भूमिकाएँ",
	create_user: "उपयोगकर्ता बनाएँ",
	users: "उपयोगकर्ता (Users)",
	add_user: "उपयोगकर्ता जोड़ें",
	add_role: "भूमिका जोड़ें",
	is_admin: "एडमिन है",
	default_permissions: "डिफ़ॉल्ट अनुमतियाँ",
	created_on: "इस दिन बनाया गया",
	email: "ईमेल",
	id: "ID",
	read: "पढ़ें",
	column_in_file: "फ़ाइल में कॉलम",
	map_to_property: "प्रॉपर्टी में मैप करें",
	default_values: "डिफ़ॉल्ट मान",
	default_values_description: "आप अनमैप किए गए कॉलम और खाली मानों के लिए एक डिफ़ॉल्ट मान का चयन कर सकते हैं:",
	property: "विशेषता (Property)",
	default_value: "डिफ़ॉल्ट मान",
	autogenerate_id: "ID स्वतः उत्पन्न करें",
	id_column_description: "कॉलम जिसका उपयोग प्रत्येक दस्तावेज़ के लिए ID के रूप में किया जाएगा",
	do_not_set_value: "मान सेट न करें",
	set_value_to_true: "मान को true पर सेट करें",
	set_value_to_false: "मान को false पर सेट करें",
	drag_and_drop_file: "फ़ाइल को यहां खींचें और छोड़ें या अपलोड करने के लिए क्लिक करें",
	error_saving_data: "डेटा सहेजने में त्रुटि",
	retry: "पुनः प्रयास करें",
	saving_data: "डेटा सहेजा जा रहा है",
	entities_saved: "इकाइयाँ सहेजी गईं",
	do_not_close_tab: "इस टैब को बंद न करें अन्यथा आयात बाधित हो जाएगा",
	import: "आयात (Import)",
	import_data: "डेटा आयात करें",
	upload_file_description: "CSV, Excel या JSON फ़ाइल अपलोड करें और इसे अपने मौजूदा स्कीमा में मैप करें",
	back: "पीछे",
	next: "अगला",
	save_data: "डेटा सहेजें",
	use_column_as_id: "इस कॉलम को ID के रूप में उपयोग करें",
	do_not_import_property: "इस विशेषता को आयात न करें",
	entities_will_be_overwritten: "समान ID वाली इकाइयों को अधिलेखित (overwritten) किया जाएगा",
	data_imported_successfully: "डेटा सफलतापूर्वक आयात किया गया",
	export: "निर्यात (Export)",
	export_data: "डेटा निर्यात करें",
	download_table_csv: "इस तालिका की सामग्री को CSV के रूप में डाउनलोड करें",
	csv: "CSV",
	json: "JSON",
	dates_as_timestamps: "टाइमस्टैम्प के रूप में तिथियां",
	dates_as_strings: "स्ट्रिंग्स के रूप में तिथियां",
	flatten_arrays: "ऐरे को फ़्लैट करें",
	download: "डाउनलोड",
	large_number_of_documents: "इस संग्रह में बड़ी संख्या में दस्तावेज़ हैं ({{count}})।",
	too_many_documents_to_export: "इस संग्रह में इतने दस्तावेज़ ({{count}}) हैं कि ब्राउज़र उन्हें एक ही फ़ाइल में निर्यात नहीं कर सकता (सीमा {{limit}})। निर्यात से पहले इसे फ़िल्टर करें।",
	include_undefined_values: "अपरिभाषित (undefined) मान शामिल करें",
	submit: "सबमिट करें",
	no_filterable_properties: "कोई फ़िल्टर करने योग्य गुण उपलब्ध नहीं हैं",
	apply_filters: "फ़िल्टर लागू करें",
	filter_presets: "प्रीसेट",
	filter_preset_apply: "प्रीसेट लागू करें",
	filter_preset_active: "सक्रिय: {{label}}",
	list: "सूची",
	table_view_mode: "तालिका",
	cards: "कार्ड",
	board: "बोर्ड",
	initialize_kanban_order_desc: "यह उन सभी आइटम को अनुक्रमिक क्रम मान निर्दिष्ट करेगा जिनके पास एक नहीं है। आइटम प्रत्येक कॉलम के भीतर अपना वर्तमान क्रम बनाए रखेंगे।",
	kanban_view_not_available: "कानबन दृश्य उपलब्ध नहीं है",
	kanban_view_requires_enum: "कानबन दृश्य के लिए इकाइयों को कॉलम में समूहीकृत करने के लिए एनम (Enum) मानों वाली एक स्ट्रिंग संपत्ति की आवश्यकता होती है। कृपया इस दृश्य का उपयोग करने के लिए अपने संग्रह स्कीमा में एक Enum संपत्ति जोड़ें।",
	no_enum_values_configured: "प्रॉपर्टी \"{{property}}\" के लिए कोई एनम मान कॉन्फ़िगर नहीं किया गया है",
	items_need_backfill: "कुछ आइटम के क्रम मान नहीं होते हैं। खींचें-और-छोड़ें (Drag-and-drop) पुनर्रचना को सक्षम करने के लिए प्रारंभ करें।",
	kanban_order_not_configured: "खींचें-और-छोड़ें क्रमबद्धता कॉन्फ़िगर नहीं है। कार्ड की स्थिति बनाए रखने के लिए संग्रह सेटिंग्स में एक क्रम गुण सेट करें।",
	initialize: "प्रारंभ करें",
	confirm_multiple_delete: "एकाधिक विलोपन की पुष्टि करें?",
	delete_entity_confirm_title: "क्या आप इस {{entityName}} को हटाना चाहेंगे?",
	add_existing: "मौजूदा जोड़ें",
	unlink_entity_confirm_title: "इस {{entityName}} को इस रिकॉर्ड से हटाएँ?",
	unlink_entity_confirm_body: "यह {{collectionName}} में बना रहेगा और अन्य रिकॉर्ड के लिए उपलब्ध रहेगा।",
	confirm_multiple_unlink: "क्या इन्हें इस रिकॉर्ड से हटाना है?",
	no_filter: "कोई फ़िल्टर नहीं",
	is_true: "सत्य है",
	is_false: "असत्य है",
	error_uploading_file_size: "फ़ाइल अपलोड करने में त्रुटि: फ़ाइल {{maxSize}} बाइट से बड़ी है",
	error_uploading_file_type: "फ़ाइल अपलोड करने में त्रुटि: यह फ़ाइल प्रकार समर्थित नहीं है",
	drag_drop_files: "फ़ाइलें यहाँ खींचकर छोड़ें, या संपादित करने के लिए यहाँ क्लिक करें",
	drag_drop_file: "फ़ाइल यहाँ खींचकर छोड़ें, या संपादित करने के लिए यहाँ क्लिक करें",
	multiple_deleted: "कई हटाए गए",
	some_entities_deleted: "कुछ संस्थाएँ हटा दी गई हैं, लेकिन सभी नहीं",
	error_deleting_entities: "संस्थाओं को हटाने में त्रुटि",
	deleted: "हटाया गया",
	selection_options: "चयन विकल्प",
	selection_menu_all_loaded: "इस पृष्ठ पर सभी",
	selection_menu_all_matching: "सभी {{total}} {{collection}}",
	selection_menu_none: "कोई नहीं",
	selection_select_all_loaded: "लोड की गई पंक्तियाँ चुनें",
	selection_deselect_all: "सभी अचयनित करें",
	selection_reading_rows: "पंक्तियाँ पढ़ी जा रही हैं… {{total}} में से {{loaded}}",
	selection_reading_rows_unknown: "पंक्तियाँ पढ़ी जा रही हैं… अब तक {{loaded}}",
	selection_deleting_progress: "{{total}} में से {{completed}} हटाए गए",
	confirm_delete_selection: "क्या {{total}} {{collection}} हटाएँ?",
	confirm_delete_selection_unknown: "क्या वर्तमान फ़िल्टर से मेल खाने वाली सभी पंक्तियाँ हटाएँ?",
	confirm_delete_selection_body: "इसे पूर्ववत नहीं किया जा सकता। हर पंक्ति अलग-अलग हटाई जाती है, इसलिए इसमें कुछ समय लग सकता है।",
	export_selection_count: "{{total}} चयनित पंक्तियाँ {{format}} रूप में डाउनलोड करें",
	export_selection_all_matching: "वर्तमान फ़िल्टर से मेल खाने वाले सभी {{collection}} डाउनलोड करें",
	select_reference: "संदर्भ चुनें",
	select_references: "संदर्भ चुनें (एकाधिक)",
	account_settings: "खाता सेटिंग्स",
	profile: "Profile",
	security: "सुरक्षा",
	sessions: "Sessions",
	change_password: "पासवर्ड बदलें",
	current_password: "वर्तमान पासवर्ड",
	new_password: "नया पासवर्ड",
	confirm_password: "नया पासवर्ड की पुष्टि करें",
	password_changed: "पासवर्ड सफलतापूर्वक बदल गया। आपको लॉग आउट कर दिया जाएगा।",
	passwords_dont_match: "पासवर्ड मेल नहीं खाते",
	password_too_short: "पासवर्ड कम से कम 8 वर्णों का होना चाहिए",
	password_change_not_available: "बाहरी साइन-इन प्रदाता का उपयोग करने वाले खातों के लिए पासवर्ड बदलना उपलब्ध नहीं है।",
	changing_password: "बदला जा रहा है...",
	display_name: "प्रदर्शित नाम",
	photo_url: "फ़ोटो URL",
	save_profile: "प्रोफ़ाइल सहेजें",
	saving: "Saving...",
	no_active_sessions: "कोई सक्रिय सत्र नहीं मिला।",
	revoking: "Revoking...",
	revoke_all_sessions: "सभी सत्र रद्द करें",
	unknown_device: "अज्ञात डिवाइस",
	current: "Current",
	role_id: "भूमिका ID",
	role_name: "भूमिका का नाम",
	add_reference: "संदर्भ जोड़ें",
	add_reference_to: "{{name}} में संदर्भ जोड़ें",
	/** RolesView — table & permissions */
	role_deleted_successfully: "भूमिका सफलतापूर्वक हटाई गई",
	error_deleting_role: "भूमिका हटाने में त्रुटि",
	collection_permissions: "संग्रह की अनुमतियाँ",
	collection: "Collection",
	no_collections_configured: "कोई संग्रह कॉन्फ़िगर नहीं है",
	no_security_rules_defined: "कोई सुरक्षा नियम परिभाषित नहीं — सभी संक्रियाएँ अप्रतिबंधित हैं",
	no_rules: "कोई नियम नहीं",
	/** UsersView — snackbar */
	bootstrap_admin_success: "अब आप व्यवस्थापक हैं! रीफ़्रेश किया जा रहा है...",
	failed_to_bootstrap_admin: "व्यवस्थापक सेट नहीं किया जा सका",
	user_deleted_successfully: "उपयोगकर्ता सफलतापूर्वक हटाया गया",
	error_deleting_user: "उपयोगकर्ता हटाने में त्रुटि",
	user_created_successfully: "उपयोगकर्ता सफलतापूर्वक बनाया गया",
	invitation_sent: "{{email}} पर आमंत्रण ईमेल भेज दिया गया है। वे लिंक से अपना पासवर्ड सेट कर सकते हैं।",
	invitation_sent_title: "आमंत्रण भेजा गया",
	temporary_password: "अस्थायी पासवर्ड",
	temporary_password_description: "ईमेल कॉन्फ़िगर नहीं है। यह अस्थायी पासवर्ड उपयोगकर्ता को सुरक्षित रूप से दें। यह दोबारा नहीं दिखाया जाएगा।",
	temporary_password_email_failed_description: "ईमेल भेजा नहीं जा सका। यह अस्थायी पासवर्ड उपयोगकर्ता को सुरक्षित रूप से दें। यह दोबारा नहीं दिखाया जाएगा।",
	copy_password: "पासवर्ड कॉपी करें",
	password_copied: "पासवर्ड क्लिपबोर्ड पर कॉपी हो गया",
	/** UsersView — pagination & search */
	search_users: "उपयोगकर्ता खोजें",
	created: "बनाया गया",
	actions: "क्रियाएँ",
	no_users_found: "कोई उपयोगकर्ता नहीं मिला",
	reset_password: "पासवर्ड रीसेट करें",
	reset_password_success: "पासवर्ड सफलतापूर्वक रीसेट हो गया",
	reset_password_confirmation: "क्या आप वाकई इस उपयोगकर्ता का पासवर्ड रीसेट करना चाहते हैं?",
	reset_password_send_email: "पासवर्ड रीसेट ईमेल भेजें",
	reset_password_set_manually: "पासवर्ड मैन्युअल रूप से सेट करें",
	reset_password_set_manually_description: "पासवर्ड तुरंत बदल जाता है और कोई ईमेल नहीं भेजा जाता। इसे उपयोगकर्ता को सुरक्षित रूप से दें।",
	/** Permission-denied empty states */
	no_permission_to_view_users: "आपको उपयोगकर्ताओं को देखने की अनुमति नहीं है",
	no_permission_to_view_roles: "आपको भूमिकाओं को देखने की अनुमति नहीं है",
	no_permission_description: "यदि आपको इस अनुभाग तक पहुँच की आवश्यकता है तो किसी व्यवस्थापक से संपर्क करें।",
	error_resetting_password: "पासवर्ड रीसेट करने में त्रुटि",
	/** Editor table-bubble */
	add_row_before: "पहले पंक्ति जोड़ें",
	add_row_after: "बाद में पंक्ति जोड़ें",
	delete_row: "पंक्ति हटाएँ",
	add_column_before: "पहले स्तंभ जोड़ें",
	add_column_after: "बाद में स्तंभ जोड़ें",
	delete_column: "स्तंभ हटाएँ",
	delete_table: "तालिका हटाएँ",
	/** Editor image-bubble */
	alt_text: "वैकल्पिक पाठ",
	title: "Title",
	/** MapFieldBinding */
	a_property_of_this_map_has_error: "इस मानचित्र की एक प्रॉपर्टी में त्रुटि है",
	/** AI Collection Generator Popover */
	generate_collection_with_ai: "AI से संग्रह बनाएं",
	modify_collection_with_ai: "AI से संग्रह संशोधित करें",
	describe_collection_to_create: "वह संग्रह बताएं जिसे आप बनाना चाहते हैं।",
	describe_changes_to_make: "वे परिवर्तन बताएं जो आप इस संग्रह में करना चाहते हैं।",
	ai_placeholder_create: "उदा. नाम, मूल्य, विवरण और श्रेणी के साथ एक उत्पाद संग्रह बनाएं...",
	ai_placeholder_modify: "उदा. स्टोरेज के साथ एक थंबनेल छवि फ़ील्ड जोड़ें, मूल्य को आवश्यक बनाएं...",
	ai_assist: "AI सहायक",
	generating: "उत्पन्न हो रहा है...",
	/** Recently extracted strings for collection editor */
	this_is_subcollection_of: "यह इसका एक उपसंघ (subcollection) है",
	use_existing_paths_database: "अपने डेटाबेस में मौजूदा पथों में से एक का उपयोग करें:",
	describe_collection_ai: "AI को अपने संग्रह का वर्णन करें:",
	generate_with_ai: "AI के साथ जनरेट करें",
	create_from_json_config: "JSON कॉन्फ़िगरेशन से बनाएँ:",
	paste_json_config: "JSON कॉन्फ़िगरेशन पेस्ट करें",
	create_collection_from_file_formats: "फ़ाइल (csv, json, xls, xslx...) से एक संग्रह बनाएँ",
	select_template: "एक टेम्पलेट चुनें:",
	products: "उत्पाद",
	collection_products_subtitle: "छवियों, कीमतों और स्टॉक वाले उत्पादों का संग्रह",
	collection_users_subtitle: "ईमेल, नाम और भूमिकाओं वाले उपयोगकर्ताओं का एक संग्रह",
	blog_posts: "ब्लॉग पोस्ट",
	collection_blog_posts_subtitle: "छवियों, लेखकों और जटिल सामग्री के साथ ब्लॉग पोस्ट का एक संग्रह",
	pages: "पृष्ठ (Pages)",
	collection_pages_subtitle: "छवियों, लेखकों और जटिल सामग्री के साथ पृष्ठों का एक संग्रह",
	continue_from_scratch: "शुरुआत से जारी रखें",
	/** Admin views config */
	admin_users: "एडमिन पैनल उपयोगकर्ता",
	roles_menu: "भूमिकाएँ",
	project_settings: "परियोजना सेटिंग",
	by_signing_in_you_agree_to_our: "साइन इन करके आप हमारे नियमों और शर्तों से सहमत होते हैं",
	terms_and_conditions: "नियम और शर्तें",
	and_our: "और हमारी गोपनीयता नीति",
	privacy_policy: "गोपनीयता नीति",
	email_password: "ईमेल/पासवर्ड",
	sign_in_with_google: "Google से साइन इन करें",
	auth_user_not_found: "उपयोगकर्ता नहीं मिला",
	auth_wrong_password: "गलत पासवर्ड। कृपया पुनः प्रयास करें।",
	auth_user_disabled: "उपयोगकर्ता अक्षम है। कृपया समर्थन से संपर्क करें।",
	auth_account_exists_with_different_credential: "इस ईमेल से एक खाता पहले से मौजूद है। पिछली बार की तरह साइन इन करें।",
	auth_account_exists_sign_in_with_password: "इस ईमेल से एक खाता पहले से मौजूद है। इसके बजाय अपने ईमेल और पासवर्ड से साइन इन करें।",
	auth_email_already_in_use: "ईमेल पहले से ही उपयोग में है",
	auth_google_permissions_required: "अपने Google Cloud प्रोजेक्ट प्रबंधित करने के लिए आपको अतिरिक्त अनुमतियाँ देने की आवश्यकता है",
	auth_invalid_email_password: "कृपया ईमेल और पासवर्ड दोनों दर्ज करें",
	auth_enter_email_first: "कृपया पहले अपना ईमेल दर्ज करें",
	auth_password_reset_sent: "पासवर्ड रीसेट ईमेल भेजा गया",
	auth_sign_in_account: "अपने खाते में प्रवेश करें",
	auth_create_new_account: "एक नया अकाउंट बनाएँ",
	auth_password: "पासवर्ड",
	auth_reset_password: "पासवर्ड रीसेट करें",
	auth_new_user: "नया उपयोगकर्ता?",
	auth_have_account: "क्या आपके पास एक खाता है?",
	auth_sign_in: "साइन इन करें",
	auth_sign_up: "साइन अप करें",
	auto_setup_collections_button: "संग्रहों को स्वचालित रूप से सेट करें",
	auto_setup_collections_title: "संग्रहों की स्वचालित सेटअप",
	auto_setup_collections_desc: "अपने मौजूदा Firestore डेटा के आधार पर संग्रहों को स्वचालित रूप से सेट करें। Rebase को आपके लिए परफेक्ट एडमिन पैनल कॉन्फ़िगर करने दें।",
	this_can_take_a_minute: "इसमें एक मिनट लग सकता है",
	no_collections_found_to_setup: "सेट करने के लिए कोई संग्रह नहीं मिला",
	collections_have_been_setup: "संग्रहों को सेट कर दिया गया है",
	error_setting_up_collections: "संग्रह सेट करने में त्रुटि",
	admin_panel_ready_bring_data: "आपका पैनल तैयार है। अपना डेटा जोड़कर शुरू करें।",
	admin_panel_ready_get_started: "शुरू करें",
	auto_detect_collections: "संग्रहों को स्वचालित रूप से पहचानें",
	auto_detect_collections_desc: "Rebase आपके Firestore डेटा का विश्लेषण करेगा और आपके लिए संग्रहों को स्वचालित रूप से कॉन्फ़िगर करेगा",
	create_a_collection: "एक संग्रह बनाएं",
	create_collection_desc: "अपने संग्रह की संरचना मैन्युअल रूप से परिभाषित करें",
	read_the_docs: "दस्तावेज़ीकरण पढ़ें",
	read_the_docs_desc: "जानें कि Rebase का अधिकतम उपयोग कैसे करें",
	explore_docs: "दस्तावेज़ीकरण देखें",
	want_to_customize_with_code: "क्या आप कोड से Rebase को कस्टमाइज़ करना चाहते हैं?",
	to_scaffold_a_local_project: "एक स्थानीय प्रोजेक्ट बनाने के लिए",
	validation: "सत्यापन",
	unique: "अद्वितीय",
	required_message: "आवश्यक संदेश",
	required_tooltip: "यदि यह मान सेट नहीं है तो आप इस एंटिटी को सहेज नहीं पाएंगे",
	unique_tooltip: "एक ही मान वाली एक से अधिक एंटिटी नहीं हो सकतीं",
	lowercase: "लोअरकेस",
	uppercase: "अपरकेस",
	trim: "ट्रिम",
	exact_length: "सटीक लंबाई",
	min_length: "न्यूनतम लंबाई",
	max_length: "अधिकतम लंबाई",
	matches_regex: "रेजेक्स से मेल खाता है",
	not_valid_regexp: "वैध रेगुलर एक्सप्रेशन नहीं है",
	regex_helper: "उदा. /^\\d+$/ केवल अंकों के लिए",
	min_value: "न्यूनतम मान",
	max_value: "अधिकतम मान",
	less_than: "से कम",
	more_than: "से अधिक",
	positive_value: "सकारात्मक मान",
	negative_value: "नकारात्मक मान",
	integer_value: "पूर्णांक मान",
	invalid_regular_expression: "अमान्य रेगुलर एक्सप्रेशन",
	must_specify_target_collection: "आपको फ़ील्ड के लिए एक लक्ष्य संग्रह निर्दिष्ट करना होगा",
	need_specify_repeat_field: "आपको एक दोहराव फ़ील्ड निर्दिष्ट करना होगा",
	need_specify_block_properties: "आपको इस ब्लॉक के गुण निर्दिष्ट करने होंगे",
	incomplete_condition: "अपूर्ण शर्त - कृपया एक फ़ील्ड चुनें",
	field_name: "फ़ील्ड का नाम",
	kanban_column_property: "कानबन कॉलम प्रॉपर्टी",
	select_a_property: "एक प्रॉपर्टी चुनें",
	kanban_property_not_found: "प्रॉपर्टी \"{{property}}\" मौजूद नहीं है या एनम स्ट्रिंग प्रॉपर्टी नहीं है। कृपया एक वैध प्रॉपर्टी चुनें या चयन साफ़ करें।",
	no_enum_string_properties: "कोई एनम स्ट्रिंग प्रॉपर्टी नहीं मिली। कानबन दृश्य का उपयोग करने के लिए enumValues के साथ एक स्ट्रिंग प्रॉपर्टी जोड़ें।",
	kanban_column_description: "एंटिटीज़ को कॉलम में समूहित करने के लिए एनम मानों वाली एक स्ट्रिंग प्रॉपर्टी चुनें",
	create_property: "+ \"{{property}}\" प्रॉपर्टी बनाएं",
	order_property: "क्रम प्रॉपर्टी",
	order_property_not_found: "प्रॉपर्टी \"{{property}}\" मौजूद नहीं है या एक संख्या प्रॉपर्टी नहीं है। कृपया एक वैध प्रॉपर्टी चुनें या चयन साफ़ करें।",
	no_number_properties: "कोई संख्या प्रॉपर्टी नहीं मिली। क्रम सक्षम करने के लिए एक संख्या प्रॉपर्टी जोड़ें।",
	order_property_description: "आइटम का क्रम बनाए रखने के लिए एक संख्या प्रॉपर्टी चुनें",
	display_settings: "प्रदर्शन सेटिंग्स",
	default_row_size: "डिफ़ॉल्ट पंक्ति आकार",
	side_dialog_width: "साइड डायलॉग चौड़ाई",
	side_dialog_width_description: "वैकल्पिक रूप से एंटिटी साइड डायलॉग की चौड़ाई (पिक्सेल में) परिभाषित करें। डिफ़ॉल्ट 768px है",
	inline_editing_enabled: "डेटा को सीधे टेबल दृश्य में संपादित किया जा सकता है",
	inline_editing_disabled: "डेटा को केवल फ़ॉर्म दृश्य में संपादित किया जा सकता है",
	inline_editing_description: "फ़ॉर्म दृश्य खोले बिना सीधे टेबल दृश्य में डेटा संपादन की अनुमति दें।",
	include_json_view: "JSON दृश्य शामिल करें",
	no_json_view: "JSON दृश्य शामिल न करें",
	json_view_description: "दस्तावेज़ का JSON प्रतिनिधित्व शामिल करें।",
	not_found_suffix: "नहीं मिला",
	editor_text: "पाठ",
	editor_text_description: "बस सादे पाठ के साथ टाइप करना शुरू करें।",
	editor_heading_1: "शीर्षक 1",
	editor_heading_1_description: "बड़ा अनुभाग शीर्षक।",
	editor_heading_2: "शीर्षक 2",
	editor_heading_2_description: "मध्यम अनुभाग शीर्षक।",
	editor_heading_3: "शीर्षक 3",
	editor_heading_3_description: "छोटा अनुभाग शीर्षक।",
	editor_todo_list: "टू-डू सूची",
	editor_todo_list_description: "टू-डू सूची के साथ कार्यों को ट्रैक करें।",
	editor_bullet_list: "बुलेट सूची",
	editor_bullet_list_description: "एक सरल बुलेट सूची बनाएं।",
	editor_numbered_list: "क्रमांकित सूची",
	editor_numbered_list_description: "नंबरिंग के साथ एक सूची बनाएं।",
	editor_quote: "उद्धरण",
	editor_quote_description: "एक उद्धरण कैप्चर करें।",
	editor_code: "कोड",
	editor_code_description: "एक कोड स्निपेट कैप्चर करें।",
	editor_image: "छवि",
	editor_image_description: "अपने कंप्यूटर से एक छवि अपलोड करें।",
	editor_multiple: "एकाधिक",
	editor_link: "लिंक",
	editor_save: "सहेजें",
	editor_cancel: "रद्द करें",
	editor_remove_link: "लिंक हटाएं",
	editor_paste_or_type_link: "लिंक पेस्ट करें या टाइप करें",
	editor_open_in_new_window: "नई विंडो में खोलें",
	editor_bold: "बोल्ड",
	editor_italic: "इटैलिक",
	editor_underline: "अंडरलाइन",
	editor_strikethrough: "स्ट्राइकथ्रू",
	editor_autocomplete: "स्वतः पूर्ण",
	editor_autocomplete_description: "संदर्भ के आधार पर पाठ जोड़ें।",
	text_search_dialog_title: "पाठ खोज सक्षम करें",
	text_search_local_not_recommended: "बड़ी संग्रहों के लिए स्थानीय पाठ खोज की अनुशंसा नहीं की जाती है।",
	text_search_local_fetch_warning: "ध्यान दें कि स्थानीय पाठ खोज सक्षम करने के लिए आपके संग्रह से सभी दस्तावेज़ प्राप्त करने और उन्हें ब्राउज़र में संग्रहीत करने की आवश्यकता होगी। बड़ी संग्रहों के लिए यह अक्षम हो सकता है और अतिरिक्त लागत भी लग सकती है।",
	text_search_external_suggestion: "बड़ी संग्रहों के लिए, Algolia या Elastic Search जैसे बाहरी खोज इंजन का उपयोग करने और अपनी कॉन्फ़िगरेशन में एक खोज प्रतिनिधि निर्दिष्ट करने की सलाह दी जाती है।",
	text_search_local_description: "स्थानीय पाठ खोज आपके संग्रह में पाठ खोज सक्षम करने का सबसे सरल तरीका है। यह ब्राउज़र में संग्रह के सभी दस्तावेज़ लोड करता है और खोज स्थानीय रूप से करता है। छोटी संग्रहों के लिए यह अनुशंसित विकल्प है।",
	text_search_own_implementation: "आपने अपना स्वयं का पाठ खोज नियंत्रक लागू किया है। आप अपने संग्रह के लिए पाठ खोज सक्षम कर सकते हैं।",
	text_search_enable_for_collection: "इस संग्रह के लिए सक्षम करें",
	text_search_enable_for_project: "प्रोजेक्ट के लिए सक्षम करें",
	text_search_enabled_snackbar: "स्थानीय पाठ खोज सक्षम की गई",
	settings_heading: "सेटिंग्स",
	settings_project_name: "परियोजना का नाम",
	settings_default_language: "डिफ़ॉल्ट भाषा",
	settings_default_language_caption: "इस परियोजना के लिए आधार भाषा चुनें। उपयोगकर्ता अपनी व्यक्तिगत सेटिंग्स में इस प्राथमिकता को बदल सकते हैं।",
	settings_enable_local_text_search: "स्थानीय पाठ खोज सक्षम करें",
	settings_local_text_search_caption: "सभी संग्रहों के लिए स्थानीय पाठ खोज सक्षम करें। इससे आप Rebase खोज पट्टी का उपयोग करके अपने संग्रहों में पाठ फ़ील्ड खोज सकेंगे। ध्यान दें कि इस सुविधा के कारण अधिक पठन हो सकते हैं, क्योंकि यह आपके संग्रहों के सभी पाठ फ़ील्ड को अनुक्रमित करेगी।",
	settings_doc_history_all_collections: "सभी संग्रहों के लिए दस्तावेज़ इतिहास सक्षम",
	settings_doc_history_caption: "सक्षम होने पर, सभी संग्रहों में डिफ़ॉल्ट रूप से इतिहास सक्षम होगा। आप प्रत्येक संग्रह में इस सेटिंग को बदल सकते हैं। इतिहास प्रत्येक दस्तावेज़ की __history उपसंग्रह में सहेजा जाएगा।",
	settings_theme: "थीम",
	settings_primary_color: "प्राथमिक रंग",
	settings_secondary_color: "द्वितीयक रंग",
	settings_sample_theme_components: "नमूना थीम घटक",
	settings_drag_drop_logo: "अपना लोगो यहां खींचें और छोड़ें",
	settings_security_rules: "सुरक्षा नियम",
	settings_security_rules_description: "Rebase डेटा तक पहुंच प्रतिबंधित करने के लिए Firebase सुरक्षा नियमों का उपयोग करता है। Rebase के माध्यम से नया उपयोगकर्ता बनाते समय, कस्टम दावा rebaseUser क्लाइंट प्रोजेक्ट में उपयोगकर्ता में जोड़ा जाता है। निम्नलिखित सुरक्षा नियम जोड़कर, आप सुनिश्चित करते हैं कि Rebase उपयोगकर्ता Rebase के माध्यम से डेटा तक पहुंच सकें।",
	settings_security_rules_add_domain: "कृपया अपने प्रदाता के अनुमत डोमेन में {{domain}} डोमेन जोड़ना याद रखें",
	settings_security_rules_caption: "ये नियम डेटा तक पहुंच को केवल Rebase उपयोगकर्ताओं तक सीमित करते हैं, लेकिन डेटाबेस स्तर पर अनुमतियों को लागू नहीं करते। अनुमतियां फ्रंटएंड में लागू होती हैं, जो अधिकांश परियोजनाओं के लिए ठीक काम करती हैं। यदि आपको डेटाबेस स्तर पर अनुमतियां लागू करने की आवश्यकता है, तो आप इन सुरक्षा नियमों को संशोधित कर सकते हैं। उपयोगकर्ता को सौंपी गई भूमिकाएं Firebase प्रमाणीकरण टोकन में कस्टम दावों के रूप में सेट की जाती हैं, इसलिए आप उन्हें अपने सुरक्षा नियमों में उपयोग कर सकते हैं।",
	studio_sql_executing_query: "क्वेरी चल रही है...",
	studio_sql_query_error: "क्वेरी त्रुटि",
	studio_sql_run_query_placeholder: "परिणाम देखने के लिए एक क्वेरी चलाएँ",
	studio_sql_visual_execution_plan: "विज़ुअल निष्पादन योजना",
	studio_sql_success: "SUCCESS",
	studio_sql_no_results: "क्वेरी सफलतापूर्वक चली, लेकिन कोई परिणाम नहीं मिला।",
	studio_sql_rows: "Rows:",
	studio_sql_time: "Time:",
	studio_sql_copy_markdown: "Markdown के रूप में कॉपी करें",
	studio_sql_export_json: "JSON निर्यात करें",
	studio_sql_export_csv: "CSV निर्यात करें",
	studio_sql_format_sql: "SQL फ़ॉर्मैट करें",
	studio_sql_explain: "Explain",
	studio_sql_limit_1000: "Limit 1000",
	studio_sql_remove_from_favorites: "पसंदीदा से हटाएँ",
	studio_sql_add_to_favorites: "पसंदीदा में जोड़ें",
	studio_sql_save: "Save",
	studio_sql_run: "Run",
	studio_sql_database: "Database",
	studio_sql_role: "Role",
	studio_sql_admin: "(Admin)",
	studio_sql_select_db: "डेटाबेस चुनें",
	studio_sql_query_results: "क्वेरी परिणाम",
	studio_sql_save_snippet: "SQL स्निपेट सहेजें",
	studio_sql_snippet_name: "स्निपेट का नाम",
	studio_sql_snippet_name_placeholder: "उदा., सभी उपयोगकर्ता प्राप्त करें",
	studio_sql_snippet_saved_local: "यह आपके स्थानीय स्टोरेज में सहेजा जाएगा।",
	studio_sql_cancel: "Cancel",
	studio_sql_dangerous_operation: "खतरनाक संक्रिया मिली",
	studio_sql_dangerous_operation_body: "जो क्वेरी आप चलाने वाले हैं उसमें संभावित रूप से विनाशकारी संक्रियाएँ हैं (DELETE, DROP, TRUNCATE, या WHERE के बिना UPDATE)। क्या आप वाकई आगे बढ़ना चाहते हैं?",
	studio_sql_snippet_saved: "स्निपेट \"{{name}}\" सहेजा गया।",
	studio_sql_markdown_copied: "परिणाम Markdown के रूप में कॉपी हो गए!",
	studio_sql_markdown_copy_failed: "Markdown क्लिपबोर्ड पर कॉपी नहीं हो सका।",
	studio_sql_row_updated: "पंक्ति सफलतापूर्वक अपडेट हुई।",
	studio_sql_cannot_edit_missing_query: "पंक्तियाँ संपादित नहीं की जा सकतीं: अंतर्निहित क्वेरी अनुपलब्ध है।",
	studio_sql_cannot_resolve_table: "तालिका/प्राथमिक कुंजी निर्धारित नहीं हो सकी",
	studio_sql_missing_pk: "पंक्ति में प्राथमिक कुंजी स्तंभ अनुपलब्ध हैं: {{columns}}। सुरक्षित रूप से अपडेट नहीं किया जा सकता।",
	studio_sql_update_failed: "अपडेट विफल: {{message}}",
	studio_sql_execution_not_supported: "वर्तमान डेटा स्रोत SQL निष्पादन का समर्थन नहीं करता।",
	studio_sql_error_executing: "क्वेरी चलाते समय एक त्रुटि हुई।",
	studio_sql_error_explaining: "क्वेरी का विश्लेषण करते समय एक त्रुटि हुई।",
	studio_sql_save_first_to_favorite: "पसंदीदा में जोड़ने से पहले स्निपेट सहेजें।",
	studio_sql_collections_label: "Collections:",
	studio_sql_admin_collections_tooltip: "Tables in this query that are mapped as admin collections",
	studio_sql_edit_entity: "Edit {{name}} #{{id}}",
	studio_sql_sql_not_supported: "यह डेटा स्रोत SQL निष्पादन का समर्थन नहीं करता",
	studio_sql_fetch_error: "कनेक्शन विकल्प प्राप्त नहीं हो सके: {{message}}",
	studio_sql_unexpected_format: "अप्रत्याशित डेटा प्रारूप: {{type}}",
	studio_sql_no_tables: "इस डेटाबेस में कोई तालिका नहीं मिली।",
	studio_sql_schema_fetch_error: "स्कीमा प्राप्त नहीं हो सका: {{message}}",
	studio_sql_sidebar_snippets: "Snippets",
	studio_sql_sidebar_history: "History",
	studio_sql_sidebar_schema: "Schema",
	studio_sql_sidebar_no_snippets: "कोई सहेजा गया स्निपेट नहीं",
	studio_sql_sidebar_save_snippet_hint: "किसी क्वेरी को यहाँ देखने के लिए उसे सहेजें।",
	studio_sql_sidebar_no_history: "कोई इतिहास नहीं",
	studio_sql_sidebar_history_hint: "चलाई गई क्वेरीज़ यहाँ दिखाई देंगी।",
	studio_sql_sidebar_delete_snippet: "स्निपेट हटाएँ",
	studio_sql_close_tab: "यह क्वेरी टैब बंद करें",
	studio_sql_new_tab: "नया क्वेरी टैब",
	studio_schema_tables: "Tables",
	studio_schema_loading: "स्कीमा लोड हो रहा है...",
	studio_schema_no_tables: "कोई तालिका नहीं मिली",
	studio_schema_retry: "Retry",
	studio_schema_primary_key: "प्राथमिक कुंजी",
	studio_schema_select_all: "SELECT * FROM",
	studio_schema_insert_into: "INSERT INTO",
	studio_schema_update: "UPDATE",
	studio_schema_delete_from: "DELETE FROM",
	studio_schema_columns: "{{count}} स्तंभ",
	studio_rls_title: "पंक्ति-स्तरीय सुरक्षा",
	studio_rls_description: "अपनी तालिकाओं के लिए RLS नीतियाँ प्रबंधित करें",
	studio_rls_enabled: "RLS सक्षम",
	studio_rls_disabled: "RLS निष्क्रिय",
	studio_rls_no_rls: "RLS नहीं",
	studio_rls_enable_rls: "RLS सक्षम करें",
	studio_rls_disable_rls: "RLS निष्क्रिय करें",
	studio_rls_create_policy: "नीति बनाएँ",
	studio_rls_policies: "Policies",
	studio_rls_no_policies: "कोई नीति परिभाषित नहीं",
	studio_rls_no_policies_desc: "इस तालिका में कोई RLS नीति नहीं है। सभी पंक्तियों तक पहुँच RLS की स्थिति पर निर्भर करती है।",
	studio_rls_add_first_policy: "अपनी पहली नीति जोड़ें",
	studio_rls_force_rls: "RLS बाध्य करें",
	studio_rls_force_rls_desc: "सक्षम होने पर RLS नीतियाँ तालिका के स्वामी पर भी लागू होती हैं।",
	studio_rls_enable_force_rls: "बाध्य RLS सक्षम करें",
	studio_rls_disable_force_rls: "बाध्य RLS निष्क्रिय करें",
	studio_rls_edit: "Edit",
	studio_rls_delete: "Delete",
	studio_rls_confirm_delete_title: "नीति हटाएँ",
	studio_rls_confirm_delete_body: "क्या आप वाकई यह नीति हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।",
	studio_rls_confirm_enable_title: "पंक्ति-स्तरीय सुरक्षा सक्षम करें",
	studio_rls_confirm_enable_body: "RLS सक्षम करने पर इस तालिका की पंक्तियों तक पहुँच आपकी नीतियों के अनुसार सीमित हो जाएगी। सुनिश्चित करें कि उपयुक्त नीतियाँ मौजूद हैं।",
	studio_rls_confirm_disable_title: "पंक्ति-स्तरीय सुरक्षा निष्क्रिय करें",
	studio_rls_confirm_disable_body: "RLS निष्क्रिय करने पर सभी पंक्तियाँ सभी भूमिकाओं के लिए सुलभ हो जाएँगी। क्या आप वाकई जारी रखना चाहते हैं?",
	studio_rls_select_table: "एक तालिका चुनें",
	studio_rls_no_tables: "कोई तालिका उपलब्ध नहीं",
	studio_rls_error: "नीतियाँ लोड करने में त्रुटि",
	studio_rls_retry: "Retry",
	studio_rls_loading: "नीतियाँ लोड हो रही हैं...",
	studio_policy_edit: "नीति संपादित करें",
	studio_policy_create: "नई नीति बनाएँ",
	studio_policy_defining_rules: "इसके लिए पहुँच नियम परिभाषित किए जा रहे हैं",
	studio_policy_cancel: "Cancel",
	studio_policy_save: "नीति सहेजें",
	studio_policy_template: "टेम्पलेट से शुरू करें (वैकल्पिक)",
	studio_policy_select_template: "एक टेम्पलेट चुनें...",
	studio_policy_name: "नीति का नाम",
	studio_policy_name_placeholder: "अपनी नीति को एक स्पष्ट नाम दें",
	studio_policy_behavior: "नीति का व्यवहार",
	studio_policy_permissive: "Permissive",
	studio_policy_permissive_desc: "\"OR\" से संयोजित",
	studio_policy_restrictive: "Restrictive",
	studio_policy_restrictive_desc: "\"AND\" से संयोजित",
	studio_policy_command: "नीति की कमांड",
	studio_policy_target_roles: "लक्षित भूमिकाएँ",
	studio_policy_roles_placeholder: "भूमिकाएँ चुनें (डिफ़ॉल्ट: public)",
	studio_policy_using_expr: "USING एक्सप्रेशन",
	studio_policy_using_expr_desc: "मौजूदा पंक्तियों पर लागू (SELECT, UPDATE, DELETE)",
	studio_policy_check_expr: "WITH CHECK एक्सप्रेशन",
	studio_policy_check_expr_desc: "नई या संशोधित पंक्तियों पर लागू (INSERT, UPDATE)",
	studio_policy_help_title: "पंक्ति-स्तरीय सुरक्षा (RLS) मार्गदर्शिका",
	studio_policy_help_intro: "पंक्ति-स्तरीय सुरक्षा (RLS) से आप तय करते हैं कि कोई उपयोगकर्ता अपनी प्रमाणीकरण स्थिति या भूमिकाओं के आधार पर कौन-सी पंक्तियाँ देख या बदल सकता है।",
	studio_policy_help_step1_title: "1. एक टेम्पलेट चुनें",
	studio_policy_help_step1_desc: "यदि आप RLS में नए हैं, तो सबसे आसान तरीका ड्रॉपडाउन से एक पूर्वनिर्धारित टेम्पलेट चुनना है। यह \"उपयोगकर्ता अपनी ही पंक्तियाँ अपडेट कर सकते हैं\" जैसे सामान्य मामलों के लिए नीति स्वतः कॉन्फ़िगर कर देगा।",
	studio_policy_help_step2_title: "2. लक्षित भूमिकाएँ",
	studio_policy_help_step2_desc: "चुनें कि नीति किन उपयोगकर्ताओं पर लागू होगी:",
	studio_policy_help_role_public: "सभी पर लागू (लॉग इन हों या न हों)।",
	studio_policy_help_role_authenticated: "केवल लॉग इन किए हुए उपयोगकर्ताओं पर लागू।",
	studio_policy_help_role_anon: "केवल उन उपयोगकर्ताओं पर लागू जो लॉग इन नहीं हैं।",
	studio_policy_help_step3_title: "3. USING एक्सप्रेशन",
	studio_policy_help_step3_desc: "एक SQL शर्त जो सत्य होने पर ही मौजूदा पंक्तियाँ दिखती या बदली जा सकती हैं (SELECT, UPDATE, DELETE)।",
	studio_policy_help_step3_example: "सुनिश्चित करता है कि उपयोगकर्ता केवल वही पंक्तियाँ देखें जिनके वे स्वामी हैं।",
	studio_policy_help_step4_title: "4. WITH CHECK एक्सप्रेशन",
	studio_policy_help_step4_desc: "एक SQL शर्त जो सत्य होने पर ही नई या संशोधित पंक्तियाँ सहेजी जाती हैं (INSERT, UPDATE)।",
	studio_policy_help_step4_example: "आम तौर पर USING एक्सप्रेशन जैसा ही, जब उपयोगकर्ता केवल अपना ही डेटा जोड़ या संपादित कर सकते हों।",
	studio_policy_help_auth_vars_title: "Rebase प्रमाणीकरण वेरिएबल",
	studio_policy_help_auth_vars_desc: "Rebase ऐसे नेटिव हेल्पर फ़ंक्शन देता है जिन्हें आप अपने एक्सप्रेशन में उपयोग कर सकते हैं:",
	studio_policy_help_auth_uid: "वर्तमान उपयोगकर्ता की ID को टेक्स्ट के रूप में लौटाता है। उदाहरण:",
	studio_policy_help_auth_jwt: "पूरा JWT पेलोड JSONB के रूप में लौटाता है, ताकि आप कस्टम क्लेम जाँच सकें। उदाहरण:",
	studio_policy_help_auth_roles: "उपयोगकर्ता की भूमिका-ID को अल्पविराम से अलग स्ट्रिंग के रूप में लौटाता है। इसके साथ सर्वोत्तम:",
	studio_policy_help_docs_cta: "Postgres नीतियों के बारे में और जानने के लिए आधिकारिक दस्तावेज़ देखें।",
	studio_policy_help_read_docs: "दस्तावेज़ पढ़ें",
	studio_policy_help_got_it: "समझ गए!",
	studio_add_kanban_column: "स्तंभ जोड़ें",
	studio_add_kanban_column_title: "Kanban स्तंभ जोड़ें",
	studio_add_kanban_column_name: "स्तंभ का नाम",
	studio_add_kanban_column_name_placeholder: "नए स्तंभ का नाम",
	studio_add_kanban_column_add: "Add",
	studio_add_kanban_column_cancel: "Cancel",
	studio_collection_view_sql: "SQL संपादक",
	studio_collection_view_admin: "व्यवस्थापक दृश्य",
	studio_editor_collection_tooltip: "संग्रह का स्कीमा संपादित करें",
	studio_editor_collection_no_permission: "आपको इस संग्रह को संपादित करने की अनुमति नहीं है",
	studio_editor_collection_start_tooltip: "पथ कॉपी करें या स्कीमा संपादित करें",
	studio_editor_collection_start_no_permission: "आपको इस संग्रह में बदलाव करने की अनुमति नहीं है",
	studio_editor_collection_start_copied: "Copied!",
	studio_editor_entity_tooltip: "इकाई का स्कीमा संपादित करें",
	studio_editor_entity_no_permission: "आपको इस इकाई को संपादित करने की अनुमति नहीं है",
	studio_home_edit_collection: "संग्रह संपादित करें",
	studio_home_delete_collection: "संग्रह हटाएँ",
	studio_home_confirm_delete_title: "संग्रह हटाएँ?",
	studio_home_confirm_delete_body: "क्या आप वाकई \"{{name}}\" संग्रह हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।",
	studio_kanban_setup: "Kanban सेटअप",
	studio_missing_reference: "अनुपलब्ध संदर्भ: {{path}}",
	studio_missing_reference_create: "Create",
	studio_new_collection: "नया संग्रह",
	studio_new_collection_card: "नया संग्रह",
	studio_new_collection_no_permission: "आपको संग्रह बनाने की अनुमति नहीं है",
	studio_property_add_column: "नई प्रॉपर्टी जोड़ें",
	studio_property_add_column_no_permission: "आपको नई प्रॉपर्टी जोड़ने की अनुमति नहीं है",
	studio_add_kanban_column_desc: "\"{{property}}\" प्रॉपर्टी में एक नया विकल्प जोड़ता है। इससे Kanban बोर्ड में एक नया स्तंभ बनेगा।",
	studio_add_kanban_column_adding: "Adding...",
	studio_collection_view_edit: "Edit",
	studio_editor_collection_disabled: "संग्रह संपादन निष्क्रिय है",
	studio_editor_collection_edit: "संग्रह संपादित करें",
	studio_editor_entity_save_first: "स्कीमा बदलने से पहले आपको दस्तावेज़ सहेजना होगा",
	studio_editor_entity_edit_schema: "इस फ़ॉर्म का स्कीमा संपादित करें",
	studio_editor_collection_start_save_filter: "डिफ़ॉल्ट फ़िल्टर और क्रम सहेजें",
	studio_editor_collection_start_clear_filter: "डिफ़ॉल्ट फ़िल्टर और क्रम हटाएँ",
	studio_editor_collection_start_reset_filter: "डिफ़ॉल्ट फ़िल्टर और क्रम पर लौटें",
	studio_editor_collection_start_saved: "डिफ़ॉल्ट कॉन्फ़िगरेशन सहेजा गया",
	studio_home_duplicate_collection: "Duplicate",
	studio_home_delete: "Delete",
	studio_home_confirm_delete_no_data: "इससे कोई डेटा नहीं हटेगा, केवल व्यवस्थापक पैनल का संग्रह हटेगा",
	studio_home_collection_deleted: "संग्रह हटा दिया गया",
	studio_kanban_configure: "Kanban कॉन्फ़िगर करें",
	studio_missing_reference_error: "इस पथ के लिए कोई संग्रह नहीं: {{path}}",
	studio_new_collection_add: "नया संग्रह जोड़ें",
	db_column_type: "डेटाबेस कॉलम प्रकार",
	primary_key_unique_id: "प्राथमिक कुंजी / अद्वितीय ID",
	spread_children_as_columns: "बच्चों को कॉलम के रूप में फैलाएं",
	mode: "मोड",
	timezone: "समय क्षेत्र",
	target_collection: "लक्ष्य संग्रह",
	storage_file_name: "फ़ाइल का नाम",
	storage_path: "संग्रहण पथ",
	storage_max_size: "अधिकतम आकार (बाइट्स में)",
	storage_resize_mode: "आकार बदलने का मोड",
	storage_output_format: "आउटपुट प्रारूप",
	storage_max_width: "अधिकतम चौड़ाई (px)",
	storage_max_height: "अधिकतम ऊंचाई (px)",
	storage_quality: "गुणवत्ता (0-100)",
	storage_file_upload_config: "फ़ाइल अपलोड कॉन्फ़िगरेशन",
	storage_image_resize_config: "इमेज आकार बदलने का कॉन्फ़िगरेशन",
	storage_all_file_types: "सभी फ़ाइल प्रकार अनुमत",
	storage_allowed_file_types: "अनुमत फ़ाइल प्रकार",
	storage_include_bucket_url: "सहेजे गए मान में बकेट URL (s3://...) शामिल करें",
	storage_save_url: "संग्रहण पथ के बजाय URL सहेजें",
	datetime_automatic_value: "स्वचालित मान",
	markdown_paste_behavior: "पेस्ट व्यवहार",
	entity_saved: "सहेजा गया",
	more_actions: "अधिक क्रियाएँ",
	record_history: "इतिहास",
	inspect_record: "रिकॉर्ड जाँचें",
	inspect: "जाँचें",
	close_inspector: "इंस्पेक्टर बंद करें",
	open_in_live_site: "लाइव साइट में खोलें",
	history_revert: "इस संस्करण पर लौटें",
	history_revert_title: "डेटा को इस संस्करण पर लौटाएँ?",
	history_reverted: "चयनित संस्करण पर लौटा दिया गया",
	history_revert_error: "रिकॉर्ड लौटाने में त्रुटि",
	history_revert_dirty: "लौटाने से पहले अपने बदलाव सहेजें या रद्द करें",
	history_empty: "कोई इतिहास उपलब्ध नहीं",
	history_empty_description: "रिकॉर्ड सहेजने पर एक नया संस्करण बनता है और इतिहास में रखा जाता है।",
	history_new_entity: "इतिहास केवल मौजूदा रिकॉर्ड के लिए उपलब्ध है",
	history_no_more: "और इतिहास नहीं",
	studio_group_views: "दृश्य",
	studio_group_admin: "व्यवस्थापन",
	studio_group_settings: "सेटिंग्स",
	studio_group_database: "डेटाबेस",
	studio_group_compute: "कंप्यूट",
	studio_group_storage: "स्टोरेज",
	studio_group_api: "API",
	studio_group_access_control: "एक्सेस नियंत्रण",
	studio_tool_sql: "SQL कंसोल",
	studio_tool_sql_description: "SQL क्वेरी चलाएँ",
	studio_tool_js: "JS कंसोल",
	studio_tool_js_description: "JavaScript चलाएँ",
	studio_tool_rls: "RLS नीतियाँ",
	studio_tool_rls_description: "पंक्ति-स्तरीय सुरक्षा",
	studio_tool_storage: "स्टोरेज",
	studio_tool_storage_description: "स्टोरेज फ़ाइलें प्रबंधित करें",
	studio_tool_cron: "क्रॉन जॉब",
	studio_tool_cron_description: "निर्धारित कार्य प्रबंधित करें",
	studio_tool_schema_visualizer: "स्कीमा विज़ुअलाइज़र",
	studio_tool_schema_visualizer_description: "इंटरैक्टिव डेटाबेस ERD",
	studio_tool_branches: "ब्रांच",
	studio_tool_branches_description: "डेटाबेस ब्रांच बनाएँ और प्रबंधित करें",
	studio_tool_backups: "बैकअप",
	studio_tool_backups_description: "डेटाबेस बैकअप डाउनलोड करें",
	studio_tool_api: "API एक्सप्लोरर",
	studio_tool_api_description: "इंटरैक्टिव API दस्तावेज़ और परीक्षण",
	studio_tool_logs: "लॉग एक्सप्लोरर",
	studio_tool_logs_description: "रीयल-टाइम सिस्टम और क्वेरी लॉग",
	studio_tool_api_keys: "API कुंजियाँ",
	studio_tool_api_keys_description: "स्कोप वाली API कुंजियाँ बनाएँ और प्रबंधित करें",
	studio_backups_denied_title: "आप इस प्रोजेक्ट के बैकअप सूचीबद्ध नहीं कर सकते",
	studio_backups_denied_hint: "प्रोजेक्ट में कोई गड़बड़ी नहीं है। बैकअप सूचीबद्ध करने के लिए एडमिन कनेक्शन चाहिए, और साइन-इन किए गए खाते को मना कर दिया गया।",
	studio_backups_read_failed: "इस प्रोजेक्ट के बैकअप नहीं पढ़े जा सके",
	studio_backups_downloading: "डाउनलोड हो रहा है…",
	studio_backups_roles_file: "रोल फ़ाइल",
	studio_backups_roles_file_hint: "इस बैकअप के साथ सहेजी गई .globals.sql। यह वे डेटाबेस रोल फिर से बनाती है जिनका ज़िक्र डंप की अनुमतियाँ और पंक्ति-स्तरीय सुरक्षा करती हैं, और `rebase db restore` इसे .dump के पास से पढ़ता है, इसलिए दोनों फ़ाइलें साथ रखें।",
	studio_backups_no_roles_file: "कोई रोल फ़ाइल नहीं",
	studio_backups_no_roles_file_hint: "इस बैकअप के साथ कोई .globals.sql सहेजी नहीं गई। इसे किसी नए Postgres में रिस्टोर करने पर यह पहली ऐसी अनुमति पर रुक जाता है जो किसी ऐसे डेटाबेस रोल को दी गई हो जो वहाँ मौजूद नहीं है।",
	studio_cron_denied_title: "आप इस प्रोजेक्ट के क्रॉन जॉब सूचीबद्ध नहीं कर सकते",
	studio_cron_denied_hint: "प्रोजेक्ट में कोई गड़बड़ी नहीं है। क्रॉन API केवल एडमिन के लिए है, और साइन-इन किए गए खाते को मना कर दिया गया।",
	studio_cron_read_failed: "इस प्रोजेक्ट के क्रॉन जॉब नहीं पढ़े जा सके",
	studio_api_keys_denied_title: "आप इस प्रोजेक्ट की API कुंजियाँ सूचीबद्ध नहीं कर सकते",
	studio_api_keys_denied_hint: "प्रोजेक्ट में कोई गड़बड़ी नहीं है। API कुंजियाँ प्रबंधित करना एडमिन कार्य है, और साइन-इन किए गए खाते को मना कर दिया गया।",
	studio_api_keys_read_failed: "इस प्रोजेक्ट की API कुंजियाँ नहीं पढ़ी जा सकीं",
	studio_api_keys_empty_title: "अभी तक कोई API कुंजी नहीं",
	studio_api_keys_empty_hint: "सेवाओं के बीच प्रमाणीकरण सक्षम करने के लिए एक कुंजी बनाएँ",
	studio_api_keys_revoke: "निरस्त करें",
	studio_api_keys_select_hint: "विवरण देखने के लिए एक API कुंजी चुनें",
	studio_api_keys_permissions: "अनुमतियाँ",
	studio_api_keys_no_permissions: "कोई अनुमति कॉन्फ़िगर नहीं की गई",
	studio_api_keys_revoke_confirmation: "निरस्तीकरण की पुष्टि",
	studio_api_keys_name_label: "नाम:",
	studio_api_keys_access_label: "पहुँच:",
	studio_api_keys_admin_granted: "व्यवस्थापक भूमिका प्रदान की गई",
	studio_api_keys_admin_granted_hint: "व्यवस्थापक रूट और default_admin नीतियाँ",
	studio_api_keys_done: "हो गया",
	studio_api_keys_status_active: "सक्रिय",
	studio_api_keys_status_expired: "समाप्त",
	studio_api_keys_status_revoked: "निरस्त",
	studio_api_keys_stat_status: "स्थिति",
	studio_api_keys_stat_last_used: "अंतिम उपयोग",
	studio_api_keys_stat_expires: "समाप्ति",
	studio_api_keys_never: "कभी नहीं",
	studio_api_keys_role_service: "सेवा",
	studio_api_keys_stat_rate_limit: "दर सीमा",
	studio_api_keys_rate_limit_default: "डिफ़ॉल्ट (1000/15 मिनट)",
	studio_api_keys_stat_created_by: "निर्माता",
	studio_api_keys_copy_failed: "कॉपी नहीं हो सका",
	studio_branches_denied_title: "आप इस प्रोजेक्ट की ब्रांच सूचीबद्ध नहीं कर सकते",
	studio_branches_denied_hint: "प्रोजेक्ट में कोई गड़बड़ी नहीं है। ब्रांचिंग एडमिन कनेक्शन पर चलती है, और साइन-इन किए गए खाते को मना कर दिया गया।",
	studio_branches_read_failed: "इस प्रोजेक्ट की ब्रांच नहीं पढ़ी जा सकीं",
	studio_schema_no_collections: "कोई कलेक्शन घोषित नहीं",
	studio_schema_no_collections_body: "अभी बनाने के लिए कुछ नहीं है। config/collections/ में एक कलेक्शन जोड़ें, या यहाँ जोड़ने के लिए “कलेक्शन संपादित करें” खोलें।",
	studio_schema_introspect_hint: "जिस डेटाबेस में पहले से टेबल हैं, वहाँ rebase schema introspect आपके लिए कलेक्शन फ़ाइलें लिख देता है।",
	studio_rls_enable_confirm_title: "पंक्ति-स्तरीय सुरक्षा सक्षम करें?",
	studio_rls_enable_confirm_body: "जब तक कोई नीति अनुमति न दे, {{table}} की कोई पंक्ति पढ़ी नहीं जा सकेगी। Postgres डिफ़ॉल्ट रूप से मना करता है।",
	studio_rls_disable_confirm_title: "पंक्ति-स्तरीय सुरक्षा अक्षम करें?",
	studio_rls_disable_confirm_body: "{{table}} पर सभी नीतियाँ लागू होना बंद हो जाएँगी। जो भी टेबल तक पहुँच सकता है, हर पंक्ति पढ़ और लिख सकेगा।",
	studio_rls_drop_policy_title: "इस नीति को हटाएँ?",
	studio_rls_drop_policy_body: "{{policy}} को डेटाबेस में {{table}} से हटा दिया जाएगा। आपके कोड में कुछ नहीं लिखा जाता, इसलिए डिप्लॉय इसे वापस ला सकता है।",
	studio_rls_unapplied: "लागू नहीं",
	studio_rls_unapplied_tooltip: "आपके कोड में घोषित, डेटाबेस में अभी नहीं। बदलाव की योजना बनाकर लागू करने के लिए इसे यहाँ सहेजें, या `rebase db push` चलाएँ।",
	studio_branches_unavailable_title: "डेटाबेस ब्रांचिंग उपलब्ध नहीं है",
	studio_branches_unavailable_body: "ब्रांचिंग के लिए एडमिन कनेक्शन वाला Postgres बैकएंड चाहिए। बैकएंड के एनवायरनमेंट में ADMIN_CONNECTION_STRING सेट करें।",
	studio_backups_not_configured_title: "बैकअप कॉन्फ़िगर नहीं हैं",
	studio_backups_not_configured_body: "BACKUP_DESTINATION को किसी लोकल पाथ या s3:// अथवा gs:// URL पर सेट करें, फिर `rebase db backup` या निर्धारित क्रॉन से बैकअप बनाएँ।",
	studio_cron_empty_title: "कोई क्रॉन जॉब पंजीकृत नहीं",
	studio_cron_empty_body: "backend/crons/ में एक फ़ाइल जोड़ें जो CronJobDefinition को डिफ़ॉल्ट एक्सपोर्ट करे।",
	studio_read_the_docs: "दस्तावेज़ पढ़ें"
};
//#endregion
//#region src/locales/pt.ts
/**
* Portuguese (pt) translations for @rebasepro/app.
*
* @group Core
*/
var pt = {
	save: "Guardar",
	create: "Criar",
	create_copy: "Criar cópia",
	save_and_close: "Guardar e fechar",
	create_copy_and_close: "Criar cópia e fechar",
	create_and_close: "Criar e fechar",
	discard: "Descartar",
	clear: "Limpar",
	cancel: "Cancelar",
	edit: "Editar",
	copy: "Copiar",
	delete: "Eliminar",
	delete_not_allowed: "Selecionou pelo menos uma entidade que não pode eliminar",
	edit_entity: "Editar",
	back_to_detail: "Voltar aos detalhes",
	delete_confirmation_title: "Eliminar?",
	delete_confirmation_body: "Esta ação eliminará a entidade. Tem a certeza?",
	delete_multiple_confirmation_body: "Esta ação eliminará as entidades selecionadas. Tem a certeza?",
	unsaved_changes_title: "Alterações não guardadas",
	unsaved_changes_body: "Tem alterações não guardadas. Deseja descartá-las?",
	discard_changes: "Descartar alterações",
	keep_editing: "Continuar a editar",
	clear_form: "Limpar formulário?",
	discard_changes_confirmation: "Todas as alterações não guardadas serão perdidas. Esta ação não pode ser desfeita.",
	clear_form_confirmation: "Todos os valores introduzidos serão apagados. Esta ação não pode ser desfeita.",
	search: "Pesquisar",
	find_by_id: "Encontrar por ID",
	find_entity_by_id: "Encontrar entidade por ID",
	filter: "Filtrar",
	filters: "Filtros",
	records_in_view: "Registos nesta vista",
	clear_filter: "Limpar filtro",
	clear_filter_sort: "Limpar filtro/ordenação",
	clear_sort: "Limpar ordenação",
	sort: "Ordenar",
	sort_by: "Ordenar por",
	sort_then_by: "Depois por",
	sort_ascending: "Ordenar ascendente",
	sort_descending: "Ordenar descendente",
	sort_remove: "Remover ordenação",
	sort_move_up: "Tornar esta chave mais importante",
	sort_move_down: "Tornar esta chave menos importante",
	sort_remove_key: "Remover esta chave de ordenação",
	sort_key_position: "Chave de ordenação {{position}}",
	sort_shift_click_hint: "Shift+clique para adicionar uma coluna sob a ordenação atual",
	save_entity_before_subcollections: "Precisas de guardar a entidade antes de adicionar coleções adicionais",
	clear_all: "Limpar tudo",
	no_items: "Sem itens",
	no_entries_found: "Nenhum registo encontrado",
	all_entries_loaded: "Todos os {{count}} registos carregados",
	create_your_first_entry: "Crie o seu primeiro registo",
	no_results_filter_sort: "Sem resultados com o filtro/ordenação aplicado",
	no_results_search: "Nenhum resultado encontrado para \"{{search}}\"",
	add: "Adicionar",
	remove: "Remover",
	multiple_entities: "Múltiplas entidades",
	unsaved_changes: "Tem alterações não guardadas em {{collectionName}}.",
	so_empty: "Tão vazio...",
	no_results: "Sem resultados com o filtro/ordenação aplicado",
	refresh_data: "Atualizar dados",
	dark_mode: "Escuro",
	light_mode: "Claro",
	system_mode: "Sistema",
	join_newsletter: "Assine a newsletter, sem spam",
	ok: "Ok",
	save_collection_config: "Guardar estrutura da coleção",
	search_for_more_icons: "Pesquisar mais ícones…",
	ai_modified: "Modificado por IA",
	size_label: "Tamanho",
	group_by: "Agrupar por",
	initialize_kanban_order: "Inicializar Ordem Kanban",
	copy_id: "Copiar ID",
	add_specific: "Adicionar {{name}}",
	add_named: "Adicionar \"{{name}}\"",
	select_specific: "Selecionar {{name}}",
	select_from: "Selecionar de {{name}}",
	done: "Concluído",
	log_out: "Terminar sessão",
	column_cannot_be_edited: "Esta coluna não pode ser editada diretamente",
	close: "Fechar",
	hide_list: "Ocultar lista",
	show_list: "Mostrar lista",
	unsaved_local_changes: "Alterações locais não guardadas",
	unsaved_local_changes_description: "Este documento foi editado localmente e tem alterações não guardadas. Estas alterações locais serão perdidas se não as aplicar.",
	preview_changes: "Pré-visualizar alterações",
	apply_changes: "Aplicar alterações",
	discard_local_changes: "Descartar alterações locais",
	preview_local_changes: "Pré-visualizar alterações locais",
	preview_local_changes_description: "Estas são as alterações locais que serão aplicadas ao formulário.",
	type: "Tipo",
	string: "string",
	number: "número",
	boolean: "booleano",
	date: "data",
	map: "mapa",
	array: "lista",
	arrays_of_arrays_not_supported: "Listas de listas não são suportadas.",
	data_type_not_supported: "Tipo de dados {{dataType}} ainda não suportado",
	passkey_error_unsupported: "O seu dispositivo ou browser não suporta passkeys.",
	saved_correctly: "Guardado com sucesso",
	error_before_saving: "Erro ao preparar os dados",
	error_after_saving: "Erro no processamento pós-gravação",
	error_saving_entity: "Erro ao guardar a entidade",
	entity_does_not_exist: "Esta entidade não existe na base de dados",
	form_modified: "Este formulário foi modificado",
	form_in_sync: "O formulário atual está sincronizado com a base de dados",
	fix_errors_before_saving: "Corrija os erros destacados antes de guardar",
	admin: "Administrador",
	home: "Início",
	this_form_has_errors: "Este formulário tem erros",
	error_loading_navigation: "Erro ao carregar navegação",
	error_loading_auth: "Erro ao carregar autenticação",
	this_form_has_been_modified: "Este formulário foi modificado",
	current_form_in_sync: "O formulário atual está sincronizado com a base de dados",
	open_in_console: "Abrir na consola",
	collection_does_not_exist: "A coleção especificada não existe. Verifique a consola",
	unexpected_value: "Valor inesperado",
	unexpected_value_click_to_edit: "Valor inesperado. Clique para editar",
	unexpected_reference_value: "Valor de referência inesperado. Clique para editar",
	copy_url_to_clipboard: "Copiar URL para a área de transferência",
	open_image_in_new_tab: "Abrir imagem numa nova aba",
	reference_not_set: "Referência não definida",
	reference_does_not_exist: "A referência não existe",
	entity_not_found: "Entidade não encontrada",
	entity_not_found_body: "Este registo não existe, ou pode estar-lhe oculto pela segurança ao nível da linha.",
	back_to_collection: "Voltar a {{name}}",
	file_not_found: "Ficheiro não encontrado",
	unsaved_changes_in_entity: "Tem alterações não guardadas nesta entidade.",
	delete_this_role: "Eliminar esta função",
	no_roles_yet: "Ainda não tem nenhuma função.",
	create_default_roles: "Criar funções predefinidas",
	delete_role_confirmation: "Tem a certeza de que quer eliminar esta função?",
	delete_this_user: "Eliminar este utilizador",
	no_users_yet: "Ainda não existem utilizadores",
	add_logged_user_as_admin: "Adicionar o utilizador com sessão iniciada como administrador",
	add_current_user_as_admin: "Adicionar utilizador atual como administrador",
	create_default_roles_and_add_admin: "Criar funções predefinidas e adicionar utilizador atual como administrador",
	delete_user_confirmation: "Tem a certeza de que quer eliminar este utilizador?",
	create_your_users_and_roles: "Crie os seus utilizadores e funções",
	no_users_or_roles_defined: "Não tem utilizadores ou funções definidas. Pode criar funções predefinidas e adicionar o utilizador atual como administrador.",
	save_before_changing_schema: "Precisa de guardar o documento antes de alterar o esquema",
	edit_schema_for_this_form: "Editar esquema para este formulário",
	no_permissions_to_edit_collection: "Não tem permissões para editar esta coleção",
	browser_does_not_support_audio: "O seu browser não suporta o elemento de áudio.",
	user_not_found: "Utilizador não encontrado: {{value}}",
	delete_selected: "Eliminar",
	cannot_delete_selected: "Selecionou pelo menos uma entidade que não pode eliminar",
	add_entry: "Adicionar",
	add_on_top: "Adicionar no topo",
	add_below: "Adicionar abaixo",
	add_to_field: "Adicionar a {{fieldName}}",
	value: "Valor",
	key: "Chave",
	drag_drop_multiple: "Arraste e solte ficheiros aqui, ou clique para selecionar. Arraste para reordenar.",
	drag_drop_single: "Arraste e solte um ficheiro aqui, ou clique para selecionar um",
	open_menu: "Abrir menu",
	close_drawer: "Fechar painel lateral",
	navigation_drawer: "Painel lateral de navegação",
	collapse: "Recolher",
	expand: "Expandir",
	change_language: "Alterar idioma",
	toggle_theme: "Alternar tema",
	user_menu: "Menu do utilizador",
	error: "Erro",
	error_loading_data: "Não foi possível carregar os dados",
	error_check_server_logs: "Consulte os registos do servidor para mais detalhes.",
	error_technical_details: "Detalhes técnicos",
	error_uploading_file: "Erro ao carregar ficheiro",
	error_deleting: "Erro ao eliminar",
	error_before_delete: "Erro antes de eliminar",
	error_updating_asset: "Erro ao atualizar recurso",
	error_deleting_asset: "Erro ao eliminar recurso",
	error_firestore_index: "É necessário um índice Firestore para esta consulta.",
	create_index: "Criar índice",
	value_is_not_reference: "O valor não é uma referência.",
	click_to_edit: "Clique para editar",
	data_is_not_array_of_references: "Os dados não são uma lista de referências",
	loading: "A carregar",
	local_changes_applied: "Alterações locais aplicadas ao formulário",
	local_changes_discarded: "Alterações locais descartadas",
	are_you_sure_leave: "Tem a certeza de que quer sair desta página?",
	see_console_details: "Consulte a consola para mais detalhes.",
	drop_here_create_group: "Solte aqui para criar um novo grupo",
	filter_for_null_values: "Filtrar valores nulos",
	value_updated_elsewhere: "Este valor foi atualizado noutro local",
	add_property: "Adicionar propriedade",
	edit_name: "Editar {{name}}",
	this_entity_not_exist: "Esta entidade não existe na base de dados",
	internal_error: "ERRO interno",
	rename_group: "Renomear Grupo",
	group_name_label: "Nome do Grupo",
	group_name_empty_error: "O nome do grupo não pode estar vazio.",
	group_name_exists_error: "Este nome de grupo já existe.",
	search_collections: "Pesquisar coleções",
	views_group: "Visualizações",
	youd_need_to_save_before_additional_collections: "Precisa de guardar a sua entidade antes de adicionar coleções adicionais",
	page_not_found: "Página não encontrada",
	page_not_found_body: "Esta página não existe ou pode não ter acesso a ela",
	back_to_home: "Voltar ao início",
	default_collection_view: "Vista de coleção predefinida",
	table_view: "Tabela",
	cards_view: "Cartões",
	kanban_view: "Kanban",
	choose_how_entities_displayed_default: "Escolha como as entidades devem ser exibidas por padrão",
	document_view: "Vista de documento",
	side_panel: "Painel lateral",
	full_screen: "Ecrã inteiro",
	should_documents_opened_full_screen: "Os documentos devem ser abertos em ecrã inteiro ou num painel lateral inline",
	select_custom_view: "Selecionar vista personalizada",
	no_custom_views_defined: "Nenhuma vista personalizada definida. Defina as suas vistas personalizadas nas definições de personalização, antes de usar este diálogo.",
	select_custom_action: "Selecionar ação personalizada",
	no_custom_actions_defined: "Nenhuma ação personalizada definida. Defina as suas ações personalizadas nas definições de personalização, antes de usar este diálogo.",
	no_collections_found: "Nenhuma coleção encontrada",
	start_building_collections: "Comece a criar coleções no Rebase facilmente. Mapeie-as aos dados existentes na sua base de dados, importe de ficheiros ou use os nossos modelos.",
	create_first_collection: "Crie a sua primeira coleção",
	define_collections_programmatically: "Também pode definir coleções programaticamente.",
	edit_collection: "Editar coleção",
	no_permissions_edit_collection: "Não tem permissões para editar esta coleção",
	no_permissions_create_collection: "Não tem permissões para criar coleções",
	create_collection: "Criar coleção",
	update_collection: "Atualizar coleção",
	new_collection: "Nova coleção",
	add_new_collection: "Adicionar nova coleção",
	collection_with_name: "Coleção {{name}}",
	change_icon: "Alterar ícone",
	is_subcollection_of: "Esta é uma subcoleção de",
	name: "Nome",
	collection_name_description: "Nome desta coleção, normalmente um nome no plural (ex. Produtos)",
	path: "Caminho",
	relative_path_to_parent: "Caminho relativo ao pai (não é necessário incluir o caminho do pai)",
	path_in_database: "Caminho em que esta coleção está armazenada na base de dados",
	singular_name: "Nome singular",
	singular_name_description: "Opcionalmente defina um nome singular para as suas entidades",
	description: "Descrição",
	description_of_collection: "Descrição da coleção, pode usar markdown",
	collection_id: "ID da coleção",
	collection_id_description: "Este ID identifica esta coleção. Normalmente igual ao caminho.",
	collection_group: "Grupo de coleções",
	collection_group_description: "Um grupo de coleções consiste em todas as coleções com o mesmo caminho. Isto permite consultar várias coleções ao mesmo tempo.",
	advanced_settings: "Definições avançadas",
	doc_history_global: "Revisões do histórico do documento ativadas se ativadas globalmente",
	doc_history_enabled: "Revisões do histórico do documento ATIVADAS",
	doc_history_not_enabled: "Revisões do histórico do documento NÃO ativadas",
	doc_history_description: "Quando ativado, cada documento nesta coleção terá um histórico de alterações. Útil para fins de auditoria. Os dados são armazenados numa subcoleção do documento na sua base de dados, chamada __history.",
	document_id_generation: "Geração de IDs de documentos",
	code_defined: "Definido por código",
	users_must_define_id: "Os utilizadores devem definir um ID",
	users_can_define_id: "Os utilizadores podem definir um ID, mas não é obrigatório",
	doc_id_auto_generated: "O ID do documento é gerado automaticamente",
	config_doc_id_generation: "Configure como os IDs de documentos são gerados ao criar novas entidades.",
	enable_text_search: "Ativar pesquisa de texto para esta coleção",
	text_search_description: "Permitir pesquisa de texto para esta coleção. Se não especificou um delegado de pesquisa de texto, será usada a pesquisa de texto local integrada. Não recomendado para grandes coleções, pois pode causar problemas de desempenho e custo.",
	database_id: "ID da base de dados",
	default_text: "(predefinição)",
	custom_actions: "Ações personalizadas",
	more_info: "Mais informações",
	define_custom_actions_cli: "Defina as suas próprias ações personalizadas carregando-as com o CLI.",
	action_defined_in_code: "Esta ação está definida em código com a chave",
	add_custom_entity_action: "Adicionar ação de entidade personalizada",
	remove_this_action: "Remover esta ação?",
	remove_action_warning: "Isto não eliminará nenhum dado, apenas a ação no painel de administração",
	subcollections_of: "Subcoleções de",
	add_subcollection: "Adicionar subcoleção",
	custom_views: "Vistas personalizadas",
	define_custom_views_cli: "Defina as suas próprias vistas personalizadas carregando-as com o CLI.",
	view_defined_in_code: "Esta vista está definida em código com a chave",
	add_custom_entity_view: "Adicionar vista de entidade personalizada",
	delete_this_subcollection: "Eliminar esta subcoleção?",
	remove_collection_warning: "Isto não eliminará nenhum dado, apenas a coleção no painel de administração",
	remove_this_view: "Remover esta vista?",
	remove_view_warning: "Isto não eliminará nenhum dado, apenas a vista no painel de administração",
	no_collection_selected: "Nenhuma coleção selecionada",
	code_for_collection: "Código para",
	use_config_define_json: "Use esta configuração para definir a coleção em formato JSON.",
	customise_collection_code: "Se quiser personalizar a coleção em código, pode adicionar este código de coleção à configuração da sua aplicação de administração.",
	copied: "Copiado",
	property_cant_be_edited: "Esta propriedade não pode ser editada",
	property_not_editable_description: "Pode não ter permissão para editá-la ou está definida em código e não pode ser modificada.",
	delete_this_property: "Eliminar esta propriedade?",
	delete_property_warning: "Isto não eliminará nenhum dado, apenas modificará a coleção.",
	error_must_specify_id: "Deve especificar um id para o campo",
	error_id_format: "O id só pode conter letras, números e underscores (_), e não pode começar com um número",
	error_id_already_exists: "Já existe outro campo com este ID",
	error_must_specify_title: "Deve especificar um título para o campo",
	custom_or_other: "Personalizado/Outro",
	select_property_widget: "Selecionar um widget de propriedade",
	error_changing_data_type: "Este widget usa um tipo de dados diferente do widget inicialmente selecionado. Isto pode causar erros com dados existentes.",
	required: "Obrigatório",
	enum_form_dialog: "Diálogo de formulário enum",
	imported_data_preview: "Pré-visualização de dados importados",
	entities_with_same_id_overwritten: "Entidades com o mesmo id serão substituídas",
	collection_editor: "Editor de coleção",
	properties_in_this_group: "Propriedades neste grupo",
	data_property_mapping: "Mapeamento de propriedade de dados",
	property_edit_view: "Vista de edição de propriedade",
	all_of_these: "Todos estes",
	any_of_these: "Qualquer um destes",
	only_admins_edit_roles: "Apenas administradores podem editar funções",
	error_user_not_found: "Utilizador não encontrado",
	all_roles: "Todos os papéis",
	role: "Função",
	name_of_this_role: "Nome desta função",
	id_of_this_role: "ID desta função",
	create_entities: "Criar entidades",
	read_entities: "Ler entidades",
	update_entities: "Atualizar entidades",
	delete_entities: "Eliminar entidades",
	all_collections: "Todas as coleções",
	create_entities_in_collections: "Criar entidades em coleções",
	access_all_data_in_every_collection: "Aceder a todos os dados em todas as coleções",
	update_data_in_any_collection: "Atualizar dados em qualquer coleção",
	delete_data_in_any_collection: "Eliminar dados em qualquer coleção",
	allow_all_permissions_in_this_collections: "Permitir todas as permissões nestas coleções",
	all: "Todos",
	customise_permissions_description: "Pode personalizar as permissões que os utilizadores relacionados com esta função podem realizar nas entidades de cada coleção",
	create_collections: "Criar coleções",
	yes: "Sim",
	no: "Não",
	can_user_create_collections: "O utilizador pode criar coleções",
	edit_collections: "Editar coleções",
	only_own_collections: "Apenas as suas próprias",
	own: "Próprias",
	can_user_edit_collections: "O utilizador pode editar coleções",
	delete_collections: "Eliminar coleções",
	can_user_delete_collections: "O utilizador pode eliminar coleções",
	error_saving_role: "Ocorreu um erro ao guardar esta função",
	create_role: "Criar função",
	update: "Atualizar",
	only_admins_change_roles: "Apenas administradores podem alterar funções",
	must_be_at_least_one_admin: "Deve existir pelo menos um administrador",
	logged_user_not_found: "Utilizador com sessão iniciada não encontrado",
	user: "Utilizador",
	user_id: "ID do utilizador",
	name_of_this_user: "Nome deste utilizador",
	email_of_this_user: "Email deste utilizador",
	roles: "Funções",
	create_user: "Criar utilizador",
	users: "Utilizadores",
	add_user: "Adicionar utilizador",
	add_role: "Adicionar função",
	is_admin: "É Administrador",
	default_permissions: "Permissões predefinidas",
	created_on: "Criado em",
	email: "Email",
	id: "ID",
	read: "Ler",
	column_in_file: "Coluna no ficheiro",
	map_to_property: "Mapear para propriedade",
	default_values: "Valores predefinidos",
	default_values_description: "Pode selecionar um valor predefinido para colunas não mapeadas e valores vazios:",
	property: "Propriedade",
	default_value: "Valor predefinido",
	autogenerate_id: "Gerar ID automaticamente",
	id_column_description: "Coluna que será usada como ID para cada documento",
	do_not_set_value: "Não definir valor",
	set_value_to_true: "Definir valor como verdadeiro",
	set_value_to_false: "Definir valor como falso",
	drag_and_drop_file: "Arraste e solte um ficheiro aqui ou clique para carregar",
	error_saving_data: "Erro ao guardar dados",
	retry: "Tentar novamente",
	saving_data: "A guardar dados",
	entities_saved: "entidades guardadas",
	do_not_close_tab: "Não feche este separador ou a importação será interrompida",
	import: "Importar",
	import_data: "Importar dados",
	upload_file_description: "Carregue um ficheiro CSV, Excel ou JSON e mapeie-o para o esquema existente",
	back: "Voltar",
	next: "Seguinte",
	save_data: "Guardar dados",
	use_column_as_id: "Usar esta coluna como ID",
	do_not_import_property: "Não importar esta propriedade",
	entities_will_be_overwritten: "Entidades com o mesmo id serão substituídas",
	data_imported_successfully: "Dados importados com sucesso",
	export: "Exportar",
	export_data: "Exportar dados",
	download_table_csv: "Descarregar o conteúdo desta tabela como CSV",
	csv: "CSV",
	json: "JSON",
	dates_as_timestamps: "Datas como timestamps",
	dates_as_strings: "Datas como strings",
	flatten_arrays: "Achatar listas",
	download: "Descarregar",
	large_number_of_documents: "Esta coleção tem um grande número de documentos ({{count}}).",
	too_many_documents_to_export: "Esta coleção tem mais documentos ({{count}}) do que o navegador consegue exportar num único ficheiro (limite {{limit}}). Filtre-a antes de exportar.",
	include_undefined_values: "Incluir valores indefinidos",
	submit: "Submeter",
	no_filterable_properties: "Não há propriedades filtráveis disponíveis",
	apply_filters: "Aplicar filtros",
	filter_presets: "Predefinições",
	filter_preset_apply: "Aplicar predefinição",
	filter_preset_active: "Ativo: {{label}}",
	list: "Lista",
	table_view_mode: "Tabela",
	cards: "Cartões",
	board: "Quadro",
	initialize_kanban_order_desc: "Isto atribuirá valores de ordem sequenciais a todos os itens que não tenham um. Os itens manterão a sua ordem atual dentro de cada coluna.",
	kanban_view_not_available: "A vista Kanban não está disponível",
	kanban_view_requires_enum: "A vista Kanban requer uma propriedade de string com valores enum para agrupar entidades em colunas. Adicione uma propriedade enum ao esquema da sua coleção para usar esta vista.",
	no_enum_values_configured: "Nenhum valor enum configurado para a propriedade \"{{property}}\"",
	items_need_backfill: "Alguns itens não têm valores de ordem. Inicialize para ativar a reordenação por arrastar e soltar.",
	kanban_order_not_configured: "A ordenação por arrastar e soltar não está configurada. Defina uma propriedade de ordem nas configurações da coleção para manter as posições dos cartões.",
	initialize: "Inicializar",
	confirm_multiple_delete: "Confirmar eliminação múltipla?",
	delete_entity_confirm_title: "Deseja eliminar este/a {{entityName}}?",
	add_existing: "Adicionar existente",
	unlink_entity_confirm_title: "Remover este/a {{entityName}} deste registo?",
	unlink_entity_confirm_body: "Continua em {{collectionName}} e disponível para outros registos.",
	confirm_multiple_unlink: "remover estes deste registo?",
	no_filter: "Sem filtro",
	is_true: "é verdadeiro",
	is_false: "é falso",
	error_uploading_file_size: "Erro ao carregar o ficheiro: excede {{maxSize}} bytes",
	error_uploading_file_type: "Erro ao carregar o ficheiro: tipo de ficheiro não suportado",
	drag_drop_files: "Arraste ficheiros para aqui, ou clique para os editar",
	drag_drop_file: "Arraste um ficheiro para aqui, ou clique para o editar",
	multiple_deleted: "Exclusão múltipla",
	some_entities_deleted: "Algumas entidades foram excluídas, mas não todas",
	error_deleting_entities: "Erro ao excluir entidades",
	deleted: "excluído(a)",
	selection_options: "Opções de seleção",
	selection_menu_all_loaded: "Tudo nesta página",
	selection_menu_all_matching: "Todos os {{total}} {{collection}}",
	selection_menu_none: "Nenhum",
	selection_select_all_loaded: "Selecionar as linhas carregadas",
	selection_deselect_all: "Desmarcar tudo",
	selection_reading_rows: "A ler linhas… {{loaded}} de {{total}}",
	selection_reading_rows_unknown: "A ler linhas… {{loaded}} até agora",
	selection_deleting_progress: "Eliminados {{completed}} de {{total}}",
	confirm_delete_selection: "Eliminar {{total}} {{collection}}?",
	confirm_delete_selection_unknown: "Eliminar todas as linhas que correspondem ao filtro atual?",
	confirm_delete_selection_body: "Esta ação não pode ser anulada. Cada linha é eliminada individualmente, pelo que pode demorar algum tempo.",
	export_selection_count: "Descarregar as {{total}} linhas selecionadas em {{format}}",
	export_selection_all_matching: "Descarregar todos os {{collection}} que correspondem ao filtro atual",
	select_reference: "Selecionar referência",
	select_references: "Selecionar referências",
	account_settings: "Definições da conta",
	profile: "Profile",
	security: "Segurança",
	sessions: "Sessions",
	change_password: "Alterar palavra-passe",
	current_password: "Palavra-passe atual",
	new_password: "Nova palavra-passe",
	confirm_password: "Confirmar a nova palavra-passe",
	password_changed: "Palavra-passe alterada com sucesso. A sua sessão será terminada.",
	passwords_dont_match: "As palavras-passe não coincidem",
	password_too_short: "A palavra-passe deve ter pelo menos 8 caracteres",
	password_change_not_available: "A alteração da palavra-passe não está disponível para contas que usam fornecedores de início de sessão externos.",
	changing_password: "A alterar...",
	display_name: "Nome a apresentar",
	photo_url: "URL da fotografia",
	save_profile: "Guardar perfil",
	saving: "Saving...",
	no_active_sessions: "Não foram encontradas sessões ativas.",
	revoking: "Revoking...",
	revoke_all_sessions: "Revogar todas as sessões",
	unknown_device: "Dispositivo desconhecido",
	current: "Current",
	role_id: "ID da função",
	role_name: "Nome da função",
	add_reference: "Adicionar referência",
	add_reference_to: "Adicionar referência a {{name}}",
	/** RolesView — table & permissions */
	role_deleted_successfully: "Função eliminada com sucesso",
	error_deleting_role: "Erro ao eliminar a função",
	collection_permissions: "Permissões da coleção",
	collection: "Collection",
	no_collections_configured: "Nenhuma coleção configurada",
	no_security_rules_defined: "Não há regras de segurança definidas — todas as operações estão sem restrições",
	no_rules: "sem regras",
	/** UsersView — snackbar */
	bootstrap_admin_success: "Já é administrador! A atualizar...",
	failed_to_bootstrap_admin: "Não foi possível criar o administrador inicial",
	user_deleted_successfully: "Utilizador eliminado com sucesso",
	error_deleting_user: "Erro ao eliminar o utilizador",
	user_created_successfully: "Utilizador criado com sucesso",
	invitation_sent: "Foi enviado um email de convite para {{email}}. A ligação permite definir a palavra-passe.",
	invitation_sent_title: "Convite enviado",
	temporary_password: "Palavra-passe temporária",
	temporary_password_description: "O email não está configurado. Partilhe esta palavra-passe temporária com o utilizador de forma segura. Não será mostrada novamente.",
	temporary_password_email_failed_description: "Não foi possível entregar o email. Partilhe esta palavra-passe temporária com o utilizador de forma segura. Não será mostrada novamente.",
	copy_password: "Copiar palavra-passe",
	password_copied: "Palavra-passe copiada para a área de transferência",
	/** UsersView — pagination & search */
	search_users: "Procurar utilizadores",
	created: "Criado",
	actions: "Ações",
	no_users_found: "Nenhum utilizador encontrado",
	reset_password: "Repor palavra-passe",
	reset_password_success: "Palavra-passe reposta com sucesso",
	reset_password_confirmation: "Tem a certeza de que pretende repor a palavra-passe deste utilizador?",
	reset_password_send_email: "Enviar um email de reposição da palavra-passe",
	reset_password_set_manually: "Definir uma palavra-passe manualmente",
	reset_password_set_manually_description: "A palavra-passe é atualizada de imediato e não é enviado qualquer email. Partilhe-a com o utilizador de forma segura.",
	/** Permission-denied empty states */
	no_permission_to_view_users: "Não tem permissão para ver os utilizadores",
	no_permission_to_view_roles: "Não tem permissão para ver as funções",
	no_permission_description: "Contacte um administrador se precisar de acesso a esta secção.",
	error_resetting_password: "Erro ao repor a palavra-passe",
	/** Editor table-bubble */
	add_row_before: "Inserir linha antes",
	add_row_after: "Inserir linha depois",
	delete_row: "Eliminar linha",
	add_column_before: "Inserir coluna antes",
	add_column_after: "Inserir coluna depois",
	delete_column: "Eliminar coluna",
	delete_table: "Eliminar tabela",
	/** Editor image-bubble */
	alt_text: "Texto alternativo",
	title: "Title",
	/** MapFieldBinding */
	a_property_of_this_map_has_error: "Uma propriedade deste mapa contém um erro",
	/** AI Collection Generator Popover */
	generate_collection_with_ai: "Gerar Coleção com IA",
	modify_collection_with_ai: "Modificar Coleção com IA",
	describe_collection_to_create: "Descreva a coleção que quer criar.",
	describe_changes_to_make: "Descreva as alterações que quer fazer a esta coleção.",
	ai_placeholder_create: "ex.: Criar uma coleção de produtos com nome, preço, descrição e categoria...",
	ai_placeholder_modify: "ex.: Adicionar um campo de imagem miniatura com armazenamento, tornar o preço obrigatório...",
	ai_assist: "Assistente IA",
	generating: "A gerar...",
	/** Recently extracted strings for collection editor */
	this_is_subcollection_of: "Esta é uma subcoleção de",
	use_existing_paths_database: "Use um dos caminhos existentes na sua base de dados:",
	describe_collection_ai: "Descreva a sua coleção à IA:",
	generate_with_ai: "Gerar com IA",
	create_from_json_config: "Criar a partir de configuração JSON:",
	paste_json_config: "Colar Configuração JSON",
	create_collection_from_file_formats: "Criar uma coleção a partir de um ficheiro (csv, json, xls, xslx...)",
	select_template: "Selecionar um modelo:",
	products: "Produtos",
	collection_products_subtitle: "Uma coleção de produtos com imagens, preços e stock",
	collection_users_subtitle: "Uma coleção de utilizadores com emails, nomes e funções",
	blog_posts: "Publicações de blog",
	collection_blog_posts_subtitle: "Uma coleção de publicações de blog com imagens, autores e conteúdo complexo",
	pages: "Páginas",
	collection_pages_subtitle: "Uma coleção de páginas com imagens, autores e conteúdo complexo",
	continue_from_scratch: "Continuar do zero",
	/** Admin views config */
	admin_users: "Utilizadores do painel de administração",
	roles_menu: "Funções",
	project_settings: "Definições do projeto",
	by_signing_in_you_agree_to_our: "Ao iniciar sessão, concorda com os nossos",
	terms_and_conditions: "Termos e Condições",
	and_our: "e a nossa",
	privacy_policy: "Política de privacidade",
	email_password: "Email/palavra-passe",
	sign_in_with_google: "Iniciar sessão com Google",
	auth_user_not_found: "Utilizador não encontrado",
	auth_wrong_password: "Palavra-passe incorreta. Por favor tente novamente.",
	auth_user_disabled: "Utilizador desativado. Por favor contacte o suporte.",
	auth_account_exists_with_different_credential: "Já existe uma conta com este email. Inicie sessão como fez da última vez.",
	auth_account_exists_sign_in_with_password: "Já existe uma conta com este email. Inicie sessão com o seu email e palavra-passe.",
	auth_email_already_in_use: "O email já está em uso",
	auth_google_permissions_required: "Precisa de conceder permissões adicionais para gerir os seus projetos Google Cloud",
	auth_invalid_email_password: "Por favor introduza o email e a palavra-passe",
	auth_enter_email_first: "Por favor introduza primeiro o seu email",
	auth_password_reset_sent: "Email de redefinição de palavra-passe enviado",
	auth_sign_in_account: "Iniciar sessão na sua conta",
	auth_create_new_account: "Criar uma nova conta",
	auth_password: "Palavra-passe",
	auth_reset_password: "Redefinir palavra-passe",
	auth_new_user: "Novo utilizador?",
	auth_have_account: "Já tem uma conta?",
	auth_sign_in: "Iniciar Sessão",
	auth_sign_up: "Registar",
	auto_setup_collections_button: "Configurar coleções automaticamente",
	auto_setup_collections_title: "Configurar coleções automaticamente?",
	auto_setup_collections_desc: "Isto criará automaticamente configurações de coleção para coleções que <b>NÃO</b> estão já mapeadas",
	this_can_take_a_minute: "Isto pode demorar um ou dois minutos",
	no_collections_found_to_setup: "Nenhuma coleção encontrada para configurar.",
	collections_have_been_setup: "As coleções foram configuradas automaticamente.",
	error_setting_up_collections: "Erro ao configurar coleções automaticamente",
	admin_panel_ready_bring_data: "O seu painel de administração está pronto. Vamos importar os seus dados.",
	admin_panel_ready_get_started: "O seu painel de administração está pronto. Veja como começar.",
	auto_detect_collections: "Detetar coleções automaticamente",
	auto_detect_collections_desc: "Deixe a IA analisar a sua base de dados e gerar automaticamente esquemas de coleção.",
	create_a_collection: "Criar uma coleção",
	create_collection_desc: "Defina manualmente a sua primeira coleção do zero usando o editor visual.",
	read_the_docs: "Ler a documentação",
	read_the_docs_desc: "Saiba como personalizar campos, vistas, ações e muito mais.",
	explore_docs: "Explorar documentação",
	want_to_customize_with_code: "Quer personalizar com código? Execute",
	to_scaffold_a_local_project: "para criar um projeto local.",
	validation: "Validação",
	unique: "Único",
	required_message: "Mensagem de campo obrigatório",
	required_tooltip: "Você não poderá salvar esta entidade se este valor não estiver definido",
	unique_tooltip: "Não pode haver múltiplas entidades com o mesmo valor",
	lowercase: "Minúsculas",
	uppercase: "Maiúsculas",
	trim: "Remover espaços",
	exact_length: "Comprimento exato",
	min_length: "Comprimento mín.",
	max_length: "Comprimento máx.",
	matches_regex: "Corresponde a regex",
	not_valid_regexp: "Expressão regular inválida",
	regex_helper: "ex. /^\\d+$/ apenas para dígitos",
	min_value: "Valor mín.",
	max_value: "Valor máx.",
	less_than: "Menor que",
	more_than: "Maior que",
	positive_value: "Valor positivo",
	negative_value: "Valor negativo",
	integer_value: "Valor inteiro",
	invalid_regular_expression: "Expressão regular inválida",
	must_specify_target_collection: "Você deve especificar uma coleção de destino para o campo",
	need_specify_repeat_field: "Você precisa especificar um campo de repetição",
	need_specify_block_properties: "Você precisa especificar as propriedades deste bloco",
	incomplete_condition: "Condição incompleta - por favor selecione um campo",
	field_name: "Nome do campo",
	kanban_column_property: "Propriedade de coluna Kanban",
	select_a_property: "Selecionar uma propriedade",
	kanban_property_not_found: "A propriedade \"{{property}}\" não existe ou não é uma propriedade string com enum. Selecione uma propriedade válida ou limpe a seleção.",
	no_enum_string_properties: "Nenhuma propriedade string com enum encontrada. Adicione uma propriedade string com enumValues para usar a visualização Kanban.",
	kanban_column_description: "Selecione uma propriedade string com valores enum para agrupar entidades em colunas",
	create_property: "+ Criar propriedade \"{{property}}\"",
	order_property: "Propriedade de ordem",
	order_property_not_found: "A propriedade \"{{property}}\" não existe ou não é uma propriedade numérica. Selecione uma propriedade válida ou limpe a seleção.",
	no_number_properties: "Nenhuma propriedade numérica encontrada. Adicione uma propriedade numérica para habilitar a ordenação.",
	order_property_description: "Selecione uma propriedade numérica para manter a ordem dos itens",
	display_settings: "Configurações de exibição",
	default_row_size: "Tamanho de linha padrão",
	side_dialog_width: "Largura do diálogo lateral",
	side_dialog_width_description: "Defina opcionalmente a largura (em pixels) do diálogo lateral de entidades. O padrão é 768px",
	inline_editing_enabled: "Os dados podem ser editados diretamente na visualização de tabela",
	inline_editing_disabled: "Os dados só podem ser editados na visualização de formulário",
	inline_editing_description: "Permitir edição de dados diretamente na visualização de tabela, sem abrir a visualização de formulário.",
	include_json_view: "Incluir visualização JSON",
	no_json_view: "Não incluir visualização JSON",
	json_view_description: "Incluir a representação JSON do documento.",
	not_found_suffix: "não encontrado",
	editor_text: "Texto",
	editor_text_description: "Comece simplesmente a escrever em texto simples.",
	editor_heading_1: "Título 1",
	editor_heading_1_description: "Título de secção grande.",
	editor_heading_2: "Título 2",
	editor_heading_2_description: "Título de secção médio.",
	editor_heading_3: "Título 3",
	editor_heading_3_description: "Título de secção pequeno.",
	editor_todo_list: "Lista de Tarefas",
	editor_todo_list_description: "Acompanhe tarefas com uma lista de afazeres.",
	editor_bullet_list: "Lista com Marcadores",
	editor_bullet_list_description: "Crie uma lista simples com marcadores.",
	editor_numbered_list: "Lista Numerada",
	editor_numbered_list_description: "Crie uma lista com numeração.",
	editor_quote: "Citação",
	editor_quote_description: "Capture uma citação.",
	editor_code: "Código",
	editor_code_description: "Capture um snippet de código.",
	editor_image: "Imagem",
	editor_image_description: "Carregue uma imagem do seu computador.",
	editor_multiple: "Múltiplos",
	editor_link: "Ligação",
	editor_save: "Guardar",
	editor_cancel: "Cancelar",
	editor_remove_link: "Remover ligação",
	editor_paste_or_type_link: "Cole ou escreva a ligação",
	editor_open_in_new_window: "Abrir numa nova janela",
	editor_bold: "Negrito",
	editor_italic: "Itálico",
	editor_underline: "Sublinhado",
	editor_strikethrough: "Riscado",
	editor_autocomplete: "Preenchimento automático",
	editor_autocomplete_description: "Adicione texto com base no contexto.",
	text_search_dialog_title: "Ativar pesquisa de texto",
	text_search_local_not_recommended: "A pesquisa de texto local não é recomendada para coleções grandes.",
	text_search_local_fetch_warning: "Note que ativar a pesquisa de texto local requer descarregar todos os documentos da sua coleção e armazená-los no navegador. Isto pode ser ineficiente para coleções grandes e gerar custos adicionais.",
	text_search_external_suggestion: "Para coleções maiores, recomendamos usar um motor de pesquisa externo como Algolia ou Elastic Search e atribuir um delegado de pesquisa à sua configuração.",
	text_search_local_description: "A pesquisa de texto local é a forma mais simples de ativar a pesquisa de texto na sua coleção. Carrega todos os documentos da coleção no navegador e efetua a pesquisa localmente. É a opção recomendada para coleções pequenas.",
	text_search_own_implementation: "Implementou o seu próprio controlador de pesquisa de texto. Pode ativar a pesquisa de texto para a sua coleção.",
	text_search_enable_for_collection: "Ativar para esta coleção",
	text_search_enable_for_project: "Ativar para o projeto",
	text_search_enabled_snackbar: "Pesquisa de texto local ativada",
	settings_heading: "Definições",
	settings_project_name: "Nome do projeto",
	settings_default_language: "Idioma predefinido",
	settings_default_language_caption: "Selecione o idioma base para este projeto. Os utilizadores podem alterar esta preferência nas suas definições pessoais.",
	settings_enable_local_text_search: "Ativar pesquisa de texto local",
	settings_local_text_search_caption: "Ative a pesquisa de texto local para todas as coleções. Isto permitirá pesquisar campos de texto nas suas coleções usando a barra de pesquisa do Rebase. Note que esta funcionalidade pode resultar num maior número de leituras, pois todos os campos de texto das suas coleções serão indexados.",
	settings_doc_history_all_collections: "Histórico de documentos ativado para todas as coleções",
	settings_doc_history_caption: "Quando ativado, todas as coleções terão o histórico ativado por predefinição. Pode alterar esta definição em cada coleção. O histórico será guardado na subcoleção __history de cada documento.",
	settings_theme: "Tema",
	settings_primary_color: "Cor primária",
	settings_secondary_color: "Cor secundária",
	settings_sample_theme_components: "Exemplos de componentes do tema",
	settings_drag_drop_logo: "Arraste e solte o seu logótipo aqui",
	settings_security_rules: "Regras de segurança",
	settings_security_rules_description: "O Rebase utiliza as regras de segurança do Firebase para restringir o acesso aos dados. Ao criar um novo utilizador através do Rebase, o atributo personalizado rebaseUser é adicionado ao utilizador no projeto cliente. Ao adicionar as seguintes regras de segurança ao seu projeto, garante que os utilizadores do Rebase possam aceder aos dados através do Rebase.",
	settings_security_rules_add_domain: "Lembre-se de adicionar o domínio {{domain}} aos domínios permitidos do seu fornecedor",
	settings_security_rules_caption: "Estas regras restringem o acesso aos dados apenas aos utilizadores do Rebase, mas não aplicam as permissões ao nível da base de dados. As permissões são aplicadas no frontend, o que funciona bem para a maioria dos projetos. Se precisar de aplicar as permissões ao nível da base de dados, pode modificar estas regras de segurança. As funções atribuídas a um utilizador são definidas como atributos personalizados no token de autenticação do Firebase, pelo que pode utilizá-las nas suas regras de segurança.",
	studio_sql_executing_query: "A EXECUTAR A CONSULTA...",
	studio_sql_query_error: "Erro na consulta",
	studio_sql_run_query_placeholder: "Execute uma consulta para ver os resultados",
	studio_sql_visual_execution_plan: "Plano de execução visual",
	studio_sql_success: "SUCCESS",
	studio_sql_no_results: "A consulta foi executada com sucesso mas não devolveu resultados.",
	studio_sql_rows: "Rows:",
	studio_sql_time: "Time:",
	studio_sql_copy_markdown: "Copiar como Markdown",
	studio_sql_export_json: "Exportar JSON",
	studio_sql_export_csv: "Exportar CSV",
	studio_sql_format_sql: "Formatar SQL",
	studio_sql_explain: "Explain",
	studio_sql_limit_1000: "Limit 1000",
	studio_sql_remove_from_favorites: "Remover dos favoritos",
	studio_sql_add_to_favorites: "Adicionar aos favoritos",
	studio_sql_save: "Save",
	studio_sql_run: "Run",
	studio_sql_database: "Database",
	studio_sql_role: "Role",
	studio_sql_admin: "(Admin)",
	studio_sql_select_db: "Escolher base de dados",
	studio_sql_query_results: "Resultados da consulta",
	studio_sql_save_snippet: "Guardar excerto SQL",
	studio_sql_snippet_name: "Nome do excerto",
	studio_sql_snippet_name_placeholder: "p. ex., Obter todos os utilizadores",
	studio_sql_snippet_saved_local: "Será guardado no seu armazenamento local.",
	studio_sql_cancel: "Cancel",
	studio_sql_dangerous_operation: "Operação perigosa detetada",
	studio_sql_dangerous_operation_body: "A consulta que está prestes a executar contém operações potencialmente destrutivas (DELETE, DROP, TRUNCATE ou UPDATE sem WHERE). Tem a certeza de que pretende continuar?",
	studio_sql_snippet_saved: "Excerto «{{name}}» guardado.",
	studio_sql_markdown_copied: "Resultados copiados como Markdown!",
	studio_sql_markdown_copy_failed: "Não foi possível copiar o Markdown para a área de transferência.",
	studio_sql_row_updated: "Linha atualizada com sucesso.",
	studio_sql_cannot_edit_missing_query: "Não é possível editar as linhas: falta a consulta subjacente.",
	studio_sql_cannot_resolve_table: "Não foi possível determinar a tabela ou a chave primária",
	studio_sql_missing_pk: "Faltam colunas de chave primária na linha: {{columns}}. Não é possível atualizar em segurança.",
	studio_sql_update_failed: "Falha ao atualizar: {{message}}",
	studio_sql_execution_not_supported: "A fonte de dados atual não suporta a execução de SQL.",
	studio_sql_error_executing: "Ocorreu um erro ao executar a consulta.",
	studio_sql_error_explaining: "Ocorreu um erro ao explicar a consulta.",
	studio_sql_save_first_to_favorite: "Guarde o excerto antes de o marcar como favorito.",
	studio_sql_collections_label: "Collections:",
	studio_sql_admin_collections_tooltip: "Tables in this query that are mapped as admin collections",
	studio_sql_edit_entity: "Edit {{name}} #{{id}}",
	studio_sql_sql_not_supported: "Esta fonte de dados não suporta a execução de SQL",
	studio_sql_fetch_error: "Não foi possível obter as opções de ligação: {{message}}",
	studio_sql_unexpected_format: "Formato de dados inesperado: {{type}}",
	studio_sql_no_tables: "Não foram encontradas tabelas nesta base de dados.",
	studio_sql_schema_fetch_error: "Não foi possível obter o esquema: {{message}}",
	studio_sql_sidebar_snippets: "Snippets",
	studio_sql_sidebar_history: "History",
	studio_sql_sidebar_schema: "Schema",
	studio_sql_sidebar_no_snippets: "Não há excertos guardados",
	studio_sql_sidebar_save_snippet_hint: "Guarde uma consulta para lhe aceder aqui.",
	studio_sql_sidebar_no_history: "Sem histórico",
	studio_sql_sidebar_history_hint: "As consultas executadas aparecerão aqui.",
	studio_sql_sidebar_delete_snippet: "Eliminar excerto",
	studio_sql_close_tab: "Fechar este separador de consulta",
	studio_sql_new_tab: "Novo separador de consulta",
	studio_schema_tables: "Tables",
	studio_schema_loading: "A carregar o esquema...",
	studio_schema_no_tables: "Nenhuma tabela encontrada",
	studio_schema_retry: "Retry",
	studio_schema_primary_key: "Chave primária",
	studio_schema_select_all: "SELECT * FROM",
	studio_schema_insert_into: "INSERT INTO",
	studio_schema_update: "UPDATE",
	studio_schema_delete_from: "DELETE FROM",
	studio_schema_columns: "{{count}} colunas",
	studio_rls_title: "Segurança ao nível da linha",
	studio_rls_description: "Gerir as políticas RLS das suas tabelas",
	studio_rls_enabled: "RLS ativada",
	studio_rls_disabled: "RLS desativada",
	studio_rls_no_rls: "Sem RLS",
	studio_rls_enable_rls: "Ativar RLS",
	studio_rls_disable_rls: "Desativar RLS",
	studio_rls_create_policy: "Criar política",
	studio_rls_policies: "Policies",
	studio_rls_no_policies: "Não há políticas definidas",
	studio_rls_no_policies_desc: "Esta tabela não tem políticas RLS. O acesso a todas as linhas depende do estado da RLS.",
	studio_rls_add_first_policy: "Adicionar a sua primeira política",
	studio_rls_force_rls: "Forçar RLS",
	studio_rls_force_rls_desc: "Quando ativado, as políticas RLS aplicam-se também ao proprietário da tabela.",
	studio_rls_enable_force_rls: "Ativar RLS forçada",
	studio_rls_disable_force_rls: "Desativar RLS forçada",
	studio_rls_edit: "Edit",
	studio_rls_delete: "Delete",
	studio_rls_confirm_delete_title: "Eliminar política",
	studio_rls_confirm_delete_body: "Tem a certeza de que pretende eliminar esta política? Esta ação não pode ser anulada.",
	studio_rls_confirm_enable_title: "Ativar a segurança ao nível da linha",
	studio_rls_confirm_enable_body: "Ao ativar a RLS, o acesso às linhas desta tabela passa a ser limitado pelas suas políticas. Certifique-se de que tem políticas adequadas.",
	studio_rls_confirm_disable_title: "Desativar a segurança ao nível da linha",
	studio_rls_confirm_disable_body: "Ao desativar a RLS, todas as linhas ficarão acessíveis a todas as funções. Tem a certeza de que pretende continuar?",
	studio_rls_select_table: "Selecione uma tabela",
	studio_rls_no_tables: "Não há tabelas disponíveis",
	studio_rls_error: "Erro ao carregar as políticas",
	studio_rls_retry: "Retry",
	studio_rls_loading: "A carregar as políticas...",
	studio_policy_edit: "Editar política",
	studio_policy_create: "Criar uma nova política",
	studio_policy_defining_rules: "A definir regras de acesso para",
	studio_policy_cancel: "Cancel",
	studio_policy_save: "Guardar política",
	studio_policy_template: "Começar a partir de um modelo (opcional)",
	studio_policy_select_template: "Selecione um modelo...",
	studio_policy_name: "Nome da política",
	studio_policy_name_placeholder: "Dê à sua política um nome descritivo",
	studio_policy_behavior: "Comportamento da política",
	studio_policy_permissive: "Permissive",
	studio_policy_permissive_desc: "Combinada com «OR»",
	studio_policy_restrictive: "Restrictive",
	studio_policy_restrictive_desc: "Combinada com «AND»",
	studio_policy_command: "Comando da política",
	studio_policy_target_roles: "Funções alvo",
	studio_policy_roles_placeholder: "Selecione funções (por omissão, public)",
	studio_policy_using_expr: "Expressão USING",
	studio_policy_using_expr_desc: "Aplica-se às linhas existentes (SELECT, UPDATE, DELETE)",
	studio_policy_check_expr: "Expressão WITH CHECK",
	studio_policy_check_expr_desc: "Aplica-se às linhas novas ou modificadas (INSERT, UPDATE)",
	studio_policy_help_title: "Guia da segurança ao nível da linha (RLS)",
	studio_policy_help_intro: "A segurança ao nível da linha (RLS) permite restringir as linhas a que um utilizador pode aceder ou que pode modificar, consoante o seu estado de autenticação ou as suas funções.",
	studio_policy_help_step1_title: "1. Selecione um modelo",
	studio_policy_help_step1_desc: "Se está a começar com a RLS, o mais simples é escolher um modelo predefinido na lista. Configura automaticamente a política para casos comuns como «os utilizadores podem atualizar as suas próprias linhas».",
	studio_policy_help_step2_title: "2. Funções alvo",
	studio_policy_help_step2_desc: "Escolha a que utilizadores se aplica a política:",
	studio_policy_help_role_public: "Aplica-se a toda a gente (com sessão iniciada ou não).",
	studio_policy_help_role_authenticated: "Aplica-se apenas aos utilizadores com sessão iniciada.",
	studio_policy_help_role_anon: "Aplica-se apenas aos utilizadores que NÃO iniciaram sessão.",
	studio_policy_help_step3_title: "3. Expressão USING",
	studio_policy_help_step3_desc: "Uma condição SQL que tem de ser verdadeira para que as linhas existentes sejam visíveis ou atualizáveis (SELECT, UPDATE, DELETE).",
	studio_policy_help_step3_example: "Garante que os utilizadores só veem as linhas de que são proprietários.",
	studio_policy_help_step4_title: "4. Expressão WITH CHECK",
	studio_policy_help_step4_desc: "Uma condição SQL que tem de ser verdadeira para que as linhas novas ou modificadas sejam guardadas (INSERT, UPDATE).",
	studio_policy_help_step4_example: "Normalmente é igual à expressão USING quando os utilizadores só podem inserir ou editar os seus próprios dados.",
	studio_policy_help_auth_vars_title: "Variáveis de autenticação do Rebase",
	studio_policy_help_auth_vars_desc: "O Rebase fornece funções nativas que pode usar nas suas expressões:",
	studio_policy_help_auth_uid: "Devolve o ID do utilizador atual como texto. Exemplo:",
	studio_policy_help_auth_jwt: "Devolve todo o payload do JWT como JSONB, para que possa verificar claims personalizadas. Exemplo:",
	studio_policy_help_auth_roles: "Devolve os IDs das funções do utilizador como uma cadeia separada por vírgulas. Utilize preferencialmente com:",
	studio_policy_help_docs_cta: "Para saber mais sobre as políticas do Postgres, consulte a documentação oficial.",
	studio_policy_help_read_docs: "Ler a documentação",
	studio_policy_help_got_it: "Percebi!",
	studio_add_kanban_column: "Adicionar coluna",
	studio_add_kanban_column_title: "Adicionar coluna ao Kanban",
	studio_add_kanban_column_name: "Nome da coluna",
	studio_add_kanban_column_name_placeholder: "Nome da nova coluna",
	studio_add_kanban_column_add: "Add",
	studio_add_kanban_column_cancel: "Cancel",
	studio_collection_view_sql: "Editor SQL",
	studio_collection_view_admin: "Vista de administração",
	studio_editor_collection_tooltip: "Editar o esquema da coleção",
	studio_editor_collection_no_permission: "Não tem permissão para editar esta coleção",
	studio_editor_collection_start_tooltip: "Copiar o caminho ou editar o esquema",
	studio_editor_collection_start_no_permission: "Não tem permissão para modificar esta coleção",
	studio_editor_collection_start_copied: "Copied!",
	studio_editor_entity_tooltip: "Editar o esquema da entidade",
	studio_editor_entity_no_permission: "Não tem permissão para editar esta entidade",
	studio_home_edit_collection: "Editar coleção",
	studio_home_delete_collection: "Eliminar coleção",
	studio_home_confirm_delete_title: "Eliminar a coleção?",
	studio_home_confirm_delete_body: "Tem a certeza de que pretende eliminar a coleção «{{name}}»? Esta ação não pode ser anulada.",
	studio_kanban_setup: "Configuração do Kanban",
	studio_missing_reference: "Referência em falta: {{path}}",
	studio_missing_reference_create: "Create",
	studio_new_collection: "Nova coleção",
	studio_new_collection_card: "Nova coleção",
	studio_new_collection_no_permission: "Não tem permissões para criar coleções",
	studio_property_add_column: "Adicionar uma nova propriedade",
	studio_property_add_column_no_permission: "Não tem permissão para adicionar novas propriedades",
	studio_add_kanban_column_desc: "Adiciona uma nova opção à propriedade «{{property}}». Será criada uma nova coluna no quadro Kanban.",
	studio_add_kanban_column_adding: "Adding...",
	studio_collection_view_edit: "Edit",
	studio_editor_collection_disabled: "A edição de coleções está desativada",
	studio_editor_collection_edit: "Editar coleção",
	studio_editor_entity_save_first: "Tem de guardar o documento antes de alterar o esquema",
	studio_editor_entity_edit_schema: "Editar o esquema deste formulário",
	studio_editor_collection_start_save_filter: "Guardar o filtro e a ordenação predefinidos",
	studio_editor_collection_start_clear_filter: "Limpar o filtro e a ordenação predefinidos",
	studio_editor_collection_start_reset_filter: "Repor o filtro e a ordenação predefinidos",
	studio_editor_collection_start_saved: "Configuração predefinida guardada",
	studio_home_duplicate_collection: "Duplicate",
	studio_home_delete: "Delete",
	studio_home_confirm_delete_no_data: "Não serão eliminados dados, apenas a coleção no painel de administração",
	studio_home_collection_deleted: "Coleção eliminada",
	studio_kanban_configure: "Configurar o Kanban",
	studio_missing_reference_error: "Não há coleção para o caminho: {{path}}",
	studio_new_collection_add: "Adicionar nova coleção",
	db_column_type: "Tipo de coluna do banco de dados",
	primary_key_unique_id: "Chave primária / ID único",
	spread_children_as_columns: "Expandir filhos como colunas",
	mode: "Modo",
	timezone: "Fuso horário",
	target_collection: "Coleção de destino",
	storage_file_name: "Nome do arquivo",
	storage_path: "Caminho de armazenamento",
	storage_max_size: "Tamanho máximo (em bytes)",
	storage_resize_mode: "Modo de redimensionamento",
	storage_output_format: "Formato de saída",
	storage_max_width: "Largura máxima (px)",
	storage_max_height: "Altura máxima (px)",
	storage_quality: "Qualidade (0-100)",
	storage_file_upload_config: "Configuração de upload de arquivo",
	storage_image_resize_config: "Configuração de redimensionamento de imagem",
	storage_all_file_types: "Todos os tipos de arquivo permitidos",
	storage_allowed_file_types: "Tipos de arquivo permitidos",
	storage_include_bucket_url: "Incluir URL do bucket (s3://...) no valor salvo",
	storage_save_url: "Salvar URL em vez do caminho de armazenamento",
	datetime_automatic_value: "Valor automático",
	markdown_paste_behavior: "Comportamento ao colar",
	entity_saved: "Guardado",
	more_actions: "Mais ações",
	record_history: "Histórico",
	inspect_record: "Inspecionar registo",
	inspect: "Inspecionar",
	close_inspector: "Fechar o inspetor",
	open_in_live_site: "Abrir no site publicado",
	history_revert: "Voltar a esta versão",
	history_revert_title: "Reverter os dados para esta versão?",
	history_reverted: "Revertido para a versão selecionada",
	history_revert_error: "Erro ao reverter o registo",
	history_revert_dirty: "Guarde ou descarte as suas alterações antes de reverter",
	history_empty: "Nenhum histórico disponível",
	history_empty_description: "Ao guardar um registo é criada uma nova versão, guardada no histórico.",
	history_new_entity: "O histórico só está disponível para registos existentes",
	history_no_more: "Sem mais histórico",
	studio_group_views: "Visualizações",
	studio_group_admin: "Administração",
	studio_group_settings: "Definições",
	studio_group_database: "Base de dados",
	studio_group_compute: "Computação",
	studio_group_storage: "Armazenamento",
	studio_group_api: "API",
	studio_group_access_control: "Controlo de acesso",
	studio_tool_sql: "Consola SQL",
	studio_tool_sql_description: "Executa consultas SQL",
	studio_tool_js: "Consola JS",
	studio_tool_js_description: "Executa JavaScript",
	studio_tool_rls: "Políticas RLS",
	studio_tool_rls_description: "Segurança ao nível da linha",
	studio_tool_storage: "Armazenamento",
	studio_tool_storage_description: "Gere os ficheiros de armazenamento",
	studio_tool_cron: "Tarefas cron",
	studio_tool_cron_description: "Gere as tarefas agendadas",
	studio_tool_schema_visualizer: "Visualizador de esquema",
	studio_tool_schema_visualizer_description: "Diagrama ER interactivo da base de dados",
	studio_tool_branches: "Ramos",
	studio_tool_branches_description: "Cria e gere ramos da base de dados",
	studio_tool_backups: "Cópias de segurança",
	studio_tool_backups_description: "Descarrega cópias de segurança da base de dados",
	studio_tool_api: "Explorador de API",
	studio_tool_api_description: "Documentação de API interactiva e testes",
	studio_tool_logs: "Explorador de registos",
	studio_tool_logs_description: "Registos de sistema e consultas em tempo real",
	studio_tool_api_keys: "Chaves de API",
	studio_tool_api_keys_description: "Cria e gere chaves de API com âmbito",
	studio_backups_denied_title: "Não pode listar as cópias de segurança deste projeto",
	studio_backups_denied_hint: "O projeto não tem nada de errado. Listar cópias de segurança exige uma ligação de administrador, e a conta com sessão iniciada foi recusada.",
	studio_backups_read_failed: "Não foi possível ler as cópias de segurança deste projeto",
	studio_backups_downloading: "A descarregar…",
	studio_backups_roles_file: "Ficheiro de funções",
	studio_backups_roles_file_hint: "O .globals.sql guardado com esta cópia. Recria as funções da base de dados a que se referem as permissões e a segurança ao nível da linha do dump, e o `rebase db restore` lê-o ao lado do .dump, por isso mantenha os dois ficheiros juntos.",
	studio_backups_no_roles_file: "Sem ficheiro de funções",
	studio_backups_no_roles_file_hint: "Esta cópia não tem nenhum .globals.sql. Restaurá-la num Postgres novo para na primeira permissão concedida a uma função da base de dados que lá não existe.",
	studio_cron_denied_title: "Não pode listar as tarefas cron deste projeto",
	studio_cron_denied_hint: "O projeto não tem nada de errado. A API de cron é apenas para administradores, e a conta com sessão iniciada foi recusada.",
	studio_cron_read_failed: "Não foi possível ler as tarefas cron deste projeto",
	studio_api_keys_denied_title: "Não pode listar as chaves de API deste projeto",
	studio_api_keys_denied_hint: "O projeto não tem nada de errado. Gerir chaves de API é uma operação de administrador, e a conta com sessão iniciada foi recusada.",
	studio_api_keys_read_failed: "Não foi possível ler as chaves de API deste projeto",
	studio_api_keys_empty_title: "Ainda não há chaves de API",
	studio_api_keys_empty_hint: "Crie uma chave para permitir a autenticação entre serviços",
	studio_api_keys_revoke: "Revogar",
	studio_api_keys_select_hint: "Selecione uma chave de API para ver os detalhes",
	studio_api_keys_permissions: "Permissões",
	studio_api_keys_no_permissions: "Nenhuma permissão configurada",
	studio_api_keys_revoke_confirmation: "Confirmação de revogação",
	studio_api_keys_name_label: "Nome:",
	studio_api_keys_access_label: "Acesso:",
	studio_api_keys_admin_granted: "Papel de administrador concedido",
	studio_api_keys_admin_granted_hint: "as rotas de administração e as políticas default_admin",
	studio_api_keys_done: "Concluído",
	studio_api_keys_status_active: "Ativa",
	studio_api_keys_status_expired: "Expirada",
	studio_api_keys_status_revoked: "Revogada",
	studio_api_keys_stat_status: "Estado",
	studio_api_keys_stat_last_used: "Última utilização",
	studio_api_keys_stat_expires: "Expira",
	studio_api_keys_never: "Nunca",
	studio_api_keys_role_service: "Serviço",
	studio_api_keys_stat_rate_limit: "Limite de pedidos",
	studio_api_keys_rate_limit_default: "Predefinido (1000/15 min)",
	studio_api_keys_stat_created_by: "Criada por",
	studio_api_keys_copy_failed: "Não foi possível copiar",
	studio_branches_denied_title: "Não pode listar os ramos deste projeto",
	studio_branches_denied_hint: "O projeto não tem nada de errado. Os ramos usam a ligação de administrador, e a conta com sessão iniciada foi recusada.",
	studio_branches_read_failed: "Não foi possível ler os ramos deste projeto",
	studio_schema_no_collections: "Nenhuma coleção declarada",
	studio_schema_no_collections_body: "Ainda não há nada para desenhar. Adicione uma coleção em config/collections/, ou abra «Editar coleções» para adicionar uma aqui.",
	studio_schema_introspect_hint: "Numa base de dados que já tem tabelas, rebase schema introspect escreve os ficheiros de coleção por si.",
	studio_rls_enable_confirm_title: "Activar a segurança ao nível da linha?",
	studio_rls_enable_confirm_body: "Até que uma política o permita, nenhuma linha de {{table}} é legível. O Postgres nega por omissão.",
	studio_rls_disable_confirm_title: "Desactivar a segurança ao nível da linha?",
	studio_rls_disable_confirm_body: "Todas as políticas de {{table}} deixam de ser aplicadas. Quem alcançar a tabela pode ler e escrever todas as linhas.",
	studio_rls_drop_policy_title: "Eliminar esta política?",
	studio_rls_drop_policy_body: "{{policy}} é removida de {{table}} na base de dados. Nada é escrito no seu código, portanto um deploy pode repô-la.",
	studio_rls_unapplied: "Por aplicar",
	studio_rls_unapplied_tooltip: "Declarada no seu código, ainda não na base de dados. Guarde-a aqui para planear e aplicar a alteração, ou execute `rebase db push`.",
	studio_branches_unavailable_title: "A criação de ramos da base de dados não está disponível",
	studio_branches_unavailable_body: "Os ramos precisam de um backend Postgres com uma ligação de administrador. Defina ADMIN_CONNECTION_STRING no ambiente do backend.",
	studio_backups_not_configured_title: "As cópias de segurança não estão configuradas",
	studio_backups_not_configured_body: "Defina BACKUP_DESTINATION para um caminho local ou um URL s3:// ou gs://, e depois crie uma cópia com `rebase db backup` ou um cron agendado.",
	studio_cron_empty_title: "Nenhuma tarefa cron registada",
	studio_cron_empty_body: "Adicione um ficheiro em backend/crons/ que exporte por omissão uma CronJobDefinition.",
	studio_read_the_docs: "Consulte a documentação"
};
//#endregion
//#region src/i18n/RebaseI18nProvider.tsx
var REBASE_NS = "rebase_core";
var REBASE_LOCALE_STORAGE_KEY = "rebase_locale";
/**
* Initialises a dedicated i18next instance for Rebase's internal UI strings.
*
* This instance is isolated from any app-level i18next configuration the
* consumer may have. Mount this at the top of the Rebase component tree.
*
* @internal
*/
function RebaseI18nProvider({ locale = "en", translations, children }) {
	const i18nRef = useRef(null);
	const [ready, setReady] = React.useState(false);
	if (!i18nRef.current) {
		const instance = i18next.createInstance();
		const resources = buildResources(translations);
		let initialLocale = locale;
		const stored = readStoredString(REBASE_LOCALE_STORAGE_KEY);
		if (stored) initialLocale = stored;
		instance.use(initReactI18next).init({
			lng: initialLocale,
			fallbackLng: "en",
			ns: [REBASE_NS],
			defaultNS: REBASE_NS,
			resources,
			interpolation: { escapeValue: false }
		}, () => {
			setReady(true);
		});
		instance.on("languageChanged", (lng) => {
			if (typeof window !== "undefined") writeStoredString(REBASE_LOCALE_STORAGE_KEY, lng);
		});
		i18nRef.current = instance;
	}
	useEffect(() => {
		if (i18nRef.current && i18nRef.current.language !== locale) {
			if (!Boolean(readStoredString("rebase_locale"))) i18nRef.current.changeLanguage(locale);
		}
	}, [locale]);
	useEffect(() => {
		if (!i18nRef.current) return;
		const resources = buildResources(translations);
		for (const [lang, bundle] of Object.entries(resources)) i18nRef.current.addResourceBundle(lang, REBASE_NS, bundle[REBASE_NS], true, true);
	}, [translations]);
	if (!ready || !i18nRef.current) return null;
	return /* @__PURE__ */ jsx(I18nextProvider, {
		i18n: i18nRef.current,
		children
	});
}
/**
* Build an i18next resources object from the English baseline plus any
* consumer-provided overrides.
*/
function buildResources(translations) {
	const resources = {
		en: { [REBASE_NS]: { ...en } },
		es: { [REBASE_NS]: { ...es } },
		de: { [REBASE_NS]: { ...de } },
		fr: { [REBASE_NS]: { ...fr } },
		it: { [REBASE_NS]: { ...it } },
		hi: { [REBASE_NS]: { ...hi } },
		pt: { [REBASE_NS]: { ...pt } }
	};
	if (!translations) return resources;
	for (const [lang, overrides] of Object.entries(translations)) {
		if (!resources[lang]) resources[lang] = { [REBASE_NS]: { ...en } };
		resources[lang][REBASE_NS] = {
			...resources[lang][REBASE_NS],
			...overrides
		};
	}
	return resources;
}
//#endregion
//#region src/core/Rebase.tsx
/**
* If you are using independent components of the admin
* you need to wrap them with this main component, so the internal hooks work.
*
* This is the main component of Rebase. It acts as the provider of all the
* internal contexts and hooks.
*
* You only need to use this component if you are building a custom app.
*
* @group Core
*/
/**
* Does this socket carry the admin surface?
*
* `RebaseWebSocket` declares none of `DatabaseAdmin`'s methods — the SQL console
* is served over the same socket only when the backend enables it — so the
* question is genuinely a runtime one. It used to be asked as
* `typeof (ws as unknown as Record<string, unknown>).executeSql === "function"`
* and then answered a second time, one line down, by asserting the whole socket
* to `DatabaseAdmin`: a check and a claim, with nothing connecting them. A
* predicate makes the check *be* the narrowing, so the five method reads below
* are checked against the interface they came from.
*/
function canExecuteSql(ws) {
	return typeof ws.executeSql === "function";
}
function Rebase(props) {
	const { children, entityLinkBuilder, userConfigPersistence, dateTimeFormat, locale, client, authController: authControllerProp, storageSource: storageSourceProp, dataSources: dataSourcesProp, storageSources: storageSourcesProp, databaseAdmin, plugins: pluginsProp, slots: directSlots = [], onAnalyticsEvent, propertyConfigs, entityViews, collectionViews, entityActions, effectiveRoleController, apiUrl, translations, components: componentsProp } = props;
	const plugins = pluginsProp;
	if (plugins) {
		const keys = plugins.map((p) => p.key);
		if (new Set(keys).size !== keys.length) console.error("Duplicate plugin keys detected:", keys.filter((k, i) => keys.indexOf(k) !== i));
	}
	const localUserConfigPersistence = useBuildLocalConfigurationPersistence();
	const resolvedUserConfigPersistence = userConfigPersistence ?? localUserConfigPersistence;
	const resolvedSlots = useMemo(() => [...directSlots, ...(plugins ?? []).flatMap((p) => p.slots ?? [])], [directSlots, plugins]);
	useEffect(() => {
		const dead = new Set(UNRENDERED_SLOTS);
		const registered = new Set(resolvedSlots.map((s) => s.slot).filter((slot) => dead.has(slot)));
		for (const slot of registered) console.warn(`[Rebase] A component is registered for the "${slot}" slot, which this build declares but renders nowhere — nothing will appear. This is a gap in Rebase, not in your plugin.`);
	}, [resolvedSlots]);
	const clientAuthController = useAuthSubscription(authControllerProp ? void 0 : client?.auth);
	const authController = authControllerProp ?? clientAuthController;
	const normalizedDataSources = useMemo(() => {
		return dataSourcesProp ?? [];
	}, [dataSourcesProp]);
	const collectionResolverRef = useRef(void 0);
	const registerCollectionResolver = React.useCallback((resolver) => {
		collectionResolverRef.current = resolver;
	}, []);
	const entityDataOptions = useMemo(() => ({ resolveCollection: (slug) => collectionResolverRef.current?.(slug) }), []);
	const dataSourcesRef = useRef(null);
	const dataSourcesValue = useMemo(() => {
		const sig = normalizedDataSources.map((d) => ({
			key: d.key,
			engine: d.engine,
			transport: d.transport,
			driver: d.driver
		}));
		const prev = dataSourcesRef.current;
		if (prev && prev.sig.length === sig.length && sig.every((s, i) => prev.sig[i].key === s.key && prev.sig[i].engine === s.engine && prev.sig[i].transport === s.transport && prev.sig[i].driver === s.driver)) return prev.value;
		const registry = {};
		const sources = {};
		for (const ds of normalizedDataSources) {
			const { driver, ...definition } = ds;
			registry[ds.key] = {
				...definition,
				transport: ds.transport ?? (driver ? "direct" : "server")
			};
			if (driver) sources[ds.key] = buildRebaseData(driver, entityDataOptions);
		}
		const value = {
			registry,
			sources
		};
		dataSourcesRef.current = {
			sig,
			value
		};
		return value;
	}, [normalizedDataSources, entityDataOptions]);
	const resolvedData = useMemo(() => {
		const registeredDefault = dataSourcesValue.sources[DEFAULT_DATA_SOURCE_KEY];
		if (registeredDefault) return registeredDefault;
		if (client?.data) return wrapAsEntityData(client.data, entityDataOptions);
		const built = Object.values(dataSourcesValue.sources);
		if (built.length === 1) return built[0];
		if (built.length > 1) throw new Error("[Rebase] Several data sources are registered but none is the default. Pass a `client`, or key one `dataSources` entry \"(default)\".");
		throw new Error("Rebase requires either `client` or a `dataSources` entry with a driver to be provided");
	}, [
		client,
		dataSourcesValue,
		entityDataOptions
	]);
	const resolvedStorage = storageSourceProp ?? client?.storage;
	const [remoteStorageSources, setRemoteStorageSources] = useState([]);
	const authUser = authController.user;
	const loginSkipped = authController.loginSkipped;
	useEffect(() => {
		if (!client?.fetchStorageSources) return;
		let cancelled = false;
		client.fetchStorageSources().then((defs) => {
			if (!cancelled) setRemoteStorageSources(defs);
		}).catch((e) => {
			if (!cancelled) console.debug("[Rebase] Could not load storage sources", e);
		});
		return () => {
			cancelled = true;
		};
	}, [
		client,
		authUser,
		loginSkipped
	]);
	const storageSourcesRef = useRef(null);
	const storageSourcesValue = useMemo(() => {
		const list = storageSourcesProp ?? [];
		const prev = storageSourcesRef.current;
		if (prev && prev.remote === remoteStorageSources && prev.resolvedStorage === resolvedStorage && prev.client === client && prev.propList.length === list.length && list.every((s, i) => prev.propList[i].key === s.key && prev.propList[i].source === s.source && prev.propList[i].transport === s.transport)) return prev.value;
		const registry = {};
		const sources = {};
		for (const def of remoteStorageSources) registry[def.key] = def;
		if (client?.storageRegistry) for (const key of client.storageRegistry.list()) {
			const source = client.storageRegistry.get(key);
			if (source) sources[key] = source;
		}
		for (const ss of list) {
			const { source, ...definition } = ss;
			registry[ss.key] = definition;
			if (source) sources[ss.key] = source;
			else if (!sources[ss.key] && ss.transport === "server" && ss.key !== DEFAULT_STORAGE_SOURCE_KEY && client?.createStorageSource) sources[ss.key] = client.createStorageSource(ss.key);
		}
		if (resolvedStorage && !sources[DEFAULT_STORAGE_SOURCE_KEY]) sources[DEFAULT_STORAGE_SOURCE_KEY] = resolvedStorage;
		const value = {
			registry,
			sources
		};
		storageSourcesRef.current = {
			propList: list.map((s) => ({
				key: s.key,
				source: s.source,
				transport: s.transport
			})),
			remote: remoteStorageSources,
			resolvedStorage,
			client,
			value
		};
		return value;
	}, [
		storageSourcesProp,
		remoteStorageSources,
		resolvedStorage,
		client
	]);
	const resolvedDatabaseAdmin = useMemo(() => {
		if (databaseAdmin) return databaseAdmin;
		const defaultDriver = normalizedDataSources.find((d) => d.key === DEFAULT_DATA_SOURCE_KEY && d.driver)?.driver ?? (!client && normalizedDataSources.length === 1 ? normalizedDataSources[0].driver : void 0);
		if (defaultDriver?.admin) return defaultDriver.admin;
		const ws = client?.ws;
		if (ws && canExecuteSql(ws)) {
			const wsAdmin = ws;
			return {
				executeSql: wsAdmin.executeSql.bind(wsAdmin),
				fetchAvailableDatabases: wsAdmin.fetchAvailableDatabases?.bind(wsAdmin),
				fetchAvailableRoles: wsAdmin.fetchAvailableRoles?.bind(wsAdmin),
				fetchApplicationRoles: wsAdmin.fetchApplicationRoles?.bind(wsAdmin),
				fetchCurrentDatabase: wsAdmin.fetchCurrentDatabase?.bind(wsAdmin),
				fetchUnmappedTables: wsAdmin.fetchUnmappedTables?.bind(wsAdmin),
				fetchTableMetadata: wsAdmin.fetchTableMetadata?.bind(wsAdmin),
				...typeof wsAdmin.createBranch === "function" ? {
					createBranch: wsAdmin.createBranch.bind(wsAdmin),
					deleteBranch: wsAdmin.deleteBranch.bind(wsAdmin),
					listBranches: wsAdmin.listBranches.bind(wsAdmin)
				} : {}
			};
		}
	}, [
		databaseAdmin,
		client,
		normalizedDataSources
	]);
	if (!resolvedStorage && typeof console !== "undefined") console.warn("[Rebase] No storageSource provided. File upload features will not work. Provide storageSource via props or through a RebaseClient.");
	const pluginsLoading = plugins?.some((p) => p.loading) ?? false;
	const loading = authController.initialLoading || pluginsLoading;
	const customizationController = useMemo(() => ({
		dateTimeFormat,
		locale,
		entityLinkBuilder,
		plugins,
		resolvedSlots,
		entityViews: entityViews ?? [],
		collectionViews: collectionViews ?? [],
		entityActions: entityActions ?? [],
		propertyConfigs: propertyConfigs ?? {},
		components: componentsProp
	}), [
		dateTimeFormat,
		locale,
		entityLinkBuilder,
		plugins,
		resolvedSlots,
		entityViews,
		collectionViews,
		entityActions,
		propertyConfigs,
		componentsProp
	]);
	const analyticsController = useMemo(() => ({ onAnalyticsEvent }), []);
	const fallbackEffectiveRoleController = useBuildEffectiveRoleController();
	const activeEffectiveRoleController = effectiveRoleController ?? fallbackEffectiveRoleController;
	const modeController = useBuildModeController();
	const adminModeController = useBuildAdminModeController();
	if (authController.authError) return /* @__PURE__ */ jsx(CenteredView, {
		maxWidth: "md",
		children: /* @__PURE__ */ jsx(ErrorView, {
			title: "Error loading auth",
			error: authController.authError
		})
	});
	const content = /* @__PURE__ */ jsx(RebaseI18nProvider, {
		locale,
		translations,
		children: /* @__PURE__ */ jsx(GlobalComponentOverrideProvider, {
			overrides: componentsProp,
			children: /* @__PURE__ */ jsx(SnackbarProvider, { children: /* @__PURE__ */ jsx(ModeControllerProvider, {
				value: modeController,
				children: /* @__PURE__ */ jsx(AdminModeControllerProvider, {
					value: adminModeController,
					children: /* @__PURE__ */ jsx(RebaseClientInstanceContext.Provider, {
						value: client,
						children: /* @__PURE__ */ jsx(AnalyticsContext.Provider, {
							value: analyticsController,
							children: /* @__PURE__ */ jsx(CustomizationControllerContext.Provider, {
								value: customizationController,
								children: /* @__PURE__ */ jsx(UserConfigurationPersistenceContext.Provider, {
									value: resolvedUserConfigPersistence,
									children: /* @__PURE__ */ jsx(StorageSourcesContext.Provider, {
										value: storageSourcesValue,
										children: /* @__PURE__ */ jsx(StorageSourceContext.Provider, {
											value: resolvedStorage,
											children: /* @__PURE__ */ jsx(CollectionResolverRegistrationContext.Provider, {
												value: registerCollectionResolver,
												children: /* @__PURE__ */ jsx(DataSourcesContext.Provider, {
													value: dataSourcesValue,
													children: /* @__PURE__ */ jsx(RebaseDataContext.Provider, {
														value: resolvedData,
														children: /* @__PURE__ */ jsx(DatabaseAdminContext.Provider, {
															value: resolvedDatabaseAdmin,
															children: /* @__PURE__ */ jsx(AuthControllerContext.Provider, {
																value: authController,
																children: /* @__PURE__ */ jsx(EffectiveRoleControllerContext.Provider, {
																	value: activeEffectiveRoleController,
																	children: /* @__PURE__ */ jsx(DialogsProvider, { children: /* @__PURE__ */ jsx(SchemaDriftProvider, { children: /* @__PURE__ */ jsx(RebaseRegistryProvider, { children: /* @__PURE__ */ jsx(RebaseInternal, {
																		loading,
																		children
																	}) }) }) })
																})
															})
														})
													})
												})
											})
										})
									})
								})
							})
						})
					})
				})
			}) })
		})
	});
	const resolvedApiUrl = apiUrl || client?.baseUrl || (typeof window !== "undefined" ? window.location.origin : "");
	if (resolvedApiUrl) return /* @__PURE__ */ jsx(ApiConfigProvider, {
		apiUrl: resolvedApiUrl,
		apiPath: client?.apiPath,
		getAuthToken: authController.getAuthToken,
		children: content
	});
	return content;
}
function RebaseInternal({ loading, children }) {
	const context = useRebaseContext();
	const customizationController = useCustomizationController();
	const childrenResult = typeof children === "function" ? children({
		context,
		loading
	}) : children;
	const plugins = customizationController.plugins;
	const authController = context.authController;
	const authReady = !loading && !authController.authLoading && (Boolean(authController.user) || authController.loginSkipped);
	if (plugins && plugins.length > 0) return /* @__PURE__ */ jsxs(PluginProviderStack, {
		plugins,
		scope: "root",
		scopeProps: { context },
		children: [authReady && /* @__PURE__ */ jsx(PluginLifecycleManager, {
			plugins,
			context
		}), childrenResult]
	});
	return childrenResult;
}
//#endregion
//#region src/core/RebaseRouter.tsx
function RebaseRouter({ children, basePath }) {
	return /* @__PURE__ */ jsx(RouterProvider, { router: createBrowserRouter([{
		path: basePath ? `${removeInitialAndTrailingSlashes$1(basePath)}/*` : "/*",
		element: children
	}]) });
}
//#endregion
//#region src/core/RebaseRoutes.tsx
/**
* A drop-in replacement for react-router's `<Routes>` that preserves the
* underlying view when a side dialog navigates the URL to a different path.
*
* When opening a side dialog from e.g. `/posts` to `authors/123#side`,
* the `base_location` stored in router state is used so the route tree
* keeps rendering the original `/posts` view underneath the dialog overlay.
*
* Additionally, registered views from `useNavigationStateController()` are
* automatically routed — no need to manually map them to `<Route>` elements.
* Views with `nestedRoutes: true` get a wildcard route (slug/*) as well.
* Explicitly declared `children` routes take priority.
*/
function RebaseRoutes({ children }) {
	const location = useLocation();
	return /* @__PURE__ */ jsx(Routes, {
		location: location.state?.base_location ?? location,
		children
	});
}
//#endregion
//#region src/auth/useRebaseAuthController.ts
/**
* Auth controller hook for JWT-based authentication.
*
* This is a thin React binding over the headless SDK `client.auth`.
* It synchronizes React state with the SDK's internal state and delegates
* all operations to the SDK to ensure a single source of truth for
* authentication sessions across the entire application.
*
* @param props Configuration options
* @returns RebaseAuthController instance
*/
function useRebaseAuthController(props = {}) {
	const { client, onSignOut, defineRolesFor } = props;
	const auth = client?.auth;
	const [user, setUser] = useState(null);
	const [authLoading, setAuthLoading] = useState(false);
	const [initialLoading, setInitialLoading] = useState(true);
	const [authError, setAuthError] = useState(null);
	const [authProviderError, setAuthProviderError] = useState(null);
	const [loginSkipped, setLoginSkipped] = useState(false);
	const [extra, setExtra] = useState(null);
	const [authConfig, setAuthConfig] = useState(null);
	const isMountedRef = useRef(true);
	const onSignOutRef = useRef(onSignOut);
	onSignOutRef.current = onSignOut;
	const defineRolesForRef = useRef(defineRolesFor);
	defineRolesForRef.current = defineRolesFor;
	const clearError = useCallback(() => {
		setAuthProviderError(null);
	}, []);
	useEffect(() => {
		if (!auth) {
			setInitialLoading(false);
			return;
		}
		isMountedRef.current = true;
		const updateState = async (session) => {
			if (!isMountedRef.current) return;
			let userToSet = session?.user ?? null;
			const defineRolesFor = defineRolesForRef.current;
			if (userToSet && defineRolesFor) try {
				const roles = await defineRolesFor(userToSet);
				if (roles && isMountedRef.current) userToSet = {
					...userToSet,
					roles
				};
			} catch (e) {
				console.error("Error defining roles:", e);
			}
			if (isMountedRef.current) setUser(userToSet);
		};
		const syncState = async (event, session) => {
			await updateState(session);
			if (event === "SIGNED_OUT") {
				clearFetchCache();
				if (isMountedRef.current) setLoginSkipped(false);
				onSignOutRef.current?.();
			}
		};
		updateState(auth.getSession());
		auth.isInitialized().then(() => {
			if (isMountedRef.current) {
				updateState(auth.getSession());
				setInitialLoading(false);
			}
		}).catch(() => {
			if (isMountedRef.current) setInitialLoading(false);
		});
		auth.getAuthConfig().then((config) => {
			if (isMountedRef.current) setAuthConfig(config);
		}).catch((e) => {
			console.warn("[Rebase] Could not load the backend auth configuration; the login view will fall back to defaults.", e);
		});
		const unsubscribe = auth.onAuthStateChange(syncState);
		return () => {
			isMountedRef.current = false;
			unsubscribe();
		};
	}, [auth]);
	const getAuthToken = useCallback(async () => {
		if (!auth) throw new Error("Rebase client with auth is required");
		const session = auth.getSession();
		if (session?.accessToken && session.expiresAt > Date.now() + 1e4) return session.accessToken;
		return (await auth.refreshSession()).accessToken;
	}, [auth]);
	const signOut = useCallback(async () => {
		if (!auth) return;
		setAuthLoading(true);
		try {
			await auth.signOut();
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			setAuthProviderError(err);
		} finally {
			setAuthLoading(false);
		}
	}, [auth]);
	const emailPasswordLogin = useCallback(async (email, password) => {
		if (!auth) throw new Error("Rebase client with auth is required");
		setAuthLoading(true);
		setAuthProviderError(null);
		try {
			await auth.signInWithEmail(email, password);
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			setAuthProviderError(err);
			throw error;
		} finally {
			setAuthLoading(false);
		}
	}, [auth]);
	const register = useCallback(async (email, password, displayName) => {
		if (!auth) throw new Error("Rebase client with auth is required");
		setAuthLoading(true);
		setAuthProviderError(null);
		try {
			await auth.signUp(email, password, displayName);
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			setAuthProviderError(err);
			throw error;
		} finally {
			setAuthLoading(false);
		}
	}, [auth]);
	const googleLogin = useCallback(async (payload) => {
		if (!auth) throw new Error("Rebase client with auth is required");
		setAuthLoading(true);
		setAuthProviderError(null);
		try {
			await auth.signInWithGoogle(payload);
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			setAuthProviderError(err);
			throw error;
		} finally {
			setAuthLoading(false);
		}
	}, [auth]);
	const oauthLogin = useCallback(async (providerId, payload) => {
		if (!auth) throw new Error("Rebase client with auth is required");
		setAuthLoading(true);
		setAuthProviderError(null);
		try {
			await auth.signInWithOAuth(providerId, payload);
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			setAuthProviderError(err);
			throw error;
		} finally {
			setAuthLoading(false);
		}
	}, [auth]);
	const skipLogin = useCallback(() => {
		setLoginSkipped(true);
		setUser(null);
	}, []);
	const forgotPassword = useCallback(async (email) => {
		if (!auth) throw new Error("Rebase client with auth is required");
		setAuthLoading(true);
		setAuthProviderError(null);
		try {
			await auth.resetPasswordForEmail(email);
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			setAuthProviderError(err);
			throw error;
		} finally {
			setAuthLoading(false);
		}
	}, [auth]);
	const resetPassword = useCallback(async (token, password) => {
		if (!auth) throw new Error("Rebase client with auth is required");
		setAuthLoading(true);
		setAuthProviderError(null);
		try {
			await auth.resetPassword(token, password);
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			setAuthProviderError(err);
			throw error;
		} finally {
			setAuthLoading(false);
		}
	}, [auth]);
	const changePassword = useCallback(async (oldPassword, newPassword) => {
		if (!auth) throw new Error("Rebase client with auth is required");
		setAuthLoading(true);
		setAuthProviderError(null);
		try {
			await auth.changePassword(oldPassword, newPassword);
			await auth.signOut();
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			setAuthProviderError(err);
			throw error;
		} finally {
			setAuthLoading(false);
		}
	}, [auth]);
	const updateProfile = useCallback(async (displayName, photoURL) => {
		if (!auth) throw new Error("Rebase client with auth is required");
		setAuthLoading(true);
		setAuthProviderError(null);
		try {
			return await auth.updateUser({
				displayName,
				photoURL
			});
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			setAuthProviderError(err);
			throw error;
		} finally {
			setAuthLoading(false);
		}
	}, [auth]);
	const fetchSessions = useCallback(async () => {
		if (!auth) throw new Error("Rebase client with auth is required");
		return await auth.getSessions();
	}, [auth]);
	const revokeSession = useCallback(async (sessionId) => {
		if (!auth) throw new Error("Rebase client with auth is required");
		await auth.revokeSession(sessionId);
	}, [auth]);
	const revokeAllSessions = useCallback(async () => {
		if (!auth) throw new Error("Rebase client with auth is required");
		await auth.revokeAllSessions();
	}, [auth]);
	return {
		user,
		authLoading,
		initialLoading,
		authError,
		authProviderError,
		loginSkipped,
		needsSetup: authConfig?.needsSetup ?? false,
		registrationEnabled: authConfig?.registrationEnabled ?? false,
		getAuthToken,
		getApiUrl: () => client?.baseUrl,
		signOut,
		emailPasswordLogin,
		register,
		googleLogin,
		oauthLogin,
		skipLogin,
		forgotPassword,
		resetPassword,
		changePassword,
		updateProfile,
		fetchSessions,
		revokeSession,
		revokeAllSessions,
		clearError,
		setAuthProviderError,
		extra,
		setExtra,
		capabilities: {
			emailPasswordLogin: true,
			googleLogin: !!props.googleClientId,
			registrationEnabled: authConfig?.registrationEnabled ?? false,
			passwordReset: authConfig?.passwordReset ?? false,
			adminPasswordReset: authConfig?.adminPasswordReset ?? true,
			sessionManagement: true,
			profileUpdate: true,
			emailVerification: authConfig?.emailVerification ?? false,
			enabledProviders: authConfig?.enabledProviders ?? []
		}
	};
}
//#endregion
//#region src/auth/api.ts
/**
* Auth-config fetch helper for the Rebase React frontend.
*
* The former per-endpoint auth client (login, register, refresh, OAuth,
* sessions, …) has been removed: `useRebaseAuthController` now delegates to
* the headless SDK's `client.auth` (from `@rebasepro/client`), which is the
* single source of truth for the auth session. The only piece that remains
* here is the unauthenticated `/api/auth/config` probe used to detect
* bootstrap mode and enabled providers before a session exists.
*/
async function handleResponse(response) {
	let data;
	try {
		data = await response.json();
	} catch (parseError) {
		throw new RebaseApiError(`Server returned non-JSON response (status: ${response.status})`, {
			status: response.status,
			code: "PARSE_ERROR"
		});
	}
	if (!response.ok) throw new RebaseApiError(data.error?.message || "Request failed", {
		status: response.status,
		code: data.error?.code || "UNKNOWN_ERROR"
	});
	return data;
}
/**
* Wrapper for fetch that catches generic network failures (like server down)
* and translates them to a {@link RebaseApiError} with code `NETWORK_ERROR`.
*/
async function fetchWithHandling(input, init) {
	try {
		return await fetch(input, init);
	} catch (error) {
		if (error instanceof TypeError && error.message.includes("Failed to fetch")) throw new RebaseApiError("Failed to connect to the backend server. The backend might be down or failed to initialize (e.g., database connection timeout).", {
			code: "NETWORK_ERROR",
			cause: error
		});
		throw new RebaseApiError("Network error: " + (error instanceof Error ? error.message : String(error)), {
			code: "NETWORK_ERROR",
			cause: error
		});
	}
}
/**
* Create a fresh `AuthConfigCache` instance.
* Callers own the cache object and pass it into `fetchAuthConfig` / `clearAuthConfigCache`.
*/
function createAuthConfigCache() {
	return {
		cached: null,
		inflight: null
	};
}
/**
* Fetch auth configuration / status from the backend
* This is an unauthenticated endpoint used to detect bootstrap mode.
*
* Results are cached for the session lifetime.
* Concurrent calls are deduplicated: only one network request is made
* and all callers share the same promise.
*/
async function fetchAuthConfig(apiUrl, cache, apiPath = DEFAULT_API_PATH) {
	if (cache.cached) return cache.cached;
	if (cache.inflight) return cache.inflight;
	cache.inflight = (async () => {
		return handleResponse(await fetchWithHandling(`${apiUrl.replace(/\/+$/, "")}${apiPath}/auth/config`, {
			method: "GET",
			headers: { "Content-Type": "application/json" }
		}));
	})();
	try {
		const result = await cache.inflight;
		cache.cached = result;
		return result;
	} finally {
		cache.inflight = null;
	}
}
/**
* Clear the cached auth config (e.g. on logout or for testing).
*/
function clearAuthConfigCache(cache) {
	cache.cached = null;
	cache.inflight = null;
}
//#endregion
//#region src/util/icon_list.ts
var iconSynonyms = {};
var iconsSearch = new Fuse(iconKeys.map((importName) => {
	return {
		key: importName,
		synonyms: importName in iconSynonyms ? iconSynonyms[importName] : ""
	};
}), {
	isCaseSensitive: false,
	shouldSort: true,
	ignoreLocation: true,
	distance: 0,
	keys: ["key", "synonyms"]
});
//#endregion
//#region src/util/icons.tsx
var iconKeysMap = iconKeys.reduce((acc, key) => {
	const lower = key.toLowerCase();
	const snake = key.replace(/([A-Z])/g, "_$1").toLowerCase().replace(/^_/, "");
	acc[lower] = key;
	acc[snake] = key;
	acc[key] = key;
	return acc;
}, {});
/**
* Render an icon element from a string key or existing React element.
*
* Whether a key names an icon is decided here, against `iconKeys` — an array
* of strings. The component behind the key is fetched by `LucideIconByName` on
* first use: this used to index lucide's full `icons` map, which is the whole
* library and cannot be tree-shaken.
*/
function getIcon(iconKey, className, color, size) {
	if (React.isValidElement(iconKey)) return iconKey;
	if (!iconKey) return void 0;
	if (typeof iconKey === "string") {
		const lowerKey = iconKey.toLowerCase();
		const slugifiedKey = slugify(iconKey).replace(/-/g, "_");
		const mappedKey = iconKeysMap[iconKey] || iconKeysMap[lowerKey] || iconKeysMap[slugifiedKey];
		if (!mappedKey) return;
		return /* @__PURE__ */ jsx(LucideIconByName, {
			name: mappedKey,
			size: typeof size === "number" ? size : iconSize[size ?? "medium"],
			className: cls(color ? colorClassesMapping[color] : "", "select-none shrink-0", className)
		});
	}
	console.warn("Invalid icon key provided:", iconKey);
}
var IconForView = React.memo(function IconForView({ collectionOrView, className, color, size = "medium" }) {
	if (!collectionOrView) return /* @__PURE__ */ jsx(Fragment, {});
	const icon = getIcon(collectionOrView.icon, className, color, size);
	if (collectionOrView?.icon && icon) return icon;
	let pathname = slugify(("singularName" in collectionOrView ? collectionOrView.singularName : void 0) ?? collectionOrView.name).replace(/-/g, "_");
	let key;
	if (pathname in iconKeysMap) key = iconKeysMap[pathname];
	if (!key) {
		pathname = slugify(collectionOrView.slug).replace(/-/g, "_");
		if (pathname in iconKeysMap) key = iconKeysMap[pathname];
	}
	const iconsCount = coolIconKeys.length;
	if (!key) key = coolIconKeys[hashString(collectionOrView.slug) % iconsCount];
	const sizeInPx = typeof size === "number" ? size : iconSize[size];
	return /* @__PURE__ */ jsx(LucideIconByName, {
		name: key,
		size: sizeInPx,
		className: cls(color ? colorClassesMapping[color] : "", "select-none shrink-0", className)
	});
}, (prevProps, nextProps) => {
	return deepEqual(prevProps.collectionOrView?.icon, nextProps.collectionOrView?.icon) && prevProps.collectionOrView?.name === nextProps.collectionOrView?.name && prevProps.collectionOrView?.slug === nextProps.collectionOrView?.slug && prevProps.collectionOrView?.singularName === nextProps.collectionOrView?.singularName && deepEqual(prevProps.color, nextProps.color) && prevProps.className === nextProps.className && prevProps.size === nextProps.size;
});
//#endregion
//#region src/util/createFormexStub.tsx
function createFormexStub(values) {
	const errorMessage = "You are in a read-only context. You cannot modify the formex controller.";
	return {
		debugId: "",
		values,
		initialValues: values,
		touched: {},
		dirty: false,
		errors: {},
		submitCount: 0,
		isSubmitting: false,
		isValidating: false,
		version: 0,
		canUndo: false,
		canRedo: false,
		setValues: () => {
			throw new Error(errorMessage);
		},
		setTouched(touched) {
			throw new Error(errorMessage);
		},
		setFieldValue: () => {
			throw new Error(errorMessage);
		},
		setFieldTouched: () => {
			throw new Error(errorMessage);
		},
		setDirty: () => {
			throw new Error(errorMessage);
		},
		setSubmitCount: () => {
			throw new Error(errorMessage);
		},
		setFieldError: () => {
			throw new Error(errorMessage);
		},
		handleChange: () => {
			throw new Error(errorMessage);
		},
		handleBlur: () => {
			throw new Error(errorMessage);
		},
		handleSubmit: () => {
			throw new Error(errorMessage);
		},
		validate: () => {
			throw new Error(errorMessage);
		},
		resetForm: () => {
			throw new Error(errorMessage);
		},
		setSubmitting: () => {
			throw new Error(errorMessage);
		},
		undo: () => {
			throw new Error(errorMessage);
		},
		redo: () => {
			throw new Error(errorMessage);
		}
	};
}
//#endregion
//#region src/util/entity_cache.ts
var LOCAL_STORAGE_PREFIX = "entity_cache::";
/**
* The in-session handoff channel: an edit in flight, parked here by the layout
* that is being replaced and picked up by the one taking over — the split's
* "hide list", full screen's "show list", the side panel's "open full screen".
* A record loaded from it opens *dirty*, because that is what it is for.
*
* Deliberately **not** hydrated from `sessionStorage`, and it is the one thing
* here that isn't. The persisted half of this file is the local-changes backup:
* a draft that outlives a reload, offered back through the banner or applied on
* open, according to the collection's `localChangesBackup`. Loading those
* drafts into this map on startup made every one of them look like a handoff —
* the first visit to the record after a reload silently opened dirty carrying a
* draft the user had walked away from, and the banner that exists to ask about
* it never appeared.
*/
var entityCache = /* @__PURE__ */ new Map();
var isSessionStorageAvailable = typeof sessionStorage !== "undefined";
function customReplacer(key) {
	const value = this[key];
	if (value instanceof Date) return {
		__type: "Date",
		value: value.toISOString()
	};
	if (value instanceof EntityReference) return {
		__type: "EntityReference",
		id: value.id,
		path: value.path,
		driver: value.driver,
		databaseId: value.databaseId
	};
	if (value instanceof EntityRelation) return {
		__type: "EntityRelation",
		id: value.id,
		path: value.path,
		data: value.data
	};
	if (value instanceof GeoPoint) return {
		__type: "GeoPoint",
		latitude: value.latitude,
		longitude: value.longitude
	};
	if (value instanceof Vector) return {
		__type: "Vector",
		value: value.value
	};
	return value;
}
function customReviver(_key, value) {
	if (value && typeof value === "object" && "__type" in value) {
		const record = value;
		switch (record.__type) {
			case "date":
			case "Date": return new Date(record.value);
			case "reference":
			case "EntityReference": return new EntityReference({
				id: record.id,
				path: record.path,
				driver: record.driver,
				databaseId: record.databaseId
			});
			case "relation":
			case "EntityRelation": return new EntityRelation(record.id, record.path, record.data);
			case "GeoPoint": return new GeoPoint(record.latitude, record.longitude);
			case "Vector": return new Vector(record.value);
			default: return value;
		}
	}
	return value;
}
/**
* Saves data to the local-changes backup in `sessionStorage`.
*
* The backup only — a draft that survives a reload is not an edit in flight.
* {@link saveEntityToMemoryCache} is the other channel; see {@link entityCache}
* for what separates them.
* @param path - The unique path/key for the data.
* @param data - The data to cache and persist.
*/
function saveEntityToCache(path, data) {
	if (isSessionStorageAvailable) try {
		const key = LOCAL_STORAGE_PREFIX + path;
		const entityString = JSON.stringify(data, customReplacer);
		sessionStorage.setItem(key, entityString);
	} catch (error) {
		console.error(`Failed to save entity for path "${path}" to sessionStorage:`, error);
	}
}
/**
* Consumes a handoff. The channel is one-shot: whoever picks an edit up owns it
* from then on, and an edit left behind here is one that reopens a record dirty
* long after the user stopped looking at it.
*/
function removeEntityFromMemoryCache(path) {
	entityCache.delete(path);
}
/**
* Parks an edit in flight for the layout about to take over. See
* {@link entityCache}: this is the handoff, not the backup.
*/
function saveEntityToMemoryCache(path, data) {
	entityCache.set(path, data);
}
function getEntityFromMemoryCache(path) {
	return entityCache.get(path);
}
/**
* Retrieves a entity from the local-changes backup in `sessionStorage`.
*
* The backup only — the handoff channel is read through
* {@link getEntityFromMemoryCache}, and the two mean different things to the
* form that receives them.
* @param path - The unique path/key for the entity.
* @returns The cached entity or `undefined` if not found.
*/
function getEntityFromCache(path) {
	if (isSessionStorageAvailable) try {
		const key = LOCAL_STORAGE_PREFIX + path;
		const entityString = sessionStorage.getItem(key);
		if (entityString) return JSON.parse(entityString, customReviver);
	} catch (error) {
		console.error(`Failed to load entity for path "${path}" from sessionStorage:`, error);
	}
}
/**
* Removes a entity from both the in-memory cache and `sessionStorage`.
* @param path - The unique path/key for the entity to remove.
*/
function removeEntityFromCache(path) {
	console.debug("Removing entity from cache", path);
	entityCache.delete(path);
	if (isSessionStorageAvailable) try {
		const key = LOCAL_STORAGE_PREFIX + path;
		sessionStorage.removeItem(key);
	} catch (error) {
		console.error(`Failed to remove entity for path "${path}" from sessionStorage:`, error);
	}
}
function flattenKeys(obj, prefix = "", result = []) {
	if (isObject(obj) || Array.isArray(obj)) if (!isPlainObject(obj) && prefix) result.push(prefix);
	else {
		const record = obj;
		for (const key in record) if (Object.prototype.hasOwnProperty.call(record, key)) {
			const newKey = prefix ? Array.isArray(obj) ? `${prefix}[${key}]` : `${prefix}.${key}` : key;
			const val = record[key];
			if (isObject(val) || Array.isArray(val)) flattenKeys(val, newKey, result);
			else result.push(newKey);
		}
	}
	return result;
}
//#endregion
//#region src/util/useStorageUploadController.tsx
function useStorageUploadController({ entityId, entityValues, path, value, property, propertyKey, storageSource, storageSourceRegistry, disabled, onChange }) {
	useAuthController();
	const storageSources = useStorageSources();
	const storage = property.type === "string" ? property.storage : property.type === "array" && property.of.type === "string" ? property.of.storage : void 0;
	const resolvedStorageSource = useMemo(() => resolveStorageSource({
		sourceKey: storage?.storageSource === void 0 ? void 0 : resourceKeyOf(storage.storageSource),
		registry: storageSourceRegistry,
		sources: storageSources.sources,
		defaultSource: storageSource
	}), [
		storage?.storageSource,
		storageSourceRegistry,
		storageSources.sources,
		storageSource
	]);
	const multipleFilesSupported = property.type === "array";
	if (!storage) throw Error("Storage meta must be specified");
	const processFile = storage?.processFile;
	const metadata = storage?.metadata;
	const size = "medium";
	const imageResize = storage?.imageResize;
	const internalInitialValue = getInternalInitialValue(multipleFilesSupported, value, metadata, size);
	const [initialValue, setInitialValue] = useState(value);
	const [internalValue, setInternalValue] = useState(internalInitialValue);
	useEffect(() => {
		if (!deepEqual(initialValue, value)) {
			setInitialValue(value);
			setInternalValue(internalInitialValue);
		}
	}, [
		internalInitialValue,
		value,
		initialValue
	]);
	const fileNameBuilder = useCallback(async (file) => {
		if (storage.fileName) {
			const fileName = await resolveStorageFilenameString({
				input: storage.fileName,
				storage,
				values: entityValues,
				entityId,
				path,
				property,
				file,
				propertyKey
			});
			if (!fileName || fileName.length === 0) throw Error("You need to return a valid filename");
			return fileName;
		}
		return randomString() + "_" + file.name;
	}, [
		entityId,
		entityValues,
		path,
		property,
		propertyKey,
		storage
	]);
	return {
		internalValue,
		setInternalValue,
		storage,
		fileNameBuilder,
		storagePathBuilder: useCallback((file) => {
			return resolveStoragePathString({
				input: storage.storagePath,
				storage,
				values: entityValues,
				entityId,
				path,
				property,
				file,
				propertyKey
			}) ?? "/";
		}, [
			entityId,
			entityValues,
			path,
			property,
			propertyKey,
			storage
		]),
		onFileUploadComplete: useCallback(async (uploadedPath, entry, metadata, uploadedUrl) => {
			console.debug("onFileUploadComplete", uploadedPath, entry);
			let uploadPathOrDownloadUrl = uploadedPath;
			if (storage.includeBucketUrl) if (!uploadedUrl) console.warn("includeBucketUrl is set but no fully-qualified storage URL was returned by the StorageSource. Falling back to the storage path.");
			else uploadPathOrDownloadUrl = uploadedUrl;
			if (storage.storeUrl) uploadPathOrDownloadUrl = (await resolvedStorageSource.getSignedUrl(uploadedPath)).url;
			if (storage.postProcess && uploadPathOrDownloadUrl) uploadPathOrDownloadUrl = await storage.postProcess(uploadPathOrDownloadUrl);
			if (!uploadPathOrDownloadUrl) {
				console.warn("uploadPathOrDownloadUrl is null");
				return;
			}
			let newValue;
			entry.storagePathOrDownloadUrl = uploadPathOrDownloadUrl;
			entry.metadata = metadata;
			newValue = [...internalValue];
			newValue = removeDuplicates(newValue);
			setInternalValue(newValue);
			const fieldValue = newValue.filter((e) => !!e.storagePathOrDownloadUrl).map((e) => e.storagePathOrDownloadUrl);
			if (multipleFilesSupported) onChange(fieldValue);
			else onChange(fieldValue ? fieldValue[0] : null);
		}, [
			internalValue,
			multipleFilesSupported,
			onChange,
			storage,
			resolvedStorageSource
		]),
		onFileUploadError: useCallback((entry) => {
			console.debug("onFileUploadError", entry);
			const newValue = internalValue.filter((item) => item.id !== entry.id);
			setInternalValue(newValue);
		}, [internalValue]),
		onFilesAdded: useCallback(async (acceptedFiles) => {
			if (!acceptedFiles.length || disabled) return;
			if (processFile) try {
				acceptedFiles = await Promise.all(acceptedFiles.map(async (file) => {
					const processedFile = await processFile(file);
					if (!processedFile) return file;
					return processedFile;
				}));
			} catch (e) {
				console.error("Error processing file with custom code. Attempting to continue uploading.", e);
			}
			let newInternalValue;
			if (multipleFilesSupported) newInternalValue = [...internalValue, ...await Promise.all(acceptedFiles.map(async (file) => {
				if (imageResize && isImageFile(file)) file = await resizeImage(file, imageResize);
				return {
					id: getRandomId(),
					file,
					fileName: await fileNameBuilder(file),
					metadata,
					size
				};
			}))];
			else {
				let file = acceptedFiles[0];
				if (imageResize && isImageFile(file)) file = await resizeImage(file, imageResize);
				newInternalValue = [{
					id: getRandomId(),
					file,
					fileName: await fileNameBuilder(file),
					metadata,
					size
				}];
			}
			newInternalValue = removeDuplicates(newInternalValue);
			setInternalValue(newInternalValue);
		}, [
			disabled,
			fileNameBuilder,
			internalValue,
			metadata,
			multipleFilesSupported,
			size,
			imageResize
		]),
		multipleFilesSupported,
		/** The resolved StorageSource for this property — may differ from the
		*  default context source when `StorageConfig.storageSource` is set. */
		resolvedStorageSource
	};
}
function getInternalInitialValue(multipleFilesSupported, value, metadata, size) {
	let strings = [];
	if (multipleFilesSupported) {
		if (Array.isArray(value) && value.every((v) => typeof v === "string")) strings = value ?? [];
	} else if (typeof value === "string") strings = value ? [value] : [];
	return strings.map((entry) => ({
		id: getRandomId(),
		storagePathOrDownloadUrl: entry,
		metadata,
		size
	}));
}
function removeDuplicates(items) {
	return items.filter((item, i) => {
		return (items.map((v) => v.storagePathOrDownloadUrl).indexOf(item.storagePathOrDownloadUrl) === i || !item.storagePathOrDownloadUrl) && (items.map((v) => v.file).indexOf(item.file) === i || !item.file);
	});
}
function getRandomId() {
	return Math.floor(Math.random() * Math.floor(Number.MAX_SAFE_INTEGER));
}
/**
* Check if a file is an image type supported for resizing
*/
function isImageFile(file) {
	return file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp";
}
/**
* Resize and compress an image using compressorjs.
*/
async function resizeImage(file, imageResize) {
	const maxWidth = imageResize?.maxWidth;
	const maxHeight = imageResize?.maxHeight;
	const quality = (imageResize?.quality ?? 80) / 100;
	const mode = imageResize?.mode ?? "contain";
	let mimeType = file.type;
	if (imageResize?.format && imageResize.format !== "original") mimeType = `image/${imageResize.format}`;
	return new Promise((resolve, reject) => {
		new Compressor(file, {
			quality,
			maxWidth,
			maxHeight,
			mimeType,
			...mode === "cover" || mode === void 0 ? {
				width: maxWidth,
				height: maxHeight,
				resize: "cover"
			} : {},
			success: (result) => {
				resolve(new File([result], file.name, {
					type: result.type,
					lastModified: Date.now()
				}));
			},
			error: reject
		});
	});
}
//#endregion
//#region src/util/enums.ts
function getColorScheme(enumValues, key) {
	const labelOrConfig = getLabelOrConfigFrom(enumValues, key);
	if (!labelOrConfig?.color) return getColorSchemeForSeed(key.toString());
	if (typeof labelOrConfig === "object" && "color" in labelOrConfig) {
		if (typeof labelOrConfig.color === "string") return getColorSchemeForKey(labelOrConfig.color);
		if (typeof labelOrConfig.color === "object") return labelOrConfig.color;
	}
}
function isEnumValueDisabled(labelOrConfig) {
	return typeof labelOrConfig === "object" && labelOrConfig.disabled;
}
function buildEnumLabel(labelOrConfig) {
	if (labelOrConfig === void 0) return void 0;
	if (typeof labelOrConfig === "object") return labelOrConfig.label;
	else return labelOrConfig;
}
//#endregion
//#region src/util/constants.ts
var STUDIO_NAVIGATION_GROUPS = [
	"Database",
	"Compute",
	"API",
	"Storage",
	"Access Control"
];
//#endregion
//#region src/internal/common.tsx
/**
* Layout width primitives used by the framework's own side-panel and form
* dialog sizing logic.
*
* @internal Not part of the stable public API — exported only because
* `@rebasepro/cms` and `@rebasepro/studio` need them to compute the same
* dialog widths as the framework. Do not depend on these for app code; they
* may change shape or be removed without a major version bump.
*/
var CONTAINER_FULL_WIDTH = "100vw";
/** @internal See {@link CONTAINER_FULL_WIDTH}. */
var FORM_CONTAINER_WIDTH = "768px";
/**
* How wide the side panel opens when neither the caller nor the collection
* says otherwise.
*
* Sized from what it has to hold rather than from a round number: the form's
* content column plus its metadata rail plus gutters, which is also the width
* at which the form stops being a single stack and picks up the same
* two-column layout it has in full screen. Capped against the viewport so a
* panel never becomes the whole window on a laptop — at that point you want
* full screen, and there is a button for it.
*
* @internal See {@link CONTAINER_FULL_WIDTH}.
*/
var SIDE_PANEL_DEFAULT_WIDTH = "min(1160px, 88vw)";
//#endregion
//#region src/internal/useRestoreScroll.tsx
var scrollsMap = {};
/**
* Restores per-route scroll position on a scrollable container, keyed by
* router location. Used by the framework's own home page implementations.
*
* @internal Not part of the stable public API — exported only because
* `@rebasepro/cms` and `@rebasepro/studio` reuse it in their home page
* components. Its behavior (module-level scroll cache, router coupling) is
* an implementation detail and may change without a major version bump.
*/
function useRestoreScroll() {
	const location = useLocation();
	const containerRef = useRef(null);
	const [scroll, setScroll] = React.useState(0);
	const [direction, setDirection] = React.useState("down");
	const prevScrollRef = useRef(0);
	const handleScroll = useCallback(() => {
		if (!containerRef.current || !location.key) return;
		const scrollTop = containerRef.current.scrollTop;
		scrollsMap[location.key] = scrollTop;
		setScroll(scrollTop);
		setDirection(scrollTop > prevScrollRef.current ? "down" : "up");
		prevScrollRef.current = scrollTop;
	}, [location.key]);
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		container.addEventListener("scroll", handleScroll, { passive: true });
		return () => {
			if (container) container.removeEventListener("scroll", handleScroll);
		};
	}, [handleScroll]);
	useEffect(() => {
		const savedScroll = scrollsMap[location.key];
		if (!containerRef.current || !savedScroll) return;
		const timeoutId = setTimeout(() => {
			if (!containerRef.current) return;
			containerRef.current.scrollTo({
				top: savedScroll,
				behavior: "auto"
			});
		}, 0);
		return () => clearTimeout(timeoutId);
	}, [location.key]);
	return {
		containerRef,
		scroll,
		direction
	};
}
//#endregion
//#region src/components/UnsavedChangesDialog.tsx
function UnsavedChangesDialog({ open, handleOk, handleCancel, body, title }) {
	const { t } = useTranslation();
	return /* @__PURE__ */ jsxs(Dialog, {
		onEscapeKeyDown: () => {
			handleCancel();
		},
		open,
		children: [
			/* @__PURE__ */ jsx(DialogTitle, {
				variant: "h6",
				children: title
			}),
			/* @__PURE__ */ jsx(DialogContent, { children: /* @__PURE__ */ jsx(Typography, { children: body ?? t("unsaved_changes") }) }),
			/* @__PURE__ */ jsxs(DialogActions, { children: [/* @__PURE__ */ jsxs(Button, {
				variant: "text",
				onClick: handleCancel,
				autoFocus: true,
				children: [
					" ",
					t("cancel"),
					" "
				]
			}), /* @__PURE__ */ jsxs(Button, {
				onClick: handleOk,
				children: [
					" ",
					t("ok"),
					" "
				]
			})] })
		]
	});
}
//#endregion
//#region src/hooks/useStudioBridge.tsx
var NOOP_BRIDGE = {
	collectionRegistry: {
		getCollection: () => void 0,
		getRawCollection: () => void 0,
		getParentReferencesFromPath: () => [],
		getParentCollectionSlugs: () => [],
		getParentEntityIds: () => [],
		convertIdsToPaths: () => [],
		initialised: false
	},
	schemaEditing: {
		available: false,
		updateCollection: async () => {
			throw new Error("No collection editor is mounted — check `available` before calling this.");
		}
	},
	sidePanelController: {
		open: () => {},
		replace: () => {},
		close: () => {}
	},
	urlController: {
		basePath: "/",
		baseCollectionPath: "/c",
		urlPathToDataPath: () => "",
		homeUrl: "/",
		isUrlCollectionPath: () => false,
		buildUrlCollectionPath: () => "",
		buildAppUrlPath: () => "",
		resolveDatabasePathsFrom: () => "",
		navigate: () => {}
	},
	navigationState: {
		loading: false,
		refreshNavigation: () => {}
	},
	breadcrumbs: {
		breadcrumbs: [],
		set: () => {}
	},
	capabilities: { codebase: true }
};
var StudioBridgeContext = createContext(NOOP_BRIDGE);
/**
* Provider that injects admin capabilities into Studio.
* Accepts partial overrides — any field not provided falls back to noop.
*
* Usage (in app wiring, when the admin is present):
* ```tsx
* <StudioBridgeProvider value={{
*     collectionRegistry: useCollectionRegistryController(),
*     sidePanelController: useSidePanel(),
*     urlController: useUrlController(),
*     navigationState: useNavigationStateController(),
*     breadcrumbs: useBreadcrumbsController(),
* }}>
*     <RebaseStudio ... />
* </StudioBridgeProvider>
* ```
*/
function StudioBridgeProvider({ value, children }) {
	const merged = React.useMemo(() => ({
		...NOOP_BRIDGE,
		...value
	}), [
		value.collectionRegistry,
		value.sidePanelController,
		value.urlController,
		value.navigationState,
		value.breadcrumbs,
		value.capabilities
	]);
	return /* @__PURE__ */ jsx(StudioBridgeContext.Provider, {
		value: merged,
		children
	});
}
/**
* Source editing through the admin's plan/apply dialog. `available` is false
* when no collection editor is mounted.
*/
function useStudioSchemaEditing() {
	return useContext(StudioBridgeContext).schemaEditing;
}
/** Collection registry — returns noop if the admin is not present. */
function useStudioCollectionRegistry() {
	return useContext(StudioBridgeContext).collectionRegistry;
}
/** Side panel controller — returns noop if the admin is not present. */
function useStudioSidePanelController() {
	return useContext(StudioBridgeContext).sidePanelController;
}
/** URL controller — returns noop if the admin is not present. */
function useStudioUrlController() {
	return useContext(StudioBridgeContext).urlController;
}
/** Navigation state — returns noop if the admin is not present. */
function useStudioNavigationState() {
	return useContext(StudioBridgeContext).navigationState;
}
/** Breadcrumbs controller — returns noop if the admin is not present. */
function useStudioBreadcrumbs() {
	return useContext(StudioBridgeContext).breadcrumbs;
}
/** What the host can do — see {@link StudioCapabilities}. */
function useStudioCapabilities() {
	return useContext(StudioBridgeContext).capabilities;
}
var StudioBridgeRegistryContext = createContext(null);
/**
* Provider that creates a self-assembling bridge.
*
* Mount this above the controller providers. Each controller calls
* `useBridgeRegistration(key, value)` to inject its implementation.
* The bridge context value is automatically kept in sync.
*
* ```tsx
* <StudioBridgeRegistryProvider>
*     <CollectionRegistryProvider>   // auto-registers
*     <SidePanelProvider>           // auto-registers
*     <UrlProvider>                  // auto-registers
*         <RebaseStudio />           // consumes bridge
*     </UrlProvider>
*     </SidePanelProvider>
*     </CollectionRegistryProvider>
* </StudioBridgeRegistryProvider>
* ```
*/
function StudioBridgeRegistryProvider({ children }) {
	const [version, setVersion] = useState(0);
	const slicesRef = useRef({});
	const register = useCallback((key, value) => {
		slicesRef.current[key] = value;
		setVersion((v) => v + 1);
	}, []);
	const unregister = useCallback((key) => {
		delete slicesRef.current[key];
		setVersion((v) => v + 1);
	}, []);
	const registry = useMemo(() => ({
		register,
		unregister
	}), [register, unregister]);
	const bridgeValue = useMemo(() => ({
		...NOOP_BRIDGE,
		...slicesRef.current
	}), [version]);
	return /* @__PURE__ */ jsx(StudioBridgeRegistryContext.Provider, {
		value: registry,
		children: /* @__PURE__ */ jsx(StudioBridgeContext.Provider, {
			value: bridgeValue,
			children
		})
	});
}
//#endregion
//#region src/hooks/useBridgeRegistration.tsx
/**
* Registers a value into the self-assembling Studio bridge.
*
* Each controller (collectionRegistry, sidePanel, url, navigation, breadcrumbs)
* calls this hook on mount to inject its real implementation into the bridge.
* On unmount the slice is automatically removed, reverting to the noop default.
*
* Usage:
* ```tsx
* function SomeProvider({ children }) {
*     const controller = useBuildSomeController();
*     useBridgeRegistration("sidePanelController", controller);
*     return <SomeContext.Provider value={controller}>{children}</SomeContext.Provider>;
* }
* ```
*/
function useBridgeRegistration(key, value) {
	const registry = useContext(StudioBridgeRegistryContext);
	useEffect(() => {
		if (!registry) return;
		registry.register(key, value);
		return () => registry.unregister(key);
	}, [
		registry,
		key,
		value
	]);
}
//#endregion
//#region src/collections/collection_view_config.ts
function resolveDefaultSelectedView(defaultSelectedView, params) {
	if (!defaultSelectedView) return;
	else if (typeof defaultSelectedView === "string") return defaultSelectedView;
	else return defaultSelectedView(params);
}
function getLocalChangesBackup(collection) {
	if (!collection.localChangesBackup) return "manual_apply";
	return collection.localChangesBackup;
}
/**
* Returns the primary keys for a entity collection by inspecting the properties
* and finding any properties with `isId`.
* Fallbacks to `["id"]` if no properties are marked as `isId: true`.
* @param collection
*/
/**
* A collection's child collections, as the panel's view model.
*
* `getSubcollections` in `@rebasepro/common` returns the contract type, so the
* children of a resolved collection arrived un-flattened and reading
* `child.icon` or `child.hideFromNavigation` failed. Resolving is not recursive
* on purpose — children are fetched lazily through a thunk
* (`childCollections`/`subcollections`), so walking the whole tree eagerly to
* flatten it would defeat that.
*/
function getAdminSubcollections(collection) {
	return getAdminEntityChildViews(collection).map((view) => view.collection);
}
/**
* A collection's child views as the panel's view model, keeping the `source`
* that says what kind of list each one is.
*
* The tabs need it: on a junction-backed relation the parent owns the *link*,
* so the destructive action is a removal from this record, not a delete. Every
* caller that renders a tab should prefer this over
* {@link getAdminSubcollections}, which flattens that distinction away.
*/
function getAdminEntityChildViews(collection) {
	return (getEntityChildViews(collection) ?? []).map((view) => ({
		key: view.key,
		collection: resolveAdminCollection(view.collection),
		source: view.source
	}));
}
//#endregion
//#region src/collections/entity-display.ts
/**
* What the collection declares for a role, before deciding which form it is.
*
* One field states it: `display[role]`. `admin.titleProperty` used to be a
* second way to say `display.title` and is gone — a role with two spellings is
* a role with two readers, and this one had grown seven.
*/
function getDeclaredSource(collection, role) {
	const declared = collection.display?.[role];
	return declared === void 0 ? void 0 : declared;
}
/**
* The property path a role is declared to read, when it is declared as a path.
*
* Returns `undefined` for a role filled by a resolver — a resolver has no key —
* and for a role the collection says nothing about, which is then derived.
*
* @group Collections
*/
function getDisplayPropertyKey(collection, role) {
	const declared = getDeclaredSource(collection, role);
	return typeof declared === "string" ? declared : void 0;
}
/**
* The resolver a role is declared to use, when it is declared as one.
*
* @group Collections
*/
function getDisplayResolver(collection, role) {
	const declared = getDeclaredSource(collection, role);
	return typeof declared === "function" ? declared : void 0;
}
/**
* True when the collection states this role at all, in either form.
*
* The derivation is a guess about what a collection probably means; a statement
* outranks it, and the heuristics that look for "the first enum" or "the leading
* relation" have to stand down when one exists.
*
* @group Collections
*/
function hasDeclaredDisplay(collection, role) {
	return getDeclaredSource(collection, role) !== void 0;
}
//#endregion
//#region src/collections/entity_image_preview.ts
/**
* The property that fills a record's image slot.
*
* `admin.display.image` first, then six fallbacks in descending confidence —
* the first image-typed storage property, an array of them, a URL rendered as an
* image, and so on. The ladder stays for collections that say nothing; a
* collection that names its picture is not guessed at.
*/
function getEntityImagePreviewPropertyKey(collection) {
	const declared = getDisplayPropertyKey(collection, "image");
	if (declared) return declared;
	for (const key in collection.properties) {
		const property = collection.properties[key];
		if (property.type === "string" && property.storage?.acceptedFiles?.includes("image/*")) return key;
	}
	for (const key in collection.properties) {
		const property = collection.properties[key];
		if (property.type === "array" && !Array.isArray(property.of) && property.of?.type === "string" && property.of.storage?.acceptedFiles?.includes("image/*")) return key;
	}
	for (const key in collection.properties) {
		const property = collection.properties[key];
		if (property.type === "string" && property.admin?.urlPreview === "image") return key;
	}
	for (const key in collection.properties) {
		const property = collection.properties[key];
		if (property.type === "array" && property.of && !Array.isArray(property.of) && property.of.type === "string" && property.of.admin?.urlPreview === "image") return key;
	}
	for (const key in collection.properties) {
		const property = collection.properties[key];
		if (property.type === "string" && property.storage && !property.storage.acceptedFiles) return key;
	}
	for (const key in collection.properties) {
		const property = collection.properties[key];
		if (property.type === "array" && !Array.isArray(property.of) && property.of?.type === "string" && property.of.storage && !property.of.storage.acceptedFiles) return key;
	}
}
//#endregion
//#region src/collections/entity-display-cache.ts
/** The identity of one role of one record, as a cache key. */
function entityDisplayKey(path, entityId, role) {
	return `${role} ${path} ${entityId ?? ""}`;
}
var EntityDisplayCache = class {
	entries = /* @__PURE__ */ new Map();
	listeners = /* @__PURE__ */ new Set();
	/**
	* The resolved value, or `undefined` when this pair has not been resolved
	* yet. `null` is a resolved absence, and the two must stay distinct: a
	* caller that reads "not yet" as "nothing" flickers its fallback in on every
	* mount.
	*/
	peek(key) {
		const entry = this.entries.get(key);
		if (!entry || "promise" in entry) return void 0;
		return entry.value;
	}
	/** True while a resolution for this pair is in flight. */
	isLoading(key) {
		const entry = this.entries.get(key);
		return Boolean(entry && "promise" in entry);
	}
	/**
	* Resolve once per record and role. Concurrent callers share the first
	* call's promise; later callers get the cached value with no promise at all.
	*
	* A resolver that throws is recorded as "nothing" rather than retried: the
	* alternative is every render re-running a call that just failed. And it is
	* reported here, which is a correction.
	*
	* It used to say "the caller that saw the rejection is the one that logs
	* it", and `useEntityDisplay` duly attached a `.catch()` that warned. But
	* both failure paths below swallow and return a *resolved* promise, so that
	* catch could never run — the two halves each did the reasonable thing and
	* between them the log was unreachable. A resolver that blew up produced a
	* blank chip and total silence, which is the failure mode
	* `EntityDisplayResolver`'s own contract ("treated as `undefined` and logged
	* once") exists to rule out.
	*
	* Reporting belongs here for the reason the caller could not do it: this is
	* the one place that runs exactly once per key, so "once" is a property of
	* the code rather than a hope about how many components mount.
	*/
	resolve(key, resolver) {
		const entry = this.entries.get(key);
		if (entry) return "promise" in entry ? entry.promise : Promise.resolve(entry.value);
		let produced;
		try {
			produced = resolver();
		} catch (error) {
			this.fail(key, error);
			return Promise.resolve(null);
		}
		if (!isPromise(produced)) {
			const value = normalize(produced);
			this.set(key, value);
			return Promise.resolve(value);
		}
		const promise = produced.then((resolved) => {
			const value = normalize(resolved);
			this.set(key, value);
			return value;
		}).catch((error) => {
			this.fail(key, error);
			return null;
		});
		this.entries.set(key, { promise });
		return promise;
	}
	/**
	* Drop what is known about a record, so the next ask resolves again. Called
	* after a write: the row that just saved may be called something else now.
	*/
	invalidate(path, entityId) {
		const suffix = ` ${path} ${entityId ?? ""}`;
		let changed = false;
		for (const key of [...this.entries.keys()]) if (entityId === void 0 ? key.includes(` ${path} `) || key.endsWith(` ${path} `) : key.endsWith(suffix)) {
			this.entries.delete(key);
			changed = true;
		}
		if (changed) this.emit();
	}
	/** Drop everything. The user signed out, or the app swapped datasource. */
	clear() {
		if (this.entries.size === 0) return;
		this.entries.clear();
		this.emit();
	}
	subscribe(listener) {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}
	set(key, value) {
		this.entries.set(key, { value });
		this.emit();
	}
	/**
	* Record a failed resolution as "nothing", and say so once.
	*
	* The key is the message: it is `<role> <path> <id>`, which is exactly what a
	* reader needs to find the resolver that blew up. A warning with no key would
	* tell them a display resolver failed somewhere in a list of fifty rows.
	*
	* `console.warn` rather than a thrown error, because this runs while a row is
	* rendering: the contract is that a title which cannot be fetched must not
	* take down the row that shows it.
	*/
	fail(key, error) {
		console.warn(`[rebase] Could not resolve display value for ${key}:`, error);
		this.set(key, null);
	}
	emit() {
		for (const listener of [...this.listeners]) listener();
	}
};
/**
* Empty strings and empty arrays are absences, not values — a title of `"  "`
* would otherwise beat the derived one and render as a blank heading.
*/
function normalize(value) {
	if (value === void 0 || value === null) return null;
	if (typeof value === "string") {
		const trimmed = value.trim();
		return trimmed.length > 0 ? trimmed : null;
	}
	if (Array.isArray(value)) return value.length > 0 ? value : null;
	return value;
}
function isPromise(value) {
	return typeof value?.then === "function";
}
//#endregion
//#region src/collections/filter-operator-resolution.ts
/**
* Default operators offered per property type, before engine capabilities and
* per-property narrowing are applied. These mirror what the built-in filter
* fields can render.
*/
var COMPARISON_OPS = [
	"==",
	"!=",
	">",
	">=",
	"<",
	"<="
];
var NULL_CHECK_OPS = ["is-null", "is-not-null"];
var MEMBERSHIP_OPS = ["in", "not-in"];
var PATTERN_OPS = [
	"like",
	"ilike",
	"not-like",
	"not-ilike"
];
var DEFAULT_OPS_BY_TYPE = {
	string: [
		...COMPARISON_OPS,
		...MEMBERSHIP_OPS,
		...PATTERN_OPS,
		...NULL_CHECK_OPS
	],
	number: [
		...COMPARISON_OPS,
		...MEMBERSHIP_OPS,
		...NULL_CHECK_OPS
	],
	date: [...COMPARISON_OPS, ...NULL_CHECK_OPS],
	boolean: [
		"==",
		"!=",
		...NULL_CHECK_OPS
	],
	reference: [
		"==",
		"!=",
		...MEMBERSHIP_OPS,
		...NULL_CHECK_OPS
	],
	relation: [
		"==",
		"!=",
		...MEMBERSHIP_OPS,
		...NULL_CHECK_OPS
	]
};
/** Operators offered when the property is an *array of* a filterable type. */
var ARRAY_OPS = ["array-contains", "array-contains-any"];
/**
* Whether a relation is one the collection's engine can compile into a
* `WHERE`.
*
* Which kinds those are is the *driver's* answer, not this function's, so it
* comes from {@link DataSourceCapabilities.filterableRelationKinds}. The admin
* is one UI over Postgres, MongoDB, Firestore and whatever a developer
* registers, and "a many-to-many compiles to an `EXISTS` over the junction" is
* a fact about the Postgres driver — true today, and not the sort of thing to
* assert on an engine's behalf.
*
* Offering an uncompilable filter is not a cosmetic bug. The Postgres driver
* used to drop a filter key it could not resolve, which *widened* the result
* set — filtering a many-to-many column returned every row instead of none.
* That now fails closed with a 400, which is correct for a real schema drift
* and wrong as the answer to a control the admin itself put on screen.
*
* A relation whose kind cannot be determined at all — no inline `relation`
* block and no stamped `resolvedRelation` — stays filterable. The server
* resolves relations before the config reaches the admin, so this is the
* hand-built-property case, and silently dropping a working filter there would
* be its own regression.
*/
function isFilterableRelation(property, engine) {
	if (property.type !== "relation") return true;
	const relationProperty = property;
	const kind = relationProperty.relation?.kind ?? relationProperty.resolvedRelation?.kind;
	if (!kind) return true;
	return (getDataSourceCapabilities(engine).filterableRelationKinds ?? DEFAULT_FILTERABLE_RELATION_KINDS).includes(kind);
}
/**
* Resolve which filter operators the UI should offer for a property.
*
* The result is the **intersection** of three sets:
* 1. what the engine can execute — {@link DataSourceCapabilities.filterOperators}
*    (e.g. Firestore cannot run the LIKE family);
* 2. what makes sense for the property type (e.g. no `>` on booleans);
* 3. the developer's optional narrowing — `property.admin.filterOperators`.
*
* Returns an empty array when the property is not filterable — by type, by
* developer narrowing (`filterOperators: []`), or because it is a relation the
* query layer has no column for (see {@link isFilterableRelation}).
*
* @group Models
*/
function resolveFilterOperators({ property, isArray, engine }) {
	if (!isFilterableRelation(property, engine)) return [];
	const typeDefaults = isArray ? ARRAY_OPS : DEFAULT_OPS_BY_TYPE[property.type] ?? [];
	if (typeDefaults.length === 0) return [];
	const engineOps = new Set(getDataSourceCapabilities(engine).filterOperators ?? ALL_WHERE_FILTER_OPS);
	const narrowing = property.admin?.filterOperators;
	const narrowingSet = narrowing !== void 0 ? new Set(narrowing) : void 0;
	return typeDefaults.filter((op) => engineOps.has(op) && (narrowingSet === void 0 || narrowingSet.has(op)));
}
//#endregion
//#region src/collections/form-layout.ts
/**
* Columns in the form grid.
*
* A local literal rather than an import of `FORM_GRID_COLUMNS`: everything else
* this module takes from `@rebasepro/cms-types` is a *type*, erased at build
* time, and a runtime value would make this pure function depend on that
* package's built output — which is exactly what broke the dev server, since
* `@rebasepro/app` resolves that package to its dist. `satisfies PropertySpan`
* keeps the two honest: a full-width field and the column count are the same
* number by definition.
*/
var GRID_COLUMNS = 4;
/**
* The most fields a form can hold and still be laid out as one column.
*
* Columns are a density tool: they buy back height on a form long enough to
* scroll. A form this short fits on any surface without scrolling, so pairing
* its fields buys nothing and costs the reading order — the eye zig-zags across
* values that are next to each other only because of their types. The users
* form was the case that showed it: Email beside Name, then Roles alone on the
* row below, in a dialog with room to spare.
*/
var SINGLE_COLUMN_MAX_FIELDS = 4;
/** Does this property's editor need the full width of the column? */
function needsFullWidth(property) {
	switch (property.type) {
		case "map":
		case "binary":
		case "vector": return true;
		case "array": {
			if (property.oneOf) return true;
			const of = Array.isArray(property.of) ? void 0 : property.of;
			if (!of) return true;
			if (of.type !== "string" && of.type !== "number") return true;
			if (of.type === "string" && (of.storage || of.admin?.markdown || of.admin?.multiline)) return true;
			return false;
		}
		case "string": return Boolean(property.storage || property.admin?.markdown || property.admin?.multiline);
		default: return false;
	}
}
/**
* The span a property gets when nothing is declared.
*
* Deliberately coarse: three buckets, chosen by how much room the *editor*
* needs, not by how important the field is. Importance is what `admin.form`
* sections are for.
*/
function deriveSpan(property, isTitleProperty) {
	if (needsFullWidth(property)) return 4;
	if (isTitleProperty) return 4;
	switch (property.type) {
		case "number":
		case "boolean": return 1;
		case "date":
		case "geopoint":
		case "reference":
		case "relation": return 2;
		case "array": return 2;
		case "string": return 2;
		default: return 2;
	}
}
/**
* Can the user still type this id?
*
* A `manual` id is a real field while creating and frozen once the row exists.
* Every generated strategy (`uuid`, `cuid`, a raw SQL default) is never typed.
* Getting this wrong in the "frozen" direction would make manual-id collections
* uncreatable, so it fails towards showing the field.
*/
function isIdPropertyEditable(property, status) {
	if (!("isId" in property) || !property.isId) return true;
	if (status === "existing") return false;
	return property.isId === "manual";
}
function isIdProperty(property) {
	return Boolean(property && "isId" in property && property.isId);
}
/**
* An audit timestamp the database maintains — `created_at`, `updated_at`.
*
* Not a guess from the name: `autoValue` says the column is written by the
* system on create or update, so the field is never typed. Rendering it as an
* input in the middle of the form promised an edit that cannot happen, and the
* record block already shows both values — the same two dates twice on one
* screen.
*/
function isAuditTimestamp(property) {
	return Boolean(property && property.type === "date" && property.autoValue);
}
/**
* Close the trailing gap in each row.
*
* Without this a section of half-width fields with an odd count leaves a hole —
* `name | sku` then `brand | ␣␣` — a field stranded beside half a row of
* nothing, which reads as a mistake rather than a layout. The remainder is
* spread across the row's derived fields rather than dumped on the last one, so
* a lone half-width field becomes full width and a pair grows evenly. Fields
* with an explicit span are never resized.
*/
function fillRows(fields) {
	const rows = [];
	let row = [];
	let used = 0;
	for (const field of fields) {
		if (used + field.span > GRID_COLUMNS && row.length) {
			rows.push(row);
			row = [];
			used = 0;
		}
		row.push(field);
		used += field.span;
	}
	if (row.length) rows.push(row);
	return rows.flatMap((entries) => {
		let gap = GRID_COLUMNS - entries.reduce((sum, f) => sum + f.span, 0);
		if (gap <= 0) return entries;
		const growable = entries.map((f, i) => [f, i]).filter(([f]) => !f.spanExplicit);
		if (!growable.length) return entries;
		const next = [...entries];
		let cursor = 0;
		while (gap > 0) {
			const [, index] = growable[cursor % growable.length];
			next[index] = {
				...next[index],
				span: next[index].span + 1
			};
			gap -= 1;
			cursor += 1;
		}
		return next;
	});
}
function buildField(key, collection, titlePropertyKey) {
	const property = collection.properties?.[key];
	if (!property) return collection.additionalFields?.some((f) => f.key === key) ? {
		key,
		span: 4,
		additional: true
	} : void 0;
	if (isHidden(property)) return void 0;
	const admin = property.admin;
	const explicit = admin?.span !== void 0;
	return {
		key,
		span: admin?.span ?? deriveSpan(property, key === titlePropertyKey),
		additional: false,
		spanExplicit: explicit
	};
}
/**
* Resolve the layout for a collection's generated form.
*
* Never drops a field: anything not named by a section lands in a trailing
* group, so adding a column to the database cannot make it silently invisible
* in the panel.
*/
function resolveFormLayout({ collection, fieldKeys, status }) {
	const config = collection.form;
	const titlePropertyKey = getDisplayPropertyKey(collection, "title");
	const available = /* @__PURE__ */ new Map();
	for (const key of fieldKeys) {
		const field = buildField(key, collection, titlePropertyKey);
		if (field) available.set(key, field);
	}
	const idKeys = [...available.keys()].filter((key) => isIdProperty(collection.properties?.[key]));
	if (idKeys.length === 1) {
		const property = collection.properties?.[idKeys[0]];
		if (!isIdPropertyEditable(property, status)) available.delete(idKeys[0]);
	}
	const showRecordMetaResolved = config?.showRecordMeta ?? !collection.hideIdFromForm;
	if (showRecordMetaResolved) for (const key of [...available.keys()]) {
		const property = collection.properties?.[key];
		if (config?.sidebar?.includes(key)) continue;
		if (isAuditTimestamp(property)) available.delete(key);
	}
	const sidebar = [];
	if (config?.sidebar) for (const key of config.sidebar) {
		const field = available.get(key);
		if (!field) continue;
		sidebar.push(field);
		available.delete(key);
	}
	const showRecordMeta = showRecordMetaResolved;
	const sections = [];
	if (config?.sections?.length) for (const section of config.sections) {
		const fields = [];
		for (const key of section.properties) {
			const field = available.get(key);
			if (!field) continue;
			fields.push(field);
			available.delete(key);
		}
		const titled = Boolean(section.title);
		sections.push({
			key: section.key,
			title: section.title,
			collapsible: section.collapsible ?? titled,
			collapsed: titled ? Boolean(section.collapsed) : false,
			fields,
			readVariant: section.readVariant
		});
	}
	const leftovers = fieldKeys.map((key) => available.get(key)).filter((f) => Boolean(f));
	if (leftovers.length) sections.push({
		key: sections.length ? "__other" : "__main",
		collapsible: false,
		collapsed: false,
		fields: leftovers
	});
	const singleColumn = sections.reduce((sum, section) => sum + section.fields.length, 0) <= SINGLE_COLUMN_MAX_FIELDS;
	return {
		sections: sections.filter((s) => s.fields.length > 0).map((s) => ({
			...s,
			fields: fillRows(singleColumn ? s.fields.map((f) => f.spanExplicit ? f : {
				...f,
				span: GRID_COLUMNS
			}) : s.fields)
		})),
		sidebar,
		showRecordMeta,
		hasRail: sidebar.length > 0 || showRecordMeta
	};
}
//#endregion
//#region src/collections/navigation_utils.ts
function removeInitialAndTrailingSlashes(s) {
	return removeInitialSlash(removeTrailingSlash(s));
}
function removeInitialSlash(s) {
	if (s.startsWith("/")) return s.slice(1);
	else return s;
}
function removeTrailingSlash(s) {
	if (s.endsWith("/")) return s.slice(0, -1);
	else return s;
}
function addInitialSlash(s) {
	if (s.startsWith("/")) return s;
	else return `/${s}`;
}
function getLastSegment(path) {
	const cleanPath = removeInitialAndTrailingSlashes(path);
	if (cleanPath.includes("/")) {
		const segments = cleanPath.split("/");
		return segments[segments.length - 1];
	}
	return cleanPath;
}
function resolveCollectionPathIds(path, allCollections) {
	let remainingPath = removeInitialAndTrailingSlashes(path);
	if (!remainingPath) return "";
	let currentCollections = allCollections;
	const resolvedPathParts = [];
	while (remainingPath.length > 0) {
		if (!currentCollections || currentCollections.length === 0) {
			console.warn(`resolveCollectionPathIds: Path structure implies subcollections, but none found before segment starting with "${remainingPath}" in original path "${path}". Appending remaining original path.`);
			resolvedPathParts.push(remainingPath);
			remainingPath = "";
			break;
		}
		let foundMatch = false;
		const potentialMatches = currentCollections.flatMap((col) => [{
			col,
			match: col.slug
		}]).filter((p) => p.match && remainingPath.startsWith(p.match)).sort((a, b) => b.match.length - a.match.length);
		if (potentialMatches.length > 0) {
			const { col: foundCollection, match: matchString } = potentialMatches[0];
			resolvedPathParts.push(foundCollection.slug);
			remainingPath = removeInitialSlash(remainingPath.substring(matchString.length));
			if (remainingPath.length === 0) {
				foundMatch = true;
				break;
			}
			const idSeparatorIndex = remainingPath.indexOf("/");
			let entityId;
			if (idSeparatorIndex > -1) {
				entityId = remainingPath.substring(0, idSeparatorIndex);
				remainingPath = remainingPath.substring(idSeparatorIndex + 1);
			} else {
				entityId = remainingPath;
				remainingPath = "";
				console.warn(`resolveCollectionPathIds: Path seems to end with a entity ID "${entityId}" instead of a collection segment in original path "${path}". This might indicate an invalid input path.`);
			}
			resolvedPathParts.push(entityId);
			currentCollections = getSubcollections(foundCollection);
			foundMatch = true;
			if (!currentCollections && remainingPath.length > 0) {
				console.warn(`resolveCollectionPathIds: Path continues after entity ID "${entityId}", but no subcollections are defined for the preceding collection "${foundCollection.slug}" in path "${path}". Appending remaining original path.`);
				resolvedPathParts.push(remainingPath);
				remainingPath = "";
				break;
			}
		}
		if (!foundMatch) {
			console.warn(`resolveCollectionPathIds: Collection definition not found for segment starting with "${remainingPath}" in original path "${path}". Appending remaining original path.`);
			resolvedPathParts.push(remainingPath);
			remainingPath = "";
			break;
		}
	}
	return resolvedPathParts.join("/");
}
/**
* Find the corresponding view at any depth for a given path.
* Note that path or segments of the paths can be collection aliases.
* @param slugOrPath
* @param collections
*/
function getCollectionBySlugWithin(slugOrPath, collections) {
	const subpaths = removeInitialAndTrailingSlashes(slugOrPath).split("/");
	if (subpaths.length % 2 === 0) throw Error(`getCollectionBySlug: Collection paths must have an odd number of segments: ${slugOrPath}`);
	const subpathCombinations = getCollectionPathsCombinations(subpaths);
	let result;
	for (let i = 0; i < subpathCombinations.length; i++) {
		const subpathCombination = subpathCombinations[i];
		const navigationEntry = collections && collections.sort((a, b) => (a.slug ?? "").localeCompare(b.slug ?? "")).find((entry) => entry.slug === subpathCombination);
		if (navigationEntry) {
			if (subpathCombination === slugOrPath) result = navigationEntry;
			else if (getSubcollections(navigationEntry).length > 0) {
				const newPath = slugOrPath.replace(subpathCombination, "").split("/").slice(2).join("/");
				if (newPath.length > 0) result = getCollectionBySlugWithin(newPath, getSubcollections(navigationEntry));
			}
		}
		if (result) break;
	}
	return result;
}
/**
* Get the subcollection combinations from a path:
* "sites/es/locales" => ["sites/es/locales", "sites"]
* @param subpaths
*/
function getCollectionPathsCombinations(subpaths) {
	const entries = subpaths.length > 0 && subpaths.length % 2 === 0 ? subpaths.splice(0, subpaths.length - 1) : subpaths;
	const length = entries.length;
	const result = [];
	for (let i = length; i > 0; i = i - 2) result.push(entries.slice(0, i).join("/"));
	return result;
}
//#endregion
//#region src/collections/navigation_from_path.ts
function getNavigationEntriesFromPath(props) {
	const { path, collections = [], currentFullPath } = props;
	const subpathCombinations = getCollectionPathsCombinations(removeInitialAndTrailingSlashes(path).split("/"));
	const result = [];
	for (let i = 0; i < subpathCombinations.length; i++) {
		const subpathCombination = subpathCombinations[i];
		const collection = collections && collections.find((entry) => entry.slug === subpathCombination);
		if (collection) {
			const collectionPath = currentFullPath && currentFullPath.length > 0 ? currentFullPath + "/" + collection.slug : collection.slug;
			result.push({
				type: "collection",
				id: collection.slug,
				slug: collectionPath,
				path: collectionPath,
				collection
			});
			const restOfThePath = removeInitialAndTrailingSlashes(removeInitialAndTrailingSlashes(path).replace(subpathCombination, ""));
			const nextSegments = restOfThePath.length > 0 ? restOfThePath.split("/") : [];
			if (nextSegments.length > 0) {
				const entityId = nextSegments[0];
				const path = collectionPath + "/" + entityId;
				result.push({
					type: "entity",
					entityId,
					slug: collectionPath,
					path,
					parentCollection: collection
				});
				if (nextSegments.length > 1) {
					const newPath = nextSegments.slice(1).join("/");
					if (!collection) throw Error("collection not found resolving path: " + collection);
					const entityViews = collection.entityViews;
					const customView = entityViews && entityViews.map((entry) => resolveEntityView(entry, props.contextEntityViews)).filter((v) => v != null).find((entry) => entry.key === newPath);
					const subcollections = getSubcollections(collection);
					if (customView) result.push({
						type: "custom_view",
						slug: collectionPath,
						entityId,
						path: path + "/" + customView.key,
						view: customView
					});
					else if (subcollections) result.push(...getNavigationEntriesFromPath({
						path: newPath,
						collections: subcollections,
						currentFullPath: path,
						contextEntityViews: props.contextEntityViews
					}));
				}
			}
			break;
		}
	}
	return result;
}
function resolveEntityView(entityView, contextEntityViews) {
	if (typeof entityView === "string") return contextEntityViews?.find((entry) => entry.key === entityView);
	else return entityView;
}
//#endregion
//#region src/collections/parent_references_from_path.ts
function getParentReferencesFromPath(props) {
	const { path, collections = [], currentFullPath } = props;
	const subpathCombinations = getCollectionPathsCombinations(removeInitialAndTrailingSlashes(path).split("/"));
	const result = [];
	for (let i = 0; i < subpathCombinations.length; i++) {
		const subpathCombination = subpathCombinations[i];
		const collection = collections && collections.find((entry) => entry.slug === subpathCombination);
		if (collection) {
			const collectionPath = currentFullPath && currentFullPath.length > 0 ? currentFullPath + "/" + collection.slug : collection.slug;
			const restOfThePath = removeInitialAndTrailingSlashes(removeInitialAndTrailingSlashes(path).replace(subpathCombination, ""));
			const nextSegments = restOfThePath.length > 0 ? restOfThePath.split("/") : [];
			if (nextSegments.length > 0) {
				const entityId = nextSegments[0];
				const path = collectionPath + "/" + entityId;
				result.push(new EntityReference({
					id: entityId,
					path: collectionPath
				}));
				if (nextSegments.length > 1) {
					const newPath = nextSegments.slice(1).join("/");
					if (!collection) throw Error("collection not found resolving path: " + collection);
					if (getSubcollections(collection).length > 0) result.push(...getParentReferencesFromPath({
						path: newPath,
						collections: getSubcollections(collection),
						currentFullPath: path
					}));
				}
			}
			break;
		}
	}
	return result;
}
//#endregion
//#region src/collections/title-property.ts
/**
* How good a property is as the human-readable title of an entity.
* Higher wins; ties are broken by declaration order.
* `DISQUALIFIED` means the property can never be a title.
*/
var SCORE = {
	DISQUALIFIED: -1,
	/**
	* Points at another entity. Only readable once the target resolves, and it
	* renders as the raw key until then — last resort, for collections (like
	* junction tables) that hold no text of their own.
	*/
	REFERENCE: 5,
	RELATION: 10,
	/** A user picker: resolves to a person's name, like a relation does */
	USER: 10,
	/** Long form text — readable, but a description, not a name */
	LONG_TEXT: 20,
	/** A closed set of values: shared by many rows, so it identifies nothing */
	ENUM: 30,
	/** Identifying, but PII and usually secondary to a name */
	EMAIL: 45,
	/** Any plain, short, free text field */
	PLAIN_TEXT: 60,
	/** Free text whose name says it holds the entity's label */
	NAMED: 100
};
/**
* Keys whose name states outright that the property is the entity label.
* This is a *bonus* on top of the structural rules — never a requirement, and
* never the mechanism that keeps identifiers out of the title slot.
*/
var TITLE_LIKE_KEYS = /* @__PURE__ */ new Set([
	"name",
	"fullname",
	"displayname",
	"title",
	"label",
	"heading",
	"subject",
	"username",
	"nickname"
]);
function normalizeKey(key) {
	return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}
function isHidden$1(property) {
	return Boolean(property.admin?.hideFromCollection);
}
/**
* Every column on this collection that stores a foreign key: the `localKey` of
* each owning relation (declared inline on a property or in `relations[]`), the
* source column of a junction, and the key a many-to-many joins on.
*
* These hold another entity's id, so they must never fill the title slot even
* though they are declared as plain strings.
*/
function getForeignKeyColumns(collection) {
	const keys = /* @__PURE__ */ new Set();
	const addRelationKeys = (relation) => {
		switch (relation.kind) {
			case "belongsTo":
				keys.add(relation.localKey ?? generateForeignKeyName(relation.relationName ?? ""));
				break;
			case "manyToMany":
				if (relation.through?.sourceColumn) keys.add(relation.through.sourceColumn);
				break;
			case "via": {
				const from = relation.joinPath?.[0]?.on?.from;
				for (const column of Array.isArray(from) ? from : [from]) if (column) keys.add(column);
				break;
			}
			default: break;
		}
	};
	for (const relation of (isRelationalCollectionConfig(collection) ? collection.relations : void 0) ?? []) addRelationKeys(relation);
	for (const [key, propertyRaw] of Object.entries(collection.properties ?? {})) {
		const property = propertyRaw;
		if (!property || isPropertyBuilder(property) || property.type !== "relation") continue;
		if (property.relation) addRelationKeys({
			...property.relation,
			relationName: property.relation.relationName ?? key
		});
	}
	return keys;
}
/**
* True when the property is declared to hold an opaque identifier rather than
* something a person reads: a primary key, a foreign key, a UUID column, or a
* picker bound to an auth user id. Decided from the property *schema*, never
* from the key name.
*/
function isIdentifierProperty(property, key, idKeys, foreignKeys) {
	if (idKeys.has(key)) return true;
	if (foreignKeys.has(key)) return true;
	if ("isId" in property && property.isId) return true;
	if (property.type === "string" && property.columnType === "uuid") return true;
	return false;
}
function scoreTitleCandidate(property, key, idKeys, foreignKeys) {
	if (isHidden$1(property)) return SCORE.DISQUALIFIED;
	if (isIdentifierProperty(property, key, idKeys, foreignKeys)) return SCORE.DISQUALIFIED;
	if (isStorageProperty(property)) return SCORE.DISQUALIFIED;
	if (property.type === "relation") return (property.resolvedRelation ? property.resolvedRelation.cardinality === "many" : property.relation?.kind === "hasMany" || property.relation?.kind === "manyToMany") ? SCORE.DISQUALIFIED : SCORE.RELATION;
	if (property.type === "reference") return SCORE.REFERENCE;
	if (property.type !== "string") return SCORE.DISQUALIFIED;
	if (property.userSelect) return SCORE.USER;
	if (property.enum) return SCORE.ENUM;
	if (property.admin?.multiline || property.admin?.markdown) return SCORE.LONG_TEXT;
	if (property.email) return SCORE.EMAIL;
	if (TITLE_LIKE_KEYS.has(normalizeKey(key))) return SCORE.NAMED;
	return SCORE.PLAIN_TEXT;
}
/**
* All properties that could serve as the entity title, best first.
*
* Candidates are ranked from the property *schema* — identifiers (primary
* keys, foreign keys, UUID columns, user pickers), images and hidden fields are
* excluded structurally, so a collection whose id column is called something
* other than `id` is handled the same as one where it isn't.
*
* When the collection declares `propertiesOrder` the developer has already
* stated what comes first, so qualifying candidates keep that order; otherwise
* they are ranked (a name beats a description beats a relation).
*
* @group Collections
*/
function getTitlePropertyCandidates(collection) {
	if (!collection.properties) return [];
	const declared = getDisplayPropertyKey(collection, "title");
	if (declared && getPropertyInPath(collection.properties, declared)) return [declared];
	const idKeys = new Set(getPrimaryKeys(collection));
	const foreignKeys = getForeignKeyColumns(collection);
	const explicitOrder = collection.propertiesOrder;
	const order = explicitOrder ?? Object.keys(collection.properties);
	const scored = [];
	order.forEach((key, index) => {
		const property = collection.properties[key];
		if (!property || isPropertyBuilder(property)) return;
		const score = scoreTitleCandidate(property, key, idKeys, foreignKeys);
		if (score === SCORE.DISQUALIFIED) return;
		scored.push({
			key,
			score,
			index
		});
	});
	if (!explicitOrder) scored.sort((a, b) => b.score - a.score || a.index - b.index);
	return scored.map((candidate) => candidate.key);
}
/**
* The relation a collection *leads* with, if it leads with one.
*
* A junction-shaped collection — a pipeline entry, a membership, a booking —
* holds no label of its own: it is "this candidate, for that vacancy". The
* general ranking puts relations last (they only read once the target
* resolves), so the title slot would otherwise fall to whatever free text
* happens to follow: a note, a comment. Preview surfaces that render one row
* per entity (list, cards, board) prefer the leading relation instead.
*
* The decision is structural — the first property that could be a title, in the
* order the developer declared (or ordered) them. Declaration order is a
* statement about what the collection is about; property *names* are not
* consulted here.
*
* Returns `undefined` when that first property is anything but a
* single-cardinality relation, or when the collection states its own
* `display.title`.
*
* @group Collections
*/
function getLeadingRelationTitleKey(collection) {
	if (!collection.properties) return void 0;
	if (hasDeclaredDisplay(collection, "title")) return void 0;
	const idKeys = new Set(getPrimaryKeys(collection));
	const foreignKeys = getForeignKeyColumns(collection);
	const order = collection.propertiesOrder ?? Object.keys(collection.properties);
	for (const key of order) {
		const property = collection.properties[key];
		if (!property || isPropertyBuilder(property)) continue;
		if (scoreTitleCandidate(property, key, idKeys, foreignKeys) === SCORE.DISQUALIFIED) continue;
		return property.type === "relation" ? key : void 0;
	}
}
/**
* The property that should fill the title slot for a collection, ignoring any
* concrete values. Prefer {@link getTitlePropertyKeyForValues} when an entity
* is at hand — it can skip candidates that happen to be empty or to hold an id.
*
* @group Collections
*/
function getTitlePropertyKey(collection) {
	return getTitlePropertyCandidates(collection)[0];
}
var UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var LONG_HEX_REGEX = /^[0-9a-f]{24,}$/i;
var CUID_REGEX = /^c[a-z0-9]{20,}$/i;
/**
* True when a value reads as a machine identifier (UUID, ObjectId/long hex,
* cuid) rather than as something worth showing to a person.
*
* @group Collections
*/
function looksLikeIdentifierValue(value) {
	if (typeof value !== "string") return false;
	const trimmed = value.trim();
	return UUID_REGEX.test(trimmed) || LONG_HEX_REGEX.test(trimmed) || CUID_REGEX.test(trimmed);
}
/**
* The title property for a concrete entity: the best-ranked candidate that
* actually carries a readable value. Candidates that are empty, that repeat the
* entity id, or that hold an opaque identifier are skipped, so a free-text
* column that happens to store UUIDs never ends up as the title.
*
* Returns the top-ranked candidate when none of them has a usable value, so
* callers can still render a placeholder for that property.
*
* @group Collections
*/
function getTitlePropertyKeyForValues(collection, values, entityId) {
	const candidates = getTitlePropertyCandidates(collection);
	if (!values || candidates.length === 0) return candidates[0];
	for (const key of candidates) {
		const value = values[key];
		if (value === void 0 || value === null || value === "") continue;
		if (typeof value === "string") {
			if (looksLikeIdentifierValue(value)) continue;
			if (entityId !== void 0 && value === String(entityId)) continue;
		}
		return key;
	}
	return candidates[0];
}
//#endregion
export { AIIcon, AIModifiedIndicator, AdminModeControllerContext, AdminModeControllerProvider, AnalyticsContext, ApiConfigProvider, AuthControllerContext, CONTAINER_FULL_WIDTH, CollectionComponentOverrideProvider, CollectionResolverRegistrationContext, CollectionScopeContext, CollectionScopeProvider, ComponentOverrideContext, ConfirmationDialog, CustomizationControllerContext, DEFAULT_API_PATH, DEFAULT_PAGE_SIZE, DataDriverContext, DataSourcesContext, DialogsControllerContext, DialogsProvider, EffectiveRoleControllerContext, EffectiveRoleControllerProvider, EntityDisplayCache, ErrorTooltip, ErrorView, FORM_CONTAINER_WIDTH, GlobalComponentOverrideProvider, IconForView, LanguageToggle, LoginView, ModeControllerContext, ModeControllerProvider, NavigationBlockerProvider, NotFoundPage, PluginProviderStack, REBASE_LOCALE_STORAGE_KEY, Rebase, RebaseAuth, RebaseClientInstanceContext, RebaseDataContext, RebaseI18nProvider, RebaseLogo, RebaseRegistryProvider, RebaseRouter, RebaseRoutes, SIDE_PANEL_DEFAULT_WIDTH, STUDIO_NAVIGATION_GROUPS, SUMMARY_RANK, SchemaDriftBanner, SchemaDriftProvider, SnackbarProvider, StorageSourceContext, StorageSourcesContext, StudioBridgeContext, StudioBridgeProvider, StudioBridgeRegistryContext, StudioBridgeRegistryProvider, UnsavedChangesDialog, UserConfigurationPersistenceContext, UserDisplay, UserSelectPopover, UserSettingsView, addInitialSlash, apiBaseOf, applyPropertyConditions, buildCollapsedDefaults, buildEnumLabel, canSummariseProperty, clearAuthConfigCache, clearFetchCache, createAuthConfigCache, createFormexStub, deleteEntityWithCallbacks, deriveSpan, en, entityDisplayKey, es, fetchAuthConfig, fillRows, flattenKeys, getAdminEntityChildViews, getAdminSubcollections, getCollectionBySlugWithin, getCollectionPathsCombinations, getColorScheme, getColumnKeysForProperty, getDisplayPropertyKey, getDisplayResolver, getEntityFromCache, getEntityFromMemoryCache, getEntityImagePreviewPropertyKey, getEntityPreviewKeys, getEntityTitlePropertyKey, getFormFieldKeys, getIcon, getLastSegment, getLeadingRelationTitleKey, getLocalChangesBackup, getNavigationEntriesFromPath, getParentReferencesFromPath, getPropertyInPath, getRedundantChildViewColumnIds, getRelationIncludeParams, getRowHeight, getSubcollectionColumnId, getTitlePropertyCandidates, getTitlePropertyKey, getTitlePropertyKeyForValues, hasDeclaredDisplay, iconsSearch, isAuditTimestamp, isDisabled, isEnumValueDisabled, isFilterableRelation, isHidden, isIdPropertyEditable, isLongTextProperty, isReadOnly, isSchemaDriftError, isStorageProperty, looksLikeIdentifierValue, populateFetchCache, rankSummaryProperty, readStoredString, removeEntityFromCache, removeEntityFromMemoryCache, removeInitialAndTrailingSlashes, removeInitialSlash, removeStoredString, removeTrailingSlash, resolveCollectionPathIds, resolveComponentRef, resolveDefaultSelectedView, resolveFilterOperators, resolveFormLayout, saveEntityToCache, saveEntityToMemoryCache, saveEntityWithCallbacks, useAdminModeController, useAnalyticsController, useApiBase, useApiConfig, useAuthController, useAuthSubscription, useBackendStorageSource, useBridgeRegistration, useBrowserTitleAndIcon, useBuildAdminModeController, useBuildEffectiveRoleController, useBuildLocalConfigurationPersistence, useBuildModeController, useClipboard, useCollapsedGroups, useCollection, useCollectionScope, useColumnIds, useComponentOverride, useCustomizationController, useData, useDataSources, useDataTableController, useDebouncedData, useDialogsController, useEffectiveRoleController, useFetch, useLargeLayout, useModeController, useNavigationBlocker, useNavigationGroupLabel, usePermissions, useRebaseAuthController, useRebaseClient, useRebaseContext, useRebaseRegistry, useRebaseRegistryDispatch, useRelationSelector, useResolvedComponent, useRestoreScroll, useSchemaDriftContext, useScrollRestoration, useSlot, useSnackbarController, useStorageSource, useStorageSources, useStorageUploadController, useStudioBreadcrumbs, useStudioCapabilities, useStudioCollectionRegistry, useStudioNavigationState, useStudioSchemaEditing, useStudioSidePanelController, useStudioUrlController, useTranslation, useUnsavedChangesDialog, useUserConfigurationPersistence, writeStoredString };

//# sourceMappingURL=index.es.js.map